import DOMPurify from 'dompurify';
import { Decimal } from 'decimal.js';
import { round } from 'mathjs';
import { safeSanitize } from '../../utils/safe_sanitize';
import {
  formatWordListToken,
  normalizeListFormat,
  resolveListFormat,
} from '../../utils/list_counter_format';
import { awaitDocumentSettled } from '../../managers/document_settle';
import { buildTextIndex } from '../../managers/document_text_index';
import { validateModelOutputAndRouteProposals } from '../../managers/document_patch_compiler';
import {
  buildGeneratedBlockDeletion,
  collectRequestElements,
  createGeneratedAnnotations,
  decorateBlockDeletionMarker,
  integrateGeneratedDeleteBlocks,
  integrateGeneratedInsertBlocks,
  integrateGeneratedRedline,
  integrateGeneratedTableRows,
  mergeDocumentProposalRejectionNotes,
  nextDiffIndex,
  coalesceCounterpartyReviews,
  counterpartyCommentCard,
  counterpartyUnavailableNotice,
  changeExcerpt,
  reviewMarkerSectionIds,
} from '../../managers/document_redline_integration';
import {
  buildGeneratedBlockInsertion,
  buildVersionControlListItem,
} from '../../managers/version_control_list_item';
import {
  parseOrderedListitem,
  persistAcceptedTableRow,
  rebuildPersistedTableRows,
  serializeOrderedListitem,
} from '../../managers/table_row_persistence';
import {
  confirmExitWithUnresolvedChanges,
  countUnresolvedChanges,
  setReviewItemStatus,
} from '../../managers/version_control_review_state';
import {
  normalizeTenantPartialMarkers,
} from '../../managers/version_control_partial_markers';
import {
  advisoryChangesAsNotes,
  buildNotePlacements,
} from '../../managers/document_note_placement';
import {
  anchorBlockForCaret,
  contentToInsert,
  INSERT_REFUSALS,
  removeGeneratedRequest,
} from '../../managers/document_insert_here';
import { validateCounterpartyCommentResponse } from '../../managers/counterparty_comment_validation';
import { gateCounterpartyCommentVerdicts } from '../../managers/counterparty_comment_verdict';
import { logAutopilotPayload } from '../../utils/autopilot_debug';
import { enrichProposalsWithClausebook } from '../../managers/clausebook_flow';
// TEMPORARY, with `PolicyFeatures`.
import { policyFeaturesEnabled } from '../../utils/policy_features';

/* global moment, stateAbbreviationToName, toTitleCase, toWords, isVowel, toRoman, fromRoman, isLowercaseLetter,
isUppercaseLetter, lettersToNumber, numberToLettersIncrement, pad, rangy, _, html2pdf */

// typedef for documentation
/**
 * LeaseField: A string representing a field in the lease, e.g. "lease.tenantInfo.name".
 *
 * @typedef { round } LeaseField
 */


window.isDebug = document.location.search.indexOf('debug') !== -1;

// Azure OpenAI Responses API. Reasoning models emit `reasoning` items before the
// assistant message, so the answer is not always the first item of `output`.
// Mirrors `responses_api_output_text` in api/services_controller.rb.
function responsesApiOutputText(responseData) {
  const output = (responseData && responseData.output) || [];
  const message = output.find((item) => item && item.type === 'message');
  if (!message) {
    throw new Error('response is missing a message item');
  }

  const content = Array.isArray(message.content) ? message.content : [];
  const textItem = content.find((item) => item && item.type === 'output_text');
  const text = textItem && textItem.text;
  if (!text || !text.trim()) {
    throw new Error('response is missing output_text');
  }

  return text;
}

angular.module('LeasePilot').controller('LeaseEditorController', [
  '$timeout',
  '$scope',
  '$compile',
  '$parse',
  '$filter',
  '$rootScope',
  '$templateCache',
  '$templateRequest',
  '$cookies',
  '$mdDialog',
  '$window',
  '$http',
  '$q',
  'DocumentService',
  'BuildingService',
  'CompanyService',
  'FreeTextService',
  'AllOverrideService',
  'LeaseEditorService',
  'ProcessStatusService',
  'AttachmentService',
  'RolesService',
  '$mdToast',
  'FreeTextsBulkService',
  'VersionControlService',
  'DownloadService',
  'CleanFreeTextService',
  'ApiService',
  'LeaseVarService',
  'LabelsService',
  'DealTermsImportService',
  'AiServicesConsentService',
  function(
    $timeout,
    $scope,
    $compile,
    $parse,
    $filter,
    $rootScope,
    $templateCache,
    $templateRequest,
    $cookies,
    $mdDialog,
    $window,
    $http,
    $q,
    DocumentService,
    BuildingService,
    CompanyService,
    FreeTextService,
    AllOverrideService,
    LeaseEditorService,
    ProcessStatusService,
    AttachmentService,
    RolesService,
    $mdToast,
    FreeTextsBulkService,
    VersionControlService,
    DownloadService,
    CleanFreeTextService,
    ApiService,
    LeaseVarService,
    LabelsService,
    DealTermsImportService,
    AiServicesConsentService,
  ) {
    $rootScope.forms = window.forms;
    $rootScope.buildings = window.buildings;
    
    window.sources = {
      forms: window.forms,
      buildings: window.buildings,
    }
    
    if ($window.lease) {
      $scope.lease = $window.lease;

      if ($scope.lease.building) {
        $scope.building = $scope.lease.building;
        $scope.building.templateUrl ||= "blank.html";
        $scope.building.specificUrl ||= $scope.lease.company.name.toLowerCase() + "/" + $scope.building.templateUrl.toLowerCase();
        $scope.building.displayName ||= $scope.building.name;
        $scope.building.dashboardName ||= $scope.building.displayName;
      }
    } else {
      $scope.lease = null;
    }

    window.scopeVar = $scope;
    
    $scope.isAdmin = window.isAdmin;
    $scope.$filter = $filter;
    $scope.Math = window.Math;
    $scope.moment = moment;
    $scope.parentLeaseObject = null;
    $scope.selectedItem = {
      name: '',
    };
    $scope.sectionNumberMap = {
      article21: {},
    };
    $scope.btns = {
      bold: {
        label: 'B',
        state: false,
        tooltip: 'Bold',
        command: 'bold',
      },
      italic: {
        label: 'I',
        state: false,
        tooltip: 'Italic',
        command: 'italic',
      },
      underline: {
        label: 'U',
        state: false,
        tooltip: 'Underline',
        command: 'underline',
      },
    };
    // TODO - remove when old design is not relevant anymore
    $scope.datePickerOptions = {
      datepickerMode: 'month',
      minMode: 'month',
      showWeeks: 'false',
    };

    $scope.showConceptEditWarning = false;
    $scope.editingWithConceptTitle = false;
    $scope.showEmptyConceptTitle = false;
    $scope.leftPane = 'deal-terms';

    // The comments sidebar has three appenders: the tenant-import flow
    // (generateSidebar), the generated-redline flow
    // (prepareDocumentChangeProposals) and version_control_manager's
    // enterVCView. Only the first two can compile — enterVCView has no
    // $compile — so "a <comments> already exists" is not the same question as
    // "the sidebar is live". Bailing on a bare element left by enterVCView
    // would suppress the sidebar for the rest of the session, so adopt and
    // compile it instead, and mark it so a later appender neither duplicates
    // the element nor compiles a second directive onto it.
    function ensureCommentsSidebar() {
      const container = document.querySelector('.lease.lease--modified');
      if (!container) return null;

      let comments = container.querySelector('comments');
      if (comments && comments.hasAttribute('data-compiled')) return comments;

      if (!comments) {
        comments = document.createElement('comments');
        container.appendChild(comments);
      }

      comments.setAttribute('data-compiled', '');
      $compile(comments)($scope);

      return comments;
    }

    // Resolves the concept list, or gives up on it. Phase two is faster with
    // one, and correct without one — so it waits, but never past the point
    // where reading the document whole would have been quicker. The request it
    // feeds carries a twenty-minute ceiling, and an optimisation must not
    // inherit that.
    function conceptsWithin(pending, waitMs) {
      return $q
        .race([
          $q.when(pending || []),
          $timeout(function() {
            console.warn(
              '[documentProposals] concept list did not arrive in time; ' +
                'reading the lease with one reader',
            );
            return [];
          }, waitMs),
        ])
        .catch(function() {
          return [];
        });
    }

    const CONCEPT_LIST_WAIT_MS = 45 * 1000;

    // Long enough to say "still working", not to give up.
    //
    // This used to be a 45-second ceiling raced against the request, and the
    // race is what made it dangerous: it resolved null, the loaders came down,
    // and nothing consumed the real answer when it arrived. A full-document
    // import takes minutes, so the whole reading was paid for and discarded —
    // the drafter watched the spinners vanish while the request was still in
    // flight in the network panel, and no card ever appeared.
    //
    // Now the wait is only a message, and the answer applies whenever it lands.
    const COUNTERPARTY_COMMENT_SLOW_MS = 45 * 1000;
    // A leak guard, not a deadline. It takes the loaders down so the panel is
    // not left spinning on a request that died in transit — but it does not
    // cancel anything, so an answer arriving after it still renders.
    const COUNTERPARTY_COMMENT_GIVE_UP_MS = 6 * 60 * 1000;

    // TEMPORARY, with `PolicyFeatures`: the first clause. Not redundant with the
    // serializer forcing the company flag false. This is the only guard in front
    // of `counterpartyUnavailableNotice`, whose `no_declared_positions` copy
    // names the playbook to a drafter who is not supposed to know it exists.
    function counterpartyCommentReviewEnabled() {
      if (!policyFeaturesEnabled()) return false;

      const company = ($scope.lease && $scope.lease.company) || {};
      return company.counterpartyCommentReviewEnabled !== false;
    }

    function counterpartyCommentReviewWithin(pending) {
      const slow = $timeout(function() {
        $scope.counterpartyCommentSlow = true;
      }, COUNTERPARTY_COMMENT_SLOW_MS);

      const giveUp = $timeout(function() {
        $scope.counterpartyCommentReviewPending = false;
        $scope.counterpartyCommentSlow = false;
        $scope.counterpartyCommentUnavailable = counterpartyUnavailableNotice(
          'took_too_long',
        );
        clearCounterpartyCommentCards();
      }, COUNTERPARTY_COMMENT_GIVE_UP_MS);

      // Not a race. The request is what settles this chain, so a late answer
      // still reaches `applyCounterpartyCommentReviews` and still replaces
      // whatever the give-up timer put on screen.
      return $q.when(pending).finally(function() {
        $timeout.cancel(slow);
        $timeout.cancel(giveUp);
        $scope.counterpartyCommentSlow = false;
      });
    }

    // design §5: a change touching an ⟦f…⟧ atom is a deal-term change, not a
    // text change. `_isAffectingLeaseVar` is that signal, and it stands on its
    // own — the card names the field whether or not Adjust Manually can reach
    // it.
    //
    // The element search deliberately mirrors comments_directive's
    // `findLeaseVar`, which is what Adjust Manually itself uses: the same node
    // set, the same `closest` (never a descendant), and the same
    // `non-click="true"` exclusion. Anything looser lets the card offer a
    // button that hands `searchByLeaseVarElement` an undefined element.
    function counterpartyCommentDealTerm(item) {
      if (!item || !item._isAffectingLeaseVar) return null;

      const markers = document.querySelectorAll(
        `.lease.lease--modified .diff-item[diff-index="${item._index}"]`,
      );
      let leaseVar = null;
      for (const marker of markers) {
        const found = marker.closest(
          '.transformed--lease-var:not([non-click="true"])',
        );
        if (found) {
          leaseVar = found;
          break;
        }
      }

      if (!leaseVar) return { field: null, label: null, adjustable: false };

      const rawField = leaseVar.getAttribute('var');
      // LabelsService keys its maps on the full path — `lease.additionalRentType`,
      // not `additionalRentType` — and normalize() strips only the index
      // subscript. Stripping the prefix as well misses every entry and silently
      // falls back to the raw path.
      const labelKey = LabelsService.normalize(String(rawField || ''));

      return {
        field: rawField,
        label:
          ($rootScope.labels && $rootScope.labels[labelKey]) ||
          ($rootScope.fullLabels && $rootScope.fullLabels[labelKey]) ||
          null,
        adjustable: true,
      };
    }

    // One "still running" line for the annotation, not one per change.
    //
    // While the reading is pending every card says exactly the same sentence, so
    // an annotation holding three changes rendered that sentence three times in
    // three bordered boxes — repetition that says nothing the first one did not.
    // Stacking is worth it once the cards carry effects, a rationale and an
    // excerpt to tell them apart; until then they are indistinguishable by
    // construction.
    //
    // Split here rather than filtered in the template: `ng-repeat` over a filter
    // expression builds a new array every digest, which is how this screen's
    // sibling aborted its digest and half-rendered itself.
    function syncCounterpartyCardViews(annotation) {
      if (!annotation) return;

      const cards = annotation._counterpartyCommentCards || [];
      annotation._counterpartyReadCards = cards.filter(function(entry) {
        return entry.card.state !== 'enrichment_pending';
      });
      annotation._counterpartyPendingCount =
        cards.length - annotation._counterpartyReadCards.length;
    }

    function clearCounterpartyCommentCards() {
      ($scope.diffgram.items || []).forEach(function(item) {
        if (
          item &&
          item._counterpartyCommentCard &&
          item._counterpartyCommentCard.state === 'enrichment_pending'
        ) {
          if (item._annotation) {
            item._annotation._counterpartyCommentCards = (
              item._annotation._counterpartyCommentCards || []
            ).filter(function(entry) {
              return (
                entry.card.diffIndex !== item._counterpartyCommentCard.diffIndex
              );
            });
            syncCounterpartyCardViews(item._annotation);
          }
          delete item._counterpartyCommentCard;
        }
      });
    }

    function setCounterpartyCommentCard(item, card) {
      item._counterpartyCommentCard = card;
      if (!item._annotation) return;

      const otherCards = (
        item._annotation._counterpartyCommentCards || []
      ).filter(function(entry) {
        return entry.card.diffIndex !== card.diffIndex;
      });
      // The excerpt is what makes stacked cards distinguishable, so it is
      // resolved once here rather than per digest — `item.Html` does not change
      // for the life of the card, and the alternative is a function binding
      // inside an ng-repeat that re-parses HTML on every keystroke.
      otherCards.push({
        card: card,
        request: item,
        excerpt: changeExcerpt(item.Html),
      });
      otherCards.sort(function(left, right) {
        return left.card.diffIndex - right.card.diffIndex;
      });
      // Which boxes start open. One card in the annotation is not a stack and
      // has nothing to collapse; beyond that only the card carrying advice
      // opens itself, because the rest are a reading the drafter can take from
      // the summary line. Resolved here rather than bound as an expression so
      // it does not re-evaluate for every card on every digest.
      otherCards.forEach(function(entry) {
        entry.expanded =
          otherCards.length === 1 || entry.card.state === 'recommendation';
      });
      item._annotation._counterpartyCommentCards = otherCards;
      syncCounterpartyCardViews(item._annotation);
    }

    function showCounterpartyCommentPending() {
      // The enrichment can land before this $timeout does; a loader that
      // overwrote a finished card would be a state regression, not a delay.
      if (!$scope.counterpartyCommentReviewPending) return;

      ($scope.diffgram.items || []).forEach(function(item, diffIndex) {
        if (item && item._status === 'unresolved' && item._annotation) {
          setCounterpartyCommentCard(
            item,
            counterpartyCommentCard({
              diffIndex: diffIndex,
              pending: true,
            }),
          );
        }
      });
    }

    function applyCounterpartyCommentReviews(response) {
      if (!response) return;

      const data = response.data || {};
      // A refusal, not a failure. The server declined to spend a request whose
      // every answer would have been a description; say so where the reviews
      // would have been.
      if (data.success === false) {
        $scope.counterpartyCommentUnavailable = counterpartyUnavailableNotice(
          data.reason,
        );
        $scope.counterpartyCommentReviewPending = false;
        clearCounterpartyCommentCards();
        return;
      }
      $scope.counterpartyCommentUnavailable = null;
      const gatedReviews = gateCounterpartyCommentVerdicts(data.reviews, {
        ownerSide: data.ownerSide,
        playbookConcepts: data.playbookConcepts,
      });
      const validated = validateCounterpartyCommentResponse(
        gatedReviews,
        $scope.diffgram.items,
        data.vocabulary,
      );

      // After validation, so the merge never joins a review the validator was
      // about to throw away, and before card creation, so one edit is one card.
      const reviews = coalesceCounterpartyReviews(
        validated.reviews,
        $scope.diffgram.items,
      );

      $scope.counterpartyCommentReviews = reviews;
      $scope.counterpartyCommentReviewRejected = validated.rejected;
      clearCounterpartyCommentCards();
      reviews.forEach(function(review) {
        const item = $scope.diffgram.items[review.diffIndex];
        const recommendationContext =
          data.recommendationContexts &&
          data.recommendationContexts[review.concept];
        item._counterpartyCommentReview = review;
        setCounterpartyCommentCard(
          item,
          counterpartyCommentCard({
            review: review,
            dealTerm: counterpartyCommentDealTerm(item),
            recommendationContext: recommendationContext,
          }),
        );
      });
      $scope.counterpartyCommentReviewPending = false;
      $rootScope.$broadcast('counterpartyComments:reviewed', validated);
    }

    function reviewCounterpartyComments(index) {
      if (!counterpartyCommentReviewEnabled() || !index || !index.text) {
        return;
      }

      $scope.counterpartyCommentReviewPending = true;
      // Cleared on every attempt, so a notice from a previous import does not
      // outlive the setup change that fixed it.
      $scope.counterpartyCommentUnavailable = null;
      $scope.counterpartyCommentSlow = false;
      // The import path calls in from a bare setTimeout, so no digest is
      // running here. Both scope mutations re-enter one through $timeout: this
      // one to raise the loaders, and applyCounterpartyCommentReviews below to
      // replace them.
      $timeout(showCounterpartyCommentPending);

      // Fire-and-forget from the import path. The model request is resolved in
      // its own promise-chain link.
      AiServicesConsentService.ensureAiServicesConsent()
        .then(function() {
          return counterpartyCommentReviewWithin(
            ApiService.reviewCounterpartyComments({
              documentId: $scope.lease.id,
              projection: index.text,
              documentType: $scope.lease.type,
            }),
          );
        })
        .then(function(response) {
          if (!response) {
            $scope.counterpartyCommentReviewPending = false;
            clearCounterpartyCommentCards();
            return;
          }

          return $timeout(function() {
            applyCounterpartyCommentReviews(response);
          });
        })
        .catch(function(error) {
          $scope.counterpartyCommentReviewPending = false;
          clearCounterpartyCommentCards();
          if (error) {
            console.warn(
              '[counterpartyComments] enrichment unavailable; continuing without commentary',
            );
          }
        });
    }

    function buildCounterpartyCommentIndex() {
      const root = document.querySelector('.lease.lease--original');
      if (!root) return null;

      try {
        return buildTextIndex(root);
      } catch (error) {
        console.warn(
          '[counterpartyComments] projection unavailable; continuing without commentary',
        );
        return null;
      }
    }

    $scope.prepareDocumentChangeProposals = function(applyPromise, context) {
      context = context || {};
      const root = document.querySelector('.lease.lease--original');
      // Stage timings for the second pass. "It takes three minutes and feels
      // slow" could not be acted on because nothing here was measured — the
      // server logs token counts, and the client logged nothing at all. These
      // say whether the wall clock is the model calls, the settle barrier, or
      // building the projection over a large lease.
      const startedAt = performance.now();
      const timings = {};
      const mark = name => {
        timings[name] = Math.round(performance.now() - startedAt);
      };

      $rootScope.$broadcast('documentProposals:phaseChanged', {
        phase: 'settling',
      });

      let settledRoot = null;
      return awaitDocumentSettled(applyPromise, {
        $rootScope,
        $timeout,
        root,
        scope: $scope,
      }).then(function(settled) {
        settledRoot = settled;
        mark('settledAtMs');
        // The concept list was drawn while the drafter reviewed phase one's
        // fields, so it is normally long resolved by now — but they can click
        // through faster than it returns, so it is waited for rather than
        // assumed.
        //
        // Waited for HERE, in its own link of the chain, and not inside the
        // $timeout below. An `await` in that callback hands control back in a
        // microtask outside the digest, and the broadcast that follows is the
        // exact thing the next comment says must not happen there.
        return conceptsWithin(context.concepts, CONCEPT_LIST_WAIT_MS);
      }).then(function(concepts) {
        mark('conceptsReadyAtMs');
        // awaitDocumentSettled resolves a native promise, so this callback runs
        // in a microtask AFTER the digest that ended settlement — $$phase is
        // null and nothing is scheduled. Broadcasting from here would mutate the
        // import card's isolate scope with no digest to re-evaluate its ng-if,
        // leaving the user stuck on "Preparing phase 2". Re-enter through
        // $timeout so the phase-two panel actually renders.
        return $timeout(function() {
          // Projection construction must remain downstream of the complete
          // settlement barrier. P8-04 consumes this event to request proposals.
          const index = buildTextIndex(settledRoot);
          mark('projectionBuiltAtMs');
          $scope.secondPassDocumentIndex = index;
          $rootScope.$broadcast('documentProposals:projectionReady', {
            index,
            concepts,
          });
          return ApiService.proposeDocumentChanges({
            documentId: $scope.lease.id,
            projection: index.text,
            sourceText: context.sourceText,
            notes: context.notes,
            fieldConstraints: context.fieldConstraints,
            concepts,
          }).then(async function(response) {
            mark('proposalsReceivedAtMs');
            logAutopilotPayload('proposals', response.data);
            const modelOutput = await enrichProposalsWithClausebook({
              proposals: response.data.proposals,
              concepts,
              documentId: $scope.lease.id,
              api: ApiService,
            });
            const serverNotes = response.data.notes;
            // Subjects no reader covered because its call failed. Carried to
            // the panel rather than into the lease, because a run that read
            // three quarters of the deal looks exactly like a document that
            // only mentioned three quarters of it.
            const readerFailures = response.data.readerFailures || [];
            const baseIndex = nextDiffIndex($scope.diffgram.items);
            const validated = validateModelOutputAndRouteProposals(
              index,
              modelOutput,
              {
                'data-provenance': 'document-proposal',
                'diff-index': baseIndex,
              },
            );
            const proposals = validated.proposals;
            const routed = validated.routed;
            const integration = integrateGeneratedRedline(
              $scope.diffgram,
              routed,
              proposals,
              {
                baseIndex,
                compileMarkers: markers => $compile(markers)($scope),
                sourceDocument: context.sourceDocument,
              },
            );
            const insertedBlocks = await integrateGeneratedInsertBlocks(
              $scope.diffgram,
              routed,
              {
                baseIndex: nextDiffIndex($scope.diffgram.items),
                insertBlockListItem: insertGeneratedBlockListItem,
                compileMarkers: markers => $compile(markers)($scope),
                sourceDocument: context.sourceDocument,
              },
            );
            const deletedBlocks = integrateGeneratedDeleteBlocks(
              $scope.diffgram,
              routed,
              {
                baseIndex: nextDiffIndex($scope.diffgram.items),
                deleteBlockItem: deleteGeneratedBlockItem,
                compileMarkers: markers => $compile(markers)($scope),
                sourceDocument: context.sourceDocument,
              },
            );
            const tableRows = integrateGeneratedTableRows(
              $scope.diffgram,
              routed,
              {
                baseIndex: nextDiffIndex($scope.diffgram.items),
                getNextFreeTextValue: () => $scope.getNextFreeTextValue(),
                getNewPid: getNewVersionControlPid,
                compileMarkers: markers => $compile(markers)($scope),
                sourceDocument: context.sourceDocument,
              },
            );
            // Every refusal becomes a suggestion in the lease body rather than
            // an entry in a side list. A refusal means this system could not
            // make the edit; it does not mean the deal term stopped applying,
            // and a drafter reading the lease is the person who has to act on
            // it. Placement runs before annotations so a note is reviewed
            // through the same card, accept and reject path as every other
            // suggestion.
            DealTermsImportService.queueFieldConflicts(routed.fieldChanges);
            const rejected = mergeDocumentProposalRejectionNotes(
              [
                ...validated.rejected,
                ...integration.rejected,
                ...insertedBlocks.rejected,
                ...deletedBlocks.rejected,
                ...tableRows.rejected,
              ],
              serverNotes,
            );
            const advisoryNotes = advisoryChangesAsNotes(
              DealTermsImportService.advisoryChanges,
            );
            DealTermsImportService.advisoryChanges = [];
            const placement = buildNotePlacements(rejected.concat(advisoryNotes), index, {
              root: settledRoot,
            });
            const placedNotes = await integrateGeneratedInsertBlocks(
              $scope.diffgram,
              { blockInsertions: placement.placements },
              {
                baseIndex: nextDiffIndex($scope.diffgram.items),
                insertBlockListItem: insertGeneratedBlockListItem,
                compileMarkers: markers => $compile(markers)($scope),
                sourceDocument: context.sourceDocument,
              },
            );
            if (placedNotes.rejected.length > 0) {
              console.warn(
                `[documentProposals] ${placedNotes.rejected.length} drafting note(s) had no paragraph style ` +
                  'in this form that could carry them and were not placed:',
                placedNotes.rejected,
              );
            }

            $scope.annotations = $scope.annotations || {};
            const generatedActual = createGeneratedAnnotations(
              $scope.diffgram,
              $scope.annotations,
              {
                setDiffgramItemActions: item =>
                  $scope.annotationsHelper.setDiffgramItemActions(item),
              },
            );
            $scope.diffgram.actual = [
              ...($scope.diffgram.actual || []),
              ...generatedActual,
            ];
            $scope.diffgram.resolved = [];

            if (generatedActual.length > 0) {
              $scope.diffgram.on = true;
              $scope.leftPane = null;
              const preview = document.querySelector('.preview');
              if (preview) preview.classList.add('review-changes');

              ensureCommentsSidebar();
            }
            // Carry how many changes actually reached the document. Zero is a
            // legitimate outcome — every proposal can fail grounding or fail to
            // locate — but the panel must be able to say so, rather than telling
            // a drafter to review changes that were never created.
            mark('doneAtMs');
            // Cumulative marks, so each stage's own cost is the gap to the
            // previous one. Printed as a table because the useful reading is
            // the relative size of the gaps, not any single number.
            console.info('[documentProposals] stage timings (ms from start)', {
              ...timings,
              // Which rung of the placement ladder each note landed on. A run
              // where everything is 'document-end' placed nothing well, which
              // is invisible from the document itself.
              notePlacement: placement.rungCounts,
              settleMs: timings.settledAtMs,
              projectionMs: timings.projectionBuiltAtMs - timings.settledAtMs,
              modelCallMs:
                timings.proposalsReceivedAtMs - timings.projectionBuiltAtMs,
              integrateMs: timings.doneAtMs - timings.proposalsReceivedAtMs,
              projectionCharacters: index.text.length,
              conceptCount: concepts.length,
              proposalCount: modelOutput ? modelOutput.length : 0,
              cardCount: generatedActual.length,
            });
            $rootScope.$broadcast('documentProposals:phaseChanged', {
              phase: 'changes_proposed',
              appliedCount: generatedActual.length,
              rejectedCount: rejected.length,
              noteCount: placedNotes.markers.length,
              readerFailures,
              timings,
            });
            return index;
          });
        });
      });
    };

    $scope.toggleLeftPane = (pane) => {
      // TEMPORARY, with `PolicyFeatures`. `showChecks()` in the share modal and
      // the download notice both come through here, and off `develop` the panel
      // they ask for is not in the DOM — so this would open an empty pane.
      if (pane === 'checks' && !policyFeaturesEnabled()) return;

      if (pane === $scope.leftPane) {
        $scope.leftPane = null;
      } else {
        if (pane === 'clausebook') {
          const clausebook = document.querySelector('#clausebook');

          if  (clausebook.getAttribute("data-src")) {
            clausebook.setAttribute("src", clausebook.getAttribute("data-src"));
            clausebook.removeAttribute("data-src");
          }
        } else if (pane === 'autopilot') {
          const autopilot = document.querySelector('#autopilot');

          if  (autopilot.getAttribute("data-src")) {
            autopilot.setAttribute("src", autopilot.getAttribute("data-src"));
            autopilot.removeAttribute("data-src");
          }

          autopilotManager.updateDocumentContext();
        }

        $scope.leftPane = pane;
      }
    }

    function getConceptDisplayTitle(conceptTitleElement){
      let name = conceptTitleElement.getAttribute('name');

      if (!name || name.length ===0) {
        const concept = conceptTitleElement.closest('concept');
        name = concept.getAttribute('var');
      }

      if(name && name.length !== 0 && name.indexOf('.') !== -1 ){
        name = name.split('.')[1];
      }

      if(name){
        name = _.startCase(name);
        return `The marked text is part of the "${name}" abstract field`;
      }
      else{
        return "";
      }
    }

    $scope.toggleShowAbstractedFields = function() {
      $scope.showEmptyConceptTitle = !$scope.showEmptyConceptTitle;

      if ($scope.showEmptyConceptTitle) {
        window.track.event(
          new ShowConceptTitlesEvent({
            context: $rootScope.getContext()
          })
        );
      } else {
        window.track.event(
          new HideConceptTitlesEvent({
            context: $rootScope.getContext()
          })
        );
      }

      document.querySelectorAll("concept-title").forEach(el => {
        if (!$scope.showEmptyConceptTitle) {
          el.removeAttribute("title");
        } else {
          el.setAttribute("title", getConceptDisplayTitle(el));
        }
      });
    };

    window.cachedBlocks = {};
    function cacheBlocks() {      
      const selection = rangy.getSelection();

      // Store a copy of all the blocks in the selection for comparison purposes
      if (LeaseEditorService.isSelecting()) {
        const selectedBlocks = new Set(); // Use Set to ensure uniqueness
        const freeTexts = LeaseEditorService.findAllContentEditableInSelection();
        
        freeTexts.forEach(node => {
          if (node.get) {
            node = node.get(0);
          }

          if (node.getBlockData) {
            const blockData = node.getBlockData();
            
            if (blockData && blockData.containingNode) {
              const block = blockData.containingNode;
              if (block.closest('.lease') && !block.closest('.lease--modified')) {
                selectedBlocks.add(block);
              }
            }
          }
        });

        // Convert Set to object using pid as key
        window.cachedBlocks = {};
        window.cachedBlocks = Array.from(selectedBlocks).reduce((acc, block) => {
          const pid = block.getAttribute('pid');
          if (pid) {
            if (!window.cachedBlocks[pid]) {
              acc[pid] = block.innerText;
            }
          }
          return acc;
        }, {});
      } else {
        // Get the block from the current position of the selection and cache that
        const focusNode = selection.focusNode;

        if (!focusNode) {
          return;
        }
        
        // If focusNode is a text node, get its parent
        const element = focusNode.nodeType === Node.TEXT_NODE ? focusNode.parentElement : focusNode;
        
        // Get the containing block for the current position
        const blockData = element.getBlockData();
        if (blockData && blockData.containingNode) {
          const block = blockData.containingNode;
          // Only include blocks that are in the lease and not modified
          if (block.closest('.lease') && !block.closest('.lease--modified')) {
            const pid = block.getAttribute('pid');
            if (pid) {
              if (!window.cachedBlocks[pid]) {
                window.cachedBlocks = {
                  [pid]: block.innerText
                };
              }
            }
          }
        }
      }
    }

    $scope.currentTopAnchors = Object.defineProperty(
      $scope,
      "currentTopAnchors",
      {
        get: function() {
          return $scope._currentTopAnchors;
        },
        set: function(value) {
          if (!value.getTopAnchor) {
            return;
          }

          setTimeout(() => {
            if($scope._currentTopAnchors && $scope._currentTopAnchors.length){
              $scope._currentTopAnchors.forEach((node)=>{
                node.classList.remove('in-edit');
                const tbl = node.closest('generate-table');
                  if(tbl){
                    tbl.classList.remove('in-edit');
                  }
              });
            }
            if (!LeaseEditorService.isSelecting()) {
              $scope._currentTopAnchors = [value];
            } else {
              if ($scope._currentTopAnchors) {
                if ($scope._currentTopAnchors.indexOf(value) === -1) {
                  let startNode = $scope._currentTopAnchors[0];
                  let endNode = value;
                  let currentBlockData = startNode.getBlockData();
  
                  if (value.tagName === 'TABLE') {
                    endNode = Array.from(value.querySelectorAll(`[pid="${value.getAttribute('pid')}"]`)).at(-1);
                  }
  
                  if (currentBlockData?.containingNode) {
                    while (currentBlockData?.containingNode && currentBlockData.containingNode != endNode) {
                      currentBlockData = currentBlockData.getNextVisibleBlock();
  
                      if (currentBlockData?.containingNode) {
                        let inTable = currentBlockData.containingNode.closest("table[pid]");
  
                        if (inTable) {
                          $scope._currentTopAnchors.push(inTable);
                        } else {
                          $scope._currentTopAnchors.push(currentBlockData.containingNode);
                        }
                      }
                    }
                  }
                }
              } else {
                $scope._currentTopAnchors = [value];
              }
            }
  
            if($scope._currentTopAnchors && $scope._currentTopAnchors.length){
              $scope._currentTopAnchors.forEach((node)=>{
                if(!$scope.lease.isLocked){
                  node.classList.add('in-edit');
                  const tbl = node.closest('generate-table');
                  if(tbl){
                    tbl.classList.add('in-edit');
                  }
                }
              });
            }
  
            if (window?.user?.company?.companyProfile?.conceptTitleProtection) {
              $scope.editingWithConceptTitle = document.querySelector('.in-edit concept-title',true) !== null;
            }
          });
        }
      }
    );

    $scope.currentlyEditedNode = Object.defineProperty(
      $scope,
      "currentlyEditedNode",
      {
        get: function() {
          return $scope._currentlyEditedNode;
        },
        set: function(value) {
          $scope._currentlyEditedNode = value;
          if (value && value.getTopAnchor) {
            $scope.currentTopAnchors = value.getTopAnchor();
          }
        }
      }
    );

    $scope.currentlyEditedNode = null;
    $scope.lists = [];
    $scope.subdocuments = [];
    $scope.currentlyChanging = '';
    $scope.changeIsBeingMade = false;
    $scope.highlightScrollIndex = 0;
    $scope.numberOfHighlighted = 0;
    $scope.allChangesSaved = [];
    $scope.inInitialLoad = false;
    $scope.isPrerender =
      navigator.userAgent.toString().indexOf('Prerender') >= 0;
    $scope.isConnected = true;
    $scope.isControlCharacters = false;
    $scope.isRuler = false;
    $scope.lastFormUpdate = null; //
    $scope.colorPalette = [
      '#fffd38',
      '#29fd2e',
      '#2dfffe',
      '#fd28fc',
      '#0b24fb',
      '#fc0d1b',
      '#020c7e',
      '#11807f',
      '#0f7f12',
      '#7f0f7e',
      '#7e0308',
      '#807f17',
      '#808080',
      '#c0c0c0',
      '#000000',
    ];

    window.isLeaseIfDeprecated = false;

    $rootScope.getContext = function() {
      let context = 'normal';

      if ($scope.diffgram.on) {
        context = 'version control';
      }

      return context;
    };

    



    window.pageLayersManager = new PageLayersManager($scope, $rootScope,LeaseEditorService,$q);
    $scope.addPageBreak = window.pageLayersManager.addPageBreak;
    downloadManager.init(
      $scope,
      $window,
      ProcessStatusService,
      $rootScope,
      LeaseEditorService,
      ApiService,
      DownloadService,
      CleanFreeTextService
    );
    clipboardManager.init(
      $scope,
      $compile,
      $q,
      LeaseEditorService,
      ApiService,
      $timeout
    );

    window.isTestEnv = window.isTestEnv || window._environment === 'test';
    if (window.isTestEnv) {
      $scope.isLoading = false;
    } else {
      $scope.isLoading = true;

      setTimeout(() => {
        if ($scope.isLoading) {
          // Something went wrong, lets remove the loader
          // TODO: In the future we might want to show some message to the user
          // or manually raise an error of some sort so we'll get notified about it
          $scope.safeApply(() => {
            $scope.isLoading = false;
          });
        }
      }, 60 * 1000);
    }

    function calcLastFormUpdate() {
      const date = $rootScope.lease.form.updatedAt;
      const dateStr = moment(date).format('MM/DD/YY HH:mm:ss');
      $scope.lastFormUpdate = dateStr;
    }

    // formatted values for lease
    $scope.formattedValues = {};

    // list of free texts of the lease
    $scope.leaseFreeTexts = [];

    $rootScope.adminMode = false;

    // default provisions before replacement by loaded FreeText
    $rootScope.defaultProvisions = {};

    $scope.editingTimeout = {};

    // download process indicator
    $scope.download = false;

    $scope.editingEnabled = true;
    $scope.showRevertChanges = false;
    $scope.addedDropListener = false;
    $scope.isFirstLeaseLoading = true;
    $scope.termsIncludingFreeRent = null;
    $scope.leaseLockLoaded = false;

    // global download error message
    $scope.downloadErrorMessage =
      'Oops... something went wrong with the download process, please wait a couple of seconds and try again.';

    // the most recently used freetext value
    $scope.lastFreeText = 1000000;
    // display the dropdown or not
    $scope.toggleListItemDropdown = function(outline) {
      if (outline) {
        $scope.currentOutline = { name: outline };
        $scope.updateAddListItems();

        $scope.safeApply(() => {
          setTimeout(() => {
            $("#insert").click();
            document.querySelector("#insert-list").parentElement.classList.add("open");

            let selectedOutline = null;
            if (selectedOutline = document.querySelector(".outline.selected")) {
              selectedOutline.classList.remove("selected");
            }
          });
        });
      }

      $scope.outlineToInsert = outline;
    };

    // cache for each list ID, the last used ID
    $scope.listsLastUsedIdCache = {};

    // in order to track list items and associate them with each other
    $scope.nextUnusedListItemId = 1;

    $scope.addItemDisabled = false;
    $scope.addOutlinesDisabled = false;
    $scope.changeListItemLevel = false;


    $scope.listLevels = [];

    $rootScope.lastMouseButtonClicked = undefined;

    $rootScope.tenantName = function() {
      return LeaseEditorService.getLeaseTenantName();
    };

    $rootScope.findAppScope = function() {
      return $scope;
    };

    $rootScope.getLeaseId = function() {
      return $rootScope.lease.id;
    };

    function getTermLength() {
      let totalTermLength = 0;

      if ($scope.lease.leaseYears) {
        totalTermLength += $scope.lease.leaseYears * 12;
      }
      if ($scope.lease.leaseMonths) {
        totalTermLength += $scope.lease.leaseMonths;
      }

      return totalTermLength;
    }

    function getAreaSize() {
      let areaSize = $scope.lease.premiseAreaSize;

      if ($scope.lease.premisesModType === 'expansion') {
        areaSize += $scope.lease.expansionPremiseAreaSize;
      } else if ($scope.lease.premisesModType === 'reduction') {
        areaSize = $scope.lease.reductionRemainingAreaSize;
      } else if ($scope.lease.premisesModType === 'relocation') {
        areaSize = $scope.lease.relocationPremisesAreaSize;
      }

      return areaSize;
    }

    function getFreeRentAreaSize() {
      let areaSize = $scope.lease.premiseAreaSize;

      if ($scope.lease.premisesModType === 'expansion') {
        if ($scope.lease.hasExtensionOfTerm) {
          if ($scope.lease.rentTableType === 'blended') {
            areaSize += $scope.lease.expansionPremiseAreaSize;
          } else if ($scope.lease.rentTableType === 'separate') {
            areaSize = $scope.lease.expansionPremiseAreaSize;
          }
        } else {
          areaSize = $scope.lease.expansionPremiseAreaSize;
        }
      } else if ($scope.lease.premisesModType === 'reduction') {
        areaSize = $scope.lease.reductionRemainingAreaSize;
      } else if ($scope.lease.premisesModType === 'relocation') {
        areaSize = $scope.lease.relocationPremisesAreaSize;
      }

      return areaSize;
    }


    $rootScope.getAmendmentId = function() {
      return $rootScope.lease && $rootScope.lease.type === 'Amendment'
        ? $rootScope.getLeaseId()
        : null;
    };

    $scope.goToSMLView = function(){
      leaseIfManager.toggleShowAllView();
    }

    $scope.downloadAbstract = async function(doc) {
      $scope.safeApply(() => {
        $scope.processing = true;
        ProcessStatusService.start("downloadAbstract");
      });

      const documentId = $scope.lease.id;
      const instructions = $scope.lease.form.customAbstractInstructions || "";
      const format = $scope.lease.form.customAbstractFormat || "";
      const documentType = $scope.lease.type.toLowerCase();
      const documentFlavor = $scope.lease.form.flavor.toLowerCase();

      const abstractResponse = await ApiService.abstract({ 
        doc, 
        instructions,
        format, 
        documentType, 
        documentFlavor
      });

      if ($scope.lease.form.abstractFormId) {
        const content = responsesApiOutputText(abstractResponse.data).replace("```json", "").replace("```", "");
        window.abstractData = JSON.parse(content);

        ApiService.saveAbstract({
          documentId,
          json: content,
        });

        const iframe = angular.element('<iframe/>', {
          id: 'download-abstract',
          name: 'download-abstract',
          style:
            'position: absolute; top: 0; left: 0; width: 1px; height: 1px; z-index: -1; opacity: 0;',
          'event-name': "downloadAbstract",
        });

        angular
          .element('iframe[event-name="downloadAbstract"]')
          .remove();

        const iframeSrc = '/abstracts/' + $scope.lease.id;
        iframe.attr('src', iframeSrc);
        angular.element('body').append(iframe);
  
        $scope.$on("downloadAbstract", function(event, data) {
          console.log(new Date().getTime() + ' on event: ', "downloadAbstract");
          console.log(new Date().getTime() + ' data ', data);
          ApiService.htmlToWord(data.file, window.ASSETS_DESIRED_STYLES).then(
            function successCallback(response) {
              $scope.safeApply(() => {
                $scope.processing = false;
                ProcessStatusService.end('downloadAbstract');
              });
    
              const fileType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
              const fileName = `${LeaseEditorService.getFileName().replace(".docx", "")} - Abstract.docx`;

              ApiService.saveAbstract({
                documentId,
                docx: new File([response.data], fileName, {
                  type: fileType
                })
              });

              DownloadService.download(response.data, fileType, fileName);
            },
            function errorCallback(response) {
              $scope.safeApply(() => {
                $scope.processing = false;
                ProcessStatusService.end('downloadAbstract');
              });
              console.log(response);
            }
          );
        });
      } else {
        const converter = new showdown.Converter();
        const container = document.createElement("div");
        const paragraphs = responsesApiOutputText(abstractResponse.data).split("\n");
        paragraphs.forEach((paragraph) => {
          if (paragraph) {
            container.innerHTML += converter.makeHtml(paragraph.trim());
          }
        });
        const html = container.outerHTML;
        const file = new Blob([html], { type: "text/html" });
  
        ApiService.htmlToWord(file, window.ASSETS_DESIRED_STYLES)
          .then(function successCallback(response) {
            $scope.safeApply(() => {
              $scope.processing = false;
              ProcessStatusService.end('downloadAbstract');
            });
  
            const fileType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
            const fileName = `${LeaseEditorService.getFileName().replace(".docx", "")} - Abstract.docx`;
            DownloadService.download(response.data, fileType, fileName);
          },
          function errorCallback(response) {
            $scope.safeApply(() => {
              $scope.processing = false;
              ProcessStatusService.end('downloadAbstract');
            });
            console.log(response);
          },
        );
      }
    }

    $scope.exportLease = function() {
      var clonedLease = _.cloneDeep($scope.lease);
      clonedLease.freeTexts = $scope.leaseFreeTexts;
      var type = 'application/json';
      DownloadService.download(JSON.stringify(clonedLease), type, 'exported_lease_' + $scope.lease.id);
    };

    $scope.duplicate = function() {
      const leaseId = $scope.lease.id;

      $http({
        method: 'post',
        url: `/api/leases/${leaseId}/duplicate`,
        headers: {
          'Content-Type': undefined,
        },
      }).then(function(response) {
        const doc = response.data;
        delete doc.id;

        var document = new DocumentService(doc);
        document.create().then(function (doc) {
          const documentType = doc.type.toLowerCase();
          const dealType = doc.form.dealType;
          const flavor = doc.form.flavor;
          const version = doc.form.version;
          const building = doc.building.dashboardName;
          let tenant = "";

          if (doc.tenantInfo) {
            tenant =
              dealType === "office"
                ? doc.tenantInfo.name
                : doc.tenantInfo.tradeName;
          }

          window.track.event(
            new DuplicateDocumentEvent({
              id: $scope.lease.id,
              newDoc: doc.id,
              url: `/${documentType}s/${doc.id}/edit`,
            })
          );

          window.track.event(
            new CreateANewDocumentEvent({
              id: doc.id,
              documentType,
              dealType,
              flavor,
              version,
              building,
              tenant,
            })
          );
          
          window.location.href = "/";
        });
      });
    };

    $scope.openCreateNewDocumentDialog = function() {
      const dealTerms = $scope.lease;
      const companyId = window.user.company.id;

      $mdDialog.show({
        controller:
          ('DialogController', ['$scope', '$mdDialog', DialogController]),
        template:
          `<md-dialog><new-document-modal deal-terms="${dealTerms}" company-id="${companyId}"></new-document-modal></md-dialog>`,
        parent: angular.element(document.body),
        clickOutsideToClose: true,
        fullscreen: false,
      });

      function DialogController($scope, $mdDialog) {
        $scope.cancel = function() {
          $mdDialog.cancel();
        };
      }
    };

    $scope.openTinyMceDialog = function(sectionId) {
      $mdDialog.show({
        template:
          "<md-dialog><tiny-mce-editor section-id='" +
          sectionId +
          "'/></md-dialog>",
        parent: angular.element(document.body),
        fullscreen: false,
      });

      window.track.event(new OpenTheAdvancedEditEvent({
        context: $rootScope.getContext(),
      }));
    };

    $scope.showCreateVersionDialog = function(event) {
      $mdDialog.show({
        template:
          '<md-dialog><new-version-modal></new-version-modal></md-dialog>',
        parent: angular.element(document.body),
        targetEvent: event,
        clickOutsideToClose: true,
        fullscreen: false,
      });
    };

    $scope.showCompareDialog = function(event) {
      $mdDialog.show({
        template: '<md-dialog><compare-modal></compare-modal></md-dialog>',
        parent: angular.element(document.body),
        targetEvent: event,
        clickOutsideToClose: false,
        fullscreen: false,
      });
    };

    $scope.showAbstractDialog = function(event) {
      $mdDialog.show({
        template: '<md-dialog><abstract-modal></abstract-modal></md-dialog>',
        parent: angular.element(document.body),
        targetEvent: event,
        clickOutsideToClose: false,
        fullscreen: false,
      });
    };

    $scope.showImportChangesDialog = function(event) {
      let template;

      if (!$scope.lease.form.isVersionControlSupported) {
        template = '<import-changes-not-supported-modal></import-changes-not-supported-modal>';
      } else {
        template = '<import-changes-modal></import-changes-modal>';
      }

      $mdDialog.show({
        template: `<md-dialog>${template}</md-dialog>`,
        parent: angular.element(document.body),
        targetEvent: event,
        clickOutsideToClose: false,
        fullscreen: false,
      });
    };

    // Listen for import state changes to trigger updates
    $rootScope.$on('dealTermsImport:applyChange', function() {
      // Trigger an update to save the changes
      if ($scope.update) {
        $scope.update();
      }
    });

    $scope.showExpressAbstractDialog = function(event) {
      let template = '<express-abstract-modal></express-abstract-modal>';

      $mdDialog.show({
        template: `<md-dialog class="md-dialog--express-abstract">${template}</md-dialog>`,
        parent: angular.element(document.body),
        targetEvent: event,
        clickOutsideToClose: false,
        fullscreen: false,
      });
    }

    $scope.showEventLogDialog = function(event) {
      let template = '<event-log-modal></event-log-modal>';

      $mdDialog.show({
        template: `<md-dialog class="md-dialog--event-log">${template}</md-dialog>`,
        parent: angular.element(document.body),
        targetEvent: event,
        clickOutsideToClose: false,
        fullscreen: false,
      });
    };

    $scope.showVersionControlUnsupportedChanges = function(event) {
      $mdDialog.show({
        template: `<md-dialog><version-control-unsupported-modal count="${$scope.diffgram.unsupported.length}"></version-control-unsupported-modal></md-dialog>`,
        parent: angular.element(document.body),
        targetEvent: event,
        clickOutsideToClose: false,
        fullscreen: false,
      });
    };

    $scope.collapseAllCards = function() {
      $('.collapse-card.active .collapse-card__heading').each(function() {
        $(this).trigger('click');
      });
    };

    $scope.isCardActive = false;

    $(document).on('click', '.collapse-card__heading', function() {
      $timeout(function() {
        $scope.isCardActive = $('.collapse-card.active').length > 0;
        $scope.$broadcast('cardTriggered');
      });
    });

    /* --------------------------------------------------------------------------------------------------------------
       -- Selection / backspace functionality ----------------------------------------------------------------------- */

    // TODO: remove once bolding works on big selections
    $rootScope.decorationButtonsDisabled = true;
    $scope.disableDecorationButtons = function(yesDisable) {
      if (yesDisable !== $rootScope.decorationButtonsDisabled) {
        $rootScope.decorationButtonsDisabled = yesDisable;
        // set manually to make sure changes apply even if scope doesn't update
        if (yesDisable) {
          $rootScope.decorationButtonsDisabled = true;
          $('.deco-button').attr('disabled', true);
          $('.deco-button').addClass('disabled');
        } else if ($scope.editingEnabled) {
          $rootScope.decorationButtonsDisabled = false;
          $('.deco-button').attr('disabled', false);
          $('.deco-button').removeClass('disabled');
        }
      }
      $scope.disableAddPageBreak();
      $scope.disableAddImage();
    };

    $scope.disablePageBreak = true;
    $scope.disableAddPageBreak = function() {
      const sel = rangy.getSelection();

      const contentEditable = LeaseEditorService.findClosestContentEditable(sel.focusNode);
      const container = LeaseEditorService.findClosestContainer(contentEditable);
      const allContentEditableInContainer = $(container).find('.editable');
      const contentEditableIndex = allContentEditableInContainer.index(contentEditable);

      // We don't support creating new-paragraphs where there's logic after
      // the location of the caret (logic = there are other free-text in the same container)
      if (
        LeaseEditorService.isSelecting() ||
        contentEditableIndex < allContentEditableInContainer.length - 1
      ) {
        $scope.disablePageBreak = true;
      } else {
        $scope.disablePageBreak = false;
      }
    }


    $scope.disableImageButton = true;
    $scope.disableAddImage = function() {
      const sel = rangy.getSelection();

      const contentEditable = LeaseEditorService.findClosestContentEditable(sel.focusNode);
      const container = LeaseEditorService.findClosestContainer(contentEditable);
      const allContentEditableInContainer = LeaseEditorService.findAllContentEditable(container);
      const contentEditableIndex = allContentEditableInContainer.index(contentEditable);

      // We don't support creating new-paragraphs where there's logic after
      // the location of the caret (logic = there are other free-text in the same container)
      if (
        LeaseEditorService.isSelecting() ||
        contentEditableIndex < allContentEditableInContainer.length - 1
      ) {
        $scope.disableImageButton = true;
      } else {
        $scope.disableImageButton = false;
      }
    }



    /* ========================================================================== */

    $scope.calculateDistanceY = function(rect, y) {
      return Math.abs(y - (rect.top + rect.height / 2));
    };

    $scope.checkSameLine = function(elements, mouseX, mouseY) {
      var result = {
        distance: undefined,
        element: undefined,
        rect: undefined,
        direction: undefined,
        isSameLine: false,
      };

      // Find the first block ancestor element
      var CEdit = elements;

      for (var i = 0; i < CEdit.length; i++) {
        var paragraphs = $(CEdit).find('p');
        var rects = CEdit[i].getClientRects();

        // In some rare scenarios, paragraphs can be found inside content-editable
        // (usually, those are coming from building variables)
        // but the containing span doesn't have any size, that's why we're calculating
        // their rects too
        for (
          var paragraphIndex = 0;
          paragraphIndex < paragraphs.length;
          paragraphIndex++
        ) {
          rects = _.union(rects, paragraphs[paragraphIndex].getClientRects());
        }

        for (var rectIndex = 0; rectIndex < rects.length; rectIndex++) {
          var rect = rects[rectIndex];
          var threshold = 0;

          if (rect.width === 0) {
            continue;
          }

          if (
            rect.top - threshold < mouseY &&
            mouseY < rect.bottom + threshold
          ) {
            var minDistance;
            var direction;

            var left = Math.sqrt(
              Math.pow(mouseX - rect.left, 2) +
                Math.pow(mouseY - (rect.top + rect.height / 2), 2),
            );
            var right = Math.sqrt(
              Math.pow(mouseX - rect.right, 2) +
                Math.pow(mouseY - (rect.top + rect.height / 2), 2),
            );

            if (minDistance === undefined || left < minDistance) {
              minDistance = left;
              direction = 'start';
            }
            if (minDistance === undefined || right < minDistance) {
              minDistance = right;
              direction = 'end';
            }

            if (
              result.distance === undefined ||
              minDistance < result.distance
            ) {
              result.distance = minDistance;
              result.element = CEdit[i];
              result.rect = rect;
              result.direction = direction;
              result.isSameLine = true;
            }
          }
        }
      }

      return result;
    };

    // This function is being used from `placeCaretOnClosestChar`
    // It splits the HTML into two parts while making sure we're not breaking the HTML
    // (splitting by text alone can break the HTML)
    $scope.splitHTML = function(element) {
      var html = $(element).html();
      var text = $(element).text();
      var parts = $(element)[0].childNodes;

      if (parts.length > 1) {
        // First lets split by HTML parts
        var part1 = '';
        var part2 = '';

        for (var i = 0; i < parts.length; i++) {
          if (i < parts.length / 2) {
            if (parts[i].nodeType === Node.ELEMENT_NODE) {
              // element
              part1 = part1 + parts[i].outerHTML;
            } else if (parts[i].nodeType === Node.TEXT_NODE) {
              // text
              part1 = part1 + parts[i].textContent;
            }
          } else {
            if (parts[i].nodeType === Node.ELEMENT_NODE) {
              // element
              part2 = part2 + parts[i].outerHTML;
            } else if (parts[i].nodeType === Node.TEXT_NODE) {
              // text
              part2 = part2 + parts[i].textContent;
            }
          }
        }

        part1 = "<span class='split'>" + part1 + '</span>';
        part2 = "<span class='split'>" + part2 + '</span>';
      } else {
        // When there are no more HTML parts to split, split by text
        part1 =
          "<span class='split'>" + text.substr(0, text.length / 2) + '</span>';
        part2 =
          "<span class='split'>" +
          text.substr(text.length / 2, text.length - 1) +
          '</span>';
      }

      $(element).html(part1 + part2);
    };

    $scope.placeCaretOnClosestChar = function(element, mouseX, mouseY) {
      let originalHTML = $(element).html();
      let anchor = element;
      let iterator = element;
      let depth = 50; // If we reach this depth, we halt!
      let result;
      let direction;

      do {
        $scope.splitHTML(iterator);
        depth--;

        if (depth === 0) {
          // Change it all back!
          $(anchor).html(originalHTML);
          return;
        }

        let found;
        let nodes = iterator.childNodes;
        let minDistance;
        let subset = [];

        // First, let's find all those in the same line
        for (
          let elementIndex = 0;
          elementIndex < nodes.length;
          elementIndex++
        ) {
          // TODO: we can skip `span`s that contain other `span`s
          let currElement = nodes[elementIndex];
          let rects = currElement.getClientRects();

          for (let rectIndex = 0; rectIndex < rects.length; rectIndex++) {
            let r = rects[rectIndex];
            let d = $scope.calculateDistanceY(r, mouseY);

            if ((minDistance === undefined || d <= minDistance) && currElement.textContent.length > 0) {
              if (d < minDistance) {
                subset = [];
              }

              minDistance = d;
              subset.push({
                element: currElement,
                rect: r,
              });
            }
          }
        }

        // Now, let's narrow it down to one node element, the closest on the x axis
        for (let subsetIndex = 0; subsetIndex < subset.length; subsetIndex++) {
          let s = subset[subsetIndex];

          if (
            (s.rect.x <= mouseX && mouseX <= s.rect.x + s.rect.width) ||
            ((s.rect.x + (s.rect.width / 2) >= mouseX ||
              subsetIndex + 1 == subset.length))
          ) {
            if (!found) {
              let left = Math.abs(window.reservedCaretPosition - s.rect.left);
              let right = Math.abs(window.reservedCaretPosition - s.rect.right);
              found = s.element;
              direction = left < right ? "left" : "right";
            }
          }
        }

        // Fallback in case we didn't find anything
        if (!found) {
          found = nodes[0];
        }

        iterator = found;
        result = found;
      } while (iterator.textContent.length > 1);

      if (iterator.textContent.length <= 1) {
        // Keep a marker in the location we found
        if (direction === "left") {
          $(result).prepend('!@#$%');
        } else {
          $(result).append('!@#$%');
        }
        // Find the offset
        let text = $(anchor).text();
        let offsetAll = text.indexOf('!@#$%');
        // Change it all back!
        $(anchor).html(originalHTML);

        // Place the marker at the right place, eureka!
        let parts = $(anchor)[0].childNodes;
        let offset = offsetAll;
        for (let i = 0; i < parts.length; i++) {
          if (
            parts[i].nodeType === Node.TEXT_NODE ||
            parts[i].nodeName === 'BR'
          ) {
            if (offset > parts[i].length) {
              offset = offset - parts[i].length;
            } else {
              let range = rangy.createRange();
              range.setStart(parts[i], offset);
              range.collapse(true);
              let sel = rangy.getSelection();
              sel.removeAllRanges();
              sel.addRange(range);
              sel.focusNode.parentElement.scrollIntoViewIfNeeded();
              break;
            }
          }
        }
      } else {
        // Change it all back!
        $(anchor).html(originalHTML);
      }
    };

    // This function retrieves list of the direct parents of all text nodes in a given element
    var getEditableTextNodesIn = function(el) {
      return $(el)
        .find2('*')
        .contents()
        .filter(function() {
          return (
            (this.nodeType == Node.TEXT_NODE || this.nodeName === 'BR') &&
            $(this.parentElement).attr('contenteditable') !== 'false'
          );
        })
        .map(function() {
          if (this.parentElement.childNodes.length > 1) {
            $(this).wrap('<span></span>');
          }
          return this.parentElement;
        });
    };

    $scope.getCaretContainingElement = function(element, rect, mouseX, mouseY) {
      var found;

      var minDistance;
      var subset = [];

      var nodes = getEditableTextNodesIn(element);

      // First, let's find all those in the same line
      for (var elementIndex = 0; elementIndex < nodes.length; elementIndex++) {
        var currElement = nodes[elementIndex];

        // Skip the markers
        if (
          $(currElement).hasClass('SOCE') ||
          $(currElement).hasClass('EOCE')
        ) {
          continue;
        }

        var rects = currElement.getClientRects();

        for (var rectIndex = 0; rectIndex < rects.length; rectIndex++) {
          var r = rects[rectIndex];
          var d = $scope.calculateDistanceY(r, mouseY);

          if ((minDistance === undefined || d <= minDistance) && currElement.textContent.length > 0) {
            if (d < minDistance) {
              subset = [];
            }

            minDistance = d;
            subset.push({
              element: currElement,
              rect: r,
            });
          }
        }
      }

      // Now, let's narrow it down to one node element, the closest on the x axis
      for (var subsetIndex = 0; subsetIndex < subset.length; subsetIndex++) {
        var s = subset[subsetIndex];

        if (
          (s.rect.x <= mouseX && mouseX <= s.rect.x + s.rect.width) ||
          ((s.rect.x + s.rect.width >= mouseX ||
            subsetIndex + 1 == subset.length) &&
            !found)
        ) {
          found = s.element;
        }
      }

      // Fallback in case we didn't find anything
      if (!found) {
        found = nodes[0];
      }

      return found;
    };

    $scope.getCaretNewPlacement = function(elements, mouseX, mouseY, force) {
      // Don't proceed if there's a selection
      var sel = rangy.getSelection();
      if (LeaseEditorService.isSelecting() && LeaseEditorService.isMeaningfulSelection()) {
        return;
      }

      // If we're inside of an editable segment, halt since we're using native behavior
      if (
        $(sel.anchorNode).closest('.SOCE, .EOCE').length === 0 &&
        $(sel.anchorNode).closest('.editable').length === 1 &&
        !force
      ) {
        var closestContentEditable = LeaseEditorService.findClosestContentEditable(
          sel.anchorNode,
        );
        // ensure cursor is blinking
        sel.move('character', 0);
        $(closestContentEditable).focus();
        return;
      }

      var minDistance;
      var subset = [];
      var sameLineData = $scope.checkSameLine(elements, mouseX, mouseY);
      var result = {
        element: undefined,
        rect: undefined,
        direction: undefined,
      };

      // First, check if the cursor is in-line
      if (sameLineData.isSameLine && !force) {
        result = sameLineData;
      } else {
        // First, let's find all those in the same line
        for (
          var elementIndex = 0;
          elementIndex < elements.length;
          elementIndex++
        ) {
          // TODO: we can skip `span`s that contain other `span`s
          var currElement = elements[elementIndex];
          var rects = currElement.getClientRects();

          for (var rectIndex = 0; rectIndex < rects.length; rectIndex++) {
            var r = rects[rectIndex];

            var d = $scope.calculateDistanceY(r, mouseY);

            if ((minDistance === undefined || d <= minDistance) && currElement.textContent.length > 0) {
              if (d < minDistance) {
                subset = [];
              }

              minDistance = d;
              subset.push({
                element: currElement,
                rect: r,
              });
            }
          }
        }

        // Now, let's narrow it down to one node element, the closest on the x axis
        for (var subsetIndex = 0; subsetIndex < subset.length; subsetIndex++) {
          var s = subset[subsetIndex];

          // Skip the markers
          if ($(s).hasClass('SOCE') || $(s).hasClass('EOCE')) {
            continue;
          }

          if (s.rect.x <= mouseX && mouseX <= s.rect.x + s.rect.width) {
            result = s;
          }
        }

        // Fallback in case we didn't find anything
        if (!result.element) {
          minDistance = undefined;

          for (
            elementIndex = 0;
            elementIndex < elements.length;
            elementIndex++
          ) {
            var element = elements[elementIndex];
            rects = element.getClientRects();

            for (rectIndex = 0; rectIndex < rects.length; rectIndex++) {
              var rect = rects[rectIndex];

              var d = $scope.calculateDistanceY(rect, mouseY);

              if ((minDistance === undefined || d <= minDistance) && element.textContent.length > 0) {
                minDistance = d;
                result.element = element;
                result.rect = rect;
              }
            }
          }
        }
      }

      if (result.element) {
        // First, let's set the contenteditable back to `true` so it'll be editable
        result.element = LeaseEditorService.findClosestContentEditable(result.element);

        var coorX;
        var coorY =
          $('.preview').scrollTop() + result.rect.top + result.rect.height / 2;
        if (!sameLineData.isSameLine || force) {
          // verify that mouseX is within the element bounds, if not - round the mouseX to the closest bound
          if (mouseX < result.rect.left) {
            mouseX = Math.ceil(result.rect.left);
          } else if (mouseX > result.rect.right) {
            mouseX = Math.floor(result.rect.right);
          }

          // place the caret inside the rect in exact position
          // if (result.rect.left <= mouseX && mouseX <= result.rect.right) {
          coorX = mouseX;
          var containingElement = $scope.getCaretContainingElement(
            result.element,
            result.rect,
            mouseX,
            mouseY,
          );
          if (containingElement) {
            $scope.placeCaretOnClosestChar(containingElement, mouseX, mouseY);
          }
          sel = rangy.getSelection();
          var closestContentEditable = LeaseEditorService.findClosestContentEditable(
            sel.anchorNode,
          );
        }
      }
    };

    /* ========================================================================== */

    function getActionByTexts(savedText, changedText, defaultText) {
      var action;
      var oldText = LeaseEditorService.prepareForComparison(defaultText);
      var newText = LeaseEditorService.prepareForComparison(changedText);
      if (!savedText || savedText.type === 'override') {
        action = 'create';
      } else if (oldText === newText) {
        action = 'delete';
      } else {
        action = 'update';
      }
      return action;
    }

    function getReverseAction(action) {
      var actionPairs = {
        create: 'delete',
        delete: 'create',
        update: 'update',
      };
      return actionPairs[action];
    }

    function createBulk(contentEditableList) {
      var contentEditableListData = [];
      var contentEditableListUndoData = [];
      contentEditableList.forEach(function(value) {
        var sectionId = $(value).attr('section-id');

        if (!sectionId) {
          return;
        }

        // What gets stored has to be the *contents* of the provision's editable,
        // never the editable itself. Callers resolve elements in different ways --
        // document.querySelector('[section-id=..]') in the accept and reject paths,
        // closest('[section-id]') from the clipboard -- and any of them can land on
        // a node that wraps the editable rather than being it. Serializing that node
        // stores markup carrying ng-model and section-id for this same section, and
        // loading it back compiles a second ng-model for the section, whose $render
        // stores it again.
        //
        // If this node contains another element declaring the same section, that
        // inner one is the real editable, so descend before serializing.
        var sourceNode = $(value).get(0);

        if (sourceNode && sourceNode.querySelector) {
          try {
            var innerDeclaration = sourceNode.querySelector(
              '[section-id="' + sectionId + '"]'
            );

            if (innerDeclaration) {
              sourceNode = innerDeclaration;
            }
          } catch (e) {
            // Unusable section id for a selector; fall back to the node as given.
          }
        }

        var changedProvision = $(sourceNode).html();
        var prevText = $scope.leaseFreeTexts[sectionId]
          ? $scope.leaseFreeTexts[sectionId].text
          : null;
        var action = getActionByTexts(
          $scope.leaseFreeTexts[sectionId],
          changedProvision,
          ($rootScope.defaultProvisions[sectionId] ?
             $rootScope.defaultProvisions[sectionId].html :
             ''),
        );

        contentEditableListData.push({
          sectionId: sectionId,
          leaseId: $rootScope.getLeaseId(),
          amendmentId: $rootScope.getAmendmentId(),
          text: changedProvision,
          action: action,
        });

        contentEditableListUndoData.push({
          sectionId: sectionId,
          leaseId: $rootScope.getLeaseId(),
          amendmentId: $rootScope.getAmendmentId(),
          text: prevText,
          action: getReverseAction(action),
        });
      });

      function executeRedoBulkAction(contentEditableList, shouldRecompile) {
        var deferred = $q.defer();
        $scope.startSaving();

        var createCallback = function(newFreeTexts) {
          var promises = [];
          var list = newFreeTexts.freeTexts;
          contentEditableList.forEach(function(ft) {
            var action = ft.action;
            if (ft.action === 'create') {
              // get the resource object so the database ID can be tracked for updates
              let original = _.find(list, function(x) {
                return x.sectionId == ft.sectionId;
              });

              original.text = ft.text;
              ft = original;
            }

            const node = document.querySelector("[section-id='" + ft.sectionId + "']");

            if (node && node.parentElement) {
              var defer = $q.defer();
              promises.push(defer.promise);

              $("[section-id='" + ft.sectionId + "']").trigger('afterSave', [
                ft,
                action,
                shouldRecompile,
                defer,
              ]);
            }
          });

          $q.all(promises).then(function() {
            $scope.structureChanged();
            $scope.endSaving();
            var currSelection = $rootScope.getSelectionData();
            deferred.resolve({
              selection: currSelection,
            });
          });
        };

        if ($rootScope.adminMode) {
          createCallback({
            freeTexts: contentEditableList,
          });
        } else {
          new FreeTextsBulkService(contentEditableList)
            .create()
            .then(createCallback);
        }
        return deferred.promise;
      }

      function executeUndoBulkAction(contentEditableList, shouldRecompile) {
        var deferred = $q.defer();
        $scope.startSaving();

        var createCallback = function(newFreeTexts) {
          var promises = [];
          var list = newFreeTexts.freeTexts;
          contentEditableList.forEach(function(ft) {
            var action = ft.action;
            if (ft.action === 'create') {
              // get the resource object so the database ID can be tracked for updates
              let original = _.find(list, function(x) {
                return x.sectionId == ft.sectionId;
              });

              original.text = ft.text;
              ft = original;
            }

            const node = document.querySelector("[section-id='" + ft.sectionId + "']");

            if (node && node.parentElement) {
              var defer = $q.defer();
              promises.push(defer.promise);

              $("[section-id='" + ft.sectionId + "']").trigger('afterSave', [
                ft,
                action,
                shouldRecompile,
                defer,
              ]);
            }
          });

          $q.all(promises).then(function() {
            $scope.structureChanged();
            $scope.endSaving();
            var currSelection = $rootScope.getSelectionData();
            deferred.resolve({
              selection: currSelection,
            });
          });
        };

        if ($rootScope.adminMode) {
          createCallback({
            freeTexts: contentEditableList,
          });
        } else {
          new FreeTextsBulkService(contentEditableList)
            .create()
            .then(createCallback);
        }
        return deferred.promise;
      }

      $scope.addToHistory({
        id: 'bulk',
        desc: 'created bulk of free-texts',
        redo: function(isFirstTime) {
          return executeRedoBulkAction(contentEditableListData, !isFirstTime);
        },
        undo: function() {
          return executeUndoBulkAction(contentEditableListUndoData, true);
        },
      });
    }
    $scope.createBulk = createBulk;


    function getAllContentEditableInElement(element){
      return element.querySelectorAll('[contenteditable][section-id]');
    }

    function removePageBreaksInSelection() {
      let sel = rangy.getSelection();
      let range = sel.getRangeAt(0);
      let pageBreaks = range.getNodes().filter(node => {
        return (
          node.tagName &&
          node.tagName === "BR" &&
          !node.closest("[free-text]") &&
          (node.nextElementSibling &&
            node.nextElementSibling.tagName === "PAGE-BREAK")
        );
      });

      let actionsCounter = 0;
      pageBreaks.forEach(node => {
        actionsCounter++;
        pageLayersManager.deletePageBreak(null, node);
      });

      return actionsCounter;
    }

    function getTablesInSelectedFreeText() {
      let sel = rangy.getSelection();
      let range = sel.getRangeAt(0);
      let tables = [];
      range.getNodes().forEach(node => {
        let table = node.closest ? node.closest("table") : null;
        if (
          table &&
          table.closest("[free-text]") &&
          tables.indexOf(table) === -1
        ) {
          tables.push(table);
        }
      });
      return tables;
    }

    async function newDeleteSelection(      
          preventCreateBatch,
          blocksArray,
          textNodes,
          caretNodePrev,
          caretNodeNext,
          caretNode,
          isBackwards,
          isForceBackwards
    ) {

      const deferred = $q.defer();
      
      const firstBlockTextNodes = blocksArray[0].getAllEditableTextNodes();
      const lastBlockTextNodes = blocksArray.at(-1).getAllEditableTextNodes();
      let splitFirstBlock = firstBlockTextNodes[0] !== textNodes[0];
      let splitLastBlock = lastBlockTextNodes.at(-1) !== textNodes.at(-1);

      if(splitFirstBlock && !blocksArray[0].canSplit()){
        splitFirstBlock = false;
        blocksArray.splice(0,1);
      }

      if(splitLastBlock && !blocksArray.at(-1).canSplit()){
        splitLastBlock = false;
        blocksArray.splice(blocksArray.length-1,1);
      }
      

      const  containerElement = document.createElement("ins");
      containerElement.classList.add("diff-item", "diff-item--accepted");
      const firstRoundTrip = blocksArray[0].getRoundTripNode();

      const listItemObjectsArray = [];
      const newStyleArray = [];
      const restyleElements = JSON.parse(JSON.stringify($scope.lease.restyle));

      let listItemUndoTracking = {};
      let listItemObjects = [];
      

      /// save data 
      const freeTextArray = [];
      blocksArray.forEach((block)=>{
          Array.from( block.containingNode.querySelectorAll('[section-id]')).forEach((item)=>{
            freeTextArray.push(item);
          });

          const blockId = (block && block.containingNode)
            ? block.containingNode.getAttribute("pid")
            : null;

          if (blockId) {
            delete restyleElements[blockId];
          }
      });

      let currentUndoIndex = $scope.histIndex;
      let currentListItems = JSON.parse(JSON.stringify($scope.listItems));
      const currentReStyle = JSON.parse(JSON.stringify($scope.lease.restyle));
      const currentListItemUndoTracking = JSON.parse(
        JSON.stringify($scope.listItemUndoTracking)
      );
      const currentListItemObjects = JSON.parse(
        JSON.stringify($scope.listItemObjects)
      );

      if (firstRoundTrip) {
        firstRoundTrip.parentElement.insertBefore(containerElement, firstRoundTrip);
      } else {
        blocksArray[0].containingNode.parentElement.insertBefore(
          containerElement,
          blocksArray[0].containingNode
        );
      }


      if (splitFirstBlock) {
        const {
          itemObject,
          paragraphStyle
        } = blocksArray[0].cloneAndDeleteInDirection(textNodes[0], true);
        listItemObjectsArray.push(itemObject);

        if (paragraphStyle) {
          newStyleArray.push(paragraphStyle);
        }
      }

      if (splitLastBlock) {
        const { itemObject, paragraphStyle } = blocksArray
          .at(-1)
          .cloneAndDeleteInDirection(textNodes.at(-1), false);
        listItemObjectsArray.push(itemObject);

        if (paragraphStyle) {
          newStyleArray.push(paragraphStyle);
        }
      }


      function addListItemToDom(currentListItemObject,block){
        let template = listItemsManager.createListItemFromTemplate(
          currentListItemObject.level,
          block.listLevel.format,
          block.listLevel.name,
          currentListItemObject,
          true,
          false
        );
        while (template.firstElementChild) {
          let el = template.firstElementChild;
          el.remove;
          el.setAttribute(
            "inserted-list-item-id",
            $scope.nextUnusedListItemId
          );
          containerElement.appendChild(el);
        }

        listItemUndoTracking[
          `${currentListItemObject.headerFreeText}-${currentListItemObject.bodyFreeText}`
        ] = $scope.nextUnusedListItemId;
        listItemObjects[
          `${currentListItemObject.headerFreeText}-${currentListItemObject.bodyFreeText}`
        ] = currentListItemObject;
        $scope.nextUnusedListItemId++;

      }

      if(splitFirstBlock){
        addListItemToDom(listItemObjectsArray[0],blocksArray[0]);
      }

      if(splitLastBlock){
        addListItemToDom(listItemObjectsArray.at(-1),blocksArray.at(-1));
      }


      ///  --------------------------

      const redo = function(
        restyle,
        listItemUndoTracking,
        listItemObjects,
        listItemObjectsArray,
        blocksArray,
        containerElement,
        block2PID,
        $scope,
        stateBeforeRedo
      ) {
        return function() {          
          const deferred = $q.defer();

          for (
            let blockIndex = 0;
            blockIndex < blocksArray.length;
            blockIndex++
          ) {
            let orgBlock = blocksArray[blockIndex];
            if (!orgBlock.containingNode.parentElement) {
              let pid = orgBlock.containingNode.getAttribute("pid");
              let el = document.querySelector(`[pid="${pid}"]`);
              let newBlock = el ? el.getBlockData() : null;
              if (newBlock) {
                blocksArray[blockIndex] = newBlock;
              }
            }
          }

         

          $scope.listItems = $scope.listItems.concat(listItemObjectsArray);
          $scope.lease.orderedListitem = $scope.serializeOrderedListitem();

          
          Object.keys(listItemUndoTracking).forEach(key => {
            const values = key.split('-');
            const el = document.querySelector(`[free-text="${values[0]}"]`);
            const idElement  = el ? el.closest('[inserted-list-item-id]') :null;
            const newId = idElement ? idElement.getAttribute('inserted-list-item-id') : null;
            $scope.listItemUndoTracking[key] = newId || listItemUndoTracking[key];
          });

          Object.keys(listItemObjects).forEach(key => {
            $scope.listItemObjects[key] = listItemObjects[key];
          });

          blocksArray.forEach((sourceBlock)=>{
            
            let orderListItem = listItemsManager.getInsertedListItemByElement(sourceBlock.containingNode);
            if (orderListItem) {
              orderListItem.deleted = true;
              $scope.lease.orderedListitem = $scope.serializeOrderedListitem();
            }
            
            sourceBlock.markAsDeletedElement();
          });

          $scope.lease.restyle = restyle;

          containerElement.classList.add("lp-show-element", "diff-item");
          containerElement.classList.remove("lp-hide-element");

          $scope.restyle();
          setTimeout($scope.listsRefresh);
          
          $scope.update();

          const stateAfterRedo = window.captureStateFromBlocks(blocksArray);

          Object.keys(stateAfterRedo).forEach(key => {
            const pid = key;
            window.track.event(new ReviseTextEvent({
              pid: pid,
              before: stateBeforeRedo[pid],
              after: stateAfterRedo[pid],
              context: $scope.$root.getContext()
            }));
            window.cachedBlocks[pid] = stateAfterRedo[pid];
          });

          deferred.resolve();
          return deferred.promise;
        };
      };

      const undo = function(
        restyle,
        listItemUndoTracking,
        listItemObjects,
        listItemObjectsArray,
        blocksArray,
        containerElement,
        $scope,
        caretNode,
        isBackwards,
        isForceBackwards,
        stateAfterUndo
      ) {
        return function() {
          const deferred = $q.defer();

          const stateBeforeUndo = window.captureStateFromBlocks(blocksArray);
          
          for (
            let blockIndex = 0;
            blockIndex < blocksArray.length;
            blockIndex++
          ) {
            let orgBlock = blocksArray[blockIndex];
            if (!orgBlock.containingNode.parentElement) {
              let pid = orgBlock.containingNode.getAttribute("pid");
              let el = document.querySelector(`[pid="${pid}"]`);
              let newBlock = el ? el.getBlockData() : null;
              if (newBlock) {
                blocksArray[blockIndex] = newBlock;
              }
            }
          }

          $scope.listItems = listItemObjectsArray;
          $scope.lease.orderedListitem = $scope.serializeOrderedListitem();

          $scope.listItemUndoTracking = listItemUndoTracking;

          Object.keys($scope.listItemUndoTracking).forEach(key => {
            const values = key.split('-');
            const el = document.querySelector(`[free-text="${values[0]}"]`);
            const idElement  = el ? el.closest('[inserted-list-item-id]') :null;
            const newId = idElement ? idElement.getAttribute('inserted-list-item-id') : null;
            $scope.listItemUndoTracking[key] = newId || listItemUndoTracking[key];
          });


          $scope.listItemObjects = listItemObjects;
          $scope.lease.restyle = restyle;

          blocksArray.forEach((sourceBlock)=>{
            let orderListItem = listItemsManager.getInsertedListItemByElement(sourceBlock.containingNode);
            if (orderListItem) {
              delete orderListItem.deleted;
              $scope.lease.orderedListitem = $scope.serializeOrderedListitem();
            }
            sourceBlock.unMarkAsDeletedElement();
          });

          containerElement.classList.remove("lp-show-element");
          containerElement.classList.add("lp-hide-element");

          if (!isBackwards && !isForceBackwards) {
            $rootScope.placeCaretAtStart(caretNode);
          } else {
            $rootScope.placeCaretAtEnd(caretNode);
          }

          $scope.restyle();
          setTimeout($scope.listsRefresh);
          $scope.update();
          deferred.resolve();

          Object.keys(stateBeforeUndo).forEach(key => {
            const pid = key;
            window.track.event(new ReviseTextEvent({
              pid: pid,
              before: stateBeforeUndo[pid],
              after: stateAfterUndo[pid],
              context: $scope.$root.getContext()
            }));
            window.cachedBlocks[pid] = stateAfterUndo[pid];
          });

          return deferred.promise;
        };
      };

      const stateBeforeRedo = window.captureStateFromBlocks(blocksArray);
      const stateAfterUndo = stateBeforeRedo;
      
      await $scope.addToHistory({
        id: "restyle split delete selection :" ,
        desc: "restyle split delete selection ",
        ignoreCaret : true,
        redo: redo(
          restyleElements,
          listItemUndoTracking,
          listItemObjects,
          listItemObjectsArray,
          blocksArray,
          containerElement,
          null,
          $scope,
          stateBeforeRedo,
        ),
        undo: undo(
          currentReStyle,
          currentListItemUndoTracking,
          currentListItemObjects,
          currentListItems,
          blocksArray,
          containerElement,
          $scope,
          caretNode,
          isBackwards,
          isForceBackwards,
          stateAfterUndo
        )
      });


      const freeTextArrayNumbers = [];
      containerElement.querySelectorAll('[free-text]').forEach((el)=>{
        freeTextArrayNumbers.push(el.getAttribute('free-text'));
      });

      
      await  document.waitForFreeTextCompile(freeTextArrayNumbers);
      

      document.querySelectorAll('.keep-visible').forEach((el)=>{
        el.classList.remove('keep-visible');
      });


      // change caret node  caretNode
      if(splitFirstBlock){      
        const block = containerElement.querySelector('.SOCE').getBlockData();
        block.containingNode.classList.add('keep-visible');
        let newPrevNode =  block.getLastTextNode();
        if(caretNodePrev == caretNode){
          caretNode = newPrevNode;
        }
        caretNodePrev = newPrevNode;
      }


      
      if(splitLastBlock){
      
        const spans = containerElement.querySelectorAll('.SOCE');
        const block = spans[spans.length-1].getBlockData();
        block.containingNode.classList.add('keep-visible');

        let newNextNode =  block.getFirstTextNode();
        if(caretNodeNext == caretNode){
          caretNode = newNextNode;
        }
        caretNodeNext = newNextNode;
      }

      $scope.createBulk(freeTextArray);

      deferred.resolve({caretNodePrev : caretNodePrev , caretNodeNext : caretNodeNext , caretNode : caretNode });
      
      return deferred.promise;

    }

    $rootScope.deleteSelection = async function(
      beforeSaveFunc,
      isKeepSegment,
      isForceBackwards,
      preventCreateBatch
    ) {
      var actionsCount = 0;
      // save current history location
      let currentUndoIndex = $scope.histIndex;

      function getCaretNode(isForward, textNodes) {
        var caretNode;
        var isCaretInNewContainer = false;

        if (isForward) {
          caretNode = textNodes[textNodes.length - 1];
          caretNode = caretNode.nextSibling;

          if (!caretNode) {
            caretNode = textNodes[textNodes.length - 1];
            do {
              caretNode = caretNode.parentElement;

              if (caretNode.nextSibling) {
                caretNode = caretNode.nextSibling;
              }
            } while (caretNode.nodeType !== 1);
          }

          if (!caretNode || $(caretNode).is('.SOCE, .EOCE')) {
            if (isKeepSegment) {
              caretNode = textNodes[textNodes.length - 1];
              caretNode = LeaseEditorService.findClosestContentEditable(caretNode);
              caretNode = $(caretNode).find('.PLACEHOLDER');
            } else {
              var contentEditable = LeaseEditorService.findClosestContentEditable(
                textNodes[textNodes.length - 1],
              );
              caretNode = LeaseEditorService.findNextContentEditable(contentEditable);
              if (caretNode && caretNode.length > 0) {
                isCaretInNewContainer = true;
                caretNode = $(caretNode)
                  .find('.PLACEHOLDER')
                  .get(0);
                caretNode = LeaseEditorService.skipEmptyNodes(caretNode, true);
                caretNode = caretNode.childNodes[0];
              }
            }
          }
        } else {
          caretNode = textNodes[0];
          caretNode = caretNode.previousSibling;

          if (!caretNode) {
            caretNode = textNodes[0];
            do {
              caretNode = caretNode.parentElement;

              if (caretNode.previousSibling) {
                caretNode = caretNode.previousSibling;
              }
            } while (caretNode.nodeType !== 1);
          }

          if (!caretNode || $(caretNode).is('.SOCE, .EOCE')) {
            if (isKeepSegment) {
              caretNode = textNodes[0];
              caretNode = LeaseEditorService.findClosestContentEditable(caretNode);
              caretNode = $(caretNode)
                .find('.PLACEHOLDER')
                .get(0);
            } else {
              var contentEditable = LeaseEditorService.findClosestContentEditable(
                textNodes[0],
              );
              caretNode = LeaseEditorService.findPreviousContentEditable(
                contentEditable,
              );
              if (caretNode && caretNode.length > 0) {
                isCaretInNewContainer = true;
                caretNode = $(caretNode)
                  .find('.EOCE')
                  .get(0);
                caretNode = LeaseEditorService.skipEmptyNodes(caretNode, false);
                caretNode =
                  caretNode.childNodes[caretNode.childNodes.length - 1];
              }
            }
          }
        }

        if (!isCaretInNewContainer && caretNode) {
          isKeepSegment = true;
        }

        const isEditable = LeaseEditorService.findClosestContentEditable(caretNode).length > 0;
        if (isKeepSegment && isEditable) {
          keepContentEditableVisible = LeaseEditorService.findClosestContentEditable(
            caretNode,
          );
          keepContainerVisible = LeaseEditorService.findClosestContainer(
            keepContentEditableVisible,
          );
          keepContainerVisible.addClass('keep-visible');
        }

        return caretNode;
      }

      function placeCaret(caretNode) {
        var contentEditable = LeaseEditorService.findClosestContentEditable(caretNode);
        LeaseEditorService.enableEditingForSegment(contentEditable);
        if (!isBackwards && !isForceBackwards) {
          $rootScope.placeCaretAtStart(caretNode);
        } else {
          $rootScope.placeCaretAtEnd(caretNode);
        }
      }

      function processContentEditable(contentEditable, caretNode, isBackwards) {
        var container = LeaseEditorService.findClosestContainer(contentEditable);
        var isNewListItem =
          $(contentEditable).closest('.inserted-list-item').length > 0;
        var isBullet = $(contentEditable).attr('bullet') === 'true';
        var isNotDeletable = $(contentEditable).attr('deletable') === 'false';

        var childNodes = $(contentEditable).get(0).childNodes;
        var childNodesLength = childNodes.length;
        for (var i = childNodesLength - 1; i >= 0; i--) {
          if (
            LeaseEditorService.isEmpty($(childNodes[i]), false, true) &&
            !LeaseEditorService.isSelfClosing(childNodes[i]) &&
            !$(childNodes[i]).is('.SOCE, .PLACEHOLDER, .EOCE') &&
            !$.contains(childNodes[i], caretNode)
          ) {
            $(childNodes[i]).remove();
          }
        }

        contentEditable = $(contentEditable);
        caretNode = $(caretNode);
        if (LeaseEditorService.isEmpty(contentEditable, true)) {
          if (
            !isKeepSegment ||
            (isKeepSegment &&
              !$.contains(contentEditable.get(0), caretNode.get(0)))
          ) {
            contentEditable.html('');

            if (isBullet) {
              contentEditable.html(
                "<li class='empty-li'>" +
                  window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_NO_BREAKING_SPACE +
                "</li>"
              );
            }

            if (isNewListItem) {
              var insertedListItemId = LeaseEditorService.closestNewListItemId(
                contentEditable,
              );
              var relatedContentEditable = $(
                ".inserted-list-item[inserted-list-item-id='" +
                  insertedListItemId +
                  "']:has(.editable)",
              );
              var listItemContainer;
              var listItemContentEditable;

              for (var i = 0; i < relatedContentEditable.length; i++) {
                // caretNode could've been deleted as part of the deletion of the list-item
                // move the caret to the prev/next node, according to `isBackwards`
                if (!$.contains(relatedContentEditable.get(i), caretNode)) {
                  if (!isBackwards) {
                    listItemContainer = relatedContentEditable.last();
                    listItemContentEditable = LeaseEditorService.findAllContentEditable(
                      listItemContainer,
                    );
                    listItemContentEditable = listItemContentEditable.last();
                    listItemContentEditable = LeaseEditorService.findNextContentEditable(
                      listItemContentEditable,
                    );
                    caretNode = $(listItemContentEditable)
                      .find('.SOCE')
                      .get(0);
                    caretNode = LeaseEditorService.skipEmptyNodes(caretNode, true);
                  } else {
                    listItemContainer = relatedContentEditable.first();
                    listItemContentEditable = LeaseEditorService.findAllContentEditable(
                      listItemContainer,
                    );
                    listItemContentEditable = listItemContentEditable.first();
                    listItemContentEditable = LeaseEditorService.findPreviousContentEditable(
                      listItemContentEditable,
                    );
                    caretNode = $(listItemContentEditable)
                      .find('.EOCE')
                      .get(0);
                    caretNode = LeaseEditorService.skipEmptyNodes(caretNode, false);
                  }
                  break;
                }
              }
            }
          }

          LeaseEditorService.addMarkers(contentEditable);
        }

        caretNode = $(caretNode);
        if (LeaseEditorService.isEmpty(container, true)) {
          if (!isNotDeletable) {
            if (
              !isKeepSegment ||
              (isKeepSegment &&
                !$.contains(container.get(0), caretNode.get(0)) &&
                !container.hasClass('keep-visible'))
            ) {
              if (LeaseEditorService.shouldHideEmptyParagraph(contentEditable)) {
                LeaseEditorService.hideEmptyParagraph(
                  contentEditable,
                  container,
                );
                structureChanged = true;
              }
            }
          }
        }

        return caretNode.get(0);
      }

      function clearSelection(range, nodes) {
        
        for (var i = 0; i < nodes.length; i++) {

          if(nodes[i] && nodes[i].parentElement && nodes[i].parentElement.closest && nodes[i].parentElement.closest('deleted-element') ){
            continue;
          }
          
          if (LeaseEditorService.isSelfClosing(nodes[i])) {
            nodes[i].remove();
          } else {
            sel.removeAllRanges();
            const range = rangy.createRange();
            range.selectNode(nodes[i]);
            range.deleteContents(nodes[i]);
          }
          
        }

        return range;
      }

      actionsCount+= removePageBreaksInSelection();
      let tablesInSelection = getTablesInSelectedFreeText();
      var sel = rangy.getSelection();
      var originalRange = sel.getRangeAt(0);
      var isBackwards = sel.isBackwards();
      var allContentEditableInSelection = LeaseEditorService.findAllContentEditableInSelection();
      var range;
      var keepContentEditableVisible;
      var keepContainerVisible;
      var structureChanged = false;

      if (typeof isKeepSegment === 'undefined') {
        isKeepSegment = false;
      }

      originalRange.splitBoundaries();
      originalRange.getNodes().forEach(function (node, index) {
        node.nodeIndex = index;
      });
      var textNodes = originalRange.getNodes([3], function(node) {
        const isEditable = $(node).closest('.editable').length > 0;
        const parentNode = node.parentElement;
        const isSOCE = $(parentNode).hasClass('SOCE');
        const isEmptyPlaceholder =
          $(parentNode).hasClass('PLACEHOLDER') &&
          (node.textContent === window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_NO_BREAKING_SPACE || node.textContent === window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_SPACE);
        const isEOCE = $(parentNode).hasClass('EOCE');
        const isSkip = $(parentNode).hasClass('SKIP');
        const isListPadding =
          $(parentNode).hasClass('word-indent') &&
          parentNode.style.font.indexOf('7pt') !== -1;

        return (
          isEditable &&
          !isSOCE &&
          !isEmptyPlaceholder &&
          !isEOCE &&
          !isSkip &&
          !isListPadding
        );
      });
      var elementNodes = originalRange.getNodes([1], function(node) {
        // Filter out everything that's not inside a `contenteditable`
        // We're looking only for empty elements like <br/>, <img/> etc'
        return (
          (
            $(node).closest('.editable').length > 0 &&
            node.childNodes.length === 0 &&
            node.innerText.length > 0
          ) ||
          node.tagName === "LEASE-VAR" ||
          node.tagName === "CRR" ||
          node.tagName === "BR" ||
          node.tagName === "SOFT-RETURN"
        );
      });

      if (textNodes.length === 0) {
        return;
      }

      // Take note for the place of the caret after deleting the selected text
      var caretNode;
      var nodes = _.sortBy(_.concat(textNodes, elementNodes), 'nodeIndex');
      

      var caretNodeNext = getCaretNode(true, nodes);
      var caretNodePrev = getCaretNode(false, nodes);
      var node;

      if (!!beforeSaveFunc) {
        node = document.createTextNode(
          window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_NO_BREAKING_SPACE,
        );
        caretNode = $(node).insertBefore(nodes[0]);
      } else if (!isBackwards && !isForceBackwards) {
        caretNode = caretNodeNext;
      } else {
        if(caretNodePrev && caretNodePrev.nodeType === Node.ELEMENT_NODE && caretNodePrev.classList.contains('PLACEHOLDER')) {
          caretNode = caretNodeNext;
          isBackwards = !isBackwards;
          isForceBackwards = false;
        } else {
          caretNode = caretNodePrev;
        }
      }

      const blocksIdArray = [];
      const blocksArray = [];
      textNodes.forEach(tNode => {
        const parent = tNode.parentElement;
        const block = parent ? parent.getBlockData() : null;
        const blockId =
          block && block.containingNode
            ? block.containingNode.getAttribute("pid")
            : null;
        if (blockId && blocksIdArray.indexOf(blockId) === -1) {
          blocksIdArray.push(blockId);
          blocksArray.push(block);
        }
      });
     

      let hasInsertListItemsAnywhere =
        $scope.features && $scope.features.insertListItemsAnywhere
          ? $scope.features.insertListItemsAnywhere
          : false;

      hasInsertListItemsAnywhere = hasInsertListItemsAnywhere || window.isDebug;

      const blocksInTable = blocksArray.filter(block => {
        return block.containingNode && block.containingNode.closest("table");
      });


      
      let newDeleteRes = null;
      if (blocksArray.length > 1 && hasInsertListItemsAnywhere && blocksInTable.length == 0) {
        window.allowSetDecoration = true;

        newDeleteRes = await newDeleteSelection(
          preventCreateBatch,
          blocksArray,
          textNodes,
          caretNodePrev,
          caretNodeNext,
          caretNode,
          isBackwards,
          isForceBackwards
        );

        caretNodePrev = newDeleteRes.caretNodePrev;
        caretNodeNext = newDeleteRes.caretNodeNext;
        caretNode = newDeleteRes.caretNode;        
      }

      // Delete the content from the selected text, going through the nodes one-by-one
      range = clearSelection(range, nodes, caretNodeNext, caretNodePrev);
      
      window.allowSetDecoration = false;

      // Note: when hiding an entire paragraph, we always move the caret to the next
      // available content-editable
      // but since delete/backspace generates `isBackwards=false`, if we won't intervene
      // the caret will be placed at the previous available content-editable.
      // in this scenario, we intervene to switch the direction.
      var contentEditable;
      if (isKeepSegment) {
        if (!isBackwards) {
          contentEditable = LeaseEditorService.findClosestContentEditable(
            caretNodePrev,
          );
        } else {
          contentEditable = LeaseEditorService.findClosestContentEditable(
            caretNodeNext,
          );
        }
        if (
          $(caretNodePrev).closest("table").length === 0 &&
          $(caretNodeNext).closest("table").length === 0 &&
          LeaseEditorService.isEmpty(caretNodePrev, true) &&
          LeaseEditorService.isEmpty(caretNodeNext, true)
        ) {
          isBackwards = !isBackwards;
          if (!isBackwards) {
            caretNode = caretNodeNext;
            contentEditable = LeaseEditorService.findClosestContentEditable(
              caretNodePrev,
            );
          } else {
            caretNode = caretNodePrev;
            contentEditable = LeaseEditorService.findClosestContentEditable(
              caretNodeNext,
            );
          }
        }
        if ($('.keep-visible').length > 1) {
          // Now that we finally decided of the direction,
          // we can delete the second option
          $(contentEditable)
            .closest('.keep-visible')
            .removeClass('keep-visible');
          caretNode = processContentEditable(
            contentEditable,
            caretNode,
            isBackwards,
          );
        }
      } else {
        // This is used when the first/last content-editable in the document is deleted
        // so we switch the direction
        if (!caretNode) {
          caretNode = caretNodeNext || caretNodePrev;
          isBackwards = !isBackwards;
        }
      }
      $('.keep-visible').removeClass('keep-visible');

      // drop duplicates of free-texts that belong to the same list-item
      allContentEditableInSelection = _.uniqBy(allContentEditableInSelection, function(x) {
        return $(x).closest('[inserted-list-item-id]').attr('inserted-list-item-id') || x;
      });

      // After clearing the content from the selected text from nodes in the selection,
      // some segments can be empty but still contain empty spans
      // so lets clear them entirely
      _.reverse(allContentEditableInSelection).forEach(function(element) {
        if (contentEditable) {
          $(contentEditable).trigger('lp:contenteditable:change');
        }

        // we don't want to call `processContentEditable` twice, (maybe we did it already for `isKeepSegment`)
        if (
          contentEditable && contentEditable[0] === element[0] || 
          $(element).closest('.deleted-element').get(0)
        ) {
          return;
        }

        caretNode = processContentEditable(element, caretNode, isBackwards);
      });

      // Place the caret in the right place
      if (caretNode instanceof jQuery) {
        caretNode = caretNode.get(0);
      }
      placeCaret(caretNode);

      if (beforeSaveFunc) {
        beforeSaveFunc();
      }

      tablesInSelection.forEach((table) => {
        if(table.textContent === "") {
          table.remove();
        }
      });

      if (!newDeleteRes) {
        createBulk(allContentEditableInSelection);
      }
      
      if (!preventCreateBatch) {
        $scope.histRegisterBatch($scope.histStack.length - 1, $scope.histIndex - currentUndoIndex);
      }

      if (structureChanged) {
        $scope.structureChanged();
      }
    };

    function isCaretInAnnotationArea() {
      var sel = rangy.getSelection();
      var focusNode = sel.focusNode;
      if (!focusNode) {
        return false;
      }
      var diffArea = focusNode.parentElement.closest('.highlight');
      var caretInAnnotationArea = !!(diffArea);
      return caretInAnnotationArea;
    }

    function currentAnnotationHasDeletedMarkers() {
      var diffList = document.querySelectorAll('.annotation.annotation--current [diff-index]');
      var originalLease = document.querySelector('.lease.lease--original');
      var markersChanged = false;
      diffList.forEach(function(diff) {
        var index = diff.getAttribute('diff-index');
        if (
          !$scope.diffgram.items[index]._isAffectingLeaseVar &&
          !$scope.diffgram.items[index]._isAffectingCrossReference &&
          !$scope.diffgram.items[index]._isAffectingAutoNumber
        ) {
          var originalMarker = originalLease.querySelector('[diff-index="' + index + '"]');
          if (!$scope.diffgram.items[index]._isBlocked) {
            if(!originalMarker) {
              $scope.diffgram.items[index]._hasDeletedMarkers = true;
              markersChanged = true;
            } else {
              $scope.diffgram.items[index]._hasDeletedMarkers = false;
            }
          }
        }
      });
      return markersChanged;
    }

    function initDiffgramWarnings(isFocusOnOriginalLease) {
      $timeout(function() {
        if (!$scope.diffgram.currentAnnotation)  {
          return;
        }
        $scope.caretInAnnotationArea = false;
        if (currentAnnotationHasDeletedMarkers()) {
          $scope.diffgram.currentAnnotation._hasDeletedMarkers = true;
        } else {
          $scope.diffgram.currentAnnotation._hasDeletedMarkers = false;
          if (isFocusOnOriginalLease && isCaretInAnnotationArea()) {
            $scope.caretInAnnotationArea = true;
          }
        }
      }, 0);
    }

    function openCorrespondingAnnotation() {
      var sel = rangy.getSelection();
      var focusNode = sel.focusNode;
      if (!focusNode) {
        return;
      }
      if (focusNode.nodeType === Node.TEXT_NODE) {
        focusNode = focusNode.parentElement;
      }
      var pid = focusNode.closest('[pid]') ? focusNode.closest('[pid]').getAttribute('pid') : null;
      if (pid) {
        pid = getCalculatedSectionId(focusNode, pid);
        var newAnnotation = document.querySelector(`.annotation[data-pid="${pid}"]`);
        if (newAnnotation) {
          $scope.annotationsHelper.jumpToAnnotation(pid);
        }
      }
    }

    function isActiveImage() {
      return !!(document.querySelector('img.active'));
    }

    $rootScope.isParagraph = function (focusNode) {
      if (!focusNode) {
        return false;
      }

      if (focusNode.nodeType === Node.TEXT_NODE) {
        focusNode = focusNode.parentElement;
      }

      let container = LeaseEditorService.findClosestContainer($(focusNode));

      if (container[0] && container[0].classList.contains('new-paragraph')) {
        return true;
      }

      if (focusNode.nodeType === Node.ELEMENT_NODE && focusNode.classList.contains('new-paragraph')) {
        return true;
      } else if ($rootScope.isListItem(container)) {
        return false;
      } else {
        return true;
      }
    }

    function disableIndentButtons(isDisable) {
      if (isDisable !== $scope.indentDisable) {
        $scope.indentDisable = isDisable;
        // set manually to make sure changes apply even if scope doesn't update
        $('.indent-btn').attr('disabled', isDisable);
      }
    }

    $scope.updateEmptyConceptTitleDisplay = function (){
      if (window.user?.company?.companyProfile?.conceptTitleProtection) {
        document.querySelectorAll('.empty-concept-title').forEach((concept) => {
          concept.classList.remove('empty-concept-title');
        });

        document.querySelectorAll('.in-edit concept-title').forEach((concept) => {
          if (concept.innerText.replace(/\u200B/g,'').trim().length === 0) {
            concept.classList.add('empty-concept-title');
          }
        });
      }
    }

    // bind to keydown for larger deletions (ie. when selection is made from outside of a contenteditable
    $scope.bindLeaseEvents = function() {
      // Disable B/I/U buttons when selecting big selections
      // TODO: remove once bolding works on big selections
      $(document).on('selectionchange', _.throttle(function(e) {
        if ($scope.diffgram.processing) {
          return;
        }

        $scope.updateEmptyConceptTitleDisplay();

        if ($scope.diffgram.on) {
          openCorrespondingAnnotation();
          initDiffgramWarnings(true);
        }

        if(!LeaseEditorService.isSelecting){
          return;
        }
        if (LeaseEditorService.isSelecting()) {
          var isSelectionOutsideLease =
            $(rangy.getSelection().anchorNode)
              .closest('.lease')
              .not('.lease--modified').length === 0;
          $scope.disableDecorationButtons(isSelectionOutsideLease);
        } else if (LeaseEditorService.isBlinkingCaret()) {
          $scope.disableDecorationButtons(false);

          var sel = rangy.getSelection();
          var focusNode = sel.focusNode;
          var contentEditable = LeaseEditorService.findClosestContentEditable(
            sel.anchorNode,
          );
          contentEditable = $(contentEditable).get(0);

          if (contentEditable) {
            LeaseEditorService.enableEditingForSegment(contentEditable);
            disableIndentButtons(!$rootScope.isParagraph(focusNode) && !isActiveImage());
          }
        } else {
          $scope.disableDecorationButtons(true);
          disableIndentButtons(!isActiveImage());
        }

        LeaseEditorService.skipMarkers();
        autopilotManager.updatePromptContext();
      }, 250, { trailing: true }));

      $(document).on('click', 'crr', function (e) {
        e.preventDefault();
        let id = e.currentTarget.id;
        let crb = document.querySelector(`crb#${id}`, true);
        let oldActive = document.querySelector('crb-active');
        if(oldActive) {
          oldActive.classList.remove('crb-active');
        }
        if(crb) {
          crb.classList.add('crb-active');
          crb.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      });

      $scope.handleCopy = async function(event) {
        if (LeaseEditorService.isSelecting()) {
          const sel = rangy.getSelection();
          const range = sel.getRangeAt(0);
          const commonAncestorContainer = range.commonAncestorContainer;
  
          if (commonAncestorContainer.closest(".lease")) {
            LeaseEditorService.disableEditingForSegment(sel.anchorNode);
            await LeaseEditorService.copySelection();
            window.track.event(new CopyEvent({
              context: $rootScope.getContext(),
            }));
  
            if (event) {
              event.preventDefault();
            }
          }
        }
      }

      $scope.handleCut = async function(event) {
        if (LeaseEditorService.isSelecting()) {
          const sel = rangy.getSelection();
          const range = sel.getRangeAt(0);
          const rangeCount = sel.rangeCount;
          const rangeToString = range.toString();
          const commonAncestorContainer = range.commonAncestorContainer;
  
          // First, let's copy the selection to the clipboard
          if (commonAncestorContainer.closest(".lease")) {
            LeaseEditorService.disableEditingForSegment(sel.anchorNode);
            await LeaseEditorService.copySelection();
            window.track.event(new CutEvent({
              context: $rootScope.getContext(),
            }));

            if (event) {
              event.preventDefault();
            }
  
            // Then, delete the selection
            if (rangeCount > 0) {
              if (rangeToString.length > 0) {
                await $rootScope.deleteSelection(null, null, true);
              }
            }
          }
        }
      }

      document.addEventListener('cut', async function(e) {
        $scope.handleCut(e);
      });

      document.addEventListener('copy', async function(event) {
        $scope.handleCopy(event);
      });

      $(document).on('selectionchange', _.throttle(function(e) {
        if ($scope.diffgram.processing) {
          return;
        }

        var sel = rangy.getSelection();
        if (!sel.anchorNode || sel.anchorNode === document) {
          return;
        }

        if (sel.anchorNode.parentElement.closest('#edit-building-language')) {
          LeaseEditorService.setContentEditableColor(window.CONSTS.ADMIN_COLOR);
          return;
        }


        if(!LeaseEditorService.isSelecting){
          return;
        }
        if (LeaseEditorService.isSelecting()) {
          let closestContentEditable;

          if (sel.isBackwards()) {
            closestContentEditable = $(
              LeaseEditorService.findClosestContentEditable(sel.focusNode),
            );
          } else {
            closestContentEditable = $(
              LeaseEditorService.findClosestContentEditable(sel.anchorNode),
            );
          }

          if ($(closestContentEditable).length > 0) {
            // There's a selection, let's disable editing to allow multi-segment selection
            LeaseEditorService.disableEditingForSegment(closestContentEditable);
          }
        }
      }, 250, { trailing: true }));

      // Moving the pointer within one paragraph re-resolves the same segment every
      // 250ms, and each call walks the surrounding containers -- several
      // full-document block scans -- to reach a state the document is already in.
      // Remember what the last move applied and skip the repeat. Cleared on mouseup
      // and mousedown so a drag, or any edit that follows one, always re-applies.
      var lastHoverSegment = null;
      var lastHoverWasDrag = null;

      $scope.resetHoverEditingMemo = function() {
        lastHoverSegment = null;
        lastHoverWasDrag = null;
      };

      $('.lease:not(.lease--modified) p, .lease:not(.lease--modified) h1, .lease:not(.lease--modified) h2, .lease:not(.lease--modified) h3, .lease:not(.lease--modified) h4, .lease:not(.lease--modified) h5, .lease:not(.lease--modified) h6').on('mousemove', _.throttle(function(e) {
        if ($scope.diffgram.processing) {
          return;
        }

        e.stopImmediatePropagation();
        e.stopPropagation();

        var element = $(this)
          .find('.editable')
          .first();

        var isDrag = e.which === window.ENUMS.MOUSE.LEFT_MOUSE_BUTTON;
        var segment = element.get(0) || null;

        // A live selection changes which elements the call touches even when the
        // hovered segment has not moved, so never skip while one exists.
        if (
          segment &&
          segment === lastHoverSegment &&
          isDrag === lastHoverWasDrag &&
          !LeaseEditorService.isSelecting()
        ) {
          return;
        }

        lastHoverSegment = segment;
        lastHoverWasDrag = isDrag;

        if (isDrag) {
          LeaseEditorService.disableEditingForSegment(element);
        } else {
          LeaseEditorService.enableEditingForSegment(element);
        }
      }, 250));
    };

    $('.lease')
      .not('.lease--modified')
      .on('mousedown mouseup', function() {
        // A press or release can start a selection or an edit, either of which
        // changes what the next hover needs to apply.
        if ($scope.resetHoverEditingMemo) {
          $scope.resetHoverEditingMemo();
        }
      });

    $('.lease')
      .not('.lease--modified')
      .on('mouseup', function(e) {
        if ($scope.diffgram.processing) {
          return;
        }

        if (
          e && e.target && e.target.classList &&
          e.target.classList.contains('empty-concept-title')
        ) {
          $rootScope.placeCaretAtStart($(e.target));
        }

        if (LeaseEditorService.isSelecting()) {
          const sel = rangy.getSelection();
          
          if (sel.isBackwards()) {
            $scope.currentlyEditedNode = sel.anchorNode.getTopAnchor();
          } else {
            $scope.currentlyEditedNode = sel.focusNode.getTopAnchor();
          }
        }

        cacheBlocks();
      });

    // close all toasts when clicking outside them
    $('.lease')
      .not('.lease--modified')
      .on('click keydown', function(e) {
        $mdToast.cancel();
        var sel = rangy.getSelection();
        if (e.target.nodeName != 'IMG' && e.target.nodeName != 'BUTTON') {
          LeaseEditorService.deselectImage();
        }
      });

    /* -------------------------------------------------------------------------------------------------------------- */

    /* --------------------------------------------------------------------------------------------------------------
     -- Undo functionality ------------------------------------------------------------------------------------------ */

    $scope.histStack = [];
    $scope.histBatcher = {};
    $scope.histIgnoreClearBatch = 0;
    $scope.histIndex = -1;
    $scope.undoDisabled = true;
    $scope.redoDisabled = true;
    $scope.isSaving = false;

    // Used to prevent a recompile from adding a new entry to the undo/redo stack.
    // This is because an undo or redo itself must recompile an element in order for it to link to
    // lease fields.
    // Object is indexed on an id (free-text/sectionId)
    $scope.noHist = {};
    $scope.shouldRecompile = {}; // similar idea for compile

    // Object to map list items to their inserted-list-item-ids for looking for undo/redo
    $scope.listItemUndoTracking = {};
    $scope.listItemObjects = {};

    // Listen for tabstop changes from ruler directive and register in undo history
    $rootScope.$on('tabstops:changed', function(event, data) {
      // Only register history if we have before/after state (from ruler modifications)
      if (!data.beforeState || !data.afterState || !data.restoreState) {
        return;
      }

      const actionDescriptions = {
        'add': 'added tabstop',
        'remove': 'removed tabstop',
        'move': 'moved tabstop',
        'change-alignment': 'changed tabstop alignment',
        'change-leader': 'changed tabstop leader'
      };

      const description = actionDescriptions[data.action] || 'modified tabstops';
      const paragraphCount = data.paragraphCount || 1;
      const suffix = paragraphCount > 1 ? ' (' + paragraphCount + ' paragraphs)' : '';

      // Persist tabstop changes to restyle object and save
      // This ensures changes are preserved when the document is reloaded
      if (!$scope.lease.restyle) {
        $scope.lease.restyle = [];
      }

      // Iterate through affected paragraphs (afterState keys are section-ids)
      Object.keys(data.afterState).forEach(function(sectionId) {
        // Get paragraph element using section-id
        const paragraph = document.querySelector('[pid="' + sectionId + '"]');
        
        if (!paragraph) {
          return;
        }

        // Get paragraph ID (pid) for restyle object
        const pid = paragraph.getAttribute('pid');
        if (!pid) {
          return;
        }

        // Get current tabstops from the paragraph's data-tabstops attribute
        let tabstops = [];
        const tabstopsAttr = paragraph.getAttribute('data-tabstops');
        if (tabstopsAttr) {
          try {
            tabstops = JSON.parse(tabstopsAttr);
          } catch (e) {
            console.warn('Failed to parse data-tabstops for paragraph', pid, e);
          }
        }

        // Initialize restyle object for this paragraph if it doesn't exist
        if (!$scope.lease.restyle[pid]) {
          let originalMarginLeft = paragraph.style.marginLeft || '0pt';
          let originalTextIndent = paragraph.style.textIndent || '0pt';
          let originalTextAlign = paragraph.style.textAlign || 'start';
          let originalFontSize = paragraph.style.fontSize || $scope.defaultFont.fontSize;
          let originalLineHeight = paragraph.style.lineHeight || $scope.defaultFont.lineHeight;
          let paragraphIndent = null;
          let textIndent = null;

          if (parseInt(originalMarginLeft) % window.CONSTS.STEP_SIZE === 0) {
            paragraphIndent = parseInt(originalMarginLeft) / window.CONSTS.STEP_SIZE;
          }

          if (parseInt(originalTextIndent) % window.CONSTS.STEP_SIZE === 0) {
            textIndent = parseInt(originalTextIndent) / window.CONSTS.STEP_SIZE;
          }

          $scope.lease.restyle[pid] = {
            paragraphId: pid,
            originalParagraphIndent: originalMarginLeft,
            originalTextIndent: originalTextIndent,
            originalTextAlign: originalTextAlign,
            originalFontSize: originalFontSize,
            originalLineHeight: originalLineHeight,
            paragraphIndent: paragraphIndent,
            textIndent: textIndent,
            textAlign: originalTextAlign,
            fontSize: originalFontSize,
            lineHeight: originalLineHeight,
          };

          $scope.setOriginalStyle(paragraph, $scope.lease.restyle[pid]);
        }

        // Update tabstops in restyle object
        $scope.lease.restyle[pid].tabstops = tabstops;

        $scope.setOriginalStyle(paragraph, $scope.lease.restyle[pid]);
      });
      

      // Persist changes to server
      $scope.update();

      $scope.addToHistory({
        id: 'tabstop-' + data.action,
        desc: description + suffix,
        redo: function() {
          var deferred = $q.defer();
          data.restoreState(data.afterState);
          
          // Update restyle object after restoring state
          Object.keys(data.afterState).forEach(function(sectionId) {
            const paragraph = document.querySelector('[pid="' + sectionId + '"]');
            
            if (!paragraph) {
              return;
            }

            const pid = paragraph.getAttribute('pid');
            if (!pid || !$scope.lease.restyle[pid]) {
              return;
            }

            const tabstopsAttr = paragraph.getAttribute('data-tabstops');
            let tabstops = [];
            if (tabstopsAttr) {
              try {
                tabstops = JSON.parse(tabstopsAttr);
              } catch (e) {
                console.warn('Failed to parse data-tabstops for paragraph', pid, e);
              }
            }

            $scope.lease.restyle[pid].tabstops = tabstops;
          });

          $scope.update();
          
          // Trigger ruler update if visible
          if ($rootScope.lpRuler) {
            $rootScope.lpRuler.update();
          }
          deferred.resolve();
          return deferred.promise;
        },
        undo: function() {
          var deferred = $q.defer();
          data.restoreState(data.beforeState);
          
          // Update restyle object after restoring state
          Object.keys(data.beforeState).forEach(function(sectionId) {
            const paragraph = document.querySelector('[pid="' + sectionId + '"]');
            
            if (!paragraph) {
              return;
            }

            const pid = paragraph.getAttribute('pid');
            if (!pid || !$scope.lease.restyle[pid]) {
              return;
            }

            const tabstopsAttr = paragraph.getAttribute('data-tabstops');
            let tabstops = [];
            if (tabstopsAttr) {
              try {
                tabstops = JSON.parse(tabstopsAttr);
              } catch (e) {
                console.warn('Failed to parse data-tabstops for paragraph', pid, e);
              }
            }

            $scope.lease.restyle[pid].tabstops = tabstops;
          });

          $scope.update();
          
          // Trigger ruler update if visible
          if ($rootScope.lpRuler) {
            $rootScope.lpRuler.update();
          }
          deferred.resolve();
          return deferred.promise;
        }
      });
    });

    function getIndentStepSize() {
      return window.CONSTS && window.CONSTS.STEP_SIZE ? window.CONSTS.STEP_SIZE : 36;
    }

    function cssLengthToPoints(lengthValue) {
      if (lengthValue === null || lengthValue === undefined || lengthValue === '') {
        return 0;
      }

      const normalizedLength = String(lengthValue).trim().toLowerCase();
      const numericValue = parseFloat(normalizedLength);

      if (isNaN(numericValue)) {
        return 0;
      }

      if (normalizedLength.endsWith('px')) {
        return numericValue * (72 / 96);
      }

      return numericValue;
    }

    // Listen for indent changes from ruler directive and register in undo history
    $rootScope.$on('indent:changed', function(event, data) {
      // Only register history if we have before/after state (from ruler modifications)
      if (!data.beforeState || !data.afterState || !data.restoreState) {
        return;
      }

      const indentTypeDescriptions = {
        'first-line': 'first-line indent',
        'hanging': 'hanging indent',
        'right-indent': 'right indent'
      };

      const description = 'adjusted ' + (indentTypeDescriptions[data.indentType] || 'indent');
      const paragraphCount = data.paragraphCount || 1;
      const suffix = paragraphCount > 1 ? ' (' + paragraphCount + ' paragraphs)' : '';

      // Persist indent changes to restyle object and save
      // This ensures changes are preserved when the document is reloaded
      if (!$scope.lease.restyle) {
        $scope.lease.restyle = [];
      }

      // Iterate through affected paragraphs (afterState keys are pids)
      Object.keys(data.afterState).forEach(function(pid) {
        // Get paragraph element using pid
        const paragraph = document.querySelector('[pid="' + pid + '"]');
        
        if (!paragraph) {
          return;
        }

        // Get current indent values from the paragraph
        const currentTextIndent = paragraph.style.textIndent || '0pt';
        const currentMarginLeft = paragraph.style.marginLeft || '0pt';
        const currentMarginRight = paragraph.style.marginRight || '0pt';
        
        // Calculate indent steps (points / STEP_SIZE).
        // Preserve fractional steps (e.g. quarter-step drags) so ruler changes
        // are persisted instead of being rounded away.
        let textIndent = null;
        let paragraphIndent = null;
        let rightIndent = null;
        
        const textIndentPoints = cssLengthToPoints(currentTextIndent);
        const marginLeftPoints = cssLengthToPoints(currentMarginLeft);
        const marginRightPoints = cssLengthToPoints(currentMarginRight);
        const stepSize = getIndentStepSize();
        textIndent = textIndentPoints / stepSize;
        paragraphIndent = marginLeftPoints / stepSize;
        rightIndent = marginRightPoints / stepSize;

        // Initialize restyle object for this paragraph if it doesn't exist
        if (!$scope.lease.restyle[pid]) {
          let originalMarginLeft = paragraph.style.marginLeft || '0pt';
          let originalTextIndent = paragraph.style.textIndent || '0pt';
          let originalTextAlign = paragraph.style.textAlign || 'start';
          let originalFontSize = paragraph.style.fontSize || $scope.defaultFont.fontSize;
          let originalLineHeight = paragraph.style.lineHeight || $scope.defaultFont.lineHeight;
          let originalMarginRight = paragraph.style.marginRight || '0pt';
          let originalParagraphIndent = null;
          let originalTextIndentSteps = null;
          let originalRightIndent = null;

          const originalMarginLeftPoints = cssLengthToPoints(originalMarginLeft);
          const originalTextIndentPoints = cssLengthToPoints(originalTextIndent);
          const originalMarginRightPoints = cssLengthToPoints(originalMarginRight);

          if (originalMarginLeftPoints % stepSize === 0) {
            originalParagraphIndent = originalMarginLeftPoints / stepSize;
          }

          if (originalTextIndentPoints % stepSize === 0) {
            originalTextIndentSteps = originalTextIndentPoints / stepSize;
          }
          if (originalMarginRightPoints % stepSize === 0) {
            originalRightIndent = originalMarginRightPoints / stepSize;
          }

          $scope.lease.restyle[pid] = {
            paragraphId: pid,
            originalParagraphIndent: originalMarginLeft,
            originalTextIndent: originalTextIndent,
            originalTextAlign: originalTextAlign,
            originalFontSize: originalFontSize,
            originalLineHeight: originalLineHeight,
            originalRightIndent: originalMarginRight,
            paragraphIndent: originalParagraphIndent,
            textIndent: originalTextIndentSteps,
            rightIndent: originalRightIndent,
            textAlign: originalTextAlign,
            fontSize: originalFontSize,
            lineHeight: originalLineHeight,
          };

          $scope.setOriginalStyle(paragraph, $scope.lease.restyle[pid]);
        }

        // Update indent values in restyle object
        $scope.lease.restyle[pid].textIndent = textIndent;
        $scope.lease.restyle[pid].paragraphIndent = paragraphIndent;
        $scope.lease.restyle[pid].rightIndent = rightIndent;

        $scope.setOriginalStyle(paragraph, $scope.lease.restyle[pid]);
      });

      // Persist changes to server
      $scope.update();

      $scope.addToHistory({
        id: 'indent-' + data.action,
        desc: description + suffix,
        redo: function() {
          var deferred = $q.defer();
          data.restoreState(data.afterState);
          
          // Update restyle object after restoring state
          Object.keys(data.afterState).forEach(function(pid) {
            const paragraph = document.querySelector('[pid="' + pid + '"]');
            
            if (!paragraph) {
              return;
            }

            if (!pid || !$scope.lease.restyle[pid]) {
              return;
            }

            const currentTextIndent = paragraph.style.textIndent || '0pt';
            const currentMarginLeft = paragraph.style.marginLeft || '0pt';
            const currentMarginRight = paragraph.style.marginRight || '0pt';
            
            const textIndentPoints = cssLengthToPoints(currentTextIndent);
            const marginLeftPoints = cssLengthToPoints(currentMarginLeft);
            const marginRightPoints = cssLengthToPoints(currentMarginRight);
            const stepSize = getIndentStepSize();
            const textIndent = textIndentPoints / stepSize;
            const paragraphIndent = marginLeftPoints / stepSize;
            const rightIndent = marginRightPoints / stepSize;

            $scope.lease.restyle[pid].textIndent = textIndent;
            $scope.lease.restyle[pid].paragraphIndent = paragraphIndent;
            $scope.lease.restyle[pid].rightIndent = rightIndent;
          });

          $scope.update();
          
          // Trigger ruler update if visible
          if ($rootScope.lpRuler) {
            $rootScope.lpRuler.update();
          }
          deferred.resolve();
          return deferred.promise;
        },
        undo: function() {
          var deferred = $q.defer();
          data.restoreState(data.beforeState);
          
          // Update restyle object after restoring state
          Object.keys(data.beforeState).forEach(function(pid) {
            const paragraph = document.querySelector('[pid="' + pid + '"]');
            
            if (!paragraph) {
              return;
            }

            if (!pid || !$scope.lease.restyle[pid]) {
              return;
            }

            const currentTextIndent = paragraph.style.textIndent || '0pt';
            const currentMarginLeft = paragraph.style.marginLeft || '0pt';
            const currentMarginRight = paragraph.style.marginRight || '0pt';
            
            const textIndentPoints = cssLengthToPoints(currentTextIndent);
            const marginLeftPoints = cssLengthToPoints(currentMarginLeft);
            const marginRightPoints = cssLengthToPoints(currentMarginRight);
            const stepSize = getIndentStepSize();
            const textIndent = textIndentPoints / stepSize;
            const paragraphIndent = marginLeftPoints / stepSize;
            const rightIndent = marginRightPoints / stepSize;

            $scope.lease.restyle[pid].textIndent = textIndent;
            $scope.lease.restyle[pid].paragraphIndent = paragraphIndent;
            $scope.lease.restyle[pid].rightIndent = rightIndent;
          });

          $scope.update();
          
          // Trigger ruler update if visible
          if ($rootScope.lpRuler) {
            $rootScope.lpRuler.update();
          }
          deferred.resolve();
          return deferred.promise;
        }
      });
    });

    // Register a batch starting at `index` going back `n` history objects
    $scope.histRegisterBatch = function(index, n) {
      // register undo
      if ($scope.histBatcher[index]) {
        $scope.histBatcher[index].undo = n;
      } else {
        $scope.histBatcher[index] = {
          undo: n,
          redo: 0,
        };
      }

      // register redo
      if ($scope.histBatcher[index - n]) {
        $scope.histBatcher[index - n].redo = n;
      } else {
        $scope.histBatcher[index - n] = {
          undo: 0,
          redo: n,
        };
      }
    };

    $scope.histClearFutureBatch = function(afterIdx) {
      $scope.histBatcher = _.pickBy($scope.histBatcher, function(v, k) {
        k = parseInt(k);
        return k <= afterIdx && k + v.redo <= +afterIdx;
      });
    };

    function restoreSelection(bookmark) {
      if (!bookmark) {
        return;
      }
      // restore selection
      $timeout(() => {
        var sel = rangy.getSelection();
        sel.moveToBookmark(bookmark);

        // scroll into view
        var focusNode = sel.focusNode;

        if (focusNode) {
          if (focusNode.nodeType === Node.TEXT_NODE) {
            focusNode = focusNode.parentElement;
          }
          focusNode.scrollIntoViewIfNeeded();
          if (!LeaseEditorService.isSelecting()) {
            // ensure caret is blinking
            var node = LeaseEditorService.findClosestContentEditable(sel.focusNode)[0];
            LeaseEditorService.enableEditingForSegment(node);
            // fallback - for some reason there is no cursor, so we manually place it.
            if (node && !$(node).is(':focus')) {
              node.focus();
              $rootScope.placeCaretAtPosition(sel.focusNode, sel.focusOffset);
            }
          }
        }
      });
    }

    $rootScope.restoreCaret = function() {
      if (!isActiveImage()) {
        restoreSelection(window.savedSelection);
      }
    };

    $rootScope.getSelectionData = function() {
      var sel = rangy.getSelection();
      if (!sel.anchorNode) return;
      // find the closest container that is common to the anchorNode and the focusNode
      var editedContainer = sel.getRangeAt(0).commonAncestorContainer;
      editedContainer =
        LeaseEditorService.findClosestContentEditable(editedContainer)[0] ||
        editedContainer;

      if (LeaseEditorService.isNewListItem(editedContainer)) {
        editedContainer = LeaseEditorService.findClosestContainer(
          $(editedContainer).closest('.inserted-list-item'),
        )[0];
      }

      return sel.getBookmark(editedContainer);
    };

    $rootScope.saveSelection = function(changeMade, bookmark) {
      // if we are in the middle of continous action - no need to save the selection again
      if (changeMade && window.savedSelection && window.prevActionIsEdit) {
        return;
      }

      var sel = rangy.getSelection();
      var contentEditable = LeaseEditorService.findClosestContentEditable($(sel.anchorNode).get(0));
      if (!bookmark && !contentEditable) {
        return;
      }
      window.prevActionIsEdit = changeMade;
      window.savedSelection = bookmark || $rootScope.getSelectionData();
    };

    /* action is an object with:
        batch <bool> - if true, redo/undo will return the number of steps in their direction to batch (0 = ignore)
                       you only need to add UNDO batchers, redo ones will automatically be inserted after undo
        redo()
        undo()
        desc <string>
     */
    $scope.addToHistory = function(action) {
      action.prevSelection = window.savedSelection;
      // empty selection obj for the next actions (to indicate that the action completed)
      window.savedSelection = null;

      $scope.shouldRecompile[action.id] = false;
      action
        .redo(true)
        .then(function(actionData) {
          $scope.updateUndoRedoStatus();

          if (actionData && actionData['selection']) {
            action.currSelection = actionData.selection;
          }
        })
        .catch(ex => {
          console.log(ex);
        });

      // remove actions past current point
      $scope.histStack = $scope.histStack.slice(0, $scope.histIndex + 1);
      if ($scope.histIgnoreClearBatch > 0) {
        $scope.histIgnoreClearBatch--;
      } else {
        $scope.histClearFutureBatch($scope.histIndex);
      }

      // add the current action to the stack
      $scope.histStack.push(action);

      $scope.histIndex = $scope.histStack.length - 1;

      // batcher: copy `prevSelection` to the first action in the batch.
      var batchInfo = $scope.histBatcher[$scope.histIndex];
      if (batchInfo && batchInfo.undo) {
        $scope.histStack[$scope.histIndex - batchInfo.undo + 1].prevSelection =
          action.prevSelection;
      }

      return $scope.histIndex;
    };



    $scope.normalizeListItemArray = function(){
      const updatedArray = [];
      
      $scope.listItems.forEach((item)=>{
        
        const items =  updatedArray.filter((obj)=>{
          return item.afterFreeText ==obj.afterFreeText && 
          item.bodyFreeText ==obj.bodyFreeText &&
          item.headerFreeText ==obj.headerFreeText 
        });

        if(items.length === 0){
          updatedArray.push(item);
        }
        
      });
      if($scope.listItems.length !== updatedArray.length ){
        $scope.listItems = updatedArray;
        $scope.lease.orderedListitem = $scope.serializeOrderedListitem();
        $scope.update();
      }
    }

    $scope.callUndo = function() {
      const retVal =  window.applyFilterVisible($scope._callUndo,[]);
      $scope.normalizeListItemArray();
      return retVal;
    }
    $scope._callUndo = function() {
      if ($scope.histIndex === -1 || $scope.undoDisabled) {
        return;
      }

      document.querySelector('.undo').classList.add('undo--pending');
      $scope.undoDisabled = true;
      $scope.safeApply();

      var batchInfo = $scope.histBatcher[$scope.histIndex];
      var batchLength = batchInfo ? batchInfo.undo || 1 : 1;

      const undoList = [];
      for (var i = 0; i < batchLength; i++) {
        var curObj = $scope.histStack[$scope.histIndex - i];
        $scope.shouldRecompile[curObj.id] = true;
        undoList.push(curObj.undo());
      }

      $q.all(undoList).then(function() {
        $scope.histIndex -= batchLength;
        var curObj = $scope.histStack[$scope.histIndex + 1];
        $scope.updateUndoRedoStatus();
        if(curObj && !curObj.ignoreCaret){
          restoreSelection(curObj.prevSelection);
        }
        LeaseEditorService.bindImgEvents($('.new-paragraph img'), $scope);

        setTimeout(function() {
          document.querySelector('.undo').classList.remove('undo--pending');
        }, 2000);
      });

      $scope.safeApply();
    };

    $scope.callRedo = function() {
      const retVal =  window.applyFilterVisible($scope._callRedo,[]);
      $scope.normalizeListItemArray();
      return retVal;
    }
    $scope._callRedo = function() {
      if (
        $scope.histIndex + 1 >= $scope.histStack.length ||
        $scope.redoDisabled
      ) {
        return;
      }

      document.querySelector('.redo').classList.add('redo--pending');
      $scope.redoDisabled = true;
      $scope.safeApply();

      var batchInfo = $scope.histBatcher[$scope.histIndex];
      var batchLength = batchInfo ? batchInfo.redo || 1 : 1;

      const redoList = [];
      for (var i = 0; i < batchLength; i++) {
        var curObj = $scope.histStack[$scope.histIndex + i + 1];
        $scope.shouldRecompile[curObj.id] = true;
        redoList.push(curObj.redo());
      }

      $q.all(redoList).then(function() {
        $scope.histIndex += batchLength;
        var curObj = $scope.histStack[$scope.histIndex];
        $scope.updateUndoRedoStatus();
        if(curObj && !curObj.ignoreCaret){
          restoreSelection(curObj.currSelection);
        }
        LeaseEditorService.bindImgEvents($('.new-paragraph img'), $scope);

        setTimeout(function() {
          document.querySelector('.redo').classList.remove('redo--pending');
        }, 2000);
      });
    };

    $scope.updateUndoRedoStatus = function() {
      if ($scope.histIndex < 0) {
        $scope.undoDisabled = true;
      } else {
        $scope.undoDisabled = false;
      }
      if ($scope.histIndex >= $scope.histStack.length - 1) {
        $scope.redoDisabled = true;
      } else {
        $scope.redoDisabled = false;
      }
      $scope.safeApply();
    };

    /* -------------------------------------------------------------------------------------------------------------- */

    /* --------------------------------------------------------------------------------------------------------------
     -- Inline editor functionality --------------------------------------------------------------------------------- */

    // TODO: disable in production (if necessary)
    $scope.debuggingMode = true;

    /**
     * Wrapper for inline editor functions, features, and variables.
     *
     * @property {boolean} enabled - True if the inline editor features should be active.  False otherwise.
     * @property {number} refreshStep - Incremented with each change of the inline editor to force it to refresh/digest.
     * @property {LeaseField[]} activeFields - Fields currently being displayed in the inline editor.
     * @property {Object.<LeaseField, LeaseField[]>} computedFields - A mapping from fields (which are computed) to the fields each one relies on.
     * @property {Object.<LeaseField, LeaseField>} computedFieldsReversed - A mapping from editor controls to the fields they are computed into.
     */
    $scope.inline = {
      on: false,
      enabled: false,
      pinned: true,
      refreshStep: 0,
      activeFields: [],
      computedFields: {
        'lease.tenantInfo.entityTypeCombined': [
          'lease.tenantInfo.entityType',
          'lease.tenantInfo.entityTypeOther',
        ],
        'lease.assigneeInfo.entityTypeCombined': [
          'lease.assigneeInfo.entityType',
          'lease.assigneeInfo.entityTypeOther',
        ],
        'lease.franchisorInfo.entityTypeCombined': [
          'lease.franchisorInfo.entityType',
          'lease.franchisorInfo.entityTypeOther',
        ],
        'lease.guarantorInfo.entityTypeCombined': [
          'lease.guarantorInfo.entityType',
          'lease.guarantorInfo.entityTypeOther',
        ],
        'lease.guarantorInfo.entities[0].entityTypeCombined': [
          'lease.guarantorInfo.entities[0].entityType',
          'lease.guarantorInfo.entities[0].entityTypeOther',
        ],
        'lease.hasSpecialRights': [
          'lease.hasRenewalOption',
          'lease.hasRightOfFirstOffer',
          'lease.hasExpansionOption',
          'lease.hasParking',
          'lease.hasTenantTerminationOption',
          'lease.hasRooftopEquipment',
        ],
        'lease.hasHardCommencementDate': [
          'lease.tenantImprovements.type',
          'lease.estimatedCommencementDate',
          'hasSpecificExpirationDate',
          'lease.hasInitialDeliveryWork',
          'estimatedCommencementDateType',
        ],
        'lease.hasBroker': [
          'lease.hasLandlordBroker',
          'lease.tenantInfo.hasBroker',
        ],
        'lease.guarantor': ['lease.guarantor', 'lease.addGuarantor'],
        'lease.fixedCam': ['lease.camType'],
        'lease.annualBaseRent': ['lease.rentPeriods[0].totalRent'],
      },
      computedFieldsReversed: {},
    };
    _.forOwn($scope.inline.computedFields, function(val, key) {
      for (var i = 0; i < val.length; i++) {
        $scope.inline.computedFieldsReversed[val[i]] = key;
      }
    });

    /**
     * Utility functions for the inline editor.
     * See each function for documentation.
     */
    $scope.inline.util = {
      /**
       * Get all the related fields for a given lease-if HTML node.
       *
       * @param {HTMLElement} node - Node for which to find related field.
       *
       * @returns {LeaseField[]} - List of lease fields which relate to the input node.
       */
      getRelateds: function(node) {
        var toRet = [];
        var $node = $(node);
        if ($node.attr('hl')) {
          toRet.push($node.attr('hl'));
        }
        if ($node.attr('hl2')) {
          toRet.push($node.attr('hl2'));
        }
        if ($node.attr('hl3')) {
          toRet.push($node.attr('hl3'));
        }
        if ($node.attr('hl4')) {
          toRet.push($node.attr('hl4'));
        }
        if ($node.attr('hl5')) {
          toRet.push($node.attr('hl5'));
        }
        if ($node.attr('hl6')) {
          toRet.push($node.attr('hl6'));
        }
        if ($node.attr('hl7')) {
          toRet.push($node.attr('hl7'));
        }
        if ($node.attr('hl8')) {
          toRet.push($node.attr('hl8'));
        }
        if ($node.attr('hl9')) {
          toRet.push($node.attr('hl9'));
        }
        if ($node.attr('hl10')) {
          toRet.push($node.attr('hl10'));
        }
        if ($node.attr('hl11')) {
          toRet.push($node.attr('hl11'));
        }
        if ($node.attr('hl12')) {
          toRet.push($node.attr('hl12'));
        }
        if ($node.attr('hl13')) {
          toRet.push($node.attr('hl13'));
        }
        if ($node.attr('hl14')) {
          toRet.push($node.attr('hl14'));
        }
        if ($node.attr('hl15')) {
          toRet.push($node.attr('hl15'));
        }
        return toRet;
      },
      /**
       * Check the fields given to ensure they are valid fields which can be linked to an editor control.
       *
       * @param {LeaseField[]} relateds - List of fields to filter for validity.
       * @param {HTMLElement} curElement - Element to check for $index for the isolate scope (for ng-repeats) to replace $index with.
       *
       * @returns {LeaseField[]} - List of valid lease fields which can be linked to editor controls.
       */
      checkRelateds: function(relateds, curElement) {
        var toReturn = [];

        if (!relateds) {
          return toReturn;
        }

        var elementScope = angular.element(curElement).scope();
        var isolateIdx = -1;
        if (elementScope) {
          isolateIdx =
            (elementScope.item && elementScope.item._originalIndex) ||
            elementScope.$index;
        }

        for (var i = 0; i < relateds.length; i++) {
          var elm = relateds[i];
          if (
            elm === 'lease.createdAt' ||
            elm === 'lease.error' ||
            elm === 'lease.company.error' ||
            elm === 'lease.freeTextError' ||
            elm.indexOf('building') >= 0
          ) {
            // filter out non-variable -> do nothing
          } else {
            // replace index if needed
            if (
              (elm.indexOf('$index') >= 0 ||
                elm.indexOf('item._originalIndex') >= 0) &&
              isolateIdx >= 0
            ) {
              elm = elm
                .replace('$index', isolateIdx)
                .replace('item._originalIndex', isolateIdx);
            }
            // check if this is a computed field that requires replacement
            toReturn = toReturn.concat(
              $scope.inline.util.computedFieldsConverter(elm),
            );
          }
        }

        return toReturn;
      },
      /**
       * For a given field, find the relevant fields which may have been used to compute that field.
       *
       * @param {LeaseField} computedField - Field in the lease which may be a computed field.
       *
       * @returns {LeaseField[]} - List containing either the input field or the fields used to compute the input field.
       */
      computedFieldsConverter: function(computedField) {
        if ($scope.inline.computedFields.hasOwnProperty(computedField)) {
          return $scope.inline.computedFields[computedField];
        } else {
          return [computedField];
        }
      },
      /**
       * For a given field, find the field which may be computed from this field.
       *
       * @param {LeaseField} inputField - Field in the lease which may be used to make a computed field.
       *
       * @returns {LeaseField} - Either the input field or the field which this field is used to compute.
       */
      computedFieldsReverseConverter: function(inputField) {
        if ($scope.inline.computedFieldsReversed.hasOwnProperty(inputField)) {
          return $scope.inline.computedFieldsReversed[inputField];
        } else {
          return inputField;
        }
      },
    };

    /**
     * EditorObj: Represents an editor control or a container for editor controls.
     *
     * @typedef {Object} EditorObj
     *
     * @property {string} type - Either "root", "card", "group", or "control" indicating the type of EditorObj.
     * @property {string} breadcrumb - Indicates the current location in the editor for this EditorObj.
     * @property {EditorObj[]} children - All children of this editorObj.
     * @property {jQuery} ref - References this node in the editor, should include all children.
     * @property {string} [title] - For type:"control", the title of the control object.
     * @property {LeaseField} [id] - For type:"control", the id of the variable modeled.
     * @property {EditorObj} [parent] - For all but type:"root", the parent card (for use in opening editor, etc.)
     */

    /**
     * Find any control nodes from the left-hand editor, and build the control objects for them.
     *
     * @param {HTMLElement} searchNode - The node in which to search for controls.
     * @param {string} breadcrumbSoFar - Represents the breadcrumb title so far within editor.
     * @param {EditorObj} parentObj - The parent object for a back reference when building EditorObjs.
     *
     * @returns {EditorObj[]} - all control nodes within the searchNode
     */
    $scope.inline.findControls = function(
      searchNode,
      breadcrumbSoFar,
      parentObj,
    ) {
      var controls = [];
      var editorControls, attributeName, groupEditorControls;

      editorControls = $(searchNode).find('editor-control[for]');

      $(editorControls).each(function() {
        var leaseVariable = $(this)
          .attr('for')
          .replace('$parent.$index', $(this).attr('parent-index'))
          .replace('$index', $(this).attr('index'));

        if (!leaseVariable) {
          return true;
        }

        var childObj = {
          type: 'control',
          id: leaseVariable,
          breadcrumb: breadcrumbSoFar,
          ref: $(this),
          parent: parentObj,
        };

        $scope.inline.indexedControls[leaseVariable] = childObj;
        controls.push(childObj);
      });

      groupEditorControls = $(searchNode).find('group-editor-control[for]');

      $(groupEditorControls).each(function() {
        var leaseVariable;
        leaseVariable = $(this).attr('for');

        var childObj = {
          type: 'group-control',
          id: leaseVariable,
          breadcrumb: breadcrumbSoFar,
          ref: $(this).attr('template'),
          parent: parentObj,
        };

        $scope.inline.indexedControls[leaseVariable] = childObj;
        controls.push(childObj);
      });
      return controls;
    };

    /**
     * Convert current left-hand editor into a tree of EditorObj.
     * Does this by automatically searches based on defined editor structure within the HTML document:
     *   - .collapse-card          (1 or more)
     *   |-- .collapse-card__title (1 per collapse-card)
     *   |-- .collapse-card__body  (1 per collapse-card)
     *   | \-- .info-group         (0 or more)
     *   |   \-- [CONTROLS]        (for each info group, 1 or more)
     *   \-- [CONTROLS]            (0 or more)
     *   CONTROLS are defined as: .input-container, md-switch, or md-radio-group, or md-checkbox
     *   NOTE: any node satisfying the above criteria to be a node must only contain one true control
     *
     * @returns {EditorObj} - Root EditorObj.
     */
    $scope.inline.processEditor = function() {
      // create root/starting point
      var editorData = {
        type: 'root',
        children: [],
        parent: null,
      };

      // iterate through editor cards
      _.forEach($('.collapse-card'), function(card) {
        var $card = $(card);
        var cardObj = {
          type: 'card',
          breadcrumb: $card
            .find('.collapse-card__title')
            .text()
            .trim(),
          children: [],
          ref: $card,
          parent: editorData,
        };

        // find all children for each card
        var children = [];
        $card
          .find('.collapse-card__body')
          .children()
          .each(function() {
            if ($(this).hasClass('info-group')) {
              // add in new control group
              var myBreadcrumb =
                cardObj.breadcrumb +
                ' > ' +
                $(this)
                  .find('.info-group-header')
                  .text()
                  .trim();
              var childObj = {
                type: 'group',
                breadcrumb: myBreadcrumb,
                ref: $(this),
                parent: cardObj,
              };
              childObj.children = $scope.inline.findControls(
                this,
                myBreadcrumb,
                childObj,
              );
              children.push(childObj);
            } else {
              // add all loose controls
              children = children.concat(
                $scope.inline.findControls(this, cardObj.breadcrumb, cardObj),
              );
            }
          });

        // assign children into card, then add the completed cardObj
        cardObj.children = children;
        editorData.children.push(cardObj);
      });

      return editorData;
    };

    /**
     * Show an editor field by opening the relevant card and focusing on the text box (if applicable).
     *
     * @param {EditorObj} controlObj - Represents the editor field we would like to show.
     */
    $scope.inline.showEditorField = function(controlObj) {
      // traverse up through parents to find path to open editor
      var obj = controlObj;
      var path = [];
      while (obj !== null) {
        path.unshift(obj); // add at beginning of path
        obj = obj.parent;
      }

      // go through path and open / navigate to the right part of the editor
      for (var i = 0; i < path.length; i++) {
        var cur = path[i];
        switch (cur.type) {
          case 'root':
            // collapse all?
            break;
          case 'card':
            if (cur.ref.hasClass('active')) {
              // card is already active/opened
            } else {
              // card is not active, so we must open it
              cur.ref.find('.collapse-card__heading').trigger('click');
            }
            break;
          case 'group':
            // do nothing -groups are already opened (as of now)
            break;
          case 'control':
            setTimeout(
              (function(cur) {
                return function() {
                  cur.ref.find2('[ng-model]').focus();
                };
              })(cur),
              200,
            );
            break;
        }
      }
    };

    /**
     * Find all linked related controls of parent elements for a given element.
     *
     * @param {HTMLElement} elem - Element to find relevant parent controls for.
     *
     * @returns {LeaseField[]} - List of relevant fields for the element's parents.
     */
    $scope.inline.findNestedControls = function(elem) {
      var nestedFields = [];
      $(elem)
        .parents('[lease-if-deprecated]')
        .each(function() {
          var $this = $(this);

          // ignore building-variable, prerender, and building lease-ifs
          if ($this.attr('lease-if-deprecated').indexOf('building') >= 0) {
            return true; // continue
          }

          var relateds = $scope.inline.util.getRelateds(this);

          relateds = $scope.inline.util.checkRelateds(relateds, this);
          if (relateds.length === 0) {
            return true; // continue
          }

          nestedFields = _.union(nestedFields, relateds);
        });

      return nestedFields;
    };

    /**
     * Open the inline editor from a click on an element.
     *
     * @param {jQuery} activeElm - Element that was clicked on to open the editor.
     * @param {LeaseField[]} relateds - Related fields to list in the inline editor.
     */
    $scope.inline.openInline = function(activeElm, relateds) {
      if ($(activeElm).closest('[lp-popout-modal]').length > 0) {
        this.hideInline();
        return;
      }
      $scope.inline.refresh();
      if ((!relateds || relateds.length === 0) && !$scope.inline.pinned) {
        $scope.inline.hideInline();
      } else {
        $scope.inline.$activeElm = $(activeElm);
        $scope.inline.showingInline = true;
        $scope.inline.activeFields = relateds;
        $scope.inline.refreshStep++;
        $scope.safeApply();
      }
    };

    /**
     * Directive mappings from the generated-directed attribute to the list of related fields.
     * We also maintain a reverse mapping for looking of the generated-directive attribute from a field.
     */
    $scope.inline.directiveMapping = {
      rentTable: [
        'lease.leaseYears',
        'lease.premiseAreaSize',
        'lease.expansionPremiseAreaSize',
        'lease.relocationPremisesAreaSize',
        'lease.reductionRemainingAreaSize',
        'lease.rentPeriods',
        'lease.leaseMonths',
        'lease.freeRent',
        'lease.hasFreeRent',
        'lease.rentInputType',
        'lease.rentCommenceAt',
        'lease.hasFreeRentAddedToTerm',
        'lease.cpiAnchorYears',
      ],
      securityDepositBurndownSchedule: [
        'lease.hasSecurityDepositBurndown',
        'lease.reductionPeriodsNumber',
        'lease.listOfLeaseYears',
      ],
    };
    $scope.inline.directiveMappingReversed = {};
    _.forOwn($scope.inline.directiveMapping, function(val, key) {
      for (var i = 0; i < val.length; i++) {
        if ($scope.inline.directiveMappingReversed[val[i]]) {
          $scope.inline.directiveMappingReversed[val[i]].push(key);
        } else {
          $scope.inline.directiveMappingReversed[val[i]] = [key];
        }
      }
    });

    /**
     * Open the inline editor from an ng-click on a directive.
     *
     * @param {Event} e - The click event from the ng-click. We will use this to extract the target click element.
     */
    $scope.inline.openInlineDirective = function(e) {
      if (!$scope.inline.on) {
        return;
      }

      $scope.inline.mouseX = e.clientX;
      $scope.inline.mouseY = e.clientY;

      var $element = $(e.target);
      if (!$element.is('[generated-directive]')) {
        $element = $element.closest('[generated-directive]');
      }
      var directiveName = $element.attr('generated-directive');

      setTimeout(function() {
        var relatedsOpen = $scope.inline.findNestedControls($element);
        relatedsOpen = _.union(
          $scope.inline.directiveMapping[directiveName],
          relatedsOpen,
        );

        relatedsOpen = $scope.inline.util.checkRelateds(relatedsOpen, $element);
        $scope.inline.openInline($element, relatedsOpen);
      });
    };

    /**
     * Close the inline editor.
     */
    $scope.inline.hideInline = function() {
      if ($scope.debuggingMode && $scope.inline.showingInline) {
        $scope.inline.$activeElm = null;
        $scope.inline.showingInline = false;
        $scope.inline.activeFields = [];
        $scope.inline.pinned = true;
        $scope.inline.refreshStep++;
        $scope.currentlyChanging = '';
        $scope.resetHighlighting();
      }
    };

    /**
     * Pin the inline editor.
     *
     * Pinning ensures that clicking on new fields, lease-ifs, and placeholders keeps the inline editor in the same
     * place on the page.
     */
    $scope.inline.pinInline = function() {
      $scope.inline.pinned = !$scope.inline.pinned;
    };

    /**
     * Open a field within the inline editor from the occurrences/scrolling buttons.
     *
     * @param {jQuery} $element - The element which we are currently controlling from the inline editor.
     * @param {LeaseField} field - The main field to keep open and focus on in the inline editor.
     */
    $scope.inline.openFromScroll = function($element, field) {
      var relateds, directiveName;

      if ($element.parent().is('lease-var')) {
        $element = $element.parent();
        relateds = $scope.inline.findNestedControls($element);
      } else if ($element.is('.inline-dashed')) {
        relateds = $element.attr('var-id').split(',');
      } else if ($element.is('[generated-directive]')) {
        directiveName = $element.attr('generated-directive');
        relateds = $scope.inline.directiveMapping[directiveName];
      } else if ($element.closest('[generated-directive]').length > 0) {
        directiveName = $element
          .closest('[generated-directive]')
          .attr('generated-directive');
        relateds = $scope.inline.directiveMapping[directiveName];
      } else {
        relateds = $scope.inline.util.checkRelateds(
          splitVariable($element.attr('lease-if-deprecated')),
          $element,
        );
      }

      // remove yourself from array and reposition at beginning
      var myIdx = relateds.indexOf(field);
      if (myIdx > -1) {
        relateds.splice(myIdx, 1);
      }
      relateds.unshift(field);

      relateds = $scope.inline.util.checkRelateds(relateds, $element);
      $scope.inline.mouseX = $element.offset().left + $element.width() / 2;
      $scope.inline.mouseY = $element.offset().top;
      $scope.inline.openInline($element, relateds);
    };

    /**
     * Scroll to the next occurrence, keeping the inline editor open for the correct field.
     */
    $scope.inline.scrollNext = function() {
      var hls = $scope.getAllHls().filter("[exclude!='true']");
      var currentEl = $(hls[$scope.highlightScrollIndex]);

      if (currentEl.get(0) && isInView(currentEl)) {
        setTimeout(function() {
          // We update the `previousField` so we'll know if the `currentField` is different
          // if it's different, we need to reset the highlight mechanism
          $scope.inline.previousField = $scope.inline.currentField;

          $scope.scrollNext();
          $scope.$apply();
        }, 10);
        setTimeout(function() {
          $scope.inline.openFromScroll(
            $($scope.getHighlightedElement()),
            $scope.inline.currentField,
          );
          $scope.$apply();
        }, 500);
      } else {
        $scope.scrollToElement(currentEl);
      }
    };

    /**
     * Scroll to the previous occurrence, keeping the inline editor open for the correct field.
     */
    $scope.inline.scrollPrev = function() {
      setTimeout(function() {
        // We update the `previousField` so we'll know if the `currentField` is different
        // if it's different, we need to reset the highlight mechanism
        $scope.inline.previousField = $scope.inline.currentField;

        $scope.scrollPrev();
        $scope.$apply();
      }, 10);
      setTimeout(function() {
        $scope.inline.openFromScroll(
          $($scope.getHighlightedElement()),
          $scope.inline.currentField,
        );
        $scope.$apply();
      }, 500);
    };

    /**
     * Toggle between "inline" editing mode and "editor" editing mode.
     *
     * When the left-panel is collapsed, we are in "inline" mode.
     * when the left-panel is open, we are in "editor" editing mode.
     */
    $scope.inline.toggle = function() {
      if ($scope.inline.on) {
        $('.inline-editor').trigger('dragstart');
        $scope.inline.refresh();

        // Hide the highlighting bar
        $scope.resetHighlighting();
        $scope.topHlBarActive = false;
      } else {
        $scope.inline.hideInline();
      }

      $('body').toggleClass('inline-editor-mode');
      $timeout(function() {
        LeaseVarService.setTitles($scope,LabelsService);
      });
    };

    /**
     * Turn off inline editing mode and open the currently active field in the left-hand editor.
     */
    $scope.inline.openFieldInEditor = function() {
      if ($scope.inline.on) {
        $scope.inline.on = false;
        $scope.inline.toggle();
      }
      $scope.inline.showEditorField(
        $scope.inline.indexedControls[$scope.inline.currentField],
      );
    };

    /**
     * List of update functions to run on enabling placeholders.  These update functions will typically
     * create and add placeholders.  It is done on a delay / async because we must first wait for the editor
     * and lease to fully update before we can add them in, and also we must do them in order to avoid duplicates.
     * @type {Array}
     */
    $scope.inline.toUpdate = [];
    $scope.inline.runUpdateTimer = null;
    /**
     * Set a timer to run the queued placeholder updates, or reset an already existing timer. Each time something
     * new is added, this is run, to ensure that the placeholders are updated all at the end.
     */
    $scope.inline.refreshUpdateTimer = function() {
      if ($scope.inline.runUpdateTimer) {
        clearTimeout($scope.inline.runUpdateTimer);
      }
      $scope.inline.runUpdateTimer = setTimeout(function() {
        for (var i = 0; i < $scope.inline.toUpdate.length; i++) {
          var run = $scope.inline.toUpdate[i];
          run();
        }
        $scope.inline.toUpdate = [];
      }, 600);
    };

    /**
     * Register a function in the queue of placeholder update functions, and set a timer to eventually run
     * all of the queued updates.
     * @param {function} updateFunc - A function of no parameters which will be added to the queue to run.
     */
    $scope.inline.registerPlaceholderUpdate = function(updateFunc) {
      $scope.inline.toUpdate.push(updateFunc);
      $scope.inline.refresh();
      $scope.inline.refreshUpdateTimer();
    };

    /**
     * Clear cached information and reprocess the left-hand editor for access from the inline editor.
     */
    $scope.inline.refresh = function() {
      if ($scope.debuggingMode) {
        $scope.inline.indexedControls = {};
        $scope.inline.processEditor();
        $scope.inline.refreshUpdateTimer();
      }
    };

    /* -------------------------------------------------------------------------------------------------------------- */

    $scope.pmt = function(ir, np, pv, fv, type) {
      /*
       * ir   - interest rate per month
       * np   - number of periods (months)
       * pv   - present value
       * fv   - future value
       * type - when the payments are due:
       *        0: end of the period, e.g. end of month (default)
       *        1: beginning of period
       */
      var pmt, pvif;

      fv || (fv = 0);
      type || (type = 0);

      if (ir === 0) return -(pv + fv) / np;

      pvif = Math.pow(1 + ir, np);
      pmt = (-ir * pv * (pvif + fv)) / (pvif - 1);

      if (type === 1) pmt /= 1 + ir;

      return pmt;
    };

    $scope.isWatchedCollectionBeingChanged = function(collection) {
      var result = false;

      if (typeof collection === 'string') {
        collection = JSON.parse(collection);
      }

      if (_.includes(collection, $scope.currentlyChanging)) {
        result = true;
      } else {
        for (var j = 0; j < collection.length; j++) {
          if (_.startsWith($scope.currentlyChanging, collection[j])) {
            result = true;
          }
        }
      }

      return result;
    };

    const sanitizeField = _.throttle(function(field) {
      const text = _.get($scope, field);
      if (typeof text === 'string') {
        let cleanText = text;
        
        cleanText = cleanText.replace(/{{.*?}}/g, '');

        cleanText = DOMPurify.sanitize(cleanText, {
          ALLOWED_TAGS: [],
          ALLOWED_ATTR: [],
        });

        _.set($scope, field, cleanText);
      }
    }, 250);

    $scope.updateCurrent = function(field = '', shouldShowHighlightingBar = true) {
      if (field.startsWith("scopeVar.")) {
        field = field.replace("scopeVar.", "");
      }

      if (field.startsWith("lease.") && !field.startsWith("lease.calc.")) {
        window.track.event(new InteractWithTheAutomationEvent({
          field: field,
          value: _.get($scope, field),
          context: $rootScope.getContext(),
        }));
      }

      if (window.isDebug) {
        console.time(`Updating field: ${field}`);
      }

      sanitizeField(field);

      $scope.changeIsBeingMade = true;

      if ($scope.inline.on) {
        $('.inline-editor').trigger('dragstart');
      }

      if ($scope.currentlyChanging != field) {
        $scope.currentlyChanging = '';

        // If the inline editor is being used, we don't want to reset the highlight
        if (!$scope.inline.on) {
          $scope.resetHighlighting();
        }

        //manage lease-var highlight
        $scope.currentlyChanging = field;
      }

      $scope.calculatedFields.updateDependencies(field);

      //manage lease-if highlight
      $timeout(function() {
        return $scope.reInsertMissingListItems();
      }, 0).then(function() {
        setTimeout(function() {
          $scope.highlightItem(field);
          $scope.anchorScroll();
          $scope.structureChanged();
        });

        // Async update and set counters
        setTimeout(function() {
          $scope.changeIsBeingMade = false;
          if (shouldShowHighlightingBar) {
            $scope.showHighlightingBar(null);
          }
          if ($scope.inline.on) {
            $scope.inline.refresh();
          }
  
          if (window.isDebug) {
            console.timeEnd(`Updating field: ${field}`);
          }
        }, 10);
  
        // Async - make sure that the first occurrence of the highlighted field is marked as 'current'
        setTimeout(function() {
          $scope.setCurrentlyUpdatingElement($scope.getHighlightedElement());
        }, 120);
  
        if (!$scope.isLoading && autopilotManager.initialized) {
          _.throttle(
            () => {
              autopilotManager.updateDocumentContext();
            },
            2000,
            { leading: false, trailing: true }
          )();
        }
      });
    };

    $scope.resetHighlighting = function() {
      $('.current')
        .removeClass('current');

      $(".adding")
        .removeClass('adding')
        .addClass('added');

      $(".adding-para")
        .removeClass('adding-para')
        .addClass('added');

      $scope.highlightScrollIndex = 0;
      $scope.numberOfHighlighted = 0;
    };

    $scope.getAllHls = function(activeField) {
      if (!activeField) {
        activeField = $scope.inline.currentField;
      }

      var toRet = $('.adding, .adding-para');

      // Exclude all the empty elements from the list by adding the `exclude=true` attribute
      toRet.each(function() {
        var text = $(this)
          .find2('*:not(:has(*)):visible')
          .text()
          .trim();
        // (1) Lets remove all undo buttons to support this feature to work with ctrl+shift+0
        text = text.replace(/undo/g, '');
        // (2) Let's remove all non-width-chars
        text = text.replace(/﻿/g, '');

        if (
          text.length === 0 &&
          $(this).find("table, img, [bullet='true']").length === 0
        ) {
          $(this).attr('exclude', 'true');
        } else {
          $(this).attr('exclude', 'false');
        }
      });

      var relevantDirectives =
        $scope.inline.directiveMappingReversed[activeField];

      if (relevantDirectives) {
        for (var i = 0; i < relevantDirectives.length; i++) {
          toRet = toRet.add(
            $("[generated-directive='" + relevantDirectives[i] + "']"),
          );
        }
      }

      return toRet;
    };

    function showHighlightingBarImmediately(index) {
      if ($window.prerenderReady) {
        // Remove the `adding` & `adding-para` classes from results
        // which aren't currently shown
        document
          .querySelectorAll('.adding, .adding-para')
          .forEach(result => {
            if (result.closest('.lp-hide')) {
              result.classList.remove('adding', 'adding-para');
            }
          });

        window.applyFilterVisible(() => {
          $scope.numberOfHighlighted = $(".adding, .adding-para")
            .filter("[exclude!='true']")
            .get().length;

          if ($scope.inline.on) {
            $scope.topHlBarActive = false;
          } else {
            $scope.topHlBarActive = true;
          }

          $scope.setCurrentlyUpdatingElement($scope.getHighlightedElement());
          if (!$scope.inline.showingInline && index) {
            $scope.safeApply();
            $scope.scrollFirst();
          } else if (index >= 0) {
            $scope.highlightScrollIndex = index;
            $scope.safeApply();
          } else {
            $scope.safeApply();
          }
        });
      }
    }

    // Makes sure that the highlighting bar is shown correctly with the proper numbers and the view is scrolled to the first item
    $scope.showHighlightingBar = function(hlIndex) {
      if ($scope.inline.showingInline) {
        // LK: This is kinda suck but it takes some time for the inline-editor to figure out
        //     which of the fields it needs to highlight so we need to postpone this accordingly
        setTimeout(function() {
          showHighlightingBarImmediately($scope.highlightScrollIndex);
        }, 500);
      } else {
        showHighlightingBarImmediately(hlIndex);
      }
    };

    $scope.hideHighlighting = function() {
      $scope.topHlBarActive = false;
      $scope.resetHighlighting();
    };

    // This is here because when changing anything through the inline editor,
    // It generate a new highlighting bar without the lease-if changes (it just happens faster)
    // So we call this every time a lease-if generates a placeholder to update the numbers
    $scope.updateHighlightingBarNumbers = function() {
      // Remove the `adding` & `adding-para` classes from results
      // which aren't currently shown
      document
        .querySelectorAll('.adding, .adding-para')
        .forEach(result => {
          if (result.closest('.lp-hide')) {
            result.classList.remove('adding', 'adding-para');
          }
        });

      window.applyFilterVisible(() => {
        $scope.numberOfHighlighted = $scope
          .getAllHls()
          .filter("[exclude!='true']")
          .get().length;
      });
    };

    $scope.highlightItem = function(item1, item2, addLeaseVars) {
      return window.applyFilterVisible($scope._highlightItem,[item1, item2, addLeaseVars]);
    }
    $scope._highlightItem = function(item1, item2, addLeaseVars) {
      var highlights;
      if (item1 && !item2) {
        highlights = $('.preview').find(
          "*[hl='" +
            item1 +
            "'], *[hl2='" +
            item1 +
            "'], *[hl3='" +
            item1 +
            "'], *[hl4='" +
            item1 +
            "'], *[hl5='" +
            item1 +
            "'], *[hl6='" +
            item1 +
            "'], *[hl7='" +
            item1 +
            "'], *[hl8='" +
            item1 +
            "'], *[hl9='" +
            item1 +
            "'], *[hl10='" +
            item1 +
            "'], *[hl11='" +
            item1 +
            "'], *[hl12='" +
            item1 +
            "'], *[hl13='" +
            item1 +
            "'], *[hl14='" +
            item1 +
            "'], *[hl15='" +
            item1 +
            "']",
        );
      } else if (item1 && item2) {
        highlights = $('.preview').find(
          "*[hl='" +
            item1 +
            "'], *[hl2='" +
            item1 +
            "'], *[hl3='" +
            item1 +
            "'], *[hl4='" +
            item1 +
            "'], *[hl5='" +
            item1 +
            "'], *[hl6'" +
            item1 +
            "'], *[hl7='" +
            item1 +
            "'], *[hl8='" +
            item1 +
            "'], *[hl9='" +
            item1 +
            "'], *[hl10='" +
            item1 +
            "'], *[hl11='" +
            item1 +
            "'], *[hl12='" +
            item1 +
            "'], *[hl13='" +
            item1 +
            "'], *[hl14='" +
            item1 +
            "'], *[hl15='" +
            item1 +
            "']",
            "*[hl='" +
            item2 +
            "'], *[hl2='" +
            item2 +
            "'], *[hl3='" +
            item2 +
            "'], *[hl4='" +
            item2 +
            "'], *[hl5='" +
            item2 +
            "'], *[hl6='" +
            item2 +
            "'], *[hl7='" +
            item2 +
            "'], *[hl8='" +
            item2 +
            "'], *[hl9='" +
            item2 +
            "'], *[hl10='" +
            item2 +
            "'], *[hl11='" +
            item2 +
            "'], *[hl12='" +
            item2 +
            "'], *[hl13='" +
            item2 +
            "'], *[hl14='" +
            item2 +
            "'], *[hl15='" +
            item2 +
            "']",
        );
      }
      if (addLeaseVars) {
        highlights = $.merge(
          highlights,
          $('.preview').find(
            '[is-changing="\'' + item1 + '\'==currentlyChanging"] span',
          ),
        );
      }

      $scope.highlight(highlights);

      // Highlight elements that contain a collection of watched properties
      var elements = $('.preview').find('[watch]');
      _.forEach(elements, function(element) {
        var highlight = $(element).attr('watch');

        if (typeof highlight === 'string') {
          highlight = JSON.parse(highlight);
        }

        if (_.includes(highlight, item1)) {
          $scope.highlight($(element));
        } else if (_.includes(highlight, item2)) {
          $scope.highlight($(element));
        } else {
          for (var i = 0; i < highlight.length; i++) {
            if (
              _.startsWith(item1, highlight[i]) ||
              _.startsWith(item2, highlight[i])
            ) {
              $scope.highlight($(element));
              break;
            }
          }
        }
      });

      var relevantDirectives = [];
      var i;
      if (item1) {
        relevantDirectives = $scope.inline.directiveMappingReversed[item1];
        if (relevantDirectives) {
          for (i = 0; i < relevantDirectives.length; i++) {
            // LK: that's the reason the rent-table is highlighted when changing the `renewalTerm`
            $scope.highlight(
              $('.preview').find(
                "[generated-directive='" + relevantDirectives[i] + "']",
              ),
            );
          }
        }
      }
      if (item2) {
        relevantDirectives = $scope.inline.directiveMappingReversed[item2];
        if (relevantDirectives) {
          for (i = 0; i < relevantDirectives.length; i++) {
            $scope.highlight(
              $('.preview').find(
                "[generated-directive='" + relevantDirectives[i] + "']",
              ),
            );
          }
        }
      }
    };

    var debouncedRenderTabsInParagraphs = _.debounce(function(paragraphs) {
      if (window.tabRendererInit && typeof window.tabRendererInit.renderTabsInParagraph === 'function') {
        paragraphs.forEach(function(paragraph) {
          window.tabRendererInit.renderTabsInParagraph(paragraph);
        });
      }
    }, 300);

    $scope.highlight = function(nodes) {
      if (nodes) {
        nodes.each(function() {
          var $this = $(this);
          $this.removeClass('added');
          $this.addClass('adding-para');
        });

        document.querySelectorAll(".adding-para .adding-para").forEach(function (node) {
          node.closest(".adding-para").classList.remove("adding-para");
        });

        

        // Re-render tabs in affected block-level paragraphs.
        // Multiple highlighted nodes may share the same containing block —
        // the Set deduplicates by DOM reference so each paragraph is rendered once.
        var blockParagraphs = new Set();
        nodes.each(function() {
          if (this.getBlockData) {
            var blockData = this.getBlockData();
            if (blockData && blockData.containingNode) {
              blockParagraphs.add(blockData.containingNode);
            }
          }
        });
        if (blockParagraphs.size > 0) {
          debouncedRenderTabsInParagraphs(Array.from(blockParagraphs));
        }

        if (!$scope.inline.showingInline) {
          $scope.showHighlightingBar();
        }
      }
    };

    $scope.execute = function(action) {
      $scope.toggleSearch(false);

      switch (action) {
        case 'undo':
          $scope.callUndo();
          $scope.disableDecorationButtons(true);
          window.track.event(new UndoEvent({
            context: $rootScope.getContext(),
          }));
          // document.execCommand("undo", false, null);
          break;
        case 'redo':
          $scope.callRedo();
          $scope.disableDecorationButtons(true);
          window.track.event(new RedoEvent({
            context: $rootScope.getContext(),
          }));
          // document.execCommand("redo", false, null);
          break;
        case 'cut':
          $scope.handleCut();
          break;
        case 'copy':
          $scope.handleCopy();
          break;
        case 'paste':
          if ($scope.currentlyEditedNode) {
            $($scope.currentlyEditedNode).trigger('paste');
          }
          $scope.disableDecorationButtons(true);
          break;
      }
    };

    // Disable selection mode if B/I/U on a selection
    $scope.selectionAction = function() {
      var sel = rangy.getSelection();
      if (sel && sel.rangeCount > 0) {
        var range = sel.getRangeAt(0);
        if (range.toString().length > 0) {
          $scope.currentlyEditedNode = range.commonAncestorContainer;
        }
      }
    };

    /* ============= Alignment & Indentation tools ============= */

    $scope.calculateIndentation = function (node, type, steps) {

      const indent_actions = {
        paragraph: 'marginLeft',
        text: 'textIndent',
      }

      
      let dataOriginalIndent = `data-original-${type}-indent`;
      let dataIndent = `data-${type}-indent`;

      const originalIndentStyle = parseInt(node.getAttribute(dataOriginalIndent));
      const currentIndentationSteps = +node.getAttribute(dataIndent);
      const updatedIndentationSteps = currentIndentationSteps + steps;
      const indentStyle = updatedIndentationSteps * window.CONSTS.STEP_SIZE
      const totalIndentationSteps = (1 * node.getAttribute(`data-paragraph-indent`)) + ((1 * node.getAttribute(`data-text-indent`)) || 0) + steps;

      return {
        originalIndentStyle: originalIndentStyle,
        currentIndentationSteps: currentIndentationSteps,
        updatedIndentationSteps: updatedIndentationSteps,
        totalIndentationSteps: totalIndentationSteps,
        indentStyle: indentStyle,
        indentAction: indent_actions[type],
      }
    }

    $scope.isUnstyled = function(container) {
      return !container.classList.contains("restyled");
    }

    $scope.setOriginalStyle = function(container, restyle) {
      if (!$scope.indentDisable && !container.classList.contains('restyled')) {

        let originalParagraphIndent = container.style.marginLeft;
        let originalTextIndent = container.style.textIndent;
        let originalRightIndent = container.style.marginRight;
        let originalTextAlign = container.style.textAlign;
        let originalFontSize = container.style.fontSize;

        if (restyle) {
          originalParagraphIndent = restyle.originalParagraphIndent;
          originalTextIndent = restyle.originalTextIndent;
          originalRightIndent = restyle.originalRightIndent;
          originalTextAlign = restyle.originalTextAlign;
          originalFontSize = restyle.originalFontSize;
        }

        container.setAttribute('data-original-paragraph-indent', originalParagraphIndent || '0pt');
        container.setAttribute('data-original-text-indent', originalTextIndent || '0pt');
        container.setAttribute('data-original-right-indent', originalRightIndent || '0pt');
        container.setAttribute('data-original-text-align', originalTextAlign || 'start');
        container.setAttribute('data-original-font-size', originalFontSize || $scope.defaultFont.fontSize);

        const marginLeft = parseInt(originalParagraphIndent);
        if (marginLeft && marginLeft % window.CONSTS.STEP_SIZE === 0) {
          container.setAttribute('data-paragraph-indent', marginLeft / window.CONSTS.STEP_SIZE);
        }

        const textIndent = parseInt(originalTextIndent);
        if (textIndent && textIndent % window.CONSTS.STEP_SIZE === 0) {
          container.setAttribute('data-text-indent', textIndent / window.CONSTS.STEP_SIZE);
        }

        const rightIndent = parseInt(originalRightIndent);
        if (rightIndent && rightIndent % window.CONSTS.STEP_SIZE === 0) {
          container.setAttribute('data-right-indent', rightIndent / window.CONSTS.STEP_SIZE);
        }

        const fontSize = originalFontSize;
        container.setAttribute("data-font-size", fontSize);
        
        container.classList.add('restyled');
      }
    }


    function undoRedoSetIndent(node, type, steps,$scope){
      return function(){
        var deferred = $q.defer();
        $scope.setIndent(node, type, steps);

        const freeText = node.closest("[free-text]");
        
        if (freeText) {
          $(freeText)
            .find('[contenteditable]')
            .trigger('change');
        }

        deferred.resolve();
        return deferred.promise;
      }
    }

    $scope.setIndent = function(node, type, steps) {
      const MAX_INDENTATION_STEPS = 10;
      const calcInfo = $scope.calculateIndentation(node, type, steps);
      const updatedIndentationSteps = calcInfo.updatedIndentationSteps;
      var dataIndent = `data-${type}-indent`;
      var indentAction = calcInfo.indentAction;

      if (calcInfo.totalIndentationSteps <= MAX_INDENTATION_STEPS) {
        const indentStyle = calcInfo.indentStyle + 'pt';
        node.style[indentAction] = indentStyle;
        node.setAttribute(dataIndent, updatedIndentationSteps);
        return updatedIndentationSteps;
      }

      return false;
    }

    $scope.indent = function(steps, type) {
      
      let currentUndoIndex = $scope.histIndex;

      const sel = rangy.getSelection();
      const startBlock = sel.anchorNode.getTopAnchor();
      const endBlock = sel.focusNode.getTopAnchor();
      const range = sel.getRangeAt(0);
      const blocks = range.getNodes([Node.ELEMENT_NODE], function(node) {
        return (
          ["P", "H1", "H2", "H3", "H4", "H5", "H6"].indexOf(node.tagName) > -1
        );
      });

      if (startBlock && startBlock.getAttribute && startBlock.getAttribute("pid") && blocks.indexOf(startBlock) === -1) {
        blocks.push(startBlock);
      }
      if (endBlock && endBlock.getAttribute && endBlock.getAttribute("pid") && blocks.indexOf(endBlock) === -1) {
        blocks.push(endBlock);
      }
      
      let focusNode = sel.focusNode;

      if (focusNode.nodeType === Node.TEXT_NODE) {
        focusNode = focusNode.parentElement;
      }

      if (!focusNode.classList.contains("new-paragraph")) {
        focusNode = LeaseEditorService.findClosestContainer(focusNode).get(0);
      }

      let listInfo = focusNode.getBlockData();

      if (LeaseEditorService.isSelecting()) {
        
        let allParagraphs = blocks;
        let paragraphsIndex = allParagraphs.map(node => {
          let p = null;
          if (node.tagName === "P") {
            p = node;
          } else {
            listInfo = node.getBlockData();
            if (steps > 0) {
              if (listInfo) {
                listInfo.moveToNextLevel();
              }
            } else {
              if (listInfo) {
                listInfo.moveToPreviousLevel();
              }
            }
          }

          if (p === null) {
            return;
          }

          if ($rootScope.isListItem($(p))) {
            return;
          }
          if (p.classList.contains("new-paragraph")) {
            let nestedIndex = p.getAttribute("data-nested-paragraph-index");
            if (nestedIndex) {
              return { type: "new-paragraph", id: nestedIndex };
            }
          } else {
            let pid = p.getAttribute("pid");
            if (pid) {
              return { type: "paragraph", id: pid };
            }
          }
        });
        for (let index = 0; index < paragraphsIndex.length; ++index) {
          let item = paragraphsIndex[index];

          if (!item) {
            continue;
          }

          let p = null;
          if (item.type === "new-paragraph") {
            p = document.querySelector(
              `[data-nested-paragraph-index="${item.id}"]`
            );
          } else if (item.type === "paragraph") {
            p = document.querySelectorAll(`[pid="${item.id}"]`).lastItem();
          }

          if (p) {
            if (type) {
              scopeVar.indentOnce(type, steps, p);
            } else {
              scopeVar.indentOnce("paragraph", steps, p);
            }
          }
        }
      } else if (!scopeVar.indentDisable || listInfo) {
        const textIndent = 1 * focusNode.getAttribute("data-text-indent");

        LeaseEditorService.skipNonWidthChar("forward");

        if (listInfo && listInfo.listLevel && listInfo.listLevel.level !== "paragraph" && !focusNode.hasAttribute('data-nested-paragraph-index')) {
          if (steps > 0) {
            if (listInfo) {
              listInfo.moveToNextLevel();
            } else if (listInfo.blockType === BLOCK_TYPE.INSERTED_LIST_ITEM_PARAGRAPH)  {
              if ((steps > 0 && textIndent === 0) || (steps < 0 && textIndent > 0)) {
                scopeVar.indentOnce("text", steps, focusNode);
              } else {
                scopeVar.indentOnce("paragraph", steps, focusNode);
              }
            }
          } else {
            if (listInfo) {
              listInfo.moveToPreviousLevel();
            } else if (listInfo.blockType === BLOCK_TYPE.INSERTED_LIST_ITEM_PARAGRAPH) {
              if (steps < 0 && textIndent > 0) {
                scopeVar.indentOnce("text", steps, focusNode);
              } else {
                scopeVar.indentOnce("paragraph", steps, focusNode);
              }
            }
          }
        } else if (contentEditableManager.isCaretAtStartContainer() || type) {
          if (type) {
            scopeVar.indentOnce(type, steps, focusNode);
          } else if ( (steps > 0 && textIndent === 0) || (steps < 0 && textIndent > 0 ) ) {
            scopeVar.indentOnce("text", steps, focusNode);
          } else {
            scopeVar.indentOnce("paragraph", steps, focusNode);
          }
        }
      }

      // Update ruler indicators after indent changes
      // Use $timeout to ensure DOM updates are complete before reading computed styles
      $timeout(function() {
        if ($rootScope.lpRuler && $rootScope.lpRuler.updateTabstopsForParagraph) {
          // Find the paragraph that was modified - use focusNode which was already processed
          let activeParagraph = null;
          
          if (focusNode) {
            if (focusNode.tagName && ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(focusNode.tagName)) {
              activeParagraph = focusNode;
            } else if (focusNode.closest) {
              activeParagraph = focusNode.closest('p, h1, h2, h3, h4, h5, h6');
            }
          }
          
          // Fallback: find from current selection
          if (!activeParagraph) {
            const sel = rangy.getSelection();
            if (sel && sel.rangeCount > 0) {
              const range = sel.getRangeAt(0);
              const container = range.commonAncestorContainer;
              let nodeToCheck = container;
              
              if (nodeToCheck && nodeToCheck.nodeType === Node.TEXT_NODE) {
                nodeToCheck = nodeToCheck.parentElement;
              }
              
              if (nodeToCheck && nodeToCheck.tagName && ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(nodeToCheck.tagName)) {
                activeParagraph = nodeToCheck;
              } else if (nodeToCheck && nodeToCheck.closest) {
                activeParagraph = nodeToCheck.closest('p, h1, h2, h3, h4, h5, h6');
              }
              
              // If still not found, try using LeaseEditorService
              if (!activeParagraph && nodeToCheck) {
                const closestContainer = LeaseEditorService.findClosestContainer(nodeToCheck);
                if (closestContainer && closestContainer.length > 0) {
                  const containerElement = closestContainer.get(0);
                  if (containerElement && ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(containerElement.tagName)) {
                    activeParagraph = containerElement;
                  } else if (containerElement && containerElement.closest) {
                    activeParagraph = containerElement.closest('p, h1, h2, h3, h4, h5, h6');
                  }
                }
              }
            }
          }
          
          if (activeParagraph) {
            $rootScope.lpRuler.updateTabstopsForParagraph(activeParagraph);
          }
        }
      }, 100);

      setTimeout(()=>{
        $scope.histRegisterBatch($scope.histStack.length - 1, $scope.histIndex - currentUndoIndex);
      });
    }

    $scope.indentOnce = function(type, steps, paragraphElement) {
      const sel = rangy.getSelection();
      let node = sel.focusNode;
      let container = node;
      const contentEditable = LeaseEditorService.findClosestContentEditable(node).get(0);

      if (paragraphElement) {
        container = paragraphElement;
        node = container;
      } else {
        if (!(node.nodeType === Node.ELEMENT_NODE && node.classList.contains('new-paragraph'))) {
          container = LeaseEditorService.findClosestContainer(node).get(0);
        }
        if (!contentEditable || !container) {
          return;
        }
      }

      let pid;
      pid = container.getAttribute('pid');
      pid = getParagraphId(pid, true);

      // If this is not a `new-paragraph` and for some reason we couldn't find it,
      // don't allow to indent the paragraph and - stop!
      if (!(pid || container.classList.contains('new-paragraph'))) {
        return;
      }

      const savedSelection = rangy.saveSelection();

      // We only save `restyle`s for top-level paragraphs
      if (!$scope.lease.restyle) {
        $scope.lease.restyle = [];
      }
      if (pid && !$scope.lease.restyle[pid]) {
        let originalMarginLeft = container.style.marginLeft || '0pt';
        let originalTextIndent = container.style.textIndent || '0pt';
        let originalTextAlign = container.style.textAlign || 'start';
        let originalFontSize = container.style.fontSize || $scope.defaultFont.fontSize;
        let originalLineHeight = container.style.lineHeight || $scope.defaultFont.lineHeight;
        let paragraphIndent = null;
        let textIndent = null;
        let textAlign = originalTextAlign;
        let fontSize = originalFontSize;
        let lineHeight = originalLineHeight;

        if (parseInt(originalMarginLeft) % window.CONSTS.STEP_SIZE === 0) {
          paragraphIndent = parseInt(originalMarginLeft) / window.CONSTS.STEP_SIZE;
        }

        if (parseInt(originalTextIndent) % window.CONSTS.STEP_SIZE === 0) {
          textIndent = parseInt(originalTextIndent) / window.CONSTS.STEP_SIZE;
        }

        $scope.lease.restyle[pid] = {
          paragraphId: pid,
          originalParagraphIndent: originalMarginLeft,
          originalTextIndent: originalTextIndent,
          originalTextAlign: originalTextAlign,
          originalFontSize: originalFontSize,
          originalLineHeight: originalLineHeight,
          paragraphIndent: paragraphIndent,
          textIndent: textIndent,
          textAlign: textAlign,
          fontSize: fontSize,
          lineHeight: lineHeight,
        };

        $scope.setOriginalStyle(container, $scope.lease.restyle[pid]);
      }

      rangy.restoreSelection(savedSelection, true);

      if (node.nodeType === Node.TEXT_NODE) {
        node = node.parentElement;
      }

      if (!node.classList.contains('new-paragraph')) {
        node = container;
      }

      // Block the exit text from the range
      const currentSteps = +node.getAttribute('data-'+ type + "-indent");
      if(currentSteps + steps < 0) {
        if(currentSteps > 0){
          steps = (-1 * currentSteps);
        }
        else{
          return;
        }
      }

      window.track.event(new IndentParagraphEvent({
        type: type,
        steps: steps,
        isNewParagraph: !pid,
        context: $rootScope.getContext(),
      }));

      if (type === 'paragraph') {
        // Save the change of the alignment in the data-model
        if (pid) {
          $scope.addToHistory({
            id: 'paragraph-indent',
            desc: '',
            redo: function() {
              const deferred = $q.defer();
              const updatedIndentationSteps = $scope.setIndent(node, type, steps);

              if (updatedIndentationSteps !== false) {
                $scope.lease.restyle[pid].paragraphIndent = updatedIndentationSteps;
                $scope.update();
              }

              deferred.resolve();

              return deferred.promise;
            },
            undo: function() {
              const deferred = $q.defer();
              const updatedIndentationSteps = $scope.setIndent(node, type, -steps);

              if (updatedIndentationSteps !== false) {
                $scope.lease.restyle[pid].paragraphIndent = updatedIndentationSteps;
                $scope.update();
              }

              deferred.resolve();

              return deferred.promise;
            },
          });

          $scope.histIgnoreClearBatch += 2;
          $scope.histRegisterBatch($scope.histIndex + 1, 2);
        } else {
          if ($scope.isUnstyled(node)) {
            $scope.setOriginalStyle(node, $scope.lease.restyle[pid]);

            rangy.restoreSelection(savedSelection, true);
          }

          $scope.addToHistory({
            id: 'paragraph-indent',
            desc: '',
            redo : undoRedoSetIndent(node, type, steps,$scope),
            undo :undoRedoSetIndent(node, type, steps *-1,$scope),
          });


        }
      } else if (type === 'text') {
        // Save the change of the alignment in the data-model
        if (pid) {
         $scope.addToHistory({
            id: 'text-indent',
            desc: '',
            redo: function() {
              const deferred = $q.defer();
              const updatedIndentationSteps = $scope.setIndent(node, type, steps);

              if (updatedIndentationSteps !== false) {
                $scope.lease.restyle[pid].textIndent = updatedIndentationSteps;
                $scope.update();
              }

              deferred.resolve();

              return deferred.promise;
            },
            undo: function() {
              const deferred = $q.defer();
              const updatedIndentationSteps = $scope.setIndent(node, type, -steps);

              if (updatedIndentationSteps !== false) {
                $scope.lease.restyle[pid].textIndent = updatedIndentationSteps;
                $scope.update();
              }

              deferred.resolve();

              return deferred.promise;
            },
          });

          $scope.histIgnoreClearBatch += 2;
          $scope.histRegisterBatch($scope.histIndex + 1, 2);
        } else {
          if ($scope.isUnstyled(node)) {
            $scope.setOriginalStyle(node, $scope.lease.restyle[pid]);

            rangy.restoreSelection(savedSelection, true);
          }

          $scope.addToHistory({
            id: 'text-indent',
            desc: '',
            redo : undoRedoSetIndent(node, type, steps,$scope),
            undo :undoRedoSetIndent(node, type, steps *-1,$scope),
          });

        }
      }
    };

    $scope.align = function(alignment) {
      let currentUndoIndex = $scope.histIndex;

      
      if (isActiveImage()) {
        $scope.alignOnce(alignment);
      } else {
        const sel = rangy.getSelection();
        const startBlock = sel.anchorNode.getTopAnchor();
        const endBlock = sel.focusNode.getTopAnchor();
        const range = sel.getRangeAt(0);
        const blocks = range.getNodes([Node.ELEMENT_NODE], function(node) {
          return (
            ["P", "H1", "H2", "H3", "H4", "H5", "H6"].indexOf(node.tagName) > -1
          );
        });
  
        if (startBlock && startBlock.getAttribute && startBlock.getAttribute("pid") && blocks.indexOf(startBlock) === -1) {
          blocks.push(startBlock);
        }
        if (endBlock && endBlock.getAttribute && endBlock.getAttribute("pid") && blocks.indexOf(endBlock) === -1) {
          blocks.push(endBlock);
        }
  
        if (LeaseEditorService.isSelecting()) {
          let allParagraphs = blocks;
          let paragraphsIndex = allParagraphs.map(p =>{
            if (p.textContent.trim().startsWith("!!SET_LEVEL")) {
              return false;
            } else if (p.classList.contains('new-paragraph'))  {
              let nestedIndex = p.getAttribute('data-nested-paragraph-index');
              if (nestedIndex){
                return { type: "new-paragraph", id: nestedIndex };
              }
            } else {
              let pid = p.getAttribute('pid');
              if (pid){
                return { type: "paragraph", id: pid };
              }
            }
          });
          for (let index = 0; index < paragraphsIndex.length; ++index) {
            let item = paragraphsIndex[index];

            if (!item) {
              continue;
            }
            
            let p = null;
            if (item.type === "new-paragraph") {
              p = document.querySelector(`[data-nested-paragraph-index="${item.id}"]`);
            } else if (item.type === "paragraph") {
              p = document.querySelectorAll(`[pid="${item.id}"]`).lastItem();
            }
  
            if (p) {
              $scope.alignOnce(alignment, p);
            }
          }
        } else {
          $scope.alignOnce(alignment);
        }
  
        setTimeout(()=>{
          $scope.histRegisterBatch($scope.histStack.length - 1, $scope.histIndex - currentUndoIndex);
        });
      }
    }

    $scope.alignOnce = function(alignment, element) {
      let node;
      let container;
      let contentEditable;
      let _isActiveImage = isActiveImage();
      if (element) {
        if (_isActiveImage) {
          node = document.querySelector('img.active');
        } else {
          node = element;
        }
        container = node;
        contentEditable = LeaseEditorService.findClosestContentEditable(node).get(0);

        if (!contentEditable) {
          const list = LeaseEditorService.findAllContentEditable(container);
          contentEditable = list[0];
        }
      } else {
        if (_isActiveImage) {
          node = document.querySelector('img.active');
          container = LeaseEditorService.findClosestContainer(node).get(0);
        } else {
          const sel = rangy.getSelection();
          node = sel.focusNode;
          container = node;
        }
        contentEditable = LeaseEditorService.findClosestContentEditable(node).get(0);

        if (!(node.nodeType === Node.ELEMENT_NODE && node.classList.contains('new-paragraph'))) {
          container = LeaseEditorService.findClosestContainer(node).get(0);
        }

        if (!contentEditable || !container) {
          return;
        }
      }

      var prevAlignment = "";
      if (container.style.textAlign === "") {
        prevAlignment = "start";
      } else {
        prevAlignment = container.style.textAlign;
      }

      let pid;
      pid = container.getAttribute('pid');
      pid = getParagraphId(pid, true);

      // If this is not a `new-paragraph` and for some reason we couldn't find it,
      // don't allow to align the paragraph and - stop!
      if (!(pid || container.classList.contains('new-paragraph'))) {
        return;
      }

      const savedSelection = rangy.saveSelection();

      // We only save `restyle`s for top-level paragraphs
      if (!$scope.lease.restyle) {
        $scope.lease.restyle = [];
      }
      if (pid && !$scope.lease.restyle[pid]) {
        let originalMarginLeft = container.style.marginLeft || '0pt';
        let originalTextIndent = container.style.textIndent || '0pt';
        let originalTextAlign = container.style.textAlign || 'start';
        let originalFontSize = container.style.fontSize || $scope.defaultFont.fontSize;
        let originalLineHeight = container.style.lineHeight || $scope.defaultFont.lineHeight;
        let paragraphIndent = null;
        let textIndent = null;
        let fontSize = originalFontSize;
        let lineHeight = originalLineHeight;

        if (parseInt(originalMarginLeft) % window.CONSTS.STEP_SIZE === 0) {
          paragraphIndent = parseInt(originalMarginLeft) / window.CONSTS.STEP_SIZE;
        }

        if (parseInt(originalTextIndent) % window.CONSTS.STEP_SIZE === 0) {
          textIndent = parseInt(originalTextIndent) / window.CONSTS.STEP_SIZE;
        }

        $scope.lease.restyle[pid] = {
          paragraphId: pid,
          originalParagraphIndent: originalMarginLeft,
          originalTextIndent: originalTextIndent,
          originalTextAlign: originalTextAlign,
          originalFontSize: originalFontSize,
          originalLineHeight: originalLineHeight,
          paragraphIndent: paragraphIndent,
          textIndent: textIndent,
          textAlign: alignment,
          fontSize: fontSize,
          lineHeight: lineHeight,
        };

        $scope.setOriginalStyle(container, $scope.lease.restyle[pid]);
      }

      rangy.restoreSelection(savedSelection, true);

      window.track.event(new AlignParagraphEvent({
        alignment: alignment,
        isNewParagraph: !pid,
        context: $rootScope.getContext(),
      }));

      // Save the change of the alignment in the data-model
      if (pid) {
        $scope.addToHistory({
          id: 'text-align',
          desc: '',
          redo: function() {
            const deferred = $q.defer();
            container.setAttribute('data-text-align', alignment);
            container.style.textAlign = alignment;

            $scope.lease.restyle[pid].textAlign = alignment;
            $scope.update();

            deferred.resolve();
            return deferred.promise;
          },
          undo: function() {
            const deferred = $q.defer();
            container.setAttribute('data-text-align', prevAlignment);
            container.style.textAlign = prevAlignment;

            $scope.lease.restyle[pid].textAlign = prevAlignment;
            $scope.update();

            deferred.resolve();
            return deferred.promise;
          },
        });

        $scope.histIgnoreClearBatch += 2;
        $scope.histRegisterBatch($scope.histIndex + 1, 2);
      } else {
        if ($scope.isUnstyled(container)) {
          $scope.setOriginalStyle(container, $scope.lease.restyle[pid]);

          rangy.restoreSelection(savedSelection, true);
        }
        container.style.textAlign = alignment;
      }
    };

    $scope.changeBlockFontSize = function(fontSize) {
      let currentUndoIndex = $scope.histIndex;
      
      const sel = rangy.getSelection();
      const startBlock = sel.anchorNode.getTopAnchor();
      const endBlock = sel.focusNode.getTopAnchor();
      const range = sel.getRangeAt(0);
      const blocks = range.getNodes([Node.ELEMENT_NODE], function(node) {
        return (
          ["P", "H1", "H2", "H3", "H4", "H5", "H6"].indexOf(node.tagName) > -1
        );
      });

      if (startBlock && startBlock.getAttribute && startBlock.getAttribute("pid") && blocks.indexOf(startBlock) === -1) {
        blocks.push(startBlock);
      }
      if (endBlock && endBlock.getAttribute && endBlock.getAttribute("pid") && blocks.indexOf(endBlock) === -1) {
        blocks.push(endBlock);
      }

      if (LeaseEditorService.isSelecting()) {
        let allParagraphs = blocks;
        let paragraphsIndex = allParagraphs.map(p =>{
          if (p.textContent.trim().startsWith("!!SET_LEVEL")) {
            return false;
          } else if (p.classList.contains('new-paragraph'))  {
            let nestedIndex = p.getAttribute('data-nested-paragraph-index');
            if (nestedIndex){
              return { type: "new-paragraph", id: nestedIndex };
            }
          } else {
            let pid = p.getAttribute('pid');
            if (pid){
              return { type: "paragraph", id: pid };
            }
          }
        });
        for (let index = 0; index < paragraphsIndex.length; ++index) {
          let item = paragraphsIndex[index];

          if (!item) {
            continue;
          }
          
          let p = null;
          if (item.type === "new-paragraph") {
            p = document.querySelector(`[data-nested-paragraph-index="${item.id}"]`);
          } else if (item.type === "paragraph") {
            p = document.querySelectorAll(`[pid="${item.id}"]`).lastItem();
          }

          if (p) {
            $scope.changeBlockFontSizeOnce(fontSize, p);
          }
        }
      } else {
        $scope.changeBlockFontSizeOnce(fontSize);
      }

      setTimeout(()=>{
        $scope.histRegisterBatch($scope.histStack.length - 1, $scope.histIndex - currentUndoIndex);
      });
    }

    $scope.changeBlockFontSizeOnce = function(fontSize, element) {
      let node;
      let container;
      let contentEditable;
      let _isActiveImage = isActiveImage();
      if (element) {
        if (_isActiveImage) {
          node = document.querySelector('img.active');
        } else {
          node = element;
        }
        container = node;
        contentEditable = LeaseEditorService.findClosestContentEditable(node).get(0);

        if (!contentEditable) {
          const list = LeaseEditorService.findAllContentEditable(container);
          contentEditable = list[0];
        }
      } else {
        if (_isActiveImage) {
          node = document.querySelector('img.active');
        } else {
          const sel = rangy.getSelection();
          node = sel.focusNode;
        }
        container = node;
        contentEditable = LeaseEditorService.findClosestContentEditable(node).get(0);

        if (!(node.nodeType === Node.ELEMENT_NODE && node.classList.contains('new-paragraph'))) {
          container = LeaseEditorService.findClosestContainer(node).get(0);
        }

        if (!contentEditable || !container) {
          return;
        }
      }

      var prevFontSize = "";
      if (container.style.fontSize === "") {
        prevFontSize = $scope.defaultFont.fontSize;
      } else {
        prevFontSize = container.style.fontSize;
      }

      let pid;
      pid = container.getAttribute('pid');
      pid = getParagraphId(pid, true);

      // If this is not a `new-paragraph` and for some reason we couldn't find it,
      // don't allow to align the paragraph and - stop!
      if (!(pid || container.classList.contains('new-paragraph'))) {
        return;
      }

      const savedSelection = rangy.saveSelection();

      // We only save `restyle`s for top-level paragraphs
      if (!$scope.lease.restyle) {
        $scope.lease.restyle = [];
      }
      if (pid && !$scope.lease.restyle[pid]) {
        let originalMarginLeft = container.style.marginLeft || '0pt';
        let originalTextIndent = container.style.textIndent || '0pt';
        let originalTextAlign = container.style.textAlign || 'start';
        let originalFontSize = container.style.fontSize || $scope.defaultFont.fontSize;
        let originalLineHeight = container.style.lineHeight || $scope.defaultFont.lineHeight;
        let paragraphIndent = parseInt(originalMarginLeft) / window.CONSTS.STEP_SIZE;
        let textIndent = parseInt(originalTextIndent) / window.CONSTS.STEP_SIZE;
        let lineHeight = originalLineHeight;

        $scope.lease.restyle[pid] = {
          paragraphId: pid,
          originalParagraphIndent: originalMarginLeft,
          originalTextIndent: originalTextIndent,
          originalTextAlign: originalTextAlign,
          originalFontSize: originalFontSize,
          originalLineHeight: originalLineHeight,
          paragraphIndent: paragraphIndent,
          textIndent: textIndent,
          textAlign: originalTextAlign,
          fontSize: fontSize,
          lineHeight: lineHeight,
        };

        $scope.setOriginalStyle(container, $scope.lease.restyle[pid]);
      }

      rangy.restoreSelection(savedSelection, true);

      window.track.event(new ChangeFontSizeEvent({
        size: fontSize,
        isNewParagraph: !pid,
        context: $rootScope.getContext(),
      }));

      // Save the change of the fontSize in the data-model
      if (pid) {
        $scope.addToHistory({
          id: 'font-size',
          desc: '',
          redo: function() {
            const deferred = $q.defer();
            const blockData = container.getBlockData();

            container.setAttribute('data-font-size', fontSize);
            container.style.fontSize = fontSize;
            
            if (blockData && blockData.node) {
              blockData.node.style.fontSize = fontSize;
            }

            $scope.lease.restyle[pid].fontSize = fontSize;
            $scope.update();

            setTimeout(() => {
              $scope.listsRefresh();
            });

            deferred.resolve();
            return deferred.promise;
          },
          undo: function() {
            const deferred = $q.defer();
            const blockData = container.getBlockData();

            container.setAttribute('data-font-size', prevFontSize);
            container.style.fontSize = prevFontSize;

            if (blockData && blockData.node) {
              blockData.node.style.fontSize = fontSize;
            }

            $scope.lease.restyle[pid].fontSize = prevFontSize;
            $scope.update();

            setTimeout(() => {
              $scope.listsRefresh();
            });

            deferred.resolve();
            return deferred.promise;
          },
        });

        $scope.histIgnoreClearBatch += 2;
        $scope.histRegisterBatch($scope.histIndex + 1, 2);
      } else {
        if ($scope.isUnstyled(container)) {
          $scope.setOriginalStyle(container, $scope.lease.restyle[pid]);

          rangy.restoreSelection(savedSelection, true);
        }
        container.style.fontSize = fontSize;
      }
    };

    $scope.changeBlockLineHeight = function(lineHeight) {
      let currentUndoIndex = $scope.histIndex;
      
      const sel = rangy.getSelection();
      const startBlock = sel.anchorNode.getTopAnchor();
      const endBlock = sel.focusNode.getTopAnchor();
      const range = sel.getRangeAt(0);
      const blocks = range.getNodes([Node.ELEMENT_NODE], function(node) {
        return (
          ["P", "H1", "H2", "H3", "H4", "H5", "H6"].indexOf(node.tagName) > -1
        );
      });

      if (startBlock && startBlock.getAttribute && startBlock.getAttribute("pid") && blocks.indexOf(startBlock) === -1) {
        blocks.push(startBlock);
      }
      if (endBlock && endBlock.getAttribute && endBlock.getAttribute("pid") && blocks.indexOf(endBlock) === -1) {
        blocks.push(endBlock);
      }

      if (LeaseEditorService.isSelecting()) {
        let allParagraphs = blocks;
        let paragraphsIndex = allParagraphs.map(p =>{
          if (p.textContent.trim().startsWith("!!SET_LEVEL")) {
            return false;
          } else if (p.classList.contains('new-paragraph'))  {
            let nestedIndex = p.getAttribute('data-nested-paragraph-index');
            if (nestedIndex){
              return { type: "new-paragraph", id: nestedIndex };
            }
          } else {
            let pid = p.getAttribute('pid');
            if (pid){
              return { type: "paragraph", id: pid };
            }
          }
        });
        for (let index = 0; index < paragraphsIndex.length; ++index) {
          let item = paragraphsIndex[index];

          if (!item) {
            continue;
          }
          
          let p = null;
          if (item.type === "new-paragraph") {
            p = document.querySelector(`[data-nested-paragraph-index="${item.id}"]`);
          } else if (item.type === "paragraph") {
            p = document.querySelectorAll(`[pid="${item.id}"]`).lastItem();
          }

          if (p) {
            $scope.changeBlockLineHeightOnce(lineHeight, p);
          }
        }
      } else {
        $scope.changeBlockLineHeightOnce(lineHeight);
      }

      setTimeout(()=>{
        $scope.histRegisterBatch($scope.histStack.length - 1, $scope.histIndex - currentUndoIndex);
      });
    }

    $scope.changeBlockLineHeightOnce = function(lineHeight, element) {
      let node;
      let container;
      let contentEditable;
      let _isActiveImage = isActiveImage();
      if (element) {
        if (_isActiveImage) {
          node = document.querySelector('img.active');
        } else {
          node = element;
        }
        container = node;
        contentEditable = LeaseEditorService.findClosestContentEditable(node).get(0);

        if (!contentEditable) {
          const list = LeaseEditorService.findAllContentEditable(container);
          contentEditable = list[0];
        }
      } else {
        if (_isActiveImage) {
          node = document.querySelector('img.active');
        } else {
          const sel = rangy.getSelection();
          node = sel.focusNode;
        }
        container = node;
        contentEditable = LeaseEditorService.findClosestContentEditable(node).get(0);

        if (!(node.nodeType === Node.ELEMENT_NODE && node.classList.contains('new-paragraph'))) {
          container = LeaseEditorService.findClosestContainer(node).get(0);
        }

        if (!contentEditable || !container) {
          return;
        }
      }

      var prevLineHeight = "";
      if (container.style.lineHeight === "") {
        prevLineHeight = $scope.defaultFont.lineHeight;
      } else {
        prevLineHeight = container.style.lineHeight;
      }

      let pid;
      pid = container.getAttribute('pid');
      pid = getParagraphId(pid, true);

      // If this is not a `new-paragraph` and for some reason we couldn't find it,
      // don't allow to align the paragraph and - stop!
      if (!(pid || container.classList.contains('new-paragraph'))) {
        return;
      }

      const savedSelection = rangy.saveSelection();

      // We only save `restyle`s for top-level paragraphs
      if (!$scope.lease.restyle) {
        $scope.lease.restyle = [];
      }
      if (pid && !$scope.lease.restyle[pid]) {
        let originalMarginLeft = container.style.marginLeft || '0pt';
        let originalTextIndent = container.style.textIndent || '0pt';
        let originalTextAlign = container.style.textAlign || 'start';
        let originalFontSize = container.style.fontSize || $scope.defaultFont.fontSize;
        let originalLineHeight = container.style.lineHeight || $scope.defaultFont.lineHeight;
        let paragraphIndent = parseInt(originalMarginLeft) / window.CONSTS.STEP_SIZE;
        let textIndent = parseInt(originalTextIndent) / window.CONSTS.STEP_SIZE;
        let textAlign = originalTextAlign;
        let fontSize = originalFontSize;

        $scope.lease.restyle[pid] = {
          paragraphId: pid,
          originalParagraphIndent: originalMarginLeft,
          originalTextIndent: originalTextIndent,
          originalTextAlign: originalTextAlign,
          originalFontSize: originalFontSize,
          originalLineHeight: originalLineHeight,
          paragraphIndent: paragraphIndent,
          textIndent: textIndent,
          textAlign: textAlign,
          fontSize: fontSize,
          lineHeight: lineHeight,
        };

        $scope.setOriginalStyle(container, $scope.lease.restyle[pid]);
      }

      rangy.restoreSelection(savedSelection, true);

      window.track.event(new ChangeLineHeightEvent({
        size: lineHeight,
        isNewParagraph: !pid,
        context: $rootScope.getContext(),
      }));

      // Save the change of the lineHeight in the data-model
      if (pid) {
        $scope.addToHistory({
          id: 'line-height',
          desc: '',
          redo: function() {
            const deferred = $q.defer();
            const blockData = container.getBlockData();

            container.setAttribute('data-line-height', lineHeight);
            container.style.lineHeight = lineHeight;
            
            if (blockData && blockData.node) {
              blockData.node.style.lineHeight = lineHeight;
            }

            $scope.lease.restyle[pid].lineHeight = lineHeight;
            $scope.update();
            $scope.listsRefresh();

            deferred.resolve();
            return deferred.promise;
          },
          undo: function() {
            const deferred = $q.defer();
            const blockData = container.getBlockData();

            container.setAttribute('data-line-height', prevLineHeight);
            container.style.lineHeight = prevLineHeight;

            if (blockData && blockData.node) {
              blockData.node.style.lineHeight = lineHeight;
            }

            $scope.lease.restyle[pid].lineHeight = prevLineHeight;
            $scope.update();
            $scope.listsRefresh();

            deferred.resolve();
            return deferred.promise;
          },
        });

        $scope.histIgnoreClearBatch += 2;
        $scope.histRegisterBatch($scope.histIndex + 1, 2);
      } else {
        if ($scope.isUnstyled(container)) {
          $scope.setOriginalStyle(container, $scope.lease.restyle[pid]);

          rangy.restoreSelection(savedSelection, true);
        }
        container.style.lineHeight = lineHeight;
      }
    };

    $scope.restyle = function() {
      
      document.querySelectorAll('.list-item-paragraph img').forEach((item)=>{
        var block =  item.getBlockData();
        if(block){
          block.containingNode.classList.add('image-container')
        }

      });
      

      if (!$scope.lease.restyle) {
        $scope.lease.restyle = [];
      }

      if ($scope.lease.restyle.reduce) {
        $scope.lease.restyle = $scope.lease.restyle.reduce(function(o, v) {
          o[v.paragraphId] = v;
          return o;
        }, {});
      }

      const styles = $scope.lease.restyle;

      for (let pid in styles) {
        const container = getParagraph(pid, true);
        const style = styles[pid];

        if (!container) {
          continue;
        }

        if (!style) {
          continue;
        }

        const originalParagraphIndent = style.originalParagraphIndent || '0pt';
        const originalTextIndent = style.originalTextIndent || '0pt';
        const originalRightIndent = style.originalRightIndent || '0pt';
        const originalTextAlign = style.originalTextAlign || 'start';
        container.setAttribute('data-original-paragraph-indent', originalParagraphIndent);
        container.setAttribute('data-original-text-indent', originalTextIndent);
        container.setAttribute('data-original-right-indent', originalRightIndent);
        container.setAttribute('data-original-text-align', originalTextAlign);

        const paragraphIndentSteps = style.paragraphIndent;
        if (paragraphIndentSteps !== null && paragraphIndentSteps !== undefined) {
          const updatedIndentationSteps = paragraphIndentSteps;
          const marginLeft = (updatedIndentationSteps * window.CONSTS.STEP_SIZE) + 'pt';
          container.style.marginLeft = marginLeft;
          container.setAttribute('data-paragraph-indent', updatedIndentationSteps);
        }

        const textIndentSteps = style.textIndent;
        if (textIndentSteps !== null && textIndentSteps !== undefined) {
          const updatedIndentationSteps = textIndentSteps;
          const textIndent = (updatedIndentationSteps * window.CONSTS.STEP_SIZE) + 'pt';
          container.style.textIndent = textIndent;
          container.setAttribute('data-text-indent', updatedIndentationSteps);
        }

        const rightIndentSteps = style.rightIndent;
        if (rightIndentSteps !== null && rightIndentSteps !== undefined) {
          const updatedIndentationSteps = rightIndentSteps;
          const marginRight = (updatedIndentationSteps * window.CONSTS.STEP_SIZE) + 'pt';
          container.style.marginRight = marginRight;
          container.setAttribute('data-right-indent', updatedIndentationSteps);
        }

        if (style.textAlign) {
          container.style.textAlign = style.textAlign;
          container.setAttribute('data-text-align', style.textAlign);
        }

        if (style.marginTop) {
          container.style.marginTop = style.marginTop;
        }

        if (style.marginBottom) {
          container.style.marginBottom = style.marginBottom;
        }

        if (style.fontSize) {
          const blockData = container.getBlockData();
          container.style.fontSize = style.fontSize;

          if (blockData && blockData.node) {
            blockData.node.style.fontSize = style.fontSize;
          }
        }

        if (style.lineHeight) {
          container.style.lineHeight = style.lineHeight;
        }

        // Restore tabstops if they exist in the restyle object
        if (style.tabstops && Array.isArray(style.tabstops) && style.tabstops.length > 0) {
          // Set the data-tabstops attribute with the saved tabstops
          container.setAttribute('data-tabstops', JSON.stringify(style.tabstops));
          
          // Re-render tabs in the paragraph if TabRenderer is available
          // The MutationObserver should pick this up automatically, but we can also trigger manually
          if (window.tabRendererInit && typeof window.tabRendererInit.renderTabsInParagraph === 'function') {
            window.tabRendererInit.renderTabsInParagraph(container);
          }
        } else if (style.tabstops && Array.isArray(style.tabstops) && style.tabstops.length === 0) {
          // Remove tabstops attribute if tabstops array is empty
          container.removeAttribute('data-tabstops');
        }

        container.classList.add('restyled');
      }

      if (window.isDownload) {
        const newParagraphs = document.querySelectorAll('.new-paragraph');

        newParagraphs.forEach(paragraph => {
          if (!paragraph.closest('[advanced-edit')) {
            const container = LeaseEditorService.findClosestContainerAbove(paragraph).get(0);
            const parentIndent = container.style.marginLeft || '0pt';
            const paragraphIndent = paragraph.style.marginLeft || '0pt';
            paragraph.style.marginLeft = parseInt(parentIndent) + parseInt(paragraphIndent) + 'pt';
          }
        });
      }
    };

    /* ============= /Alignment & Indentation tools ============= */

    $scope.changeFontSize = async function(fontSize) {
      if (LeaseEditorService.isBlinkingCaret()) {
        $scope.changeBlockFontSize(fontSize);
      } else {
        var sel = rangy.getSelection();
        var range = sel.getRangeAt(0);
        const startBlock = sel.anchorNode.getTopAnchor();
        const endBlock = sel.focusNode.getTopAnchor();
        const savedSelection = rangy.saveSelection();
        range.refresh();
        $scope.toggleSearch(false);
  
        const blocks = range.getNodes([Node.ELEMENT_NODE], function(node) {
          return (
            ["P", "H1", "H2", "H3", "H4", "H5", "H6"].indexOf(node.tagName) > -1 && 
            node.getAttribute("pid") && 
            !node.classList.contains("lp-hide-element")
          );
        });
  
        if (startBlock.getAttribute("pid") && blocks.indexOf(startBlock) === -1) {
          blocks.push(startBlock);
        }
        if (endBlock.getAttribute("pid") && blocks.indexOf(endBlock) === -1) {
          blocks.push(endBlock);
        }
  
        const blocksToChange = [];
        blocks.forEach((block) => {
          const blockData = block.getBlockData();
  
          if (blockData.isFullySelected()) {
            blocksToChange.push({
              block, 
              blockData
            });
          }
        });
  
        if (blocksToChange.length > 0) {
          for (let i = 0; i < blocksToChange.length; i++) {
            $rootScope.placeCaretAtStart(blocksToChange[i].block.querySelector(".editable"));
            $scope.changeBlockFontSize(fontSize);
          };
          
          rangy.restoreSelection(savedSelection, true);
        }
  
        setTimeout(function() {
          if (window.getSelection().type === 'Range') {
            $scope.applyDecoration("fontSize", fontSize);
          }
          if (window.getSelection().type === 'Caret') {
            $scope.execCommand("fontSize", fontSize);
          }
          $(sel.anchorNode)
            .closest('.editable')
            .focus();
          $scope.setDecorationButtonsState();
        }, 1);
      }
    };

    $scope.toggleDecoration = function(btn) {
      var sel = rangy.getSelection();
      btn.state = !btn.state;

      $scope.toggleSearch(false);

      setTimeout(function() {
        if (window.getSelection().type === 'Range') {
          $scope.applyDecoration(btn.command);
        }
        if (window.getSelection().type === 'Caret') {
          $scope.execCommand(btn.command);
        }
        $(sel.anchorNode)
          .closest('.editable')
          .focus();
        $scope.setDecorationButtonsState();
      }, 1);
    };

    $scope.toggleColorDecoration = function(command, value) {
      var sel = rangy.getSelection();

      $scope.toggleSearch(false);

      setTimeout(function() {
        if (window.getSelection().type === 'Range')
          $scope.applyDecoration(command, value);
        if (window.getSelection().type === 'Caret')
          $scope.execCommand(command, value);
        $(sel.anchorNode)
          .closest('.editable')
          .focus();
      }, 1);

      window.track.event(new HighlightTextWithColorEvent({
        type: command,
        color: value,
        context: $rootScope.getContext(),
      }));
    };

    $scope.applyDecorationToSelection = function(
      decorationType,
      decorationFunc,
    ) {
      var savedSelection = rangy.saveSelection();
      var sel = rangy.getSelection();
      var originalRange = sel.getRangeAt(0);
      var range;
      originalRange.splitBoundaries();
      var freeTexts = [];
      var nodes = originalRange.getNodes([3], function(node) {
        const isEditable = $(node).closest('.editable').length > 0;
        const parentNode = node.parentElement;
        const isSOCE = $(parentNode).hasClass('SOCE');
        const isEmptyPlaceholder =
          $(parentNode).hasClass('PLACEHOLDER') &&
          (node.textContent === window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_NO_BREAKING_SPACE || node.textContent === window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_SPACE);
        const isEOCE = $(parentNode).hasClass('EOCE');
        const isSkip = $(parentNode).hasClass('SKIP');
        const isListPadding =
          $(parentNode).hasClass('word-indent') &&
          parentNode.style.font.indexOf('7pt') !== -1;

        return (
          isEditable &&
          !isSOCE &&
          !isEmptyPlaceholder &&
          !isEOCE &&
          !isSkip &&
          !isListPadding
        );
      });

      var decorationTargetForTextNode = function(textNode) {
        if (
          !textNode ||
          textNode.nodeType !== Node.TEXT_NODE ||
          !textNode.parentElement
        ) {
          return textNode;
        }
        var leaseVar = textNode.parentElement.closest('lease-var');
        if (!leaseVar) {
          return textNode;
        }
        var spanEl = leaseVar.parentElement.closest('span');
        if (spanEl) {
          return spanEl;
        }
        return textNode;
      };

      let currentNode = null;
      for (var i = 0; i < nodes.length; i++) {
        currentNode = nodes[i];
        var decorationTarget = decorationTargetForTextNode(currentNode);
        var sectionId = $(currentNode.parentElement)
          .closest('[section-id]')
          .attr('section-id');
        if (sectionId) freeTexts.push(sectionId);
        // Unselect everything
        sel.removeAllRanges();
        range = rangy.createRange();
        range.selectNode(decorationTarget);
        sel.addRange(range);

        if (
          !$scope.btns[decorationType] ||
          $scope.btns[decorationType].state !==
            document.queryCommandState(decorationType)
        ) {
          decorationFunc(nodes[i]);
        }
      }
      freeTexts = _.uniq(freeTexts);

      let freeTextId;
      let freeText;
      for (let i = 0; i < freeTexts.length; i++) {
        freeTextId = freeTexts[i];
        freeText = document.querySelector(`[free-text="${freeTextId}"]`);

        if (freeText) {
          $(freeText)
            .find('[contenteditable]')
            .trigger('change');
        }
      }
      
      if (freeTexts.length > 1) {
        $scope.histIgnoreClearBatch += freeTexts.length;
        $scope.histRegisterBatch(
          $scope.histIndex + freeTexts.length,
          freeTexts.length,
        );
      }

      setTimeout(() => {
        rangy.restoreSelection(savedSelection, true);
      }, 250);
    };

    $scope.execCommand = function(command, value) {
      if ($rootScope.adminMode) {
        LeaseEditorService.setContentEditableColor(window.CONSTS.ADMIN_COLOR);
      }

      document.execCommand('styleWithCSS', true, null);
      document.execCommand(command, false, value);

      var sel = rangy.getSelection();
      var originalRange = sel.getRangeAt(0);

      if ($scope.btns[command] && !$scope.btns[command].state) {
        var textNodes = originalRange.getNodes([3], function(node) {
          const isEditable = $(node).closest('.editable').length > 0;
          const parentNode = node.parentElement;
          const isSOCE = $(parentNode).hasClass('SOCE');
          const isEmptyPlaceholder =
            $(parentNode).hasClass('PLACEHOLDER') &&
            (node.textContent === window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_NO_BREAKING_SPACE || node.textContent === window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_SPACE);
          const isEOCE = $(parentNode).hasClass('EOCE');
          const isSkip = $(parentNode).hasClass('SKIP');
          const isListPadding =
            $(parentNode).hasClass('word-indent') &&
            parentNode.style.font.indexOf('7pt') !== -1;

          return (
            isEditable &&
            !isSOCE &&
            !isEmptyPlaceholder &&
            !isEOCE &&
            !isSkip &&
            !isListPadding
          );
        });

        if (command === 'bold') {
          textNodes.forEach(node => {
            let parent = node.parentElement;

            if (parent.tagName.toLowerCase() !== 'span') {
              parent = parent.closest('span');
            }

            parent.style.fontWeight = 'normal';
          });
        } else if (command === 'underline') {
          textNodes.forEach(node => {
            let parent = node.parentElement;

            if (parent.tagName.toLowerCase() !== 'span') {
              parent = parent.closest('span');
            }
            parent.style.textDecoration = 'none';
          });
        } else if (command === 'italic') {
          textNodes.forEach(node => {
            let parent = node.parentElement;

            if (parent.tagName.toLowerCase() !== 'span') {
              parent = parent.closest('span');
            }
            parent.style.fontStyle = 'normal';
          });
        }
      } else if (command === 'underline'){
        let node = sel.anchorNode;
        if (node.nodeType === Node.TEXT_NODE) {
          node = node.parentElement;
        }
        if (node.style["textDecorationLine"] === "underline") {
          node.style["textDecorationLine"] = null;
          node.style["textDecoration"] = "underline"
        }
      } else if (command === 'fontSize') {
        let node = sel.anchorNode;

        if (node.nodeType === Node.TEXT_NODE) {
          node = node.parentElement;
        }

        node.style["fontSize"] = value;
      }
    };

    $scope.uploadImage = async function(file) {
      if(!file) {
        return;
      }

      if (file.name && _.endsWith(file.name.toLowerCase(), '.pdf')) {
        const images = await window.ImageHelper.getImage(file);
        $($scope.currentlyEditedNode).trigger('uploadImage', [images]);
      } else {
        var reader = new FileReader();
        reader.onload = function(image) {
          var sel = rangy.getSelection();
          if (!sel || !sel.anchorNode) {
            return;
          }
          var el = LeaseEditorService.findClosestContentEditable(sel.anchorNode);
          if (!el.length) {
            return;
          }
          $($scope.currentlyEditedNode).trigger('uploadImage', [image.target.result]);
        };
        reader.readAsDataURL(file);
      }
    }

    $scope.openUploadImgDialog = function() {
      document.getElementById('upload-image').click();
    }

    $scope.toggleControlCharacters = function() {
      $scope.isControlCharacters = !$scope.isControlCharacters;

      const spans = document
        .querySelector('.lease.lease--original')
        .querySelectorAll('.editable span');

      if ($scope.isControlCharacters) {
        window.track.event(new TurnOnControlCharactersEvent({
          context: $rootScope.getContext(),
        }));

        // Set CSS custom property with original font; CSS handles the 'lp' prefix
        const defaultFont = $scope.defaultFont?.fontFamily || 'Times New Roman';
        spans.forEach(span => {
          const originalFont = span.style.fontFamily || defaultFont;
          span.style.setProperty('--lp-original-font', originalFont);
        });
      } else {
        window.track.event(new TurnOffControlCharactersEvent({
          context: $rootScope.getContext(),
        }));

        // Remove the CSS custom property; font-family reverts to original inline style
        spans.forEach(span => {
          span.style.removeProperty('--lp-original-font');
        });
      }
    }

    $scope.toggleBookmarks = function() {
      $scope.isBookmarks = !$scope.isBookmarks;
      document.querySelector('.lease--original').classList.toggle('feature--bookmarks');
    }

    $scope.toggleRuler = function() {
        $scope.isRuler = !$scope.isRuler;
    }

    // Sync ruler state when it becomes available.
    // Reference equality only: lpRuler holds nothing but methods, and
    // angular.equals ignores functions, so a deep watch would compare equal
    // every time while still paying an angular.copy on every digest.
    $scope.$watch(function() {
      return $rootScope.lpRuler;
    }, function(newVal) {
      if (newVal && newVal.isVisible) {
        $scope.isRuler = newVal.isVisible();
      }
    });

    var cancelContentEditable = function() {
      var sel = rangy.getSelection();
      LeaseEditorService.disableEditingForSegment(sel.anchorNode);
    };

    var applyContentEditable = function() {
      var sel = rangy.getSelection();
      LeaseEditorService.enableEditingForSegment(sel.anchorNode);
    };

    $('body').on('focus', 'input[type=number]', function (e) {
      $(this).bind('mousewheel', function(e){
        e.preventDefault();
      });
    });

    $('body').on('keydown', 'input[type=number]', function (e) {
      var key = e.charCode || e.keyCode;
      if (key == window.ENUMS.KEYS.UP_ARROW || key == window.ENUMS.KEYS.DOWN_ARROW) {
        e.preventDefault();
      } else {
        return;
      }
    });

    $scope.applyDecoration = function(command, value) {
      const contentEditable = LeaseEditorService.findAllContentEditableInSelection();
      contentEditable.forEach(item => {
        let current = item;
        if (current.get) {
          current = item.get(0);
        }

        current.setAttribute(
          'original-contenteditable',
          current.getAttribute('contenteditable'),
        );
        current.setAttribute('contenteditable', true);
      });

      if (window.getSelection) {
        applyContentEditable();
        // Indicate the decoration was executed by clicking on the buttons in the toolbar
        $scope.applyDecorationToSelection(command, function(textNode) {
          const contentEditable = textNode.parentElement.closest('[contenteditable="false"]');

          if (contentEditable) {
            contentEditable.setAttribute("contenteditable", "true");
            $scope.execCommand(command, value);
            contentEditable.setAttribute("contenteditable", "false");
          } else if (textNode.textContent !== "﻿" || textNode.textContent !== "​") {
            $scope.execCommand(command, value);
          }
        });

        cancelContentEditable();
      }

      contentEditable.forEach(item => {
        let current = item;
        if (current.get) {
          current = item.get(0);
        }

        if (current.getAttribute('original-contenteditable')) {
          current.setAttribute(
            'contenteditable',
            current.getAttribute('original-contenteditable'),
          );
          current.removeAttribute('original-contenteditable');
        }
      });
    };

    $scope.resetDecoration = function() {
      _.forEach($scope.btns, function(btn) {
        btn.state = false;
      });
    };

    $scope.setDecoration = function() {
      var savedSelection = rangy.saveSelection();
      var sel = rangy.getSelection();

      if (sel.rangeCount === 0) {
        return;
      }

      if (!sel.anchorNode) {
        return;
      }

      var originalRange = sel.getRangeAt(0);
      var range;
      var statusBtn = ['bold', 'italic', 'underline'];

      originalRange.splitBoundaries();
      var nodes = originalRange.getNodes([3], function(node) {
        const isEditable = $(node).closest('.editable').length > 0;
        const parentNode = node.parentElement;
        const isSOCE = $(parentNode).hasClass('SOCE');
        const isEmptyPlaceholder =
          $(parentNode).hasClass('PLACEHOLDER') &&
          (node.textContent === window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_NO_BREAKING_SPACE || node.textContent === window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_SPACE);
        const isEOCE = $(parentNode).hasClass('EOCE');
        const isListPadding =
          $(parentNode).hasClass('word-indent') &&
          parentNode.style.font.indexOf('7pt') !== -1;
        const isSkip = parentNode.classList.contains('SKIP');

        return (
          isEditable &&
          !isSOCE &&
          !isEmptyPlaceholder &&
          !isEOCE &&
          !isListPadding &&
          !isSkip
        );
      });

      if (!nodes.length && !LeaseEditorService.isSelecting()) {
        nodes = [sel.focusNode];
      }

      // Lets stop this heavy process if the selection is unlikely to have a common decoration
      // Arbitrary, if the number of text nodes in the selection is greater than 150
      if (nodes.length > 150) {
        $scope.resetDecoration();
        return;
      }

      var btnState = nodes.length > 0;
      _.forEach(statusBtn, function(decoration) {
        if ($scope.btns[decoration]) {
          $scope.btns[decoration].state = btnState;
        }
      });

      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].style && nodes[i].style["textDecorationLine"] === "underline") {
          nodes[i].style["textDecorationLine"] = null;
          nodes[i].style["textDecoration"] = "underline"
        }
        if (LeaseEditorService.findClosestContentEditable(nodes[i]).length > 0) {
          if (LeaseEditorService.isSelecting()) {
            sel.removeAllRanges();
            range = rangy.createRange();
            range.selectNode(nodes[i]);
            sel.addRange(range);
          }
          _.forEach(statusBtn, function(decoration) {
            if (!document.queryCommandState(decoration)) {
              if ($scope.btns[decoration]) {
                $scope.btns[decoration].state = false;
              }
            }
          });
        }
      }
      // Bring back the original selection

      rangy.restoreSelection(savedSelection, true);
      $scope.setDecorationButtonsState();
    };

    $scope.setDecorationButtonsState = function() {
      // Set the actual state of the button since full "update" causes major angularJS cycle
      $('button.deco-button').each(function() {
        switch ($(this).attr('label')) {
          case 'B':
            $scope.setDecorationButtonState($(this), $scope.btns.bold);
            break;
          case 'I':
            $scope.setDecorationButtonState($(this), $scope.btns.italic);
            break;
          case 'U':
            $scope.setDecorationButtonState($(this), $scope.btns.underline);
            break;
        }
      });
    };

    $scope.setDecorationButtonState = function(button, buttonObj) {
      if (button) {
        if (buttonObj.state) {
          button.addClass('on' + buttonObj.label);
          button.removeClass('off' + buttonObj.label);
        } else {
          button.addClass('off' + buttonObj.label);
          button.removeClass('on' + buttonObj.label);
        }
      }
    };

    $scope.clearRestyle = function(container) {
      if (container.hasClass('restyled')) {
        let originalTextIndent = container.attr("data-original-text-indent");
        let originalParagraphIndent = container.attr("data-original-paragraph-indent");
        let originalRightIndent = container.attr("data-original-right-indent");
        let originalTextAlign = container.attr("data-original-text-align");
        let originalFontSize = container.attr("data-original-font-size");
        let pid;

        pid = container.attr('pid');
        pid = getParagraphId(pid, true);

        container.css("text-indent", originalTextIndent || '');
        container.css("margin-left", originalParagraphIndent || '');
        container.css("margin-right", originalRightIndent || '');
        container.css("text-align", originalTextAlign || '');
        container.css("font-size", originalFontSize || '');

        container.attr("data-original-text-indent", null);
        container.attr("data-original-paragraph-indent", null);
        container.attr("data-original-right-indent", null);
        container.attr("data-original-text-align", null);
        container.attr("data-original-font-size", null);
        container.attr("data-text-align", null);
        container.attr("data-paragraph-indent", null);
        container.attr("data-text-indent", null);
        container.attr("data-right-indent", null);
        container.attr("data-font-size", null);

        container.removeClass('restyled');

        $scope.lease.restyle[pid] = null;

        $scope.update();
      }
    }

    // reset free text areas in lease
    $scope.deleteFreeText = function(sectionId, isReset) {
      var deferred = $q.defer();
      var element = $('[section-id="' + sectionId + '"]');
      var container = LeaseEditorService.findClosestContainer(element);

      $scope.startSaving();
      // get the FreeText resource to delete
      var toRemove = $scope.leaseFreeTexts[sectionId];

      if (isReset) {
        $scope.clearRestyle(container);
      }

      // ensure that it exists (no bug letting the user click delete when not possible to delete)
      if (toRemove != null) {
        // must set to null since remove() does not automatically set it to null
        $scope.leaseFreeTexts[sectionId] = null;
        if (!$rootScope.adminMode) {
          // delete on server
          var url = '/api/free_texts/' + toRemove.id;
          FreeTextService.$delete(url).then(function() {
            deferred.resolve();
            $(container).removeClass('deleted-container');

            $scope.structureChanged();
            $scope.endSaving();
          });
        } else {
          $timeout(function() {
            $(container).removeClass('deleted-container');
            $scope.structureChanged();
          });
          deferred.resolve();
        }
      } else {
        deferred.resolve();
        $scope.endSaving();
      }
      return deferred.promise;
    };

    $scope.setHighlightScrollIndex = function() {
      if ($scope.inline.on) {
        var hls = $scope.getAllHls();
        var targetElements = hls.add('#active-element-anchor');
        var activeEl = $($scope.inline.$activeElm).is('lease-var')
          ? $($scope.inline.$activeElm).find('.adding')[0]
          : $scope.inline.$activeElm;

        var elementIndex = targetElements.index(activeEl);
        $scope.highlightScrollIndex =
          elementIndex == -1 ? 0 : elementIndex % hls.length;
      } else {
        var hls = $scope.getAllHls().filter("[exclude!='true']");
        var targetIndex = null;

        for (let i = 0; i < hls.length && targetIndex === null; i++) {
          if (isInView(hls[i])) {
            targetIndex = i;
          }
        }

        if (targetIndex === null) {
          targetIndex = 0;
        }

        $scope.highlightScrollIndex = targetIndex;
      }
    };

    $scope.scrollFirst = function() {
      window.applyFilterVisible(() => {
        $scope.setHighlightScrollIndex();
        var hls = $('.adding, .adding-para').filter("[exclude!='true']");
        var elements = hls.get();

        if (elements.length > 0) {
          if ($scope.inline.on) {
            $scope.scrollToElement($scope.inline.$activeElm);
          } else {
            $scope.scrollToElement($(elements[$scope.highlightScrollIndex]));
          }
        }
      });
    };

    $scope.scrollPrev = function() {
      window.applyFilterVisible(() => {
        // elements to scroll
        var hls = $scope.getAllHls().filter("[exclude!='true']");

        // step back (circular)
        if ($scope.highlightScrollIndex >= 1) {
          $scope.highlightScrollIndex--;
        } else {
          $scope.highlightScrollIndex = hls.length - 1;
        }

        if (hls.length) {
          var scrollElt = $(hls[$scope.highlightScrollIndex]);
          $scope.scrollToElement(scrollElt);
        }
      });
    };

    $scope.scrollNext = function() {
      // elements to scroll
      //var hls = $("*[class^='adding']");
      var hls = $scope.getAllHls().filter("[exclude!='true']");

      // step forward (circular)
      $scope.highlightScrollIndex = ++$scope.highlightScrollIndex % hls.length;

      if (hls.length) {
        var scrollElt = $(hls[$scope.highlightScrollIndex]);
        $scope.scrollToElement(scrollElt);
      }
    };

    /*
     * Scrolls to the given element in the lease preview panel.
     * @param scrollElt - a jQuery object
     *
     */
    $scope.scrollToElement = function(scrollElt) {
      var container = $('.preview');

      if (scrollElt && scrollElt.offset() && !isInView(scrollElt)) {
        // if you found it and it's visible
        // top of the screen
        var scrollLoc =
          scrollElt.offset().top -
          container.offset().top +
          container.scrollTop();

        // if element would be shorter than the screen height, scroll to a lower point so it's centered
        if (scrollElt.height() < $(container).height()) {
          scrollLoc -= ($(container).height() - scrollElt.height()) / 2;
        }

        // do the animation
        $(container).animate(
          {
            scrollTop: scrollLoc,
          },
          400,
        );
      }
    };

    const isInView = function(elem) {
      var docViewTop = $('.preview').offset().top;
      var docViewBottom = docViewTop + $('.preview').height();

      var elemTop = $(elem).offset().top;
      var elemBottom = elemTop + $(elem).height();

      var isCompletelyInView =
        elemBottom <= docViewBottom && elemTop >= docViewTop;
      var isOverflowFromView =
        elemBottom > docViewBottom && parseInt(elemTop) === docViewTop;

      return isCompletelyInView || isOverflowFromView;
    };

    $scope.anchorScroll = function() {
      if (
        $window.prerenderReady &&
        (!$scope.inline.on || $scope.inline.pinned)
      ) {
        setTimeout(function() {
          $scope.scrollFirst();
        });
      }
    };

    $scope.setCalculatedExpirationDate = function() {
      if ($scope.lease) {
        var baseDate = $scope.lease.rentCommenceAt
          ? $scope.lease.rentCommenceAt
          : $scope.lease.estimatedCommencementDate;

        if (baseDate) {
          var deliveryDate = new Date(baseDate);
          // Add years = number of years in the lease
          var years = $scope.lease.leaseYears || 0;
          var months = $scope.lease.leaseMonths || 0;
          $scope.lease.calculatedExpirationDate = moment(deliveryDate)
            .add(years, 'year')
            .add(months, 'month')
            .toDate();
          // $scope.lease.calculatedExpirationDate = new Date(deliveryDate.setFullYear(deliveryDate.getFullYear() + $scope.lease.leaseYears));

          // Subtruct the last day
          $scope.lease.calculatedExpirationDate.setDate(
            $scope.lease.calculatedExpirationDate.getDate() - 1,
          );
        }
      }
    };

    $scope.isDate = function(date) {
      return moment(date).isValid();
    };

    $scope.addYears = window.datesHelper.addYears;

    $scope.addMonths = window.datesHelper.addMonths;

    $scope.addDays = window.datesHelper.addDays;

    $scope.firstDayOfMonth = window.datesHelper.firstDayOfMonth;

    $scope.isFirstDayOfMonth = window.datesHelper.isFirstDayOfMonth;

    $scope.lastDayOfMonth = window.datesHelper.lastDayOfMonth;

    $scope.isLastDayOfMonth = window.datesHelper.isLastDayOfMonth;

    $scope.formatDateAsMonthDayYear = window.datesHelper.formatDateAsMonthDayYear;

    $scope.formatDateAsText = window.datesHelper.formatDateAsText;

    $scope.formatDateAs = window.datesHelper.formatDateAs;

    $scope.fixDate = window.datesHelper.fixDate;

    $scope.setListItems = function() {
      var deferred = $q.defer();
      var promises = [];

      if ($scope.listLevels) {
        var articleTypeExists = $scope.listLevels.find(function(ele) {
          return ele.level == "article";
        });
        var sectionTypeExists = $scope.listLevels.find(function(ele) {
          return ele.level == "section";
        });
        if (!articleTypeExists) {
          $("#article-buttons").addClass("ng-hide");
        }
        if (!sectionTypeExists) {
          $("#list-buttons").addClass("ng-hide");
        }
      } else {
        $("#article-buttons").addClass("ng-hide");
        $("#list-buttons").addClass("ng-hide");
      }

      // loads the list items json from the lease - if it has not been used yet, puts an empty list
      const overrides = $scope.overrideOrderedListitem;
      const persistedStructure = parseOrderedListitem(
        $scope.lease.orderedListitem,
      );
      const listItems = persistedStructure.listItems;
      $scope.orderedListitemSupplementalEntries =
        persistedStructure.supplementalEntries;

      const rebuiltRows = rebuildPersistedTableRows(
        document.querySelector('.lease.lease--original') || document,
        persistedStructure.tableRows,
      );
      rebuiltRows.forEach(row => $compile(row)($scope));

      const allListItems = overrides.concat(listItems);

      // goes through each item and inserts the list item

      /* mark old added articles Start  */
      $scope.listItems = allListItems;
      allListItems.forEach(item => {
        if (item.level.toLowerCase() === "article") {
          if (Object.keys(item).indexOf("isNewArticle") === -1) {
            item.isNewArticle = false;
          }
        }
      });
      /* mark old added articles End  */

      $rootScope.listItemsPromises = {};

      if (!$scope.lease.layers || !Array.isArray($scope.lease.layers)) {
        $scope.lease.layers = [];
      }

      allListItems.forEach(function(itemObject) {
        if (itemObject && itemObject.afterFreeText && pageLayersManager) {
          pageLayersManager.updateOutlineById(itemObject.afterFreeText);
        }

        listItemsManager.listsInsertItem(itemObject);
      });

      var promises = _.chain($rootScope.listItemsPromises)
        .mapValues(function(val) {
          return val.promise;
        })
        .values()
        .value();

      $q.all(promises).then(function() {
        $scope.reInsertMissingListItems();
        deferred.resolve();
        delete $rootScope.listItemsPromises;
      });

      return deferred.promise;
    };

    $scope.serializeOrderedListitem = function() {
      return serializeOrderedListitem(
        $scope.listItems,
        $scope.orderedListitemSupplementalEntries,
      );
    };

    $scope.getPctRentEscalatedUnnaturalBreakpoint = function(year) {
      if ($scope.lease) {
        var value = $scope.lease.pctRentBaseGrossSales;

        if (value !== '' && value != null) {
          if (year == 0) {
            return value;
          } else if (year) {
            for (var i = 1; i <= year; i++) {
              value =
                value *
                (1 + $scope.lease.percentageRentUnnaturalEscalation / 100);
            }
            return value;
          }
        }
      }

      // Intentionally return NaN to prevent showing $0.00 when number is not set
      return '-';
    };

    $rootScope.isBuildingLanguageNeeded = false;
    $rootScope.isBuildingLanguageLoaded = false;
  
    // When the page has no <left-editor> directive (e.g. download/blank-lease views),
    // the directive will never broadcast templatesReady. Mark ready immediately in that  
    // case so setBuildingUrl/loadDocument/updateBuilding aren't blocked indefinitely.    
    $scope._templatesReady = !document.querySelector('left-editor');

    // Track global templates readiness signaled from left_editor_directive.
    $rootScope.$on('templatesReady', function() {
      $scope._templatesReady = true;
    });

    $scope.setBuildingUrl = function(specificUrl) {
      var urlToUse =
        specificUrl ||
        ($scope.building && $scope.building.specificUrl);

      if (!$scope._templatesReady) {
        // Ensure we only register one handler.
        if (!$scope._setBuildingUrlTemplatesListenerRegistered) {
          $scope._setBuildingUrlTemplatesListenerRegistered = true;
          $scope.$on('templatesReady:setBuildingUrl', function() {
            $scope._doSetBuildingUrl(urlToUse);
          });
        }
        // Return undefined so that ng-include guards (ng-if="resourceUrl") prevent
        // premature loading with the raw, unprocessed URL.
        return undefined;
      }

      return $scope._doSetBuildingUrl(urlToUse);
    };

    $scope._doSetBuildingUrl = function(specificUrl) {
      $scope.setAssetsPaths();

      if (
        !window.isAdmin &&
        $rootScope.isBuildingLanguageNeeded &&
        $rootScope.isBuildingLanguageLoaded
      ) {
        return $scope.specificUrl;
      }

      var parts;

      specificUrl = specificUrl.toString();
      parts = specificUrl.split('/');

      if (document.querySelector("building-variable")) {
        if (parts.length == 2) {
          if ($scope.lease && typeof $scope.lease.error === 'undefined') {
            var buildingFileUrl = parts[1];
  
            var updatedAt = $scope.lease.form.updatedAt;
            if (typeof updatedAt === 'string') {
              updatedAt = new Date(updatedAt);
            }
            specificUrl =
              $scope.ASSETS_BUILDING_DIR_URL +
              "/" +
              buildingFileUrl +
              "?v=" +
              updatedAt.toISOString();
  
            $rootScope.isBuildingLanguageNeeded = true;
          } else {
            specificUrl = '';
          }
        }
      }

      if (
        $rootScope.isBuildingLanguageNeeded &&
        !$rootScope.isBuildingLanguageLoaded
      ) {
        var httpGetCompleted = false;
        var httpGetSuccess = false;
        
        $http.get(specificUrl, { cache: true }).then(function(res) {
          httpGetCompleted = true;
          httpGetSuccess = true;
          $templateCache.put(specificUrl, res.data);
        }).catch(function(error) {
          httpGetCompleted = true;
          httpGetSuccess = false;
        });

        var loadedListenerDeregister = $scope.$on('$includeContentLoaded', function(event, src) {
          if (specificUrl === src) {
            $rootScope.isBuildingLanguageLoaded = true;
            window.isBuildingLanguageLoadingError = false;
            loadedListenerDeregister();
          }
        });
        
        var errorListenerDeregister = $scope.$on('$includeContentError', function(event, src) {
          if (specificUrl === src) {
            $rootScope.isBuildingLanguageLoaded = true;
            errorListenerDeregister();

            // Only show error if $http.get completed AND failed (file doesn't exist) AND we haven't shown the error yet
            // This prevents false positives when the file exists but ng-include fails for other reasons (parsing, etc.)
            if (!window.isBuildingLanguageLoadingError && !!$scope.lease.building && httpGetCompleted && !httpGetSuccess) {
              window.isBuildingLanguageLoadingError = true;

              if (window.isDownload) {
                return;
              }
              
              let message = 'The building-specific language could not be found and may not be fully included in this document. <br/><br/>This has been automatically reported to LeasePilot and we will contact you to resolve the issue.';
              let notify = true;

              if (window.user?.company?.companyProfile?.config?.notifications?.buildingLanguageNotFound) {
                message = window.user.company.companyProfile.config.notifications.buildingLanguageNotFound.message;
                notify = window.user.company.companyProfile.config.notifications.buildingLanguageNotFound.notify;
              }

              $scope.openConfirmModal(
                message,
                null,
                function() {
                  if (notify) {
                    const data = new FormData();
                    data.append("name", scopeVar.$root.user.firstName && scopeVar.$root.user.lastName ? `${scopeVar.$root.user.firstName} ${scopeVar.$root.user.lastName}` : "");
                    data.append("email", $rootScope.user.email);
                    data.append("type", "other_problem");
                    data.append("message", `The building-specific language could not be found for ${document.location.href}`);
  
                    const options = {
                      transformRequest: angular.identity,
                      headers: { 'Content-Type': undefined },
                    };
  
                    if (window._zendesk_enabled) {
                      $http
                        .post('/api/zendesk_requests', data, options)
                        .then(function() {
                        })
                        .catch(function() {
                        });
                    }
                  }
                },
                {
                  cancel: 'Continue',
                },
              );
            }
          }
        });
      }

      $scope.specificUrl = specificUrl;

      return $scope.specificUrl;
    };

    function setBuildingOverrides(buildingOverrides) {
      var data = {};
      buildingOverrides.forEach(function(bo, i) {
        data[bo.buildingId] = data[bo.buildingId] || {};
        data[bo.buildingId][bo.bsName] = buildingOverrides[i];
      });
      $rootScope.buildingOverrides = data;
    }

    $scope.getOverrides = function() {
      $scope.inInitialLoad = false;
      setBuildingOverrides($scope.lease.form.buildingOverrides || []);

      var ordered_listitem = _.find($scope.lease.form.overrides, function(o) {
        return o.overrideType === 'ordered_listitem';
      });

      const overrideRestyle = _.find($scope.lease.form.overrides, function(o) {
        return o.overrideType === 'restyle';
      });
      
      let restyleData = null;
      if(overrideRestyle){
        restyleData = JSON.parse( overrideRestyle.orderedListitem);
        if(!lease.restyle || Object.keys(lease.restyle).length == 0){
          lease.restyle = restyleData;
        }
      }

      const overrideLayers = _.find($scope.lease.form.overrides, function(o) {
        return o.overrideType === 'layers';
      });

      let layersData = null;
      if(overrideLayers){
        layersData = JSON.parse( overrideLayers.orderedListitem);
        if( lease.layers == null || Object.keys(lease.layers).length == 0){
          lease.layers = layersData;

        }
      }


      $scope.overrideOrderedListitem = [];

      if (ordered_listitem && !$rootScope.adminMode) {
        $scope.overrideOrderedListitem = ordered_listitem.orderedListitem
          ? JSON.parse(ordered_listitem.orderedListitem)
          : [];
      }

      $scope.overrides = $scope.lease.form.overrides;
    };

    $scope.setOverrides = function() {
      var overrides = $scope.overrides
        ? $scope.overrides.reduce(function(o, v) {
            v.type = 'override';
            o[v.sectionId] = v;
            return o;
          }, {})
        : {};
      // concat overrides to freeText list
      $scope.leaseFreeTexts = Object.assign(overrides, $scope.leaseFreeTexts);
      if ($rootScope.adminMode) {
        $scope.prevOverrides = $scope.leaseFreeTexts;
      }
    };

    $scope.setAdminBuildings = function() {
      BuildingService.query({
        companyId: $rootScope.editedCompanyId,
        includeBlanks: true,
      }).then(function(response) {
        $scope.adminBuildings = response;
        $scope.currentAdminBuilding = response[0];
      });
    };

    $scope.openConfirmModal = function(
      msg,
      okCallback,
      cancelCallback,
      buttons,
      redirectUrl,
    ) {
      var confirm = $mdDialog.confirm({
        skipHide: true,
        template:
          '<div class="confirm-modal md-dialog-content">' +
          '<p class="title" >' +
          msg +
          '</p>' +
          '<div class="modal-actions">' +
          '<button ng-if=' +
          _.has(buttons, 'ok') +
          ' class="btn blue-outline" ng-click="dialog.hide()">' +
          buttons.ok +
          '</button>' +
          '<button ng-if=' +
          _.has(buttons, 'cancel') +
          ' class="btn blue active" ng-click="dialog.abort()">' +
          buttons.cancel +
          '</button>' +
          '</div>' +
          '</div>',
        onRemoving: function() {
          if (redirectUrl) {
            window.location.href = redirectUrl;
          }
        },
      });

      $mdDialog.show(confirm).then(
        function confirmPublish() {
          if (okCallback) {
            window.track.event(
              new PublishFormManagementChangesEvent({
                id: $scope.lease.formId,
                name: $scope.lease.form.displayName
              })
            );

            okCallback()
          };
        },
        function preventPublish() {
          if (cancelCallback) cancelCallback();
        },
      );
    };

    $scope.confirmPublish = function(pathToRedirect) {
      var msg =
        'Note: changes are made to the form and are not asset specific. Publishing the form will update this form template language for new deals, are you sure?';
      $scope.openConfirmModal(
        msg,
        function confirmPublish() {
          $scope.saveOverride(true, pathToRedirect);
        },
        null,
        {
          ok: 'Yes',
          cancel: 'No',
        },
      );
    };

    $scope.confirmDiscard = function(pathToRedirect) {
      var msg = 'All last changes will be discarded, continue?';
      $scope.openConfirmModal(
        msg,
        function confirmDiscard() {
          window.track.event(
            new CancelFormManagementChangesEvent({
              id: $scope.lease.formId,
              name: $scope.lease.form.displayName
            })
          );
          
          document.querySelectorAll('[override=true]').forEach(function(el) {
            el.removeAttribute("override");
          });
          
          window.location = pathToRedirect;
        },
        null,
        {
          ok: 'Yes',
          cancel: 'No',
        },
      );
    };

    $scope.setAdminMode = function(lease, role) {
      lease = lease || window.lease;
      $rootScope.adminMode = true;
      RolesService.setRole(role);
      $rootScope.editedCompanyId = lease.companyId;
      $scope.setAdminBuildings();
      $scope.setLease(lease);
    };

    function setLeaseTerms() {
      if (!$scope.lease.terms) {
        $scope.lease.terms = [];
      }

      if ($scope.lease.terms.length === 0) {
        $scope.lease.terms.push({});
      }

      if (
        $scope.lease.terms.length === 1 &&
        !$scope.lease.terms[0].isInitialized
      ) {
        if (
          $scope.lease.form.editor &&
          $scope.lease.form.editor.defaultFields.terms &&
          $scope.lease.form.editor.defaultFields.terms.length
        ) {
          $scope.lease.terms[0] = {
            ...$scope.lease.terms[0],
            ...$scope.lease.form.editor.defaultFields.terms[0],
          };
        }
        $scope.lease.terms[0].isInitialized = true;
      }
      $scope.setAvailableSpaces();
    }

    $scope.setAvailableSpaces = function() {
      var termsSpaces = _.flatMap(
        _.flatMap($scope.lease.terms, 'spaces'),
        'number',
      );
      $scope.availableSpaces = _.filter($scope.lease.premiseSuites, function(
        suite,
      ) {
        return !termsSpaces.includes(suite.number);
      });
    };

    $scope.setLeaseDefaults = function() {
      if ($scope.lease.guarantorInfo) {
        $scope.lease.individualGuarantors = $scope.lease.guarantorInfo
          .individuals
          ? $scope.lease.guarantorInfo.individuals.length > 0
            ? $scope.lease.guarantorInfo.individuals.length
            : 0
          : 0;
        $scope.lease.marriedGuarantors = $scope.lease.guarantorInfo
          .marriedCouples
          ? $scope.lease.guarantorInfo.marriedCouples.length > 0
            ? $scope.lease.guarantorInfo.marriedCouples.length
            : 0
          : 0;
        $scope.lease.entityGuarantors = $scope.lease.guarantorInfo.entities
          ? $scope.lease.guarantorInfo.entities.length > 0
            ? $scope.lease.guarantorInfo.entities.length
            : 0
          : 0;
        if (
          $scope.lease.guarantorInfo.guarantors &&
          $scope.lease.guarantorInfo.guarantors.length
        ) {
          initGuarantorsFromOldObject();
        }
        $scope.setGuarantorLists();
        $scope.initCombinedGuarantorEntities($scope.lease.guarantorInfo);
        $scope.initCombinedGuarantorEntities($scope.lease.existingGuarantorInfo);
      }

      if (!$scope.lease.existingGuarantorInfo) {
        $scope.lease.existingGuarantorInfo = {};
      }

      if (!$scope.lease.minimumSales) {
        $scope.lease.minimumSales = [];
        for (var i = 0; i < $scope.lease.termNumber; i++) {
          $scope.lease.minimumSales.push({
            value: undefined,
          });
        }
      }

      setLeaseTerms();

      if (!$scope.lease.rentBumps) {
        $scope.lease.rentBumps = [
          {
            begin: 1,
          },
        ];
      }
      if (!$scope.lease.expansion || !$scope.lease.expansion.rentBumps) {
        $scope.lease.expansion = {};
        $scope.lease.expansion.rentBumps = [
          {
            begin: 1,
          },
        ];
      }
      if (!$scope.lease.relocation || !$scope.lease.relocation.rentBumps) {
        $scope.lease.relocation = {};
        $scope.lease.relocation.rentBumps = [
          {
            begin: 1,
          },
        ];
      }
      if (!$scope.lease.reduction || !$scope.lease.reduction.rentBumps) {
        $scope.lease.reduction = {};
        $scope.lease.reduction.rentBumps = [
          {
            begin: 1,
          },
        ];
      }

      if ($scope.lease.existingGuarantorInfo) {
        if (!$scope.lease.existingGuarantorInfo.individuals) {
          $scope.lease.existingGuarantorInfo.individuals = [];
        }
        if (!$scope.lease.existingGuarantorInfo.marriedCouples) {
          $scope.lease.existingGuarantorInfo.marriedCouples = [];
        }
        if (!$scope.lease.existingGuarantorInfo.entities) {
          $scope.lease.existingGuarantorInfo.entities = [];
        }
        $scope.lease.individualExistingGuarantors = $scope.lease
          .existingGuarantorInfo.individuals
          ? $scope.lease.existingGuarantorInfo.individuals.length > 0
            ? $scope.lease.existingGuarantorInfo.individuals.length
            : 1
          : 1;
        $scope.lease.marriedExistingGuarantors = $scope.lease
          .existingGuarantorInfo.marriedCouples
          ? $scope.lease.existingGuarantorInfo.marriedCouples.length > 0
            ? $scope.lease.existingGuarantorInfo.marriedCouples.length
            : 0
          : 0;
        $scope.lease.entityExistingGuarantors = $scope.lease
          .existingGuarantorInfo.entities
          ? $scope.lease.existingGuarantorInfo.entities.length > 0
            ? $scope.lease.existingGuarantorInfo.entities.length
            : 0
          : 0;
      }

      if (!$scope.lease.additionalExistingGuarantorInfo) {
        $scope.lease.additionalExistingGuarantorInfo = {};
      }

      if (!$scope.lease.additionalExistingGuarantorInfo.individuals) {
        $scope.lease.additionalExistingGuarantorInfo.individuals = [];
      }
      if (!$scope.lease.additionalExistingGuarantorInfo.marriedCouples) {
        $scope.lease.additionalExistingGuarantorInfo.marriedCouples = [];
      }
      if (!$scope.lease.additionalExistingGuarantorInfo.entities) {
        $scope.lease.additionalExistingGuarantorInfo.entities = [];
      }
      $scope.initCombinedGuarantorEntities(
        $scope.lease.additionalExistingGuarantorInfo,
      );
      $scope.lease.individualAdditionalExistingGuarantors = $scope.lease
        .additionalExistingGuarantorInfo.individuals.length;
      $scope.lease.marriedAdditionalExistingGuarantors = $scope.lease
        .additionalExistingGuarantorInfo.marriedCouples.length;
      $scope.lease.entityAdditionalExistingGuarantors = $scope.lease
        .additionalExistingGuarantorInfo.entities.length;

      if (!$scope.lease.openingCoTenancy) {
        $scope.lease.openingCoTenancy = {};
      }

      if (!$scope.lease.openingCoTenancy.minNamedTenants) {
        $scope.lease.openingCoTenancy.minNamedTenants = 1;
      }

      if (!$scope.lease.ongoingCoTenancy) {
        $scope.lease.ongoingCoTenancy = {};
      }

      if (!$scope.lease.ongoingCoTenancy.minNamedTenants) {
        $scope.lease.ongoingCoTenancy.minNamedTenants = 1;
      }

      if (!$scope.lease.calc) {
        $scope.lease.calc = {};
      }

      // init array variables.
      var arrays = [
        'reductionPeriods',
        'renewalInfo',
        'guarantyCapBurnOffReductionPeriods',
        'abatementDates',
        'premiseSuites',
        'previousAmendment',
        'priorAssignments',
        'priorDocuments',
        'rentPeriods',
        'freeRentPeriods',
        'consentContingencies',
      ];

      for (var i = 0; i < arrays.length; i++) {
        if (!$scope.lease[arrays[i]]) {
          $scope.lease[arrays[i]] = [];
        }
      }

      if (!$scope.lease.event) {
        $scope.lease.event = {};
      }

      if (!$scope.lease.insurance) {
        $scope.lease.insurance = {};
      }

      if (!$scope.lease.event.times) {
        $scope.lease.event.times = [];
      }

      if (!$scope.lease.ongoingCoTenancy.namedTenantsList) {
        $scope.lease.ongoingCoTenancy.namedTenantsList = [];
      }

      // Rails serializes decimal columns to JSON as strings to preserve
      // precision. Coerce to Number once on load so downstream arithmetic
      // (and especially `+`) doesn't silently fall into string concatenation.
      if ($scope.lease.securityDeposit != null) {
        $scope.lease.securityDeposit = parseFloat($scope.lease.securityDeposit);
      }
      if ($scope.lease.securityDepositCents != null) {
        $scope.lease.securityDepositCents = parseFloat($scope.lease.securityDepositCents);
      }

      if (
        $scope.lease.securityDeposit == null &&
        $scope.lease.securityDepositCents != 0
      ) {
        $scope.lease.securityDeposit = $scope.lease.securityDepositCents / 100;
      }

      if ($scope.lease.rentCommenceAt) {
        $scope.lease.rentCommenceAt = new Date($scope.lease.rentCommenceAt);
      }

      if ($scope.lease.rentCommenceAt && $scope.lease.leaseYears) {
        $scope.setCalculatedExpirationDate();
      }

      if (
        $scope.lease.rentInputType === 'automatic' &&
        $scope.lease.rentPeriods
      ) {
        $scope.refreshRentParams($scope.lease.rentPeriods);
      }

      if ($scope.lease.renewalInfo && $scope.lease.renewalInfo.length > 0) {
        if (
          $scope.lease.renewalCount === null ||
          typeof $scope.lease.renewalCount === 'undefined'
        ) {
          $scope.lease.renewalCount = $scope.lease.renewalInfo.length;
        }
        if (
          (
            $scope.lease.renewalTerm === null ||
            typeof $scope.lease.renewalTerm === 'undefined'
          ) && 
          $scope.lease.renewalInfo[0].renewalRentPeriods
        ) {
          $scope.lease.renewalTerm =
            $scope.lease.renewalInfo[0].renewalRentPeriods.length;
        }
      }

      for (let i = 0; i < $scope.lease.renewalInfo.length; i++) {
        if (!$scope.lease.renewalInfo[i].renewalRentPeriods) {
          $scope.lease.renewalInfo[i].renewalRentPeriods = [];
        }
      }

      if(!$scope.lease['renewalRentBumps']) {
        $scope.lease['renewalRentBumps'] = [];
      }

      for(let i = 0; i < $scope.lease.renewalCount ; ++i) {
        if (!$scope.lease['renewalRentBumps'][i]) {
          $scope.lease['renewalRentBumps'][i] = {
            periods : [
              {
                begin: 1,
              },
            ],
          };
        }
      }

      if (
        !$scope.lease.rentPeriods || 
        $scope.lease.rentPeriods.length === 0
      ) {
        $scope.lease.rentPeriods = [];

        setTimeout(function() {
          $scope.updateNumberOfRentPeriods($scope.lease.leaseYears, $scope.lease.leaseMonths);
        });
      }

      if ($scope.lease.rentPeriods) {
        $scope.consolidateRentPeriods();
      }

      if (
        $scope.lease.renewalInfo.length !== $scope.lease.renewalCount || 
        (
          $scope.lease.renewalInfo[0] && 
          $scope.lease.renewalInfo[0].renewalRentPeriods && 
          $scope.lease.renewalInfo[0].renewalRentPeriods.length !== $scope.lease.renewalTerm
        )
      ) {
        $scope.updateNumberOfRenewalTermsAndRentPeriods($scope.lease.renewalCount, $scope.lease.renewalTerm);
      }

      if ($scope.lease.renewalInfo) {
        $scope.consolidateRenewalInfo();
      }

      if ($scope.lease.landlordWarranty) {
        $scope.countWarranties();
      }

      if ($scope.lease.leaseYears) {
        $scope.cpiAnchorYearsOptions = Array.from(
          new Array($scope.lease.leaseYears - 1),
          function(val, index) {
            return index + 1;
          },
        );
      }

      var listOfLeaseYears = [];
      var listOfLeaseYearsWords = {};
      let listOfLeaseYearsLength;

      if ($rootScope.hasEditorConfig("optionalExtensionTermLength")) {
        listOfLeaseYearsLength = 20;
      } else {
        listOfLeaseYearsLength = $scope.lease.leaseYears || 0;
      }

      for (var i = 0; i < listOfLeaseYearsLength; i++) {
        listOfLeaseYears.push(i + 1);
        listOfLeaseYearsWords[i + 1] = 'Year ' + (i + 1);
      }

      $scope.lease.listOfLeaseYears = listOfLeaseYears;
      $scope.lease.listOfLeaseYearsWords = listOfLeaseYearsWords;

      if ($scope.lease.renewalTerm) {
        $scope.cpiRenewalAnchorYearsOptions = Array.from(
          new Array($scope.lease.renewalTerm - 1),
          function(val, index) {
            return index + 1;
          },
        );
      }

      if ($scope.lease.tenantInfo) {
        if (!$scope.lease.tenantInfo.individuals) {
          $scope.lease.tenantInfo.individuals = [{}];
        }
        if ($scope.lease.tenantInfo.entityType === 'Other')
          $scope.lease.tenantInfo.entityTypeCombined =
            $scope.lease.tenantInfo.entityTypeOther;
        else
          $scope.lease.tenantInfo.entityTypeCombined =
            $scope.lease.tenantInfo.entityType;
      } else {
        $scope.lease.tenantInfo = {
          individuals: [{}],
        };
      }

      if ($scope.lease.landlordInfo) {
        if ($scope.lease.landlordInfo.entityType === 'Other')
          $scope.lease.landlordInfo.entityTypeCombined =
            $scope.lease.landlordInfo.entityTypeOther;
        else
          $scope.lease.landlordInfo.entityTypeCombined =
            $scope.lease.landlordInfo.entityType;
      } else {
        $scope.lease.landlordInfo = {
        };
      }

      if  ($scope.lease.assigneeInfo) {
        if (!$scope.lease.assigneeInfo.individuals) {
          $scope.lease.assigneeInfo.individuals = [{}];
        }
      }

      if ($scope.lease.reductionPeriods) {
        $scope.lease.reductionPeriodsNumber =
          $scope.lease.reductionPeriods.length;
        for (var i = 0; i < $scope.lease.reductionPeriods.length; i++) {
          $scope.lease.reductionPeriods[i].date = moment(
            $scope.lease.reductionPeriods[i].date,
            'YYYY-MM-DDTHH:mm:ssZ',
          ).toDate();
        }
      }
      if ($scope.lease.guarantyCapBurnOffReductionPeriods) {
        $scope.lease.guarantyCapBurnOffReductionPeriodsNumber =
          $scope.lease.guarantyCapBurnOffReductionPeriods.length;
      }
      if ($scope.lease.previousAmendment) {
        $scope.lease.previousAmendmentsNumber =
          $scope.lease.previousAmendment.length;
      }
    };

    $scope.broadcastCustomEvent = function(file) {
      if ($window.frameElement) {
        console.log(
          new Date().getTime() +
            ' completed loading. broadcasting event with file: ',
          file,
        );
        var eventName = $window.frameElement.getAttribute('event-name');
        var parentScope = $window.parent.angular
          .element($window.frameElement)
          .scope();
        parentScope.$broadcast(eventName, {
          file: file,
        });
      }
    };

    function hideEmptyElements() {
      var sectionIds = _.keys($scope.leaseFreeTexts);
      _.each(sectionIds, function(sectionId) {
        LeaseEditorService.setDeletedParagraphVisibility(
          $('[section-id="' + sectionId + '"]'),
        );
      });
    }

    /**
     * ================================ Version Control ================================
     */


    let throttleListItemsSaveTimer = null;
    function throttleListItemsSave (func,time) {
     clearTimeout(throttleListItemsSaveTimer);
     throttleListItemsSaveTimer = setTimeout(func,time);
    }

    function updateListItems () {
      $scope.update();
      listItemsManager.createListItemsMap();
    }

    function getNewVersionControlPid() {
      return `${Math.floor(Math.random() * 100000000000)}`;
    }

    async function insertBlockListItem(insertedBlock, diffgramItem) {
      return buildVersionControlListItem(insertedBlock, diffgramItem, {
        listItemsManager: $scope.listItemsManager,
        getNextFreeTextValue: () => $scope.getNextFreeTextValue(),
        getNewPid: getNewVersionControlPid,
      });
    }

    // Hoisted out of $scope.showDiffgram. The tenant-import path and the
    // second pass both delete blocks, but showDiffgram only runs for a tenant
    // import, so anything defined inside its closure is unreachable from
    // prepareDocumentChangeProposals — deleteGeneratedBlockItem threw
    // ReferenceError there. Verified to close over no showDiffgram locals.
    function deleteGeneratedBlockItem({ index, pid, proposal, target }) {
      return buildGeneratedBlockDeletion(
        { index, pid, proposal, target },
        deleteBlock,
      );
    }

    function deleteBlock(
      diffgramItem,
      diffgramIndex,
      previous,
      resolvedBlock,
    ) {
      let sectionId = diffgramItem.Section;

      if (versionControlManager && !resolvedBlock) {
        const anchor = document.querySelector(`[data-vc-id="${sectionId}"]`);
        if (anchor) {
          const block = anchor.getBlockData();
          const updatedPid = getCalculatedSectionId(
            block.containingNode,
            block.containingNode.getAttribute("pid"),
          );
          diffgramItem._pid = updatedPid;
          sectionId = anchor.getSectionId();
          diffgramItem.Section = sectionId;
        }
      }

      const section = resolvedBlock || getSection(sectionId);

      if (!section) {
        diffgramItem.unsupportedItem = true;
        $scope.diffgram.unsupported.push({
          item: diffgramItem,
          description: `Section ${sectionId} can't be found in the DOM, skipping...`,
        });
        return;
      }

      const del = document.createElement('del');
      const container =
        resolvedBlock || LeaseEditorService.findClosestContainer(section).get(0);

      if (diffgramItem.Nested > -1) {
        const nested = document.querySelector(`[data-nested-paragraph-index="${diffgramItem.Nested}"]`);

        if (nested) {
          nested.parentElement.replaceChild(del, nested);
          del.append(nested);
        }
      } else {
        let previousElementText = '';
        if (container.previousElementSibling) {
          previousElementText = container.previousElementSibling.textContent.trim();
        }

        if (
          previousElementText.indexOf("!!SET") !== -1 &&
          previousElementText.endsWith("!!")
        ) {
          const listSetupElement = container.previousElementSibling;
          container.parentElement.insertBefore(del, listSetupElement);
          del.append(listSetupElement);
          del.append(container);
        } else if (
          diffgramItem.Html === "" &&
          diffgramItem.Block === "complex"
        ) {
          let eoce = null;
          Array.from(section.children).forEach(el => {
            if (
              !el.classList.contains("SOCE") &&
              !el.classList.contains("EOCE")
            ) {
              el.remove();
              del.appendChild(el);
            } else if (el.classList.contains("EOCE")) {
              eoce = el;
            }
          });
          eoce.parentElement.insertBefore(del, eoce);
        } else {
          container.parentElement.insertBefore(del, container);
          del.append(container);
        }
      }

      decorateBlockDeletionMarker(del, { diffgramItem, diffgramIndex });
    }

    function insertGeneratedBlockListItem({ anchor, proposal, index }) {
      return buildGeneratedBlockInsertion(
        { anchor, proposal, index },
        {
          listItemsManager: $scope.listItemsManager,
          getNextFreeTextValue: () => $scope.getNextFreeTextValue(),
          getNewPid: getNewVersionControlPid,
        },
      );
    }

    /**
     * Re-place a generated suggestion at the caret, then accept it.
     *
     * Rebuild → accept → delete, in that order (document_insert_here.js):
     * rebuilding keeps every step on a path the editor already runs, accepting
     * before deleting means a failed rebuild leaves the original intact, and
     * deleting rather than rejecting keeps the audit trail honest — the drafter
     * moved this suggestion, they did not decide against it.
     */
    $scope.insertGeneratedRequestAtCaret = async function(item) {
      const root = document.querySelector('.lease.lease--original');
      const selection = rangy.getSelection();
      const range =
        selection && selection.rangeCount ? selection.getRangeAt(0) : null;

      const resolved = anchorBlockForCaret(range, root);
      if (!resolved.ok) return resolved;

      const content = contentToInsert(item);
      if (!String(content || '').trim()) {
        return {
          ok: false,
          reason: 'no_content',
          remedy: 'move_caret',
          message: INSERT_REFUSALS.no_content.message,
        };
      }

      const index = nextDiffIndex($scope.diffgram.items);
      const built = await insertGeneratedBlockListItem({
        anchor: resolved.anchor,
        proposal: { ...item.proposal, content },
        index,
      });

      if (!built || built.blocked || !built.marker) {
        // Structural, not positional: no caret anywhere in this document would
        // work, so the drafter is offered the text instead.
        return {
          ok: false,
          reason: 'unsupported_list_template',
          remedy: 'copy',
          message: INSERT_REFUSALS.unsupported_list_template.message,
        };
      }

      $scope.diffgram.items[index] = {
        ...built.item,
        _index: index,
        _isBlocked: false,
        _isResolvable: true,
        _showWarnings: false,
        _status: 'unresolved',
        _generatedMarkers: [built.marker],
        proposal: { ...item.proposal, content },
        provenance: item.provenance,
      };
      $compile([built.marker])($scope);
      $scope.annotationsHelper.setDiffgramItemActions(
        $scope.diffgram.items[index],
      );

      // The relocated block needs its own review card before it can be
      // accepted. `_accept` resolves a change through `.annotation [diff-index]`
      // and refuses outright when there is none — "annotationLookupFailed:
      // cannot accept change, no annotation node for diff-index 32" — which
      // left the suggestion sitting at the caret unaccepted while the original
      // was deleted from under it. Cards are otherwise built once, at the end of
      // the second pass; this slot is allocated long after that.
      //
      // Passed a sparse array holding only the new item: createGeneratedAnnotations
      // rebuilds a card for every item it is given, so handing it the whole
      // diffgram would duplicate every card already on screen.
      const newItems = [];
      newItems[index] = $scope.diffgram.items[index];
      const created = createGeneratedAnnotations(
        { items: newItems },
        $scope.annotations,
        {
          setDiffgramItemActions: entry =>
            $scope.annotationsHelper.setDiffgramItemActions(entry),
        },
      );
      $scope.diffgram.actual = [
        ...($scope.diffgram.actual || []),
        ...created,
      ];

      // A digest, so the card is in the DOM by the time `_accept` looks for it.
      // Registering it on the scope is not enough — the sidebar renders it on
      // the next cycle, and acceptance runs before that.
      await $timeout();

      await $scope.annotationsHelper.actions.acceptAndSave(built.marker);

      // Last, and only now that the replacement is accepted.
      removeGeneratedRequest($scope.diffgram, $scope.annotations, item);

      return { ok: true, index };
    };

    $scope.diffgram = {
      on: false,
      processing: false,
      individualRequest: false,
      isPreview: false,
    };
    $scope.annotationsHelper = {
      classes: {
          accepted: 'diff-item--accepted',
          rejected: 'diff-item--rejected',
          resolved: 'diff-item--resolved',
        },
      timers : {
        updateTablesVisibilityTimer: null,
      },
      helpers: {
        getUniqItems: function (src) {
          return  _.uniq(src);
        },
      },
      actions: {
        updateTablesVisibility : function(){
          _.throttle($scope.annotationsHelper.actions.updateTablesVisibilityInternal,500,{ trailing: true })();
        },
        updateTablesVisibilityInternal : function(){
          if(!$scope.annotationsHelper.tables){
            const tblList = [];

            document.querySelectorAll('.lease--original table del').forEach((el)=>{
              var tbl = el.closest('table');
              if(tblList.indexOf(tbl) === -1){
                tblList.push(tbl)
              }
            });
            $scope.annotationsHelper.tables = tblList;
          }
          $scope.annotationsHelper.tables.forEach((tableElement)=>{
            const freeTextInTable = tableElement.querySelectorAll('[free-text]');

            if(freeTextInTable.length !== 0 &&  freeTextInTable.length ===
              tableElement.querySelectorAll('del.diff-item--accepted [free-text]').length){
                tableElement.classList.add('hidden-table');
            }
            else{
              tableElement.classList.remove('hidden-table');
            }

          });
        },
        resolveAnnotation: function(diffItem, status) {
          const dataIndex = parseInt(diffItem);
          setReviewItemStatus(
            $scope.diffgram.items,
            dataIndex,
            status || $scope.diffgram.items[dataIndex]._status,
          );
          $scope.diffgram.resolved = document.querySelectorAll('.lease--modified .diff-item.diff-item--primary.diff-item--resolved');

          $scope.annotationsHelper.setDiffgramItemActions(
            $scope.diffgram.items[dataIndex],
          );
        },
        focusToSection : function (pid){
          if(pid){
            const el = document.querySelector('[section-id="'+pid+'"]');
            if(el)
            {
              const anchor = $(el)
              .find('.SOCE')
              .get(0);
              $rootScope.placeCaretAtStart(anchor);
            }
          }
        },
        acceptAndSave: async function (src) {
          let currentUndoIndex = $scope.histIndex;
          let sectionsToSave = [];
          let deferred = $q.defer();
          // `src` is a DOM marker looked up by diff-index (comments_directive
          // `getDiffItem`), so it is null for a request whose marker is gone.
          let annotation = src && src.items ? src : null;
          let sourceItem = src && src.items ? null : src;

          if (annotation) {
            for (let i = 0; i < annotation.items.length; i++) {
              let currentRequest = annotation.items[i];
              if (!currentRequest._showAcceptReject) {
                continue;
              }
              let item = document.querySelector(`.diff-item[diff-index="${currentRequest._index}"]`);
              sectionsToSave = [...sectionsToSave, ...await $scope.annotationsHelper.actions._accept(item)];
            }
          } else if (sourceItem) {
            sectionsToSave = await $scope.annotationsHelper.actions._accept(sourceItem);
          }

          sectionsToSave = $scope.annotationsHelper.helpers.getUniqItems(sectionsToSave);
          let elementsToSave = [];

          sectionsToSave.forEach((sectionId) => {
            const element = document.querySelector('[section-id="' + sectionId + '"]');
            if (element) {
              elementsToSave.push(element);
            }
          });
          // An accept can resolve to no editable section at all (a block change with
          // no `.editable` descendant). `createBulk` would then POST an empty bulk,
          // which the server rejects and which leaves `allChangesSaved` stuck.
          if (elementsToSave.length) {
            createBulk(elementsToSave, true);
          }
          $rootScope.waitForSave(deferred);
          await deferred.promise;
          $scope.histRegisterBatch($scope.histStack.length - 1, $scope.histIndex - currentUndoIndex);
          $scope.annotationsHelper.actions.focusToSection(sectionsToSave[0]);
        },
        rejectAndSave: async function (src) {
          let currentUndoIndex = $scope.histIndex;
          let sectionsToSave = [];
          let deferred = $q.defer();

          // Same null-`src` tolerance as `acceptAndSave` above.
          let annotation = src && src.items ? src : null;
          let sourceItem = src && src.items ? null : src;

          if (annotation) {
            for (let i = 0; i < annotation.items.length; i++) {
              let currentRequest = annotation.items[i];
              if (!currentRequest._showAcceptReject) {
                continue;
              }
              let item = document.querySelector(`.diff-item[diff-index="${currentRequest._index}"]`);
              sectionsToSave = [...sectionsToSave, ...await $scope.annotationsHelper.actions._reject(item)];
            }
          } else if (sourceItem) {
            sectionsToSave = await $scope.annotationsHelper.actions._reject(sourceItem);
          }

          sectionsToSave = $scope.annotationsHelper.helpers.getUniqItems(sectionsToSave);
          let elementsToSave = [];

          sectionsToSave.forEach((sectionId) => {
            const element = document.querySelector('[section-id="' + sectionId + '"]');
            if (element) {
              elementsToSave.push(element);
            }
          });
          // Same empty-bulk guard as `acceptAndSave` above: `_reject` can also
          // resolve to no editable section.
          if (elementsToSave.length) {
            createBulk(elementsToSave, true);
          }
          $rootScope.waitForSave(deferred);
          await deferred.promise;
          $scope.histRegisterBatch($scope.histStack.length - 1, $scope.histIndex - currentUndoIndex);
          $scope.annotationsHelper.actions.focusToSection(sectionsToSave[0]);
        },
        // Shared tail of the `_accept`/`_reject` annotation guards below. Note
        // that `DIFF_CLASSES` and `ERROR_MESSAGES` are declared inside
        // `showDiffgram` and are not in scope out here, so the class name and
        // the reason are spelled out literally.
        _markUnresolvable: function(diffgramItem, marker) {
          if (marker) {
            marker.classList.add('diff-item--blocked');
          }
          if (!diffgramItem) {
            return;
          }
          diffgramItem._isResolvable = false;
          diffgramItem._isBlocked = true;
          diffgramItem._blockedReason =
            'This change can no longer be accepted or rejected automatically. Make the change in the lease body itself and mark as resolved when finished.';
          $scope.annotationsHelper.setDiffgramItemActions(diffgramItem);
        },
        _accept: async function (item) {
          let sectionsIdsToSave = [];
          const itemId = item ? item.getAttribute('diff-index') : null;
          let diffItemObject = {
            ...$scope.diffgram.items[itemId]
          };
          const actionId = 'version-control' + itemId;
          const annotationItemElement =
            itemId === null
              ? null
              : document.querySelector(
                  '.annotation [diff-index="' + itemId + '"]',
                );
          const annotationElement =
            annotationItemElement &&
            annotationItemElement.closest('.annotation');
          if (!annotationElement) {
            // Without the annotation node there is nothing to resolve against,
            // and every step below dereferences it. Leave the lease untouched
            // and surface the change as one the drafter must handle manually.
            console.error(
              'annotationLookupFailed: cannot accept change, no annotation node for diff-index',
              itemId,
            );
            $scope.annotationsHelper.actions._markUnresolvable(
              $scope.diffgram.items[itemId],
              item,
            );
            return sectionsIdsToSave;
          }
          const annotationId = annotationElement.getAttribute('data-pid');
          const singleRequestElements = Array.from(document.querySelectorAll('[diff-index="' + itemId + '"]'));
          const allRequestsIds = $scope.annotationsHelper.actions.getAllRequestsIds(annotationElement);
          const allRequestsElements = $scope.annotationsHelper.actions.getAllRequestsElements(allRequestsIds);
          const annotationObject = {
            ...$scope.annotations[annotationId]
          };
          let classListArray = [];
          let containsListItems = false;
          let scopeListItems = [];
          $scope.listItems.forEach((item) => {
            scopeListItems.push({
              ...item
            });
          });
          let leaseListItems = $scope.lease.orderedListitem;

          allRequestsElements.forEach((element) => {
            classListArray.push([...element.classList]);
          });
          singleRequestElements.forEach((element) => {
            let listHeader = element.getAttribute('list-header');
            if (listHeader && listHeader.trim() !== '') {
              containsListItems = true;
            }
            if (element.querySelector('[list]')) {
              containsListItems = true;
            }
            if (element.matches('ins[data-table-row-change="insertion"]')) {
              containsListItems = true;
            }
          });

          let redo = function (itemId, sectionsIdsToSave, containsListItems, scopeListItems, leaseListItems, $scope, annotationId) {
            return function () {
              const singleRequestElements = Array.from(document.querySelectorAll('[diff-index="' + itemId + '"]'));
              const allRequestsElements = $scope.annotationsHelper.actions.getAllRequestsElements(allRequestsIds);
              const deferred = $q.defer();

              $scope.annotationsHelper.jumpToAnnotation(annotationId, true);
              $scope.annotationsHelper.exitIndividualRequestMode();
              $scope.annotationsHelper.actions.resolveAnnotation(
                itemId,
                'accepted',
              );

              allRequestsElements.forEach((element, index) => {
                if (classListArray[index].indexOf('diff-item--current') !== -1) {
                  if (
                    (
                      classListArray[index].indexOf('diff-item--partial') !== -1 &&
                      classListArray[index].indexOf('diff-item--partial--first') !== -1
                    ) ||
                    classListArray[index].indexOf('diff-item--partial') === -1
                  ) {
                    element.classList.remove('diff-item--current');
                    $scope.annotationsHelper.enterIndividualRequestMode(element.getAttribute('diff-index'));
                  }
                }
              });
              singleRequestElements.forEach((element) => {
                let listHeader = element.getAttribute('list-header');
                // handle accept list item
                if (listHeader && listHeader.trim() !== '' && element.closest('.lease--original')) {
                  if (isDeletion(element)) {
                    let orderListItem = listItemsManager.getInsertedListItemByElement(element);
                    if (orderListItem) {
                      orderListItem.deleted = true;
                      $scope.lease.orderedListitem = $scope.serializeOrderedListitem();
                    }
                  } else if (isInsertion(element)) {
                    $scope.annotationsHelper.actions.acceptListItem(element);
                  }
                }
                if (element.matches('ins[data-table-row-change="insertion"]')) {
                  $scope.orderedListitemSupplementalEntries = persistAcceptedTableRow(
                    $scope.orderedListitemSupplementalEntries,
                    element,
                    $scope.diffgram.items[itemId],
                  );
                  $scope.lease.orderedListitem = $scope.serializeOrderedListitem();
                }
                sectionsIdsToSave.push(...reviewMarkerSectionIds([element]));
              });
              if (containsListItems) {
                throttleListItemsSave(updateListItems, 250);
              }
              deferred.resolve();
              $scope.annotationsHelper.actions.updateTablesVisibility();
              return deferred.promise;
            }
          };

          let undo = function (itemId, classListArray, containsListItems, scopeListItems, leaseListItems, diffItemObject, annotationId, annotationObject, $scope) {
            return function () {
              const allRequestsElements = $scope.annotationsHelper.actions.getAllRequestsElements(allRequestsIds);
              const deferred = $q.defer();

              for (let index = 0; index < allRequestsElements.length; index++) {
                const element = allRequestsElements[index];
                element.classList = classListArray[index].join(" ");
              }

              $scope.annotationsHelper.jumpToAnnotation(annotationId, true);
              $scope.annotationsHelper.enterIndividualRequestMode(itemId);
              $scope.annotations[annotationId] = annotationObject;
              $scope.diffgram.items[diffItemObject._index] = {...diffItemObject};
              $scope.annotationsHelper.actions.resolveAnnotation(itemId);

              for (var index = 0; index < $scope.annotations[annotationId].items.length; index++) {
                $scope.annotations[annotationId].items[index] = $scope.diffgram.items[$scope.annotations[annotationId].items[index]._index];
              }

              if (containsListItems) {
                $scope.listItems = scopeListItems;
                $scope.lease.orderedListitem = leaseListItems;
                $scope.orderedListitemSupplementalEntries = parseOrderedListitem(
                  leaseListItems,
                ).supplementalEntries;
                throttleListItemsSave(updateListItems, 250);
              }

              deferred.resolve();
              $scope.annotationsHelper.actions.updateTablesVisibility();
              return deferred.promise;
            }
          };

          await scopeVar.addToHistory({
            id: actionId,
            desc: 'version control accept : ' + itemId,
            redo: redo(itemId, sectionsIdsToSave, containsListItems, scopeListItems, leaseListItems, $scope, annotationId),
            undo: undo(itemId, classListArray, containsListItems, scopeListItems, leaseListItems, diffItemObject, annotationId, annotationObject, $scope),
          });
          return sectionsIdsToSave;

        },
        acceptListItem: async function (ins) {
          const contentEditable = $(LeaseEditorService.findAllContentEditable($(ins))[0]);
          const block = contentEditable[0].getBlockData();
          const afterFreeText = block.getPreviousVisibleBlock().containingNode.getAttribute('PID');          
          const diffItem = $scope.diffgram.items[ins.getAttribute('diff-index')];

          let restyleObject = null;
          if(diffItem.ListHeader &&  diffItem.ListHeader == "paragraph" ){
            // get restyle object 
            restyleObject =  window.getRestyleObjectFromParagraph(ins.querySelector('.list-item-paragraph'),diffItem._pid);
            lease.restyle[diffItem._pid] =restyleObject; 
          }
          
          let itemObject = {
            level: diffItem._listLevel.level,
            headerFreeText: diffItem._listHeaderTextId,
            bodyFreeText: diffItem._listBodyTextId,
            afterFreeText: afterFreeText,
            isNewListItem: true,
            singleFT : block.containingNode.querySelectorAll('[free-text]').length ==1 ? true : false,
            index : block.getPreviousVisibleBlock().containingNode.getNodeOccurrenceIndexByAttribute('pid'),
          };

          if (diffItem._listLevel.outline) {
            itemObject.outline = diffItem._listLevel.outline;
          }

          let item = $scope.listItems.filter((obj) => {
            return obj.headerFreeText === itemObject.headerFreeText;
          });

          if (item.length === 0) {

            // update list items order : afterFreeText
            let freeTexts = diffItem._node.querySelectorAll('[free-text]');
            let lastFreeTextElement = freeTexts[freeTexts.length - 1];
            let lastFreeTextId = lastFreeTextElement.getAttribute('free-text');
            let updatedAfterFreeText = false;
            let index = 0;
            let addAtIndex = null;
            $scope.listItems.forEach((item) => {
              if (item.afterFreeText === afterFreeText && item !== itemObject) {
                item.afterFreeText = lastFreeTextId;
                updatedAfterFreeText = true;
                addAtIndex = addAtIndex ? addAtIndex : index;
              }
              index++;
            });
            if (updatedAfterFreeText) {
              $scope.listItems.splice(addAtIndex, 0, itemObject);
            } else {
              $scope.listItems.push(itemObject);
            }
          } else {
            delete item[0].deleted;
          }

          $scope.lease.orderedListitem = $scope.serializeOrderedListitem();

          let prevElement = ins.previousElementSibling;
          if(prevElement && prevElement.tagName == "IGNORE"){
            prevElement = prevElement.previousElementSibling;
          }

          let prevListItem = listItemsManager.getClosestListItem(prevElement);
          
          let prevListItemInfo = prevListItem ?  prevListItem.getListItemInfoFromNode() : null;
          if (prevListItemInfo) {
            let nextListItem = listItemsManager.listItemObjectsArray[prevListItemInfo.currentIndex + 1];
            let listNode = ins.querySelector('[list]');
            while (nextListItem && (nextListItem.node === listNode || nextListItem.static)) {
              nextListItem = listItemsManager.listItemObjectsArray[nextListItem.currentIndex + 1];
            }
            if (nextListItem && nextListItem.listLevel === diffItem._listLevel) {
              listNode.setAttribute('list', nextListItem.list);
            }
          }

        },
        _reject: async function (item) {
          let sectionsIdsToSave = [];
          const itemId = item ? item.getAttribute('diff-index') : null;
          const diffItemObject = {
            ...$scope.diffgram.items[itemId]
          };
          const annotationItemElement =
            itemId === null
              ? null
              : document.querySelector(
                  '.annotation [diff-index="' + itemId + '"]',
                );
          const annotationElement =
            annotationItemElement &&
            annotationItemElement.closest('.annotation');
          if (!annotationElement) {
            // Same guard as `_accept` above: no annotation node means nothing
            // to resolve, so reject nothing and mark the change manual.
            console.error(
              'annotationLookupFailed: cannot reject change, no annotation node for diff-index',
              itemId,
            );
            $scope.annotationsHelper.actions._markUnresolvable(
              $scope.diffgram.items[itemId],
              item,
            );
            return sectionsIdsToSave;
          }
          const singleRequestElements = Array.from(document.querySelectorAll('[diff-index="' + itemId + '"]'));
          const allRequestsIds = $scope.annotationsHelper.actions.getAllRequestsIds(annotationElement);
          const allRequestsElements = $scope.annotationsHelper.actions.getAllRequestsElements(allRequestsIds);
          const actionId = 'version-control' + itemId;
          const annotationId = annotationElement.getAttribute('data-pid');
          const annotationObject = {
            ...$scope.annotations[annotationId]
          };
          let classListArray = [];
          let containsListItems = false;
          let scopeListItems = [];
          $scope.listItems.forEach((item) => {
            scopeListItems.push({
              ...item
            });
          });
          let leaseListItems = $scope.lease.orderedListitem;
          allRequestsElements.forEach((element) => {
            classListArray.push([...element.classList]);
          });
          singleRequestElements.forEach((element) => {
            let listHeader = element.getAttribute('list-header');
            if (listHeader && listHeader.trim() !== '') {
              containsListItems = true;
            }
            if (element.querySelector('[list]')) {
              containsListItems = true;
            }
          });

          let redo = function (itemId, sectionsIdsToSave, containsListItems, scopeListItems, leaseListItems, $scope, annotationId) {
            return function () {
              const singleRequestElements = Array.from(document.querySelectorAll('[diff-index="' + itemId + '"]'));
              const allRequestsElements = $scope.annotationsHelper.actions.getAllRequestsElements(allRequestsIds);
              const deferred = $q.defer();

              $scope.annotationsHelper.jumpToAnnotation(annotationId, true);
              $scope.annotationsHelper.exitIndividualRequestMode();
              $scope.annotationsHelper.actions.resolveAnnotation(
                itemId,
                'rejected',
              );

              allRequestsElements.forEach((element, index) => {
                if (classListArray[index].indexOf('diff-item--current') !== -1) {
                  if (
                    (
                      classListArray[index].indexOf('diff-item--partial') !== -1 &&
                      classListArray[index].indexOf('diff-item--partial--first') !== -1
                    ) ||
                    classListArray[index].indexOf('diff-item--partial') === -1
                  ) {
                    element.classList.remove('diff-item--current');
                    $scope.annotationsHelper.enterIndividualRequestMode(element.getAttribute('diff-index'));
                  }
                }
              });

              singleRequestElements.forEach((element) => {
                let listHeader = element.getAttribute('list-header');
                // handle accept list item
                if (element.tagName === 'INS') {
                  let orderListItem = listItemsManager.getInsertedListItemByElement(element);
                  if (orderListItem) {
                    orderListItem.deleted = true;
                    $scope.lease.orderedListitem = $scope.serializeOrderedListitem();
                    $scope.update();
                  }
                }
                sectionsIdsToSave.push(...reviewMarkerSectionIds([element]));
              });
              if (containsListItems) {
                throttleListItemsSave(updateListItems, 250);
              }
              deferred.resolve();
              $scope.annotationsHelper.actions.updateTablesVisibility();
              return deferred.promise;
            }
          };

          let undo = function (itemId, classListArray, containsListItems, scopeListItems, leaseListItems, diffItemObject, annotationId, annotationObject, $scope) {
            return function () {
              const allRequestsElements = $scope.annotationsHelper.actions.getAllRequestsElements(allRequestsIds);
              const deferred = $q.defer();

              for (let index = 0; index < allRequestsElements.length; index++) {
                const element = allRequestsElements[index];
                element.classList = classListArray[index].join(" ");
              }

              $scope.annotationsHelper.jumpToAnnotation(annotationId, true);
              $scope.annotationsHelper.enterIndividualRequestMode(itemId);
              $scope.annotations[annotationId] = annotationObject;
              $scope.diffgram.items[diffItemObject._index] = {...diffItemObject};
              $scope.annotationsHelper.actions.resolveAnnotation(itemId);

              for (var index = 0; index < $scope.annotations[annotationId].items.length; index++) {
                $scope.annotations[annotationId].items[index] = $scope.diffgram.items[$scope.annotations[annotationId].items[index]._index];
              }

              if (containsListItems) {
                $scope.listItems = scopeListItems;
                $scope.lease.orderedListitem = leaseListItems;
                throttleListItemsSave(updateListItems, 250);
              }

              deferred.resolve();
              $scope.annotationsHelper.actions.updateTablesVisibility();
              return deferred.promise;
            }
          };

          await scopeVar.addToHistory({
            id: actionId,
            desc: 'version control accept : ' + itemId,
            redo: redo(itemId, sectionsIdsToSave, containsListItems, scopeListItems, leaseListItems, $scope, annotationId),
            undo: undo(itemId, classListArray, containsListItems, scopeListItems, leaseListItems, diffItemObject, annotationId, annotationObject, $scope),
          });
          return sectionsIdsToSave;
        },
        getAllRequestsIds : function(annotation){
          const annotationId = annotation.getAttribute('data-pid');
          const annotationObject = $scope.annotations[annotationId];
          const idsArray = [];

          for(var i=0; i< annotationObject.items.length; i++){
            idsArray.push(annotationObject.items[i]._index);
          }

          return idsArray;
        },
        getAllRequestsElements: function(items) {
          return collectRequestElements(items, $scope.diffgram.items);
        }
      },
      // Step to the card before or after the current one.
      //
      // Walks the rendered cards in the order they appear rather than doing
      // arithmetic on `data-index`. Those indexes are contiguous 0..n-1 only
      // until a card is retired, and INSERT HERE retires one every time it
      // relocates a suggestion — after which "the last index" is no longer
      // `Object.keys(annotations).length - 1`, the number in between belongs to
      // nobody, and stepping onto either one found no element and read
      // `getAttribute` of null. The order on screen is what a drafter is
      // stepping through, so it is what this follows.
      stepAnnotation: function(offset, TrackedEvent) {
        const cards = Array.from(
          document.querySelectorAll('.annotation--request'),
        );
        if (cards.length === 0) return;

        const current = String($scope.annotationsHelper.lastOpenAnnotation);
        const from = cards.findIndex(
          card => card.getAttribute('data-index') === current,
        );
        if (from !== -1) {
          $scope.annotationsHelper.closeAnnotation();
        }

        // Nothing open yet starts at the first card whichever way the drafter
        // stepped, which is what this did before.
        const target =
          from === -1
            ? cards[0]
            : cards[(from + offset + cards.length) % cards.length];

        const paragraphId = target.getAttribute('data-pid');
        const annotation = $scope.annotations[paragraphId];

        $scope.annotationsHelper.lastOpenAnnotation = parseInt(
          target.getAttribute('data-index'),
          10,
        );
        $scope.annotationsHelper.highlightAnnotation(annotation);
        target.classList.add('annotation--current');
        $scope.diffgram.currentAnnotation = annotation;
        // A card whose model has gone is still worth moving to — the drafter
        // can see it — but there is nothing to scroll the document to.
        if (annotation && annotation.container) {
          $scope.scrollToElement($(annotation.container));
        }

        var sel = rangy.getSelection();
        sel.removeAllRanges();

        window.track.event(
          new TrackedEvent({ context: $rootScope.getContext() }),
        );
      },
      prevAnnotation: function() {
        $scope.annotationsHelper.stepAnnotation(
          -1,
          MoveToThePreviousTenantRequestEvent,
        );
      },
      nextAnnotation: function() {
        $scope.annotationsHelper.stepAnnotation(
          1,
          MoveToTheNextTenantRequestEvent,
        );
      },
      jumpToAnnotation: function(pid,scrollTo = false) {
        const newAnnotation = document.querySelector(
          `.annotation[data-pid="${pid}"]`,
        );
        const nextAnnotationIndex = parseInt(
          newAnnotation.getAttribute('data-index'),
        )
        $scope.annotationsHelper.lastOpenAnnotation = nextAnnotationIndex;

        if(!newAnnotation.classList.contains('annotation--current')){
          $scope.annotationsHelper.closeAnnotation();
          if (newAnnotation) {
            newAnnotation.classList.add('annotation--current');

            if(scrollTo){
              // A generated request gets one card per request rather than one
              // per paragraph, so its annotation key is synthetic and matches
              // no `[pid]` in the document. The undo/redo entries registered by
              // `_accept`/`_reject` call through here, so resolve the scroll
              // target from the annotation itself before dereferencing.
              const annotation = $scope.annotations[pid];
              const scrollTarget =
                document.querySelector('[pid="'+pid+'"]') ||
                (annotation && annotation.container);
              if (scrollTarget) {
                scrollTarget.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              }
            }
          }
        }
        $scope.diffgram.currentAnnotation = $scope.annotations[pid];
        $scope.annotationsHelper.highlightAnnotation($scope.annotations[pid]);

        window.track.event(new OpenTenantRequestEvent({
          context: $rootScope.getContext(),
        }));
      },
      highlightAnnotation: annotation => {
        let arr = [];

        annotation.items.forEach(item => {
          if (item._node) {
            arr = [...arr, item._node];
          } else {
            let paragraph = getParagraph(item._pid);
            let container = paragraph.getTopAnchor();
            if(container) {
              arr.push(container);
            } else {
              arr.push(paragraph);
            }
          }
        });

        arr.forEach(node => {
          node.classList.add('highlight');
        });
        $scope.annotationsHelper.actions.updateTablesVisibility();
      },
      removeAnnotationHighlight: () => {
        document.querySelectorAll('.highlight').forEach(node => {
          node.classList.remove('highlight');
        });
      },
      closeAnnotation: function() {
        const currentAnnotation = document.querySelector(
          '.annotation.annotation--current',
        );
        if (currentAnnotation) {
          currentAnnotation.classList.remove('annotation--current');
          $scope.diffgram.currentAnnotation = null;
        }
        $scope.annotationsHelper.removeAnnotationHighlight();
        $scope.annotationsHelper.exitPreviewMode();
        if ($scope.diffgram.individualRequest) {
          $scope.annotationsHelper.exitIndividualRequestMode();
        }

        window.track.event(new CloseTenantRequestEvent({
          context: $rootScope.getContext(),
        }));
      },
      setDiffgramItemActions: diffgramItem => {
        diffgramItem._showAcceptReject =
          !diffgramItem._isBlocked &&
          diffgramItem._isResolvable &&
          !diffgramItem._listLevelViolation;
        diffgramItem._showAdjustManually = diffgramItem._isAffectingLeaseVar && !diffgramItem._isAffectingDocumentStructure;
      },
      resolveAnnotation: function(diffItem, status) {
        const dataIndex = parseInt(diffItem.getAttribute('diff-index'));
        setReviewItemStatus($scope.diffgram.items, dataIndex, status);
        $scope.diffgram.resolved = document.querySelectorAll('.lease--modified .diff-item.diff-item--primary.diff-item--resolved');

        $scope.annotationsHelper.setDiffgramItemActions(
          $scope.diffgram.items[dataIndex],
        );
      },
      prevRequest: function() {},
      nextRequest: function() {},
      jumpToRequest: function(requestId) {
        if ($scope.diffgram.isPreview) {
          return;
        }

        if ($scope.diffgram.individualRequest) {
          const currentRequest = document.querySelector(
            '.lease.lease--modified .diff-item.diff-item--current',
          );
          const currentRequestId = parseInt(
            currentRequest.getAttribute('diff-index'),
          );

          if (requestId !== currentRequestId) {
            $scope.annotationsHelper.enterIndividualRequestMode(requestId);
          } else {
            initDiffgramWarnings();
            $scope.annotationsHelper.exitIndividualRequestMode();
            $scope.annotationsHelper.exitPreviewMode();
          }
        } else {
          $scope.annotationsHelper.enterIndividualRequestMode(requestId);
        }
      },
      resolveRequest: function(requestId, status) {},
      preview: function() {
        if (!$scope.diffgram.isPreview) {
          $scope.annotationsHelper.enterPreviewMode();
        } else {
          $scope.annotationsHelper.exitPreviewMode();
        }
      },
      enterPreviewMode: function() {
        $scope.diffgram.isPreview = true;
      },
      exitPreviewMode: function() {
        $scope.diffgram.isPreview = false;
      },
      enterIndividualRequestMode: function(requestId) {
        // A generated redline is $compiled where it sits, in `.lease--original`
        // (document_redline_integration.js), so its `ng-click` is live from the
        // moment it is emitted. Everything below addresses the annotation card
        // copy in `.lease--modified` and the `_annotation` back-reference that
        // createAnnotations attaches — neither exists for a generated request
        // until the annotation pass runs. Without this guard a click on
        // generated text dereferences `requestNode[0]` on an empty NodeList.
        // A tenant-import request always has both, so this never fires there.
        const reviewable =
          $scope.diffgram.items && $scope.diffgram.items[requestId];
        const hasAnnotationCard = document.querySelector(
          `.lease.lease--modified .diff-item[diff-index="${requestId}"]`,
        );
        if (!reviewable || !hasAnnotationCard) {
          return;
        }

        const currentAnnotation = document.querySelector(
          '.lease.lease--modified .annotation--current',
        );
        $scope.safeApply(() => {
          $scope.diffgram.individualRequest = true;
          const currentNode = document.querySelectorAll(
            '.lease.lease--modified .diff-item.diff-item--current',
          );
          const requestNode = document.querySelectorAll(
            `.lease.lease--modified .diff-item[diff-index="${requestId}"]`,
          );
          requestNode[0].scrollIntoViewIfNeeded({
            behavior: 'smooth',
            block: 'center'
          });


          let requestItem = $scope.diffgram.items[requestId];

          if(requestItem._isGhostItem){
            let backCount = 1;
            while($scope.diffgram.items[requestId-backCount] && $scope.diffgram.items[requestId-backCount]._isGhostItem){
              backCount++;
            }
            requestItem = $scope.diffgram.items[requestId-backCount];
          }

          const annotation = $scope.annotations[requestItem._annotation.pid];
          let text = '';
          let active;

          $scope.diffgram.individualRequest = false;
          currentNode.forEach(node => {
            node.classList.remove('diff-item--current');
          });

          // Enter to individual request node only if the node is different than the one
          // we just exited
          if (currentNode[0] !== requestNode[0]) {
            $scope.diffgram.individualRequest = true;
            active = requestItem;

            requestNode.forEach(node => {
              node.classList.add('diff-item--current');
              text += node.innerText;
            });

            active.request = `${text}`;
          }

          if(active && active._index){
            annotation.activeRequest =  $scope.diffgram.items[ active._index];
          }
          else{
            annotation.activeRequest =  active;
          }
        });
        $scope.safeApply();

        window.track.event(new EnterAnIndividualTenantRequestEvent({
          context: $rootScope.getContext(),
        }));
      },
      exitIndividualRequestMode: function() {
        $scope.safeApply(() => {
          $scope.diffgram.individualRequest = false;
          $scope.annotationsHelper.exitPreviewMode();

          const currentRequest = document.querySelector(
            '.lease.lease--modified .diff-item.diff-item--current',
          );
          if (currentRequest) {
            currentRequest.classList.remove('diff-item--current');
          }

          window.track.event(new ExitAnIndividualTenantRequestEvent({
            context: $rootScope.getContext(),
          }));
        });
      },
      save: function(list) {
        list.forEach(contentEditable => {
          LeaseEditorService.enableEditingForSegment(contentEditable);
          contentEditable.dispatchEvent(new Event('change'));
        });
      },
      exitReviewMode: function() {
        const unresolvedChangeCount = countUnresolvedChanges(
          $scope.diffgram.items,
        );
        if (
          !confirmExitWithUnresolvedChanges(unresolvedChangeCount, message =>
            $window.confirm(message),
          )
        ) {
          return;
        }

        window.track.event(new FinishReviewOfTenantRequestsEvent({
          context: $rootScope.getContext(),
        }));

        location.reload();
        // Commented out temporarily until we can find a better solution
        //
        // $scope.safeApply(() => {
        //   $scope.inline.on = false;
        //   $scope.inline.toggle();
        //   $scope.annotations = {};
        //   $scope.removeVersionControlTagsFromDefaultsAndFreeText();
        //   $scope.diffgram = {
        //     on: false,
        //     processing: false,
        //     individualRequest: false,
        //     isPreview: false,
        //   };
        //   CleanFreeTextService.cleanHtmlForAcceptReject(
        //     document.querySelector('.lease.lease--original'),true
        //   );
        // });
      },
    };

    $rootScope.waitForSave = function(deferred, counter = 0) {
      if ($scope.allChangesSaved.length === 0) {
        $scope.endSaving();
        deferred.resolve();
      } else if (counter < 20) {
        counter++;
        setTimeout(() => {
          $rootScope.waitForSave(deferred, counter);
        }, 300);
      } else {
        deferred.resolve();
      }
    };

    $rootScope.getInitialAcceptRejectHtml = function(html){
      let el = document.createElement('el');
      el.innerHTML = html;
      el.querySelectorAll('ins,del').forEach((node)=>{
        node.classList.remove('diff-item--accepted','diff-item--resolved','diff-item--rejected');
      });
      return el.innerHTML;
    }

    $rootScope.updateFreeTextDefaultForVersionControl = function (html,element){
      let sectionId = element.getAttribute('section-id');
      let cleanHtml = $rootScope.getInitialAcceptRejectHtml(html);
      if(!$scope.defaultProvisions[sectionId].orgHtml){
        $scope.defaultProvisions[sectionId].orgHtml = $scope.defaultProvisions[sectionId].html;
        $scope.defaultProvisions[sectionId].html = cleanHtml;
      }
    }

    function getCalculatedSectionId(element, sectionId) {
      // Use cache if available (only if sectionIdCache exists in scope)
      let cachedResult = null;
      try {
        // Try to access sectionIdCache - will throw ReferenceError if not in scope
        const elementId = element ? (element.getAttribute('pid') || element.id || 'noelement') : 'noelement';
        const cacheKey = `${sectionId}_${elementId}`;
        if (sectionIdCache.has(cacheKey)) {
          cachedResult = sectionIdCache.get(cacheKey);
        }
      } catch (e) {
        // Cache not available in this scope, continue without caching
      }
      
      if (cachedResult !== null) {
        return cachedResult;
      }
      
      let result = sectionId;

      if (sectionId && sectionId.indexOf('+') !== -1 && element) {
        try {
          const scope = angular.element(element).scope();
          if (scope) {
            result = scope.$eval(sectionId);
          }
        } catch (ex) {
          console.error('[version control] Error calculating section ID', ex);
        }
      }

      // Cache the result if cache exists
      try {
        // Try to access sectionIdCache - will throw ReferenceError if not in scope
        if (result) {
          const elementId = element ? (element.getAttribute('pid') || element.id || 'noelement') : 'noelement';
          const cacheKey = `${sectionId}_${elementId}`;
          sectionIdCache.set(cacheKey, result);
        }
      } catch (e) {
        // Cache not available in this scope, skip caching
      }

      return result;
    }

    window.getCalculatedSectionId = getCalculatedSectionId;

    function getSection(sectionId) {
      return document.querySelector(`[data-vc-id="${sectionId}"], [section-id="${sectionId}"]`);
    }

    function getParagraph(sectionId, isInnerMost = false) {
      const section = getSection(sectionId);
      let results = Array.from($(section).closest('[pid]'));
      let container;

      if (results.length === 0) {
        results = document.querySelectorAll(`[pid="${sectionId}"]`);
      }

      if (results.length === 0) {
        results = document.querySelectorAll(`[pid*="${sectionId}"]`);

        results.forEach(p => {
          let pid = getCalculatedSectionId(p, p.getAttribute('pid'));

          if (pid === sectionId) {
            results = [p];
          }
        });
      }

      if (!isInnerMost) {
        container = results[0];
      } else {
        container = results[results.length - 1];
      }

      return container;
    }

    function getParagraphId(sectionId, isInnerMost = false) {
      // Use cache if available (only if paragraphIdCache exists in scope)
      let cachedPid = null;
      try {
        // Try to access paragraphIdCache - will throw ReferenceError if not in scope
        const cacheKey = `${sectionId}_${isInnerMost}`;
        if (paragraphIdCache.has(cacheKey)) {
          cachedPid = paragraphIdCache.get(cacheKey);
        }
      } catch (e) {
        // Cache not available in this scope, continue without caching
      }
      
      if (cachedPid !== null) {
        return cachedPid;
      }
      
      let container = getParagraph(sectionId, isInnerMost);
      let pid;

      if (container) {
        container = container.getTopAnchor();
        pid = container.getAttribute('pid');
        pid = getCalculatedSectionId(container, pid);
        // Cache the result if cache exists
        try {
          // Try to access paragraphIdCache - will throw ReferenceError if not in scope
          if (pid) {
            const cacheKey = `${sectionId}_${isInnerMost}`;
            paragraphIdCache.set(cacheKey, pid);
          }
        } catch (e) {
          // Cache not available in this scope, skip caching
        }
      }

      return pid;
    }

    function getTable(tableId) {
      return document.querySelector(`[table-id="${tableId}"]`);
    }

    function getTableParagraphId(tableId) {
      const table = getTable(tableId);
      const container = $(table)
        .closest('[pid]')
        .get(0);
      let pid;

      if (container) {
        pid = container.getAttribute('pid');
      } else if (table) {
        table.parentElement.setAttribute('pid',tableId);
        pid = tableId;
      }

      return pid;
    }


    function isInsertion(node) {
      return (
        node.nodeType === Node.ELEMENT_NODE &&
        node.tagName.toLowerCase() === 'ins'
      );
    }

    function isDeletion(node) {
      return (
        node.nodeType === Node.ELEMENT_NODE &&
        node.tagName.toLowerCase() === 'del'
      );
    }

    function cleanDiffgram(diffgram) {
      let current;
      let toBeRemoved = [];
      let deletedBlocks = [];

      for (let index = 0; index < diffgram.length; index++) {
        current = diffgram[index];

        if (current.Type === "del" && current.Block && current.Nested === -1) {
          deletedBlocks.push(current.Section);
        }

        if (
          current.Type === "del" &&
          current.Block &&
          current.Nested > -1 &&
          deletedBlocks.indexOf(current.Section) !== -1
        ) {
          toBeRemoved.push(index);
        }
      }

      for (let index = 0; index < toBeRemoved.length; index++) {
        diffgram.splice(toBeRemoved[index], 1);
      }

      return diffgram;
    }

    $scope.showDiffgram = async function(diffgram) {
      if ($scope.diffgram.processing) {
        return;
      }

      if (diffgram === "") {
        alert('Oops... something went wrong with the import process, please wait a couple of seconds and try again.');
        finishProcessing();
        return;
      }

      
      versionControlManager.setIds();
      

      $scope.annotations = {};
      $scope.diffgram.raw = JSON.parse(JSON.stringify(diffgram));
      $scope.diffgram.items = cleanDiffgram(diffgram);
      $scope.diffgram.actual = [];
      $scope.diffgram.resolved = [];
      $scope.diffgram.unsupported = [];

      // Constants
      const DEL_MARKER = '[[DEL]]';
      const DEL_END_MARKER = '[[/DEL]]';
      const INS_MARKER = '[[INS]]';
      
      const DIFF_TYPES = {
        INSERTION: 'ins',
        DELETION: 'del',
        CHANGE: 'change',
        COMPLEX_TABLE: 'complex table',
        COMPLEX_BLOCK: 'complex block',
        NEW_TABLE: 'new table'
      };
      
      const DIFF_CLASSES = {
        ITEM: 'diff-item',
        INSERTION: 'diff-item--insertion',
        DELETION: 'diff-item--deletion',
        BLOCKED: 'diff-item--blocked',
        PRIMARY: 'diff-item--primary',
        STATIC: 'diff-item--static',
        PARTIAL: 'diff-item--partial',
        CHANGE: 'diff-item--change',
        COMPLEX_TABLE: 'diff-item--complex-table',
        COMPLEX_BLOCK: 'diff-item--complex-block',
        RELOCATED: 'diff-item--relocated',
        ACCEPTED: 'diff-item--accepted',
        PROCESSING: 'diff-item--processing',
        NESTED: 'diff-item--nested'
      };
      
      const ERROR_MESSAGES = {
        TABLE_CHANGE_NOT_SUPPORTED: 'Accepting changes to tables is not currently supported. To make the change use our editor after exiting "import changes" mode.',
        TABLE_ADJUSTMENT_NOT_SUPPORTED: 'Accepting changes to tables is not currently supported. Make the needed adjustments on the lease body itself and mark as resolved when finished.',
        CHANGE_NOT_SUPPORTED: 'Accepting this change is not supported. Make any needed changes in the lease body itself and mark as resolved when finished.',
        MANUAL_CHANGE_REQUIRED: "You can't accept this change, please make the changes manually",
        AUTOMATION_CHANGE_NOT_SUPPORTED: "You can't accept changes that affects the automation, please make the changes manually",
        CROSS_REFERENCE_CHANGE_NOT_SUPPORTED: "You can't accept changes that affects cross-references, please make the changes manually",
        NOT_SUPPORTED: 'Not supported',
        SECTION_NOT_FOUND: (sectionId) => `Section ${sectionId} can't be found in the DOM, skipping...`,
        TABLE_ID_NOT_FOUND: (tableId) => `Can't find table-id=${tableId}`,
        ANCHOR_NOT_FOUND: (pid) => `Can't find anchor=${pid}`,
        LENGTH_ZERO_DELETE: (section, anchorText, offset, length) => `Section ${section} unsuported to delete with length 0 , anchor text : ${anchorText}. start offest : ${offset}. length : ${length}.`,
        SECTION_TOO_SHORT: (section, anchorText, offset, length) => `Section ${section} is too short for delete details , anchor text : ${anchorText}. start offest : ${offset}. length : ${length}.`
      };

      // Typed reason, per the P2-09 convention, so an anchoring failure can be
      // told apart from a policy refusal by a gate and by the unsupported view.
      const unanchoredDeletion = 'unanchoredDeletion';
      const unanchoredDeletionMessage =
        'This deletion could not be placed in the document. Review its text and make the change manually.';
      // The per-item catch-all below is not an item the import refused, it is
      // an item whose processing threw; named apart from the refusals so the
      // two are not read as the same outcome.
      const processingFailure = ERROR_MESSAGES.NOT_SUPPORTED;
      
      const DEFAULT_BLOCK_LENGTH = 76; // Magic number extracted to constant
      
      // DOM Cache for frequently accessed elements
      const domCache = {
        preview: null,
        leaseOriginal: null,
        pidElements: null,
        cacheTimestamp: 0,
        CACHE_DURATION: 1000, // 1 second cache validity
        
        getPreview: function() {
          if (!this.preview || Date.now() - this.cacheTimestamp > this.CACHE_DURATION) {
            this.preview = document.querySelector(".preview");
            this.cacheTimestamp = Date.now();
          }
          return this.preview;
        },
        
        getLeaseOriginal: function() {
          if (!this.leaseOriginal || Date.now() - this.cacheTimestamp > this.CACHE_DURATION) {
            this.leaseOriginal = document.querySelector('.lease.lease--original');
            this.cacheTimestamp = Date.now();
          }
          return this.leaseOriginal;
        },
        
        getPidElements: function() {
          if (!this.pidElements || Date.now() - this.cacheTimestamp > this.CACHE_DURATION) {
            this.pidElements = Array.from(document.querySelectorAll('[pid]'));
            this.cacheTimestamp = Date.now();
          }
          return this.pidElements;
        },
        
        clear: function() {
          this.preview = null;
          this.leaseOriginal = null;
          this.pidElements = null;
          this.cacheTimestamp = 0;
        }
      };
      
      // Section ID cache to avoid repeated lookups
      const sectionIdCache = new Map();
      const paragraphIdCache = new Map();

      function getNewPid() {
        return `${Math.floor(Math.random() * 100000000000)}`;
      }

      async function startProcess() {
        const contentEditable = document.querySelectorAll('[contenteditable]:not(.SOCE):not(.EOCE)');
        contentEditable.forEach(item => {
          item.setAttribute(
            'original-contenteditable',
            item.getAttribute('contenteditable'),
          );
          item.setAttribute('contenteditable', true);
        });

        $scope.safeApply(() => {
          $scope.diffgram.processing = true;
          const preview = domCache.getPreview();
          if (preview) {
            preview.classList.toggle("version-control--processing");
          }
        });

        if (window.isDebug) {
          const progressNode = document.createElement("div");
          progressNode.id = "vc-progress";
          progressNode.innerText = "Pending";
          progressNode.style.position = "fixed";
          progressNode.style.zIndex = "999999";
          progressNode.style.top = "0";
          progressNode.style.left = "0";
          progressNode.style.padding = "10px 15px";
          progressNode.style.backgroundColor = "#fc5457";
          progressNode.style.fontSize = "18px";
          document.querySelector("body").appendChild(progressNode);
        }

        setTimeout(function() {
          processDiffgramItems();
          
        });
      }

      async function processDiffgramItems(){
        let annotations = [];
        let removeDiffItems = [];
        const progressNode = document.querySelector("#vc-progress");
        const BATCH_SIZE = 15; // Process 15 items at a time to prevent UI blocking
        const items = $scope.diffgram.items;
        let currentIndex = 0;

        // Helper function to find previous non-ghost item
        function findPreviousItem(diffgramIndex, prevItem) {
          let previous = prevItem || (diffgramIndex > 0 ? items[diffgramIndex - 1] : null);

          if (previous && previous._isGhostItem) {
            // backIndex starts from 2 since we need to skip the current length + the previous item 
            let backIndex = 2;
            while (previous && previous._isGhostItem) {
              previous = items[diffgramIndex - backIndex];
              backIndex++;
            }
          }

          if (previous && previous._includedInPreviousItem) {
            let backIndex = diffgramIndex;
            while (backIndex >= 0 && previous && previous._includedInPreviousItem) {
              backIndex--;
              previous = items[backIndex];
            }
          }

          return previous;
        }

        // Helper function to initialize item properties
        function initializeItem(item, diffgramIndex) {
          item._index = diffgramIndex;
          item._status = 'unresolved';
          item._isBlocked = false;
          item._isResolvable = true;
          item._showWarnings = true;
        }

        // Helper function to process insertion items
        async function processInsertionItem(current, diffgramIndex, previous) {
          if (current.PreSection) {
            if (current.Block) {
              current._change = "block";
              current._position = "after";
              current._pid = getNewPid();
              current._node = await insertBlock(current, diffgramIndex, previous);
            } else {
              current._pid = getParagraphId(current.PreSection);
              current._position = "self";
              current._change = "text";
              insertTextWithSpanOffset(current, diffgramIndex);
            }
          } else if (current.PostSection) {
            if (current.Block) {
              current._change = "block";
              current._position = "before";
              current._pid = getNewPid();
              current._node = await insertBlock(current, diffgramIndex, previous);
            } else if (current.IsStatic) {
              current._change = "static";
              current._position = "after";
              insertStaticText(current, current.PostSection, current.Html);
            } else {
              current._change = "text";
              current._position = "self";
              current._pid = getParagraphId(current.PostSection);
              insertTextWithSpanOffset(current, diffgramIndex);
            }
          } else {
            if (!current.IsStatic) {
              current.unsupportedItem = true;
              $scope.diffgram.unsupported.push({
                item: current,
                description: ERROR_MESSAGES.NOT_SUPPORTED
              });
            }
          }
        }

        // Helper function to process deletion items
        function processDeletionItem(current, diffgramIndex, previous) {
          if (current.PostSection) {
            current._pid = getParagraphId(current.PostSection);

            if (current.IsStatic) {
              current._change = "static";
              current._position = "after";
              deleteStaticText(current, current.PostSection);
            }
          } else if (current.Section) {
            current._pid = getParagraphId(current.Section);

            if (current.IsStatic) {
              current._change = "static";
              current._position = "after";
              deleteStaticText(current, current.Section);
            } else if (current.Block) {
              current._change = "block";
              current._position = "self";
              deleteBlock(current, diffgramIndex, previous);
            } else {
              current._change = "text";
              current._position = "self";
              deleteTextWithSpanOffset(current, diffgramIndex);
            }
          } else {
            const recoveredSection = findDeletionSection(current, previous);

            if (recoveredSection) {
              current.Section = recoveredSection;
              current._pid = getParagraphId(recoveredSection);

              if (current.IsStatic) {
                current._change = 'static';
                current._position = 'after';
                deleteStaticText(current, recoveredSection);
              } else if (current.Block) {
                current._change = 'block';
                current._position = 'self';
                deleteBlock(current, diffgramIndex, previous);
              } else {
                current._change = 'text';
                current._position = 'self';
                // Passed explicitly: deleteTextWithSpanOffset otherwise
                // resolves PreSection first, which would silently override the
                // one section whose text we just proved sits at `Offset`.
                deleteTextWithSpanOffset(
                  current,
                  diffgramIndex,
                  recoveredSection,
                );
              }
            } else {
              showUnanchoredDeletion(current, diffgramIndex, previous);
            }
          }
        }

        // Snapshot of every section the deletion paths can resolve, taken once
        // per import. `Offset` is measured against the document as it was
        // imported, so a snapshot is both cheaper than re-reading the DOM for
        // each of the ~200 anchorless deletions a redline can carry and closer
        // to what the converter measured.
        let deletionAnchorCandidates = null;

        function getDeletionAnchorCandidates() {
          if (!deletionAnchorCandidates) {
            // Only `[data-vc-id]`, because that is the single attribute
            // deleteTextWithSpanOffset and deleteBlock look the anchor up by.
            deletionAnchorCandidates = Array.from(
              document.querySelectorAll('.lease.lease--original [data-vc-id]'),
            ).map(section => ({
              id: section.getAttribute('data-vc-id'),
              text: normalizeDeletionText(section.textContent),
            }));
          }

          return deletionAnchorCandidates;
        }

        function findDeletionSection(current, previous) {
          if (current.PostSection || current.Section) {
            return null;
          }

          // The converter assumes a deletion without its own section-id is a
          // static list change and only looks for a post-section in the same
          // block. Ordinary inline deletions violate that assumption, so
          // recover only when their text and offset prove the location.
          const dummy = document.createElement('span');
          dummy.innerHTML = current.Html;
          const deletedText = normalizeDeletionText(dummy.textContent);
          if (!deletedText) {
            return null;
          }

          const holdsDeletion = text =>
            text.slice(current.Offset, current.Offset + deletedText.length) ===
            deletedText;
          const candidates = getDeletionAnchorCandidates();
          const previousSectionIds = previous
            ? [previous.Section, previous.PostSection, previous.PreSection]
            : [];
          const nearbyIds = _.uniq(
            [current.PreSection].concat(previousSectionIds).filter(Boolean),
          );
          const nearbyMatches = _.uniq(
            candidates
              .filter(
                candidate =>
                  nearbyIds.indexOf(candidate.id) !== -1 &&
                  holdsDeletion(candidate.text),
              )
              .map(candidate => candidate.id),
          );

          if (nearbyMatches.length === 1) {
            return nearbyMatches[0];
          }

          const documentMatches = _.uniq(
            candidates
              .filter(candidate => holdsDeletion(candidate.text))
              .map(candidate => candidate.id),
          );

          return documentMatches.length === 1 ? documentMatches[0] : null;
        }

        function normalizeDeletionText(text) {
          return (text || '')
            .replace(/\u00a0/g, ' ')
            .replace(/[\u200b\ufeff]/g, '');
        }

        // A deletion whose anchor cannot be recovered is shown the way a
        // complex block is: a blocked review card built from the item's own
        // text, with no marker written into the lease. The text belongs to the
        // counterparty's document, not to this lease, so putting it in
        // `.lease--original` would leak it into `leaseFreeTexts` /
        // `defaultProvisions` through updateDefaultsAndFreeTextValues and then
        // be unwrapped into the lease body by removeBlockedRequests, which
        // strips the `del` and keeps its text.
        function showUnanchoredDeletion(current, diffgramIndex, previous) {
          current._change = 'text';
          current._position = 'self';
          current._showWarnings = false;
          current._isBlocked = true;
          current._isResolvable = false;
          current._isUnanchoredDeletion = true;
          current._blockedReason = unanchoredDeletionMessage;
          current._pid = findUnanchoredDeletionPid(previous);

          $scope.diffgram.unsupported.push({
            item: current,
            reason: unanchoredDeletion,
            description: unanchoredDeletionMessage,
          });

          if (!current._pid) {
            current.unsupportedItem = true;
            return;
          }

          current.Html = getUnanchoredDeletionHtml(current, diffgramIndex);
        }

        // The card still has to be filed somewhere, so it is filed against the
        // last paragraph the import did resolve - the closest thing to where
        // the deletion belonged - and falls back to the first paragraph of the
        // document for a redline that opens with one.
        function findUnanchoredDeletionPid(previous) {
          const container =
            (previous && getParagraph(previous._pid)) ||
            document.querySelector('.lease.lease--original [pid]');

          if (!container) {
            return null;
          }

          const anchor = container.getTopAnchor();
          const anchorPid = anchor.getAttribute('pid');

          return getCalculatedSectionId(anchor, anchorPid) || anchorPid;
        }

        function getUnanchoredDeletionHtml(current, diffgramIndex) {
          const source = document.createElement('span');
          source.innerHTML = current.Html;

          const marker = document.createElement('del');
          marker.textContent = source.textContent;
          marker.classList.add(
            DIFF_CLASSES.ITEM,
            DIFF_CLASSES.DELETION,
            DIFF_CLASSES.PRIMARY,
            DIFF_CLASSES.BLOCKED,
          );
          marker.setAttribute('diff-index', `${diffgramIndex}`);

          const holder = document.createElement('span');
          holder.appendChild(marker);

          return holder.innerHTML;
        }

        // Helper function to process change items
        function processChangeItem(current) {
          if (current.TableId) {
            current._change = "table";
            current._position = "self";
            current._showWarnings = false;
            current._isBlocked = true;
            current._isChange = true;
            current._pid = getTableParagraphId(current.TableId);
            current._isResolvable = false;
            current._blockedReason = ERROR_MESSAGES.TABLE_CHANGE_NOT_SUPPORTED;

            if (!current._pid) {
              current.unsupportedItem = true;
              $scope.diffgram.unsupported.push({
                item: current,
                description: ERROR_MESSAGES.TABLE_ID_NOT_FOUND(current.TableId)
              });
            } else {
              current.Html = getIncomingHtml(current);
            }
          } else {
            current.unsupportedItem = true;
            $scope.diffgram.unsupported.push({
              item: current,
              description: ERROR_MESSAGES.NOT_SUPPORTED
            });
          }
        }

        // Helper function to process complex table items
        function processComplexTableItem(current) {
          current._change = "table";
          current._position = "self";
          current._showWarnings = false;
          current._isBlocked = true;
          current._isComplexTable = true;
          current._pid = current.PreSection;
          current._blockedReason = ERROR_MESSAGES.TABLE_ADJUSTMENT_NOT_SUPPORTED;

          if (versionControlManager) {
            let sectionId = current.PreSection || current.PostSection;
            const anchor = document.querySelector(`[data-vc-id="${sectionId}"]`);
            if (!anchor) {
              current.unsupportedItem = true;
              $scope.diffgram.unsupported.push({
                item: current,
                description: ERROR_MESSAGES.SECTION_NOT_FOUND(sectionId)
              });
            } else {
              try {
                const block = anchor.getBlockData();
                if (block && block.containingNode) {
                  const pid = block.containingNode.getAttribute("pid");
                  if (pid) {
                    const updatedPid = getCalculatedSectionId(block.containingNode, pid);
                    current._pid = updatedPid;
                    current.PreSection = updatedPid;
                  }
                }
              } catch (ex) {
                console.error('[version control] Error getting block data for complex table', ex);
                current.unsupportedItem = true;
                $scope.diffgram.unsupported.push({
                  item: current,
                  description: ERROR_MESSAGES.SECTION_NOT_FOUND(sectionId)
                });
              }
            }
          }

          const pidElements = domCache.getPidElements();
          const anchor = getParagraph(current.PreSection);
          if (anchor) {
            const index = pidElements.indexOf(anchor);
            if (index >= 0 && pidElements[index + 1]) {
              const nextPid = pidElements[index + 1].getAttribute('pid');
              if (nextPid) {
                current._pid = nextPid;
              }
            }
          }

          if (!current._pid) {
            current.unsupportedItem = true;
            $scope.diffgram.unsupported.push({
              item: current,
              description: ERROR_MESSAGES.ANCHOR_NOT_FOUND(current._pid)
            });
          } else {
            current.Html = getIncomingHtml(current);
          }
        }

        // Helper function to process complex block items
        function processComplexBlockItem(current) {
          current._change = "block";
          current._position = "self";
          current._showWarnings = false;
          current._isBlocked = true;
          current._isComplexText = true;
          current._pid = current.Section;
          current._blockedReason = ERROR_MESSAGES.CHANGE_NOT_SUPPORTED;

          if (!current._pid) {
            current.unsupportedItem = true;
            $scope.diffgram.unsupported.push({
              item: current,
              description: ERROR_MESSAGES.ANCHOR_NOT_FOUND(current._pid)
            });
          } else {
            current.Html = getIncomingHtml(current);
          }
        }

        // Helper function to process a single item
        async function processItem(diffgramIndex, prevItem) {
          const current = items[diffgramIndex];
          const previous = findPreviousItem(diffgramIndex, prevItem);

          initializeItem(current, diffgramIndex);

            try {
              if (current.Type === DIFF_TYPES.NEW_TABLE) {
                current.Type = DIFF_TYPES.INSERTION;
                current.Block = "p";
                current._showWarnings = false;
              }

              if (
                current.Type === DIFF_TYPES.INSERTION &&
                current.ListHeader &&
                current.ListHeader.length !== 0 &&
                current.Nested !== -1
              ) {
                current.ListHeader = "";
              }

              if (current.Type === DIFF_TYPES.INSERTION) {
                await processInsertionItem(current, diffgramIndex, previous);
              } else if (current.Type === DIFF_TYPES.DELETION) {
                processDeletionItem(current, diffgramIndex, previous);
              } else if (current.Type === DIFF_TYPES.CHANGE) {
                processChangeItem(current);
              } else if (current.Type === DIFF_TYPES.COMPLEX_TABLE) {
                processComplexTableItem(current);
              } else if (current.Type === DIFF_TYPES.COMPLEX_BLOCK) {
                processComplexBlockItem(current);
              }

              if (current && !current.unsupportedItem) {
                if (current.IsBullet) {
                  const container = getParagraph(current._pid);
                  if (container) {
                    let listElement = container.querySelector('[list]');
                    if(listElement){
                      listElement.classList.add('version-control--is-bullet');
                    }
                  }
                }
                if (!current._includedInPreviousItem) {
                  return { annotations: [current], removeDiffItems: [] };
                }
                if (current._includedInPreviousItem) {
                  return { annotations: [], removeDiffItems: [current] };
                }
              }
              return { annotations: [], removeDiffItems: [] };
            } catch (ex) {
              current.unsupportedItem = true;
              $scope.diffgram.unsupported.push({
                item: current,
                description: processingFailure,
                error: ex.message || String(ex),
                itemIndex: diffgramIndex,
                itemType: current.Type
              });

              console.error(
                "[version control] Failed to process item",
                {
                  index: diffgramIndex,
                  type: current.Type,
                  section: current.Section || current.PreSection || current.PostSection,
                  item: current
                },
                ex
              );

              if (window.Raven) {
                Raven.captureException(ex, {
                  extra: {
                    diffgramIndex: diffgramIndex,
                    itemType: current.Type,
                    section: current.Section || current.PreSection || current.PostSection
                  }
                });
              }
              return { annotations: [], removeDiffItems: [] };
            }
          return { annotations: [], removeDiffItems: [] };
        }

        // Process items in batches to prevent UI blocking
        async function processBatch(startIndex) {
          const endIndex = Math.min(startIndex + BATCH_SIZE, items.length);
          let batchAnnotations = [];
          let batchRemoveDiffItems = [];
          let lastProcessedItem = null;

          for (let i = startIndex; i < endIndex; i++) {
            if (progressNode) {
              progressNode.innerText = `${i}/${items.length}`;
            }

            const result = await processItem(i, lastProcessedItem);
            if (result) {
              batchAnnotations = batchAnnotations.concat(result.annotations || []);
              batchRemoveDiffItems = batchRemoveDiffItems.concat(result.removeDiffItems || []);
              if (items[i] && !items[i]._isGhostItem && !items[i]._includedInPreviousItem) {
                lastProcessedItem = items[i];
              }
            }
          }

          annotations = annotations.concat(batchAnnotations);
          removeDiffItems = removeDiffItems.concat(batchRemoveDiffItems);

          // Continue with next batch if there are more items
          if (endIndex < items.length) {
            // Use requestIdleCallback if available, otherwise setTimeout
            if (window.requestIdleCallback) {
              await new Promise(resolve => {
                requestIdleCallback(() => {
                  processBatch(endIndex).then(resolve);
                }, { timeout: 100 });
              });
            } else {
              await new Promise(resolve => {
                setTimeout(() => {
                  processBatch(endIndex).then(resolve);
                }, 0);
              });
            }
          }
        }

        try {
          // Start batch processing
          await processBatch(0);

          // join close deleted blocks Start
          const delList = document.querySelectorAll("[free-text] del");
          const delArrIds = [];
          delList.forEach(del => {
            const diffItem =
              $scope.diffgram.items[del.getAttribute("diff-index")];

            if (!del.childNodes[0].tagName) {
              const previousTextNode = del.childNodes[0].getPreviousTextNodeInBlock();
              const element = previousTextNode
                ? previousTextNode.parentElement
                : null;
              if (element && element.tagName == "DEL") {
                if (
                  !del.classList.contains(DIFF_CLASSES.BLOCKED) &&
                  !element.classList.contains(DIFF_CLASSES.BLOCKED)
                )
                  diffItem._isGhostItem = true;

                del.setAttribute(
                  "diff-item",
                  element.getAttribute("diff-item")
                );
                del.setAttribute(
                  "diff-index",
                  element.getAttribute("diff-index")
                );

                del.setAttribute(
                  "ng-click",
                  element.getAttribute("ng-click")
                );

                if (
                  delArrIds.indexOf(element.getAttribute("diff-index")) == -1
                ) {
                  delArrIds.push(element.getAttribute("diff-index"));
                }
              }
            }
          });

          // diff-partial-index="${i}"
          delArrIds.forEach(id => {
            const delList = document.querySelectorAll(
              `del[diff-index="${id}"]`,
            );
            normalizeTenantPartialMarkers(delList);
          });
          

          // join close deleted blocks End

          // A scenario of nested diff-items is not supported
          // (even though can occur while processing the diffgram items)
          // We need to move nested diff-items outside
          const leaseOriginal = domCache.getLeaseOriginal();
          const nested = leaseOriginal ? leaseOriginal.querySelectorAll('.diff-item .diff-item') : [];
          let container;
          for (let nestedIndex = nested.length - 1; nestedIndex >= 0; nestedIndex--) {
            if (nested[nestedIndex] && nested[nestedIndex].hasAttribute('diff-index')) {
              const index = nested[nestedIndex].getAttribute('diff-index');
              $scope.diffgram.items[index]._showWarnings = false;
              $scope.diffgram.items[index]._isRelocated = true;
              $scope.diffgram.items[index]._isBlocked = true;
              $scope.diffgram.items[index]._blockedReason = ERROR_MESSAGES.CHANGE_NOT_SUPPORTED;
              nested[nestedIndex].classList.add(DIFF_CLASSES.RELOCATED, DIFF_CLASSES.BLOCKED);
              container = nested[nestedIndex].parentElement.closest(`.${DIFF_CLASSES.ITEM}`);
              if (container && container.parentElement) {
                container.parentElement.insertBefore(nested[nestedIndex], container.nextElementSibling);
              }
            }
          }

          // remove empty insertions that have been included in previous  items
          $scope.diffgram.items.filter((item)=>{ return item._includedInPreviousItem}).forEach((itemToRemove)=>{
            document.querySelectorAll(`[diff-index="${itemToRemove._index}"]`).forEach((item)=>{
              item.remove();
            });
          });

          setTimeout(function() {
            $scope.diffgram.on = true;
            $scope.leftPane = null;
            const preview = domCache.getPreview();
            if (preview) {
              preview.classList.toggle("review-changes");
            }

            listItemsManager.createListItemsMap();

            createAnnotations(annotations, () => {
              var close = false;
              var noChanges = true;
              var counterpartyCommentIndex = null;

              if (!addNoChangesWarning()) {
                cacheElements();
                generateSidebar();
                printVcStatus();
                generateUnsupportedView();
                updateDefaultsAndFreeTextValues();
                // Build while every rendered tracked-change marker is still in
                // the original document. finishProcessing removes blocked
                // markers as part of cleanup, which would make that input
                // incomplete if projection waited for the asynchronous call.
                counterpartyCommentIndex = buildCounterpartyCommentIndex();
                close = true;
                noChanges = false;
              }
              finishProcessing();
              $rootScope.$emit('updateImportChangesDialogStatus', {
                close: close,
                noChanges: noChanges,
                error: false,
              });
              reviewCounterpartyComments(counterpartyCommentIndex);
            });
          });
        } catch (ex) {
          // Cleanup on error
          domCache.clear();
          sectionIdCache.clear();
          paragraphIdCache.clear();
          
          $rootScope.$emit('updateImportChangesDialogStatus', {
            close: false,
            error: true,
          });
          //alert('Failed importing tenant requests. are you sure you imported the correct file?');
          console.error('[version control] Failed processing diffgram', ex);
          if (window.Raven) {
            Raven.captureException(ex);
          }
        }
      }

      function zipFiles(files) {
        var zip = new JSZip();

        files.forEach(file => {
          zip.file(file.name, file.file);
        });

        return zip.generateAsync({ type: "blob" });
      }

      function reportUnsupportedChanges() {
        var timestamp = moment().format('YYYYMMDDHHmmss');
        var leaseFileName, diffgramFileName;
        var clonedLease = _.cloneDeep($scope.lease);
        clonedLease.freeTexts = $scope.leaseFreeTexts;
        var leaseFile = new Blob([JSON.stringify(clonedLease)], {
          type: 'application/json',
        });
        var diffgramFile = new Blob([JSON.stringify($scope.diffgram.raw)], {
          type: 'application/json',
        });
        leaseFileName = `${$scope.companyName}_lease_file_${timestamp}.json`;
        diffgramFileName = `${$scope.companyName}_diffgram_file_${timestamp}.json`;

        VersionControlService.report(
          leaseFile,
          diffgramFile,
          leaseFileName,
          diffgramFileName,
          window.user.email
        ).then(function(res) {
          console.log(
            "[version control] Unsupported items report sent successfully",
            res,
          );
        });
      }

      function reportVersionControlUsage() {
        if (!$scope.lease) {
          return;
        }

        if (!window._version_control_tenant_requests_document.document) {
          return;
        }

        const clonedLease = _.cloneDeep($scope.lease);
        clonedLease.freeTexts = $scope.leaseFreeTexts;

        const timestamp = moment().format('YYYYMMDDHHmmss');
        const url = window.location.href;
        const email = window.user.email;
        const originalFile = new Blob([JSON.stringify(clonedLease)], {
          type: 'application/json',
        });

        const blockedRequests = _.groupBy($scope.diffgram.status.blocked, '_blockedReason');
        const blockedRequestsKeys = Object.keys(blockedRequests);

        const status =
        [
          { name: "All requests", value: $scope.diffgram.status.all.length },
          { name: "Normal requests", value: $scope.diffgram.status.normal.length },
          { name: "Insertions", value: $scope.diffgram.status.insertions.length },
          { name: "Deletions", value: $scope.diffgram.status.deletions.length },
          { name: "Changes", value: $scope.diffgram.status.changes.length },
          { name: "Complex Tables", value: $scope.diffgram.status.complexTables.length },
          { name: "Complex Blocks", value: $scope.diffgram.status.complexBlocks.length },
          { name: "Relocations", value: $scope.diffgram.status.relocations.length },
          { name: "Unsupported requests", value: $scope.diffgram.status.unsupported.length },
          { name: "Blocked requests", value: $scope.diffgram.status.blocked.length },
        ];

        blockedRequestsKeys.forEach(block => {
          status.push({
            name: `Blocked requests (${block})`,
            value: blockedRequests[block].length,
          });
        });

        const filesToZip = [{
            name: 'original.json',
            file: originalFile,
          },
          {
            name: 'modified.docx',
            file: window._version_control_tenant_requests_document.document,
          },
        ];

        zipFiles(filesToZip).then(function (zipFile) {
          const id = $scope.lease.id;
          const company = $scope.lease.form.company.name;
          const tenant = $scope.lease.customFields?.fileName || $scope.lease.tenantInfo?.tradeName || $scope.lease.tenantInfo?.name;
          const zipFileName = `${id}_${company}_${tenant}_${timestamp}.zip`;

          VersionControlService.usage(
            url, email, zipFile, zipFileName, status
          ).then(function(res) {
            console.log(
              "[version control] Usage report sent",
              res,
            );
          });
        });
      }

      function removeBlockedRequests() {
        var leaseVarChanges = document.querySelectorAll(
          '.lease.lease--original lease-var .diff-item',
        );
        leaseVarChanges.forEach(item => {
          var leaseVar = item.closest('lease-var');
          let elScope = angular.element(leaseVar).scope();
          $compile(leaseVar)(elScope || $scope);
        });

        // TODO: [version control] can the above be removed in favor of the following code?
        var blockedRequests = document.querySelectorAll(
          `.lease.lease--original .${DIFF_CLASSES.ITEM}.${DIFF_CLASSES.BLOCKED}`,
        );
        blockedRequests.forEach(item => {
          if (item.tagName.toLowerCase() === 'del') {
            if (item.firstChild) {
              item.parentElement.insertBefore(item.firstChild, item);
            }
            item.remove();
          } else if (item.tagName.toLowerCase() === 'ins') {
            if (item.getAttribute('diff-type') !== 'block') {
              item.remove();
            }
          }
        });
      }

      function finishProcessing() {        
        $scope.safeApply(() => {
          $scope.diffgram.processing = false;
          const preview = domCache.getPreview();
          if (preview) {
            preview.classList.toggle("version-control--processing");
          }
        });

        const progressNode = document.querySelector("#vc-progress");

        if (progressNode) {
          progressNode.remove();
        }
        
        // Clear DOM cache
        domCache.clear();
        sectionIdCache.clear();
        paragraphIdCache.clear();

        removeBlockedRequests();
        /* insertions of list items are added as accepted in order to allow the list items to get an updated number
        this code will remove the accepted status from the html in order to show the list items number without the changes.  */
        const newListItems = document.querySelectorAll(`.${DIFF_CLASSES.ACCEPTED}`);
        newListItems.forEach((node) => {
            node.classList.remove(DIFF_CLASSES.ACCEPTED);
            node.classList.remove(DIFF_CLASSES.PROCESSING);
        });

        const contentEditable = document.querySelectorAll('[contenteditable]:not(.SOCE):not(.EOCE)');
        contentEditable.forEach(item => {
          if (item.getAttribute('original-contenteditable')) {
            item.setAttribute(
              'contenteditable',
              item.getAttribute('original-contenteditable'),
            );
            item.removeAttribute('original-contenteditable');
          }
        });

        $scope.structureChanged(false);
        listItemsManager.createListItemsMap();

        setTimeout(() => {
          let item;
          let annotationRequestNode;

          for (let i = 0; i < $scope.diffgram.items.length; i++) {
            item = $scope.diffgram.items[i];
            annotationRequestNode = document.querySelector(`.lease.lease--modified [diff-index="${item._index}"]`);
            item._annotationNode = annotationRequestNode;
          }
        })
      }

      function generateUnsupportedView() {
        if (window._version_control_enable_unsupported_reporting) {
          // If there are any unsupported items, lets create a zendesk ticket
          // so it'll be on our radar, otherwise, currently all we want to know
          // is the usage and the breakdown of requests.
          if (
            $scope.diffgram.unsupported &&
            $scope.diffgram.unsupported.length > 0
          ) {
            reportUnsupportedChanges();
            $scope.showVersionControlUnsupportedChanges();
          } else {
            reportVersionControlUsage();
          }
        }
      }

      function addNoChangesWarning() {
        if (
          Object.keys($scope.annotations).length === 0 &&
          $scope.diffgram.unsupported &&
          $scope.diffgram.unsupported.length === 0
        ) {
          return true;
        }
        return false;
      }

      function cacheElements() {
        $scope.diffgram.actual = [];
        const keys = Object.keys($scope.annotations);
        for (let annotationIndex = 0; annotationIndex < keys.length; annotationIndex++) {
          const annotation = $scope.annotations[keys[annotationIndex]];
          const annotationNode = document.createElement('dummy');
          annotationNode.innerHTML = annotation.html;

          annotationNode
            .querySelectorAll(`.${DIFF_CLASSES.ITEM}.${DIFF_CLASSES.PRIMARY}:not(.${DIFF_CLASSES.STATIC})`)
            .forEach((item) => {
              $scope.diffgram.actual.push(item);
            });
        }
      }

      function generateSidebar() {
        ensureCommentsSidebar();
      }

      function findTextNodeByOffset(section, diffgramItem) {
        const container = LeaseEditorService.findClosestContainer(section).get(0);
        let iterator;
        let spanList = [];
        let walk = document.createTreeWalker(
          container,
          NodeFilter.SHOW_TEXT,
          null,
          false
        );

        while ((iterator = walk.nextNode())) {
          const parentNode = iterator.parentElement;
          const isSOCE = parentNode.classList.contains('SOCE');
          const isEmpty = iterator.textContent === window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_NO_BREAKING_SPACE || iterator.textContent === window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_SPACE;
          const isEOCE = parentNode.classList.contains('EOCE');
          const isSkip = parentNode.classList.contains('SKIP');
          const isListPadding = parentNode.style.font.indexOf('7pt') !== -1;

          if (
            !isSOCE &&
            !isEmpty &&
            !isEOCE &&
            !isSkip &&
            !isListPadding
          ) {
            spanList.push(iterator);
          }
        }

        let anchorNode;
        let anchorOffset = diffgramItem.Offset;
        let isBulletFound = false;

        for (let spanIndex = 0; spanIndex < spanList.length; spanIndex++) {
          const current = spanList[spanIndex];

          let currentLength;
          if (current.parentElement.classList.contains('version-control--is-bullet')) {
            if (!isBulletFound) {
              currentLength = 1;
              isBulletFound = true;
            } else {
              continue;
            }
          } else if (
            current.parentElement.getAttribute('list') &&
            !current.parentElement.closest('.editable') &&
            current.parentElement.querySelector(`ins:not(.${DIFF_CLASSES.STATIC}`)
          ) {
            continue;
          } else {
            currentLength = current.length;
          }

          // Ignore non-width characters which we anyway filter out while
          // preparing the document for version control
          if (
            current.textContent.startsWith(window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_NO_BREAKING_SPACE) ||
            current.textContent.startsWith(window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_SPACE)
          ) {
            anchorOffset++;
          }

          // Ignore `&nbsp` which is part of the list format
          // under the assumption they appear only at the end
          if (
            current.parentElement.getAttribute('list') &&
            current.parentElement.nextElementSibling &&
            current.parentElement.nextElementSibling.style.font.indexOf('7pt') > -1 &&
            current.textContent.contains(' ') // &nbsp
          ) {
            const count = current.textContent.split(' ').length - 1;
            anchorOffset += count;
          }

          if (!anchorNode) {
            if (anchorOffset >= currentLength) {
              anchorOffset = anchorOffset - currentLength;
            } else {
              anchorNode = current;
              anchorOffset = anchorOffset;
            }
          }

          if (anchorNode) {
            break;
          }
        }

        if (spanList[0] === anchorNode || spanList[spanList.length - 1] === anchorNode) {
          anchorNode = undefined;
        }

        if (!anchorNode) {
          anchorNode = spanList[spanList.length - 1];

          const isWhitespace = !/[^\t\n\r ]/.test(anchorNode.textContent);

          if (isWhitespace) {
            anchorNode = spanList[spanList.length - 2];
          }

          if (anchorOffset === 0) {
            anchorOffset = anchorNode.textContent.length;
          }
        }

        const isAffectingLeaseVar = isWithinLeaseVar(anchorNode);
        const isAffectingCrossReference = isWithinCrossReference(anchorNode);
        const isAffectingContentEditable = isWithinContentEditable(anchorNode);
        const isAffectingAutoNumber = isWithinAutoNumber(anchorNode);
        const isAffectingDocumentStructure = isChangeDocumentStructure(anchorNode);

        return {
          anchorNode,
          anchorOffset,
          isAffectingLeaseVar,
          isAffectingCrossReference,
          isAffectingContentEditable,
          isAffectingAutoNumber,
          isAffectingDocumentStructure,
        };
      }

      function markTextAsDeleted(section, diffgramItem) {
        let offset = diffgramItem.Offset;
        let length = diffgramItem.Length;
        const container = LeaseEditorService.findClosestContainer(section).get(0);
        let iterator;
        let spanList = [];
        let walk = document.createTreeWalker(
          container,
          NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
          null,
          false
        );

        while ((iterator = walk.nextNode())) {
          const parentNode = iterator.parentElement;
          const isSOCE = parentNode.classList.contains('SOCE');
          const isEmpty = iterator.textContent === window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_NO_BREAKING_SPACE || iterator.textContent === window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_SPACE;
          const isEOCE = parentNode.classList.contains('EOCE');
          const isSkip = parentNode.classList.contains('SKIP');
          const isListPadding = parentNode.style.font.indexOf('7pt') !== -1;

          if (iterator.nodeType === Node.TEXT_NODE) {
            if (
              !isSOCE &&
              !isEmpty &&
              !isEOCE &&
              !isSkip &&
              !isListPadding
            ) {
              spanList.push(iterator);
            }
          }

          if (iterator.nodeType === Node.ELEMENT_NODE) {
            if (LeaseEditorService.isSelfClosing(iterator)) {
              spanList.push(iterator);
            }
          }
        }

        // Delete the last text-node in case it's just whitespace
        if (
          spanList[spanList.length - 1].nodeType === Node.TEXT_NODE &&
          !/[^\t\n\r ]/.test(spanList[spanList.length - 1].textContent)
        ) {
          spanList.splice(spanList.length - 1, 1);
        }

        let anchorNode = null;
        let anchorOffset = offset;
        let focusNode = null;
        let focusOffset = offset + length;
        let startIndex = 0;
        let endIndex = 0;
        let isBulletFound = false;

        for (let spanIndex = 0; spanIndex < spanList.length; spanIndex++) {
          const current = spanList[spanIndex];

          if (LeaseEditorService.isSelfClosing(current)) {
            continue;
          }

          let currentLength = 0;
          if (current.parentElement.classList.contains('version-control--is-bullet')) {
            if (!isBulletFound) {
              currentLength = 1;
              isBulletFound = true;
            } else {
              continue;
            }
          } else if (
            current.parentElement.getAttribute('list') &&
            !current.parentElement.closest('.editable') &&
            current.parentElement.querySelector(`ins:not(.${DIFF_CLASSES.STATIC}`)
          ) {
            continue;
          } else {
            currentLength = current.length;
          }

          // Ignore non-width characters which we anyway filter out while
          // preparing the document for version control
          if (
            current.textContent.startsWith(window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_NO_BREAKING_SPACE) || 
            current.textContent.startsWith(window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_SPACE)
          ) {
            anchorOffset++;
            focusOffset++;
          }

          // Ignore `&nbsp` which is part of the list format
          // under the assumption they appear only at the end
          if (
            current.parentElement.getAttribute('list') &&
            current.parentElement.nextElementSibling &&
            current.parentElement.nextElementSibling.style.font.indexOf('7pt') > -1 &&
            current.textContent.contains(' ') // &nbsp
          ) {
            const count = current.textContent.split(' ').length - 1;
            anchorOffset += count;
            focusOffset += count;
          }

          if (!anchorNode) {
            if (anchorOffset >= currentLength) {
              anchorOffset = anchorOffset - currentLength;
            } else {
              anchorNode = current;
              startIndex = spanIndex;
            }
          }

          if (!focusNode) {
            if (focusOffset > currentLength) {
              focusOffset = focusOffset - currentLength;
            } else {
              if (anchorNode) {
                focusNode = current;
                endIndex = spanIndex;
              }
            }
          }

          if (anchorNode && focusNode) {
            break;
          }
        }

        let deletions = [];
        let current;
        let editable;

        if (diffgramItem.Length > 0) {
          for (let i = startIndex; i <= endIndex; i++) {
            current = spanList[i];
            editable = current.parentElement.closest('.editable');

            if (!editable) {
              diffgramItem._isBlocked = true;
              diffgramItem._blockedReason = "You can't accept this change, please make the changes manually";
              return;
            }

            let sectionId = editable.getAttribute('section-id');
            let isAffectingLeaseVar = isWithinLeaseVar(current);
            let isAffectingDocumentStructure = isChangeDocumentStructure(current);
            let isAffectingCrossReference = isWithinCrossReference(current);
            let isAffectingAutoNumber = isWithinAutoNumber(current);

            if (!deletions[sectionId]) {
              deletions[sectionId] = [];
            }

            if (i === startIndex && i === endIndex) {
              deletions[sectionId].push({
                anchorNode,
                anchorOffset,
                focusNode,
                focusOffset,
                isAffectingLeaseVar,
                isAffectingCrossReference,
                isAffectingAutoNumber,
                isAffectingDocumentStructure,
                isFirst: false,
                isLast: false,
              });
            } else if (i === startIndex) {
              deletions[sectionId].push({
                anchorNode,
                anchorOffset,
                focusNode: anchorNode,
                focusOffset: anchorNode.textContent.length,
                isAffectingLeaseVar,
                isAffectingCrossReference,
                isAffectingAutoNumber,
                isAffectingDocumentStructure,
                isFirst: false,
                isLast: false,
              });
            } else if (i === endIndex) {
              deletions[sectionId].push({
                anchorNode: focusNode,
                anchorOffset: 0,
                focusNode,
                focusOffset,
                isAffectingLeaseVar,
                isAffectingCrossReference,
                isAffectingAutoNumber,
                isAffectingDocumentStructure,
                isFirst: false,
                isLast: false,
              });
            } else {
              deletions[sectionId].push({
                anchorNode: spanList[i],
                anchorOffset: 0,
                focusNode: spanList[i],
                focusOffset: spanList[i].textContent.length,
                isAffectingLeaseVar,
                isAffectingCrossReference,
                isAffectingAutoNumber,
                isAffectingDocumentStructure,
                isFirst: false,
                isLast: false,
              });
            }
          }
        }

        const handledItems = [];
        const dummy = document.createElement("dummy");
        dummy.innerHTML = diffgramItem.Html;
        const children = dummy.childNodes;

        if (LeaseEditorService.isSelfClosing(children[0])) {
          let current;
          let sectionId;
          let counter = 0;

          for (let x = 0; x < children.length; x++) {
            if (LeaseEditorService.isSelfClosing(children[x])) {
              counter++;
              continue;
            } else {
              break;
            }
          }

          for (let startNodeIndex = 0; startNodeIndex < counter; startNodeIndex++) {
            let currentStartSpanIndex = startIndex - startNodeIndex - 1;

            if (
              children[startNodeIndex] &&
              spanList[currentStartSpanIndex] &&
              LeaseEditorService.isSelfClosing(children[startNodeIndex]) &&
              LeaseEditorService.isSelfClosing(spanList[currentStartSpanIndex])
            ) {
              current = spanList[currentStartSpanIndex];

              if (handledItems.indexOf(currentStartSpanIndex) === -1) {
                editable = current.parentElement.closest('.editable');
                sectionId = editable.getAttribute('section-id');

                if (!deletions[sectionId]) {
                  deletions[sectionId] = [];
                }

                deletions[sectionId].unshift({
                  anchorNode: current,
                  anchorOffset: 0,
                  focusNode: current,
                  focusOffset: 0,
                  isAffectingLeaseVar: false,
                  isAffectingCrossReference: false,
                  isAffectingAutoNumber: false,
                  isAffectingDocumentStructure: false,
                  isFirst: false,
                  isLast: false,
                });

                handledItems.push(currentStartSpanIndex)
              }
            } else {
              break;
            }
          }
        }

        if (LeaseEditorService.isSelfClosing(children[children.length - 1])) {
          let current;
          let sectionId;
          let counter = 0;

          for (let y = children.length - 1; y >= 0; y--) {
            if (LeaseEditorService.isSelfClosing(children[y])) {
              counter++;
              continue;
            } else {
              break;
            }
          }

          for (let endNodeIndex = 0; endNodeIndex < counter; endNodeIndex++) {
            let currentEndSpanIndex = endIndex + endNodeIndex + 1;

            if (
              children[children.length - endNodeIndex - 1] &&
              spanList[currentEndSpanIndex] &&
              LeaseEditorService.isSelfClosing(children[children.length - endNodeIndex - 1]) &&
              LeaseEditorService.isSelfClosing(spanList[currentEndSpanIndex])
            ) {
              current = spanList[currentEndSpanIndex];

              if (handledItems.indexOf(currentEndSpanIndex) === -1) {
                editable = current.parentElement.closest('.editable');
                sectionId = editable.getAttribute('section-id');

                if (!deletions[sectionId]) {
                  deletions[sectionId] = [];
                }

                deletions[sectionId].push({
                  anchorNode: current,
                  anchorOffset: 0,
                  focusNode: current,
                  focusOffset: 0,
                  isAffectingLeaseVar: false,
                  isAffectingCrossReference: false,
                  isAffectingAutoNumber: false,
                  isAffectingDocumentStructure: false,
                  isFirst: false,
                  isLast: false,
                });

                handledItems.push(currentEndSpanIndex)
              }
            } else {
              break;
            }
          }
        }

        const keys = Object.keys(deletions);
        deletions[keys[0]][0].isFirst = true;
        deletions[keys[keys.length - 1]][deletions[keys[keys.length - 1]].length - 1].isLast = true;

        return deletions;
      }

      function insertMarker(nodeToInsert) {
        const sel = rangy.getSelection();
        var contentEditable = nodeToInsert;
        if (sel.getRangeAt && sel.rangeCount) {
          LeaseEditorService.enableEditingForSegment(contentEditable);
          const range = sel.getRangeAt(0);
          range.insertNode(nodeToInsert);
          range.collapseAfter(nodeToInsert);
          range.select();
        }
      }

      function insertMarkerByNodeAndOffset(node, offset, marker) {
        const text = Array.from(node.textContent);
        text.splice(offset, 0, marker);
        node.textContent = text.join('');
      }

      function focusToSection(sectionId) {
        // Place the caret at the beginning of the first text node
        const section = getSection(sectionId);
        let anchor = $(section)
          .find('.SOCE')
          .get(0);
        anchor = LeaseEditorService.skipEmptyNodes(anchor, true);
        $rootScope.placeCaretAtStart(anchor);
        section.focus();

        return section;
      }

      function replaceMarker(section, marker, diffgramItem, diffgramItemHtml) {
        let html = $(section).html();

        html = html.replace(marker, diffgramItemHtml);
        $(section).html(html);
      }

      function isChangeDocumentStructure(node) {
        if (!node) {
          return false;
        }

        let anchorNode = node;

        if (anchorNode.nodeType === Node.TEXT_NODE) {
          anchorNode = anchorNode.parentElement;
        }

        let leaseVar = anchorNode.closest('lease-var');

        if (leaseVar && leaseVar.getAttribute('non-click') == 'true') {
          return true;
        }

        return false;
      }

      function isWithinLeaseVar(node) {
        if (!node) {
          return false;
        }

        let anchorNode = node;

        if (anchorNode.nodeType === Node.TEXT_NODE) {
          anchorNode = anchorNode.parentElement;
        }

        if (anchorNode.closest('lease-var')) {
          return true;
        }

        return false;
      }

      function isWithinCrossReference(node) {
        if (!node) {
          return false;
        }

        let anchorNode = node;

        if (anchorNode.nodeType === Node.TEXT_NODE) {
          anchorNode = anchorNode.parentElement;
        }

        if (anchorNode.closest('crr')) {
          return true;
        }

        return false;
      }

      function isWithinContentEditable(node) {
        const contentEditable = LeaseEditorService.findClosestContentEditable(node).get(0);
        return !!contentEditable;
      }

      function isWithinAutoNumber(node) {
        if (!node) {
          return false;
        }

        let anchorNode = node;

        if (anchorNode.nodeType === Node.TEXT_NODE) {
          anchorNode = anchorNode.parentElement;
        }

        if (
          anchorNode.closest('[list]') &&
          anchorNode.closest('[list]').closest('.editable')
        ) {
          return true;
        }

        return false;
      }

      function isInsertionRequestBlocked(insertion) {
        return insertion.isAffectingLeaseVar || insertion.isAffectingCrossReference || insertion.isAffectingAutoNumber || !insertion.isAffectingContentEditable;
      }

      function isDeletionRequestBlocked(deletions) {
        let currentBatch;

        for (let sectionId in deletions) {
          currentBatch = deletions[sectionId];

          let current;
          for (let i = currentBatch.length - 1; i >= 0; i--) {
            current = currentBatch[i];

            if (
              current.isAffectingLeaseVar ||
              current.isAffectingCrossReference ||
              current.isAffectingAutoNumber
            ) {
              return true;
            }
          }
        }

        return false;
      }

      function compareObjectsProps(obj1, obj2, arr) {
        let result = true;
        arr.forEach(prop => {
          if (obj1[prop] !== obj2[prop]) {
            result = false;
          }
        });
        return result;
      }

      function getLastPidElementTagName(element) {
        const el = element.querySelectorAll('[pid]').lastItem();
        if (el) {
          return el.tagName;
        } else {
          return null;
        }
      }

      function mergeSameListItemTemplateBlock(
        diffgramItem,
        insertedElement,
        previousItem,
      ) {
        if (
          previousItem &&
          previousItem._expectedParagraph &&
          compareObjectsProps(diffgramItem, previousItem, [
            'PreSection',
            'PostSection',
          ]) &&
          getLastPidElementTagName(insertedElement) ===
          getLastPidElementTagName(previousItem._node) &&
          !(insertedElement.firstElementChild.tagName === 'TABLE')
        ) {

          
          const nodes = Array.from(
            insertedElement.querySelectorAll("[pid]").lastItem().childNodes
          );
          const contentEditable = previousItem._bodyElement.querySelector('.editable');
          const EOCE = contentEditable.querySelector('.EOCE');

          nodes.forEach(node => {
            contentEditable.insertBefore(node, EOCE);
            });

          previousItem._expectedParagraph = false;
          diffgramItem._includedInPreviousItem = true;
        }
      }

      /**
       * update the location of the anchor
       * + skip <p/> at the end of list items
       * + make sure we are not in a table
       */
      function updateDiffgramItemAnchorSection(
        currentAnchor,
        insertedBlock
      ) {
        let anchor = currentAnchor;

        /* update anchor if we are in a table */
        if (
          anchor.closest('table') &&
          insertedBlock &&
          insertedBlock.querySelector('table')
        ) {
          anchor = anchor.closest('table');
        }

        return anchor;
      }

      function insertParagraphInListItem(
        diffgramItem,
        insertedItem,
        previousItem,
      ) {
        if (
          previousItem &&
          compareObjectsProps(diffgramItem, previousItem, [
            'PreSection',
            'PostSection',
          ])
        ) {
          let isListElement = !!diffgramItem.ListHeader;
          if (!isListElement) {
            let isPreviousListElement = previousItem.ListHeader ? true : false;
            if (isPreviousListElement) {
              let targetElement;
              if (previousItem._bodyElement) {
                targetElement = previousItem._bodyElement;
              } else if (previousItem._headerElement) {
                targetElement = previousItem._headerElement;
              }
              if (targetElement) {
                const eoceList = targetElement.querySelectorAll('.EOCE');
                const EOCE = eoceList[eoceList.length -1 ];

                if (eoceList !== 0) {
                  targetElement = eoceList[eoceList.length -1].parentElement;
                }

                insertedItem.childNodes.forEach(node => {
                  targetElement.insertBefore(node, EOCE);
                });

                diffgramItem._includedInPreviousItem = true;
              }
            }
          }
        }
      }

      async function insertBlock(diffgramItem, diffgramIndex, previousItem) {

        let isListElement = !!diffgramItem.ListHeader;
        let sectionId = diffgramItem.PreSection || diffgramItem.PostSection;

        if(!isListElement && diffgramItem.Block == "p"){
          diffgramItem.ListHeader = 'paragraph';
          isListElement = true;
        }

        let anchor = document.querySelector(`[data-vc-id="${sectionId}"]`);        

        if (anchor) {
          sectionId = anchor.getSectionId();
          if (diffgramItem.PreSection) {
            diffgramItem.PreSection = sectionId;
          }
          if (diffgramItem.PostSection) {
            diffgramItem.PostSection = sectionId;
          }
        }


        if (!diffgramItem._pid) {
          diffgramItem.unsupportedItem = true;
          $scope.diffgram.unsupported.push({
            item: diffgramItem,
            description: ERROR_MESSAGES.SECTION_NOT_FOUND(sectionId)
          });
          return null;
        }

        const section = getSection(sectionId);
        if (!section) {
          diffgramItem.unsupportedItem = true;
          $scope.diffgram.unsupported.push({
            item: diffgramItem,
            description: ERROR_MESSAGES.SECTION_NOT_FOUND(sectionId)
          });
          return null;
        }
        
        const container = LeaseEditorService.findClosestContainer(section);

        const insertedBlock = $(
          `<ins list-header="${diffgramItem.ListHeader}" class="${DIFF_CLASSES.ITEM} ${DIFF_CLASSES.INSERTION} ${DIFF_CLASSES.PRIMARY}" diff-item="diffgram.items[${diffgramIndex}]" diff-index="${diffgramIndex}" diff-pid="${diffgramItem._originalParagraphId || diffgramItem._pid}" diff-type="block" ng-click="annotationsHelper.jumpToRequest(${diffgramIndex})"></ins>`,
        );
        
        await insertBlockListItem(insertedBlock, diffgramItem);

        if (diffgramItem.unsupportedItem) {
          return;
        }

        if (diffgramItem.Nested !== -1) {
          insertedBlock[0].setAttribute('diff-type', 'text');
          insertedBlock[0].classList.add(DIFF_CLASSES.NESTED);
          diffgramItem._change = 'text';
        }

        if(!diffgramItem._isBlocked){
          mergeSameListItemTemplateBlock(
            diffgramItem,
            insertedBlock.get(0),
            previousItem,
          );
        }

        if (!diffgramItem._includedInPreviousItem) {
          insertParagraphInListItem(
            diffgramItem,
            insertedBlock.get(0),
            previousItem,
          );

          

          let anchor = container.get(0);
          const el = insertedBlock.get(0).querySelector("[pid]");
          const insBlock = insertedBlock.get(0);
          anchor = updateDiffgramItemAnchorSection(anchor, insBlock);

          if(diffgramItem.Block && diffgramItem.Block == "p" && diffgramItem.inTable == false && anchor.closest('table')){
            const tbodyWithPid = anchor.closest('TBODY[pid]')
            anchor = tbodyWithPid ? tbodyWithPid : anchor.closest('tr[pid], table[pid]');
          }

          let isListElement = !!diffgramItem.ListHeader;

          if (diffgramItem.Nested !== -1) {
            anchor = anchor.querySelector('[data-nested-paragraph-index="' + diffgramItem.Nested + '"]');
            while (anchor && anchor.nextElementSibling && anchor.nextElementSibling.tagName === "INS") {
              anchor = anchor.nextElementSibling;
            }
            insertedBlock.insertAfter(anchor);
          } else if (diffgramItem._position === "before") {
            if (!isListElement) {
              const placeholder = anchor.querySelector(".PLACEHOLDER");
              var insertBeforeElement = placeholder.firstChild;
              while (
                insertBeforeElement.nextSibling &&
                (isInsertion(insertBeforeElement.nextSibling) ||
                  isDeletion(insertBeforeElement.nextSibling))
              ) {
                insertBeforeElement = insertBeforeElement.nextSibling;
              }
              insertBeforeElement.parentElement.insertBefore(
                insertedBlock.get(0),
                insertBeforeElement
              );
            } else {
              while (
                anchor.previousElementSibling &&
                (isInsertion(anchor.previousElementSibling) ||
                  isDeletion(anchor.previousElementSibling) ||
                  (anchor.previousElementSibling &&
                    anchor.previousElementSibling.tagName.toLowerCase() ===
                    "ignore"))
              ) {
                anchor = anchor.previousElementSibling;
              }

              let updateAnchor = anchor.closest('[diff-type="block"]');
              if (updateAnchor) {
                insertedBlock.insertAfter(updateAnchor);
              } else {
                insertedBlock.insertBefore(anchor);
              }
            }
          } else if (diffgramItem._position === "after") {
            if (!isListElement) {
              const eoceList = anchor.querySelectorAll(".EOCE");
              const eoce = eoceList[eoceList.length - 1];
              eoce.parentElement.insertBefore(insertedBlock.get(0), eoce);
            } else {
              while (
                anchor.nextElementSibling &&
                (isInsertion(anchor.nextElementSibling) ||
                  isDeletion(anchor.nextElementSibling) ||
                  (anchor.nextElementSibling.tagName &&
                    (anchor.nextElementSibling.tagName.toLowerCase() === 'ignore' ||
                      (anchor.nextElementSibling.tagName.toLowerCase() === 'generate-table' &&
                        isListElement))
                  ))
              ) {
                anchor = anchor.nextElementSibling;
              }
              let updateAnchor = anchor.closest('[diff-type="block"]');
              if (updateAnchor) {
                insertedBlock.insertAfter(updateAnchor);
              } else {
                insertedBlock.insertAfter(anchor);
              }
            }
          }

          const containingDiffItem = insertedBlock.get(0).parentElement.closest('.diff-item');
          if (containingDiffItem) {
            diffgramItem._isBlocked = true;
            diffgramItem._blockedReason = "You can't accept this change, please make the changes manually";
          }

          insertedBlock.get(0).setAttribute('consolidated-pid', diffgramItem._pid);

          return insertedBlock.get(0);
        } else {
          return null;
        }
      }

      function insertStaticText(diffgramItem, sectionId, html) {
        const section = getSection(sectionId);

        if (!section) {
          diffgramItem.unsupportedItem = true;
          $scope.diffgram.unsupported.push({
            item: diffgramItem,
            description: `Section ${sectionId} can't be found in the DOM, skipping...`,
          });
          return;
        }

        const container = LeaseEditorService.findClosestContainer(section);
        const htmlToInsert = `<ins class="${DIFF_CLASSES.ITEM} ${DIFF_CLASSES.INSERTION} ${DIFF_CLASSES.PRIMARY} ${DIFF_CLASSES.STATIC}">${html}</ins>`;
        $(container).prepend(htmlToInsert);
      }

      function insertTextWithSpanOffset(diffgramItem, diffgramIndex) {
        const anchorNumber =
          diffgramItem.PreSection || diffgramItem.PostSection;
        const anchor = document.querySelector(`[data-vc-id="${anchorNumber}"]`);

        if (!anchor) {
          diffgramItem.unsupportedItem = true;
          $scope.diffgram.unsupported.push({
            item: diffgramItem,
            description: `Section ${anchorNumber} can't be found in the DOM, skipping...`
          });
          return;
        }

        const x = {
          isAffectingLeaseVar: isWithinLeaseVar(anchor),
          isAffectingCrossReference: isWithinCrossReference(anchor),
          isAffectingContentEditable: isWithinContentEditable(anchor),
          isAffectingAutoNumber: isWithinAutoNumber(anchor),
          isAffectingDocumentStructure: isChangeDocumentStructure(anchor)
        };

        const isBlocked = isInsertionRequestBlocked(x);
        const block = anchor.getBlockData();
        const style = getStyle(block.containingNode);
        const contentEditable = anchor.closest(".editable");

        diffgramItem._pid = block.containingNode.getAttribute("pid");

        diffgramItem._isResolvable =
          !x.isAffectingCrossReference && !x.isAffectingAutoNumber;
        diffgramItem._isAffectingCrossReference = x.isAffectingCrossReference;
        diffgramItem._isAffectingLeaseVar = x.isAffectingLeaseVar;
        diffgramItem._isAffectingContentEditable = x.isAffectingContentEditable;
        diffgramItem._isAffectingAutoNumber = x.isAffectingAutoNumber;
        diffgramItem._isAffectingDocumentStructure =
          x.isAffectingDocumentStructure;
        diffgramItem._isBlocked = isBlocked;

        if (isBlocked) {
          if (!x.isAffectingContentEditable) {
            diffgramItem._blockedReason =
              "Accepting this change is not supported. Make any needed changes in the lease body itself and mark as resolved when finished.";
          } else if (x.isAffectingLeaseVar) {
            diffgramItem._blockedReason =
              "You can't accept changes that affects the automation, please make the changes manually";
          } else if (x.isAffectingCrossReference || x.isAffectingAutoNumber) {
            diffgramItem._blockedReason =
              "You can't accept changes that affects cross-references, please make the changes manually";
          }
        }

        let insertElement = null;

        if (diffgramItem.PostSection) {
          insertElement = document.createElement("ins");
          insertElement.innerHTML = diffgramItem.Html;
          anchor.parentElement.insertBefore(insertElement, anchor);
        } else {
          insertElement = anchor.insertInsElement(
            diffgramItem.Offset,
            diffgramItem.Html,
            !isBlocked
          );
        }

        
        insertElement.classList.add(
          DIFF_CLASSES.ITEM,
          DIFF_CLASSES.INSERTION,
          DIFF_CLASSES.PRIMARY
        );
        insertElement.setAttribute(
          "diff-item",
          `diffgram.items[${diffgramIndex}]`
        );
        insertElement.setAttribute("diff-index", `${diffgramIndex}`);
        insertElement.setAttribute(
          "diff-pid",
          `${diffgramItem._originalParagraphId || diffgramItem._pid}`
        );
        insertElement.setAttribute("diff-type", `text`);
        insertElement.setAttribute(
          "ng-click",
          `annotationsHelper.jumpToRequest(${diffgramIndex})`
        );
        if (isBlocked) {
          insertElement.classList.add(DIFF_CLASSES.BLOCKED);
        }
      }

      


      function deleteStaticText(diffgramItem, sectionId) {
        const section = getSection(sectionId);

        if (!section) {
          diffgramItem.unsupportedItem = true;
          $scope.diffgram.unsupported.push({
            item: diffgramItem,
            description: `Section ${sectionId} can't be found in the DOM, skipping...`,
          });
          return;
        }

        const container = LeaseEditorService.findClosestContainer(section);
        $(container)
          .find('[list]')
          .addClass(`${DIFF_CLASSES.ITEM} ${DIFF_CLASSES.STATIC} ${DIFF_CLASSES.DELETION}`);
      }

      function deleteTextWithSpanOffset(
        diffgramItem,
        diffgramIndex,
        anchorSectionId,
      ) {
        // `anchorSectionId` is only passed for an anchor recovered by matching
        // the deleted text at `Offset`; it wins over the field order below
        // because it is the one anchor that has been proved.
        const anchorNumber =
          anchorSectionId ||
          diffgramItem.PreSection ||
          diffgramItem.PostSection ||
          diffgramItem.Section;
        const anchor = document.querySelector(`[data-vc-id="${anchorNumber}"]`);

        if (!anchor) {
          diffgramItem.unsupportedItem = true;
          $scope.diffgram.unsupported.push({
            item: diffgramItem,
            description: ERROR_MESSAGES.SECTION_NOT_FOUND(anchorNumber)
          });
          return;
        }
        
        let block;
        try {
          block = anchor.getBlockData();
          if (!block || !block.containingNode) {
            throw new Error('Block data or containing node is null');
          }
          const pid = block.containingNode.getAttribute("pid");
          if (pid) {
            diffgramItem._pid = pid;
          }
        } catch (ex) {
          console.error('[version control] Error getting block data for deletion', ex);
          diffgramItem.unsupportedItem = true;
          $scope.diffgram.unsupported.push({
            item: diffgramItem,
            description: ERROR_MESSAGES.SECTION_NOT_FOUND(anchorNumber)
          });
          return;
        }

        if(diffgramItem.Length == 0){
          diffgramItem.unsupportedItem = true;
          $scope.diffgram.unsupported.push({
            item: diffgramItem,
            description: ERROR_MESSAGES.LENGTH_ZERO_DELETE(
              diffgramItem.Section,
              anchor.textContent,
              diffgramItem.Offset,
              diffgramItem.Length
            ),
          });
          return;
        }

        // fix offset for fisrt node in list item and blocks
        if (
          diffgramItem.Offset + diffgramItem.Length >
          anchor.textContent.length
        ){
          const prevTextNode = anchor.childNodes[0].getPreviousTextNodeInBlock();
          
          if(prevTextNode && prevTextNode.parentElement && prevTextNode.parentElement.classList.contains('word-indent')){
            diffgramItem.Offset = 0;
          }
          else if(prevTextNode && !prevTextNode.parentElement.closest('[free-text]')){            
            diffgramItem.Offset = 0;
          }
        }

        
        if (
          diffgramItem.Offset + diffgramItem.Length <=
          anchor.textContent.length
        ) {
          const leaseVar = anchor.closest('lease-var');
          const crr = anchor.closest('crr');
          let delElement = null;

          // Extract duplicate code to single call
          delElement = anchor.insertDelElement(
            diffgramItem.Offset,            
            diffgramItem.Length
          );

          if (delElement) {

            

            const x = {
              isAffectingLeaseVar : isWithinLeaseVar(anchor),
              isAffectingCrossReference : isWithinCrossReference(anchor),
              isAffectingContentEditable : isWithinContentEditable(anchor),
              isAffectingAutoNumber : isWithinAutoNumber(anchor),
              isAffectingDocumentStructure : isChangeDocumentStructure(anchor),

            }



            diffgramItem._isResolvable = !x.isAffectingCrossReference && !x.isAffectingAutoNumber;
            diffgramItem._isAffectingCrossReference = x.isAffectingCrossReference;
            diffgramItem._isAffectingLeaseVar = x.isAffectingLeaseVar;
            diffgramItem._isAffectingContentEditable = x.isAffectingContentEditable;
            diffgramItem._isAffectingAutoNumber = x.isAffectingAutoNumber;
            diffgramItem._isAffectingDocumentStructure = x.isAffectingDocumentStructure;
            


            delElement.classList.add(
              DIFF_CLASSES.ITEM,
              DIFF_CLASSES.DELETION,
              DIFF_CLASSES.PRIMARY,
            );

            
            if(leaseVar && leaseVar.contains(delElement)){
              delElement.classList.add(DIFF_CLASSES.BLOCKED);
              diffgramItem._isBlocked = true;
            }

            if(crr && crr.contains(delElement)){
              delElement.classList.add(DIFF_CLASSES.BLOCKED);
              diffgramItem._isBlocked = true;
            }

            delElement.setAttribute(
              "diff-item",
              `diffgram.items[${diffgramIndex}]`
            );
            const freeTextElement = delElement.closest('[free-text]');
            if (freeTextElement) {
              const freeTextId = freeTextElement.getAttribute('free-text');
              if (freeTextId) {
                delElement.setAttribute('diff-partial-section-id', freeTextId);
              }
            }
            
            delElement.setAttribute("diff-index", `${diffgramIndex}`);
            delElement.setAttribute(
              "diff-pid",
              `${diffgramItem._originalParagraphId || diffgramItem._pid}`
            );
            delElement.setAttribute("diff-type", `text`);
            delElement.setAttribute(
              "ng-click",
              `annotationsHelper.enterIndividualRequestMode(${diffgramIndex})`
            );

            /// test if we need to delete block

            if(block.containingNode.areAllTextNodesMarkedForDelete()){

              const delList = block.containingNode.querySelectorAll('del');
              
              delList.forEach((del)=>{
                del.childNodes.forEach((c)=>{
                  del.parentElement.insertBefore(c,del);
                });

                const diffItemToUpdate = $scope.diffgram.items[del.getAttribute('diff-index')];
                diffItemToUpdate._isGhostItem = true;
                del.remove();
              });
              
              const newDiffitem = {
                Block: block.containingNode.tagName,
                IsBullet: false,
                IsStatic: false,
                Length: DEFAULT_BLOCK_LENGTH,                
                Nested: -1,
                Node: null,
                Offset: 0,
                PostSection: "",
                PreSection: "",
                Section: `${anchorNumber}`,
                TableId: "",
                Type: DIFF_TYPES.DELETION,
              }
              $scope.diffgram.items.splice(diffgramIndex+1,0,newDiffitem);
            }
            
          } 
        } else {
          
          diffgramItem.unsupportedItem = true;
          $scope.diffgram.unsupported.push({
            item: diffgramItem,
            description: ERROR_MESSAGES.SECTION_TOO_SHORT(
              diffgramItem.Section,
              anchor.textContent,
              diffgramItem.Offset,
              diffgramItem.Length
            ),
          });
        }

        
      }

      function generateTableHTML(container) {
        let html;

        if (container.tagName === 'TABLE') {
          html = container.getVisibleElements().outerHTML;
        } else if (container.tagName === 'TR') {
          // TODO: check if this code can be removed now that the annotation now
          // previews an entire table
          const newRow = container.cloneNode(true);
          const newTable = container.closest('table').cloneNode(true).getVisibleElements();
          newTable.innerHTML = '';
          newTable.appendChild(newRow);
          html = newTable.outerHTML;
        }

        return html;
      }

      function getIncomingHtml(diffgramItem) {
        let html = diffgramItem.Html;
        html = html.replace(/<del/g, '<1del').replace(/<\/del/g, '</1del');
        html = html.replace(/<ins/g, '<del').replace(/<\/ins/g, '</del');
        html = html.replace(/<1del/g, '<ins').replace(/<\/1del/g, '</ins');

        let el = document.createElement("el");
        el.innerHTML = html;
        el.querySelectorAll("ins, del").forEach((node, index) => {
          if (node.tagName === "DEL") {
            node.classList.add(
              DIFF_CLASSES.ITEM,
              DIFF_CLASSES.DELETION,
              DIFF_CLASSES.BLOCKED,
            );
          } else if (node.tagName === 'INS') {
            node.classList.add(
              DIFF_CLASSES.ITEM,
              DIFF_CLASSES.INSERTION,
              DIFF_CLASSES.BLOCKED,
            );
          }

          node.classList.add(DIFF_CLASSES.PRIMARY);

          if (diffgramItem._isChange) {
            node.classList.add(DIFF_CLASSES.CHANGE);
          } else if (diffgramItem._isComplexTable) {
            node.classList.add(DIFF_CLASSES.COMPLEX_TABLE);
          } else if (diffgramItem._isComplexText) {
            node.classList.add(DIFF_CLASSES.COMPLEX_BLOCK);
          }

          node.setAttribute('diff-index', diffgramItem._index);
          node.style = {};
        });

        return el.innerHTML;
      }

      function getHtml(diffgramItem, container) {
        let html;

        container = container.getTopAnchor();
        if (container.parentElement.closest('.diff-item')) {
          container = container.parentElement.closest('.diff-item');
        }

        if (diffgramItem.Type === 'change' || diffgramItem.Type === 'complex table'  || diffgramItem.Type === 'complex block' || diffgramItem._isUnanchoredDeletion) {
          // An unanchored deletion has no marker in the container to copy, so
          // its card is built from its own text the way a complex block's is.
          html = diffgramItem.Html;
        } else {
          if (
            container.tagName.toLowerCase() === 'tr' ||
            container.tagName.toLowerCase() === 'table'
          ) {
            html = generateTableHTML(container);
          } else {
            html = container.getVisibleElements().outerHTML;  
          }
        }

        return html;
      }

      function cleanHtml(item, html) {
        html = html.replace(/{{.*?}}/g, '');

        html = safeSanitize(html, {
          allowedTags: [
            'b',
            'strong',
            'i',
            'u',
            'p',
            'span',
            'div',
            'br',
            'img',
            'font',
            'table',
            'thead',
            'tbody',
            'tfoot',
            'colgroup',
            'col',
            'tr',
            'th',
            'td',
            'ins',
            'del',
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            'crr',
            'crb',
            'sup',
            'sub',
            'lease-var',
          ],
          allowedAttributes: {
            a: ['href', 'name', 'target'],
            img: ['src', 'width', 'height'],
            table: ['cellspacing', 'cellpadding', 'border'],
            td: ['colspan', 'rowspan'],
            span: ['style', 'class', 'var', 'ref'],
            ins: ['*'],
            del: ['*'],
            'lease-var': ['ng-attr-name', 'non-click'],
            '*': ['id', 'style', 'class', 'title', 'var'],
          },
          allowedClasses: {
            span: [
              'word-indent',
              'diff-item',
              'diff-item--static',
              'diff-item--insertion',
              'diff-item--deletion',
              'diff-item--blocked',
              'diff-item--current',
              'diff-item--accepted',
              'diff-item--rejected',
              'diff-item--resolved',
              'diff-item--partial',
              'diff-item--partial--first',
              'diff-item--partial--middle',
              'diff-item--partial--last',
              'transformed--lease-var',
              'transformed--crr',
            ],
          },
          allowedSchemes: ['http', 'https'],
          allowedSchemesByTag: {
            img: ['data', 'http', 'https'],
          },
        });

        let dummy = document.createElement('dummy');
        dummy.innerHTML = html;

        if(!item.Block){
          dummy.querySelectorAll('[diff-type="block"]').forEach(item => {
            item.remove();
          });
        }

        const pTags = dummy.querySelectorAll('p');
        pTags.forEach(pTag => {
          const div = document.createElement('div');
          div.innerHTML = pTag.innerHTML;
          div.classList.add("transformed--paragraph");
          div.setAttribute("style", pTag.getAttribute("style"));
          pTag.replaceWith(div);
        });
        
        const leaseVarTags = dummy.querySelectorAll('lease-var');
        leaseVarTags.forEach(leaseVarTag => {
          const span = document.createElement('span');
          span.innerHTML = leaseVarTag.innerHTML;
          span.classList.add("transformed--lease-var");
          span.setAttribute("var", leaseVarTag.getAttribute("ng-attr-name"));
          span.setAttribute("non-click", leaseVarTag.getAttribute("non-click") == 'true');
          leaseVarTag.replaceWith(span);
        });

        const crrTags = dummy.querySelectorAll('crr');
        crrTags.forEach(crrTag => {
          const span = document.createElement('span');
          span.innerHTML = crrTag.innerHTML;
          span.classList.add("transformed--crr");
          span.setAttribute("ref", crrTag.id);
          crrTag.replaceWith(span);
        });

        const nodes = dummy.querySelectorAll('.transformed--paragraph');
        nodes.forEach(node => {
          if (LeaseEditorService.isNodeWordStyle($(node))) {
            node.remove();
          }
        });

        const elementsToRemove = dummy.querySelectorAll(
          ".lp-hide-element, .remove-on-download, .deleted-container, .deleted-page-break, .deleted-element"
        );
        for (
          let removeIndex = 0;
          removeIndex < elementsToRemove.length;
          removeIndex++
        ) {
          elementsToRemove[removeIndex].remove();
        }

        html = dummy.innerHTML;

        return html;
      }
      $scope.removeVersionControlTagsFromDefaultsAndFreeText = function () {
        let itemsList = document.querySelectorAll('.diff-item');
        let sectionIds = [];
        itemsList.forEach((item) => {
          let sectionIdElement = item.closest('[section-id]');
          if(sectionIdElement){
            let sectionId = sectionIdElement.getAttribute('section-id');
            if (sectionIds.indexOf(sectionId) === -1) {
              sectionIds.push(sectionId);
            }
          }
        });
        sectionIds.forEach((sectionId) => {
          let freeTextUpdated = $scope.leaseFreeTexts[sectionId];
          let defaultProvision = $scope.defaultProvisions[sectionId];
          if (freeTextUpdated) {
            freeTextUpdated.text = CleanFreeTextService.cleanHTMLText(freeTextUpdated.text);
          } else if (defaultProvision) {
            defaultProvision.html = CleanFreeTextService.cleanHTMLText(defaultProvision.html);
          }
        });

      }
      function updateDefaultsAndFreeTextValues() {

        let itemsList = document.querySelectorAll('.diff-item');
        let sectionIds = [];
        itemsList.forEach((item) => {
          let sectionIdElement = item.closest('[section-id]');
          if(sectionIdElement){
            let sectionId = sectionIdElement.getAttribute('section-id');
            if (sectionIds.indexOf(sectionId) === -1) {
              sectionIds.push(sectionId);
            }
          }
        });
        sectionIds.forEach((sectionId) => {
          let freeTextUpdated = $scope.leaseFreeTexts[sectionId];
          let defaultProvision = $scope.defaultProvisions[sectionId];
          let currentHtmlWIthVersionControlTags = CleanFreeTextService.cleanElementBySectionId(sectionId, true);
          if (freeTextUpdated) {
            freeTextUpdated.text = currentHtmlWIthVersionControlTags;
          } else if (defaultProvision) {
            defaultProvision.html = currentHtmlWIthVersionControlTags;
          }
          else{
            hash =  LeaseEditorService.prepareForComparison(currentHtmlWIthVersionControlTags).hashCode();
            $scope.defaultProvisions[sectionId] = {hashCode :hash , html : currentHtmlWIthVersionControlTags , versionControlDeafult : true,};
          }
        });
      }

      function printVcStatus() {

        const allRequests = $scope.diffgram.actual;
        const normalRequests = allRequests
          .filter(item => !item.classList.contains(DIFF_CLASSES.BLOCKED))
          .map(item => $scope.diffgram.items[item.getAttribute('diff-index')]);
        const blockedRequests = allRequests
          .filter(item => item.classList.contains(DIFF_CLASSES.BLOCKED))
          .map(item => $scope.diffgram.items[item.getAttribute('diff-index')]);
        const insertions = allRequests
          .filter(item => item.classList.contains(DIFF_CLASSES.INSERTION))
          .map(item => $scope.diffgram.items[item.getAttribute('diff-index')]);
        const deletions = allRequests
          .filter(item => item.classList.contains(DIFF_CLASSES.DELETION))
          .map(item => $scope.diffgram.items[item.getAttribute('diff-index')]);
        const changes = allRequests
          .filter(item => item.classList.contains(DIFF_CLASSES.CHANGE) && item.classList.contains(DIFF_CLASSES.PRIMARY))
          .map(item => $scope.diffgram.items[item.getAttribute('diff-index')]);
        const complexTables = allRequests
          .filter(item => item.classList.contains(DIFF_CLASSES.COMPLEX_TABLE) && item.classList.contains(DIFF_CLASSES.PRIMARY))
          .map(item => $scope.diffgram.items[item.getAttribute('diff-index')]);
        const complexBlocks = allRequests
          .filter(item => item.classList.contains(DIFF_CLASSES.COMPLEX_BLOCK) && item.classList.contains(DIFF_CLASSES.PRIMARY))
          .map(item => $scope.diffgram.items[item.getAttribute('diff-index')]);
        const relocations = allRequests
          .filter(item => item.classList.contains(DIFF_CLASSES.RELOCATED))
          .map(item => $scope.diffgram.items[item.getAttribute('diff-index')]);

        $scope.diffgram.status = {
          all: allRequests,
          normal: normalRequests,
          blocked: blockedRequests,
          insertions: insertions,
          deletions: deletions,
          changes: changes,
          complexTables: complexTables,
          complexBlocks: complexBlocks,
          relocations: relocations,
          unsupported: $scope.diffgram.unsupported,
        };

        const groupedBlockedRequests = _.groupBy(blockedRequests, '_blockedReason');
        const groupedBlockedRequestsKeys = Object.keys(groupedBlockedRequests);
        const groupedAllBlocked = [{
          name: 'All',
          value: blockedRequests,
        }];
        groupedBlockedRequestsKeys.forEach(block => {
          groupedAllBlocked.push({
            name: block,
            value: groupedBlockedRequests[block],
          });
        });

        $scope.diffgram.status.blocked = groupedAllBlocked;

        const vcStatus =
        [
          { "All requests": allRequests },
          { "Normal requests": normalRequests },
          { "Blocked requests": groupedAllBlocked },
          { "Insertions": insertions },
          { "Deletions": deletions },
          { "Changes": changes },
          { "Complex Tables": complexTables },
          { "Complex Blocks": complexBlocks },
          { "Relocations": relocations },
          { 'Unsupported requests': $scope.diffgram.unsupported },
        ]

        console.log("Version Control", vcStatus);
      }

      function createAnnotations(items, callback) {

        setTimeout(() => {
          let container;
          let previousAnnotation = null;
          const unanchoredDeletions = [];

          items = items.filter(item => {
            return !item.unsupportedItem && !item._isGhostItem ;
          });

          for (let index = 0; index < items.length; index++) {
            let item = items[index];

            // Held back to attachUnanchoredDeletions: this card's html is the
            // item's own text rather than a snapshot of a paragraph, so
            // running it through the loop below would assign that bare text
            // over the card the paragraph's own requests had already built.
            if (item._isUnanchoredDeletion) {
              unanchoredDeletions.push(item);
              continue;
            }

            if (!item.IsStatic) {
              let containerLookupId;
              if (item.ListHeader) {
                containerLookupId = item.PostSection ?
                  item.PostSection :
                  item.PreSection;
              } else {
                containerLookupId = item._pid;
              }

              container = getParagraph(containerLookupId);

              if (container && container.closest('tr')) {
                container = container.closest('tr');
              }

              if (item.ListHeader && item._node) {
                container = item._node;
              }

              if (!container && item._node) {
                container = item._node;
              }

              if (item.Nested !== -1 && item.Block && item.Type === 'ins') {
                container = container.parentElement.closest('[pid]');
                item._pid = container.getAttribute('pid');
                delete item._node;
              }

              if (!container) {
                item.unsupportedItem = true;
                $scope.diffgram.unsupported.push({
                  item,
                  description: `pid ${item._pid} can't be found in the DOM, skipping...`,
                });
                continue;
              }

              if (container.parentElement.closest('[pid]')) {
                container = container.getTopAnchor();
                item._originalParagraphId = item._pid;
                item._pid = container.getAttribute('pid');

                if (item._node) {
                  item._node.setAttribute('consolidated-pid', item._pid);
                }
              }

              let html;
              html = getHtml(item, container);
              html = cleanHtml(item, html);

              let pid;
              // test for consolidation option
              if (item && item._node) {
                let node = item._node;
                while (
                  node.previousElementSibling &&
                  node.previousElementSibling.tagName === item._node.tagName
                ) {
                  node = node.previousElementSibling;
                }
                pid = node.getAttribute('consolidated-pid');
              } else {
                pid = item._pid;
              }

              let consolidateItem = item._pid !== pid;
              item._isConsolidatedItem = consolidateItem;

              let isForceConsolidate = false;
              if(previousAnnotation && previousAnnotation.container.querySelector('[diff-index="'+index+'"]')){
                consolidateItem = true;
                isForceConsolidate = true;
              }

              $scope.annotationsHelper.setDiffgramItemActions(item);
              if (!$scope.annotations[pid]) {
                let items = [];

                if (item.Type === 'change' || item.Type === 'complex table') {
                  const annotationNode = document.createElement('dummy');
                  annotationNode.innerHTML = html;

                  annotationNode
                    .querySelectorAll(`.${DIFF_CLASSES.ITEM}.${DIFF_CLASSES.PRIMARY}:not(.${DIFF_CLASSES.STATIC})`)
                    .forEach((diffItem) => {
                      let type;

                      if (diffItem.classList.contains(DIFF_CLASSES.INSERTION)) {
                        type = "ins";
                      } else if (diffItem.classList.contains(DIFF_CLASSES.DELETION)) {
                        type = "del";
                      }

                      items.push({
                        ...item,
                        Type: type,
                      });
                    });
                } else {
                  items.push(item);
                }

                consolidateItem = false;
                $scope.annotations[pid] = {
                  container,
                  pid,
                  type: 'diff',
                  items,
                  html,
                  status: 'unresolved',
                  isBlocked: item._isBlocked,
                  isResolvable: item._isResolvable,
                  isManageable: item.Type !== 'change',
                  index: Object.keys($scope.annotations).length,
                  _showAcceptReject: item._showAcceptReject,
                  _showAdjustManually: item._showAdjustManually,
                  _showWarnings: item.__showWarnings,
                };
              } else {
                if (consolidateItem) {
                  if(!isForceConsolidate){
                    $scope.annotations[pid].html += html;
                    $scope.annotations[pid]._isConsolidated = true;
                  } else {
                    if ($scope.annotations[pid].items[0].Type === "complex table") {
                      $scope.annotations[pid].html = $scope.annotations[pid].items[0].Html + html;
                    } else {
                      $scope.annotations[pid].html = html;
                    }
                  }
                } else {
                  $scope.annotations[pid].html = html;
                }
                $scope.annotations[pid].items.push(item);
                $scope.annotations[pid].isBlocked =
                  $scope.annotations[pid].isBlocked && item._isBlocked;

                if (!item._showAcceptReject) {
                  $scope.annotations[pid]._showAcceptReject = false;
                }
                if (!item._showAdjustManually) {
                  $scope.annotations[pid]._showAdjustManually = false;
                }
                if (item._showWarnings) {
                  $scope.annotations[pid]._showWarnings = true;
                }
              }

              let dummy = document.createElement('dummy');
              dummy.innerHTML = $scope.annotations[pid].html;

              item._annotation = $scope.annotations[pid];
              previousAnnotation =  $scope.annotations[pid];
              $scope.annotations[pid].insertions = dummy.querySelectorAll(`ins.${DIFF_CLASSES.PRIMARY}:not(.${DIFF_CLASSES.STATIC})`);
              $scope.annotations[pid].deletions = dummy.querySelectorAll(`del.${DIFF_CLASSES.PRIMARY}:not(.${DIFF_CLASSES.STATIC})`);
            }
          }

          attachUnanchoredDeletions(unanchoredDeletions);
          callback();
        });
      }

      // A deletion the import could not place still has to be reviewable, so
      // it is filed as an extra blocked entry on the card of the last
      // paragraph the import did resolve, carrying the counterparty's text.
      // It is appended rather than assigned, and only once every other card is
      // final, because its html is the item's own text and not the paragraph's.
      function attachUnanchoredDeletions(unanchoredDeletions) {
        for (let index = 0; index < unanchoredDeletions.length; index++) {
          const item = unanchoredDeletions[index];
          const container = getParagraph(item._pid);

          if (!container) {
            item.unsupportedItem = true;
            continue;
          }

          const pid = item._pid;
          const html = cleanHtml(item, item.Html);

          $scope.annotationsHelper.setDiffgramItemActions(item);

          if (!$scope.annotations[pid]) {
            $scope.annotations[pid] = {
              container,
              pid,
              type: 'diff',
              items: [item],
              html,
              status: 'unresolved',
              isBlocked: item._isBlocked,
              isResolvable: item._isResolvable,
              isManageable: false,
              index: Object.keys($scope.annotations).length,
              _showAcceptReject: item._showAcceptReject,
              _showAdjustManually: item._showAdjustManually,
              _showWarnings: item._showWarnings,
            };
          } else {
            $scope.annotations[pid].html += html;
            $scope.annotations[pid].items.push(item);
            $scope.annotations[pid].isBlocked =
              $scope.annotations[pid].isBlocked && item._isBlocked;
            $scope.annotations[pid]._showAcceptReject = false;
          }

          item._annotation = $scope.annotations[pid];

          const dummy = document.createElement('dummy');
          dummy.innerHTML = $scope.annotations[pid].html;
          $scope.annotations[pid].insertions = dummy.querySelectorAll(
            `ins.${DIFF_CLASSES.PRIMARY}:not(.${DIFF_CLASSES.STATIC})`,
          );
          $scope.annotations[pid].deletions = dummy.querySelectorAll(
            `del.${DIFF_CLASSES.PRIMARY}:not(.${DIFF_CLASSES.STATIC})`,
          );
        }
      }

      function getStyle(node) {
        let style = '';

        if (node && node.nodeType === Node.TEXT_NODE) {
          let span;
          let parent = node.parentElement;
          let tagName = parent.tagName.toLowerCase();

          if (tagName === 'span') {
            span = parent;

            if (span.getAttribute('contenteditable')) {
              span = parent.closest('span:not([contenteditable])');
            }
          } else {
            span = parent.closest('span:not([contenteditable])');
          }

          if (span) {
            style = span.getAttribute('style');
          }
        }

        if (style) {
          style = style.replace(/"/g, "'");
        }

        return style;
      }

      startProcess();
    };

    window.showDiffgram = $scope.showDiffgram;

    /**
     * =================================================================================
     */

    $scope.completeLoading = function() {
      preserveAsposeCustomStyles();

      const freeTexts = document.querySelectorAll('p:not([pid]):has([free-text], [free-text-placeholder]), tr:not([pid]):has([free-text], [free-text-placeholder]), table:not([pid]):has([free-text], [free-text-placeholder])');
      for (let i = 0; i < freeTexts.length; i++) {
        if (freeTexts[i].parentElement.nodeName.toLowerCase() !== 'generate-table') {
          let current = freeTexts[i];
          let firstFreeText = current.querySelector("[free-text], [free-text-placeholder]")
          let firstFreeTextId = firstFreeText.getAttribute("free-text") || firstFreeText.getAttribute("free-text-placeholder");
          current.setAttribute("pid", firstFreeTextId);
        }
      }

      setTimeout(()=>{
        $timeout(function() {
          const tables = document.querySelectorAll('.lease table');
          for (let tableIndex = 0; tableIndex < tables.length; tableIndex++) {
            if (
              !tables[tableIndex].hasAttribute('pid') &&
              tables[tableIndex].parentElement.nodeName.toLowerCase() !== 'generate-table'
            ) {
              const currentPidElement = tables[tableIndex].querySelector('[pid]');
              if (currentPidElement) {
                const pid = currentPidElement.getAttribute('pid');
                tables[tableIndex].setAttribute('pid', pid);
              } else if (!tables[tableIndex].closest('[pid]')) {
                console.log(tables[tableIndex]);
              }
            }
          }

          const uls = document.querySelectorAll('.lease ul');
          let li;
          let freeTextId;
          for (let ulIndex = 0; ulIndex < uls.length; ulIndex++) {
            li = uls[ulIndex].querySelector("li.empty-li");

            if (li) {
              li.innerText = window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_NO_BREAKING_SPACE;
            }

            if (uls[ulIndex].hasAttribute('free-text')) {
              freeTextId = uls[ulIndex].getAttribute('free-text');
            } else {
              freeTextId = uls[ulIndex].getAttribute('free-text-placeholder');
            }
            const div = document.createElement('div');
            div.setAttribute('pid', freeTextId);
            uls[ulIndex].parentElement.insertBefore(div, uls[ulIndex]);
            div.insertBefore(uls[ulIndex], null);
          }

          /* add editor helper polyfill for first text id and Comments End   */
          
          pageLayersManager.updatePageBreaks();

          $scope.inInitialLoad = false;
          $scope.addArticleDisabled = true;

          //create list items map
          listItemsManager.updateListItemsLevelsInDom();
          listItemsManager.createListItemsMap();

          var branch = window._current_deploy_branch;
          var timestamp = window._current_deploy_timestamp;
          var company = $scope.lease.company.name;
          var majorVersion = parseInt($scope.lease.version);
          var minorVersion =
            $scope.lease.minorVersion == null
              ? $scope.lease.form.latestMinorVersion
              : $scope.lease.minorVersion;
          var documentType = !$scope.lease.type
            ? 'lease'
            : $scope.lease.type.toLowerCase();
          var buildingVersion = $scope.lease.form.buildingVersion;
          var asset = $scope.lease.building
            ? $scope.lease.building.displayName
            : 'blank';
          var flavor = $scope.lease.form.flavor.toLowerCase();

          if (!!branch && !!timestamp) {
            console.log(
              "%cVersion was deployed from branch '" +
                branch +
                "' at " +
                timestamp,
              'color: blue; font-size: 16px;',
            );
          }
          console.log('%cCompany: ' + company, 'color: blue; font-size: 16px;');
          console.log(
            '%cLease Version: ' +
              majorVersion +
              '.' +
              minorVersion,
            'color: blue; font-size: 16px;',
          );
          console.log(
            '%cDocument Type: ' + documentType,
            'color: blue; font-size: 16px;',
          );
          console.log('%cFlavor: ' + flavor, 'color: blue; font-size: 16px;');
          console.log(
            '%cBuilding Version (' +
              (window.isLocalBuildingInformation ? 'local' : 'remote') +
              '): ' +
              buildingVersion,
            'color: blue; font-size: 16px;',
          );
          console.log('%cAsset: ' + asset, 'color: blue; font-size: 16px;');

          calcLastFormUpdate();

          $scope.bindLeaseEvents();
          LeaseEditorService.bindImgEvents($('.new-paragraph img'), $scope);
          $scope.resetHighlighting();
          $scope.updateReleaseGuarantors();
          $scope.hideWhiteSpaceInOldTables();
          $scope.appendEditingSpaceToLeaseVars();
          $scope.initializeVersionControl();

          // move normal list items to paragraphs Start
          // after reload all "normal" list items will be converted to "paragraph" list items
          
          const listItemsData = $scope.lease.orderedListitem
          ? JSON.parse($scope.lease.orderedListitem)
          : [];

          let shouldUpdateListitems = false;

          listItemsData.forEach((item)=>{
            if(item.level === "normal"){
              shouldUpdateListitems = true;
              item.level = "paragraph";
            }
          });

          if(shouldUpdateListitems){
            $scope.lease.orderedListitem = JSON.stringify(listItemsData);
            
          }

          // move normal list items to paragraphs End
          

          // insert all list items
          $scope.setListItems().then(function() {
            $scope.restyle();

            // refresh lists after initial load
            $scope.inInitialLoad = false;

            $scope.safeApply(() => {
              $scope.isLoading = false;
              $('body').removeClass('skeleton-loader');
            });

            hideEmptyElements();

            $scope.structureChanged();

            $('.lease')
              .not('.lease--modified')
              .find('span:not(.SOCE):not(.EOCE)')
              .css('white-space', 'pre-wrap');

            $('table').css('float', '');

            fixSubSupTextDecorations();
            fixEmptyParagraphs();
            fixInsertedImages();
            fixGenerateTableFirstColumnStyles();

            /* temp fix to remove spaces from list items  templates will be moved
            TODO: move this to the editorHelper model */
            document
              .querySelectorAll('p[inserted-list-item-id]>span>span, p.list-item-override>span>span')
              .forEach(node => {
                node.parentElement.cleanEmptyTextNodes();
              });
            /* end tmp fix */

            // marks prerender that document is ready
            // start download
            $window.prerenderReady = true;

          
            versionControlManager.init(
              $scope,
              $rootScope,
              null,
              ApiService,
              LeaseEditorService
            );
            
            
            if(window.downloadForVersionControl){
              versionControlManager.setIds();
              downloadManager.prepareDownload("diffgramOriginal",$rootScope);
            }
            else if ($scope.download) {
              setTimeout(function() {
                setTimeout(function() {
                  downloadManager.prepareDownload();
                });
              });
              // convert all images (with 'src' set and except for AWS) to embedded Base64 before download
              // end or download
            } else {
              $scope.safeApply(() => {
                $scope.isLoading = false;
                $('body').removeClass('skeleton-loader');
                console.log('%cLoad time: ' + performance.now() / 1000 + " seconds", 'color: red; font-size: 16px;');
              });
            }
          });

          if (!$scope.isPrerender) {
            $timeout(function() {
              if ($scope.lease.tenantInfo && $scope.building) {
                LeaseEditorService.setLeaseTitle();
              }
            }, 200);
          }

          // run the total-rent calculation on load
          $scope.calculateRentPSF($scope.lease);
          if ($scope.lease.renewalInfo && $scope.lease.renewalInfo.length > 0) {
            $scope.calculateRenewalPSF($scope.lease);
          }
          $scope.$apply();

          $rootScope.$on('showDiffgram', function(event, data) {
            $scope.showDiffgram(data.diffgram);
          });

          $scope.compileVisibleFreeTexts();
          
          setTimeout(()=>{
            if(window?.location?.href.indexOf('smlView')!== -1 && leaseIfManager){
              leaseIfManager.toggleShowAllView();
            }
          },100)

          if ($scope.isClausebookEnabled) {
            clausebookManager.init($scope, $mdDialog);
          }

          if ($scope.isCopilotEnabled) {
            autopilotManager.init($scope, $mdDialog);
          }

          // Final safety: ensure all tabs are rendered after initial load completes
          if (window.tabRendererInit && typeof window.tabRendererInit.renderAllTabs === 'function') {
            var leaseOriginal = document.querySelector('.lease.lease--original') || document.querySelector('.lease');
            if (leaseOriginal) {
              setTimeout(function() {
                setTimeout(function() {
                  window.tabRendererInit.renderAllTabs(leaseOriginal);
                });
              });
            }
          }
        }, 100);
      });
    };

    $scope.getFreeText = function() {
      $scope.setFreeTexts();
    };

    // invoked as a callback after fetching free text objects
    $scope.setFreeTexts = function() {
      // convert the free text array into an object indexed on the section ID
      if (Object.keys($scope.leaseFreeTexts).length === 0) {
        $scope.leaseFreeTexts = $scope.lease.freeTexts
          ? $scope.lease.freeTexts.reduce(function(o, v) {
              o[v.sectionId] = v;
              return o;
            }, {})
          : null;
      }
    };

    $scope.getBase64Image = function(img) {
      // Create an empty canvas element
      var canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      // Copy the image contents to the canvas
      var ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      var dataURL = canvas.toDataURL('image/png');
      return dataURL;
    };

    $scope.setCompany = function() {
      $scope.companyName = $scope.lease.company.name;
    };

    $scope.getCompany = function() {
      CompanyService.get({
        id: $scope.lease.companyId,
      }).then(
        function(company) {
          $scope.lease.company = company;
          $scope.setCompany();
        },
        function(error) {
          error.data = '';
          $scope.lease.company = {
            error: 'Error retrieving the company data (details below)',
            errorObject: error,
          };
          $scope.downloadErrorHandling();
        },
      );
    };

    $scope.setBuilding = function() {
      if ($scope.lease.building) {
        $scope.building = $scope.lease.building;
        $rootScope.buildingId = $scope.lease.building.id;
        $scope.building.displayName ||= $scope.building.name;
        $scope.building.dashboardName ||= $scope.building.displayName;
        $scope.building.version = $scope.lease.version;

        if (
          !$scope.lease.premiseAreaSize && 
          $scope.building.buildingOfficeArea && 
          $scope.hasEditorConfig('premisesAreaSize', 'useBuildingOfficeArea')
        ) {
          $scope.lease.premiseAreaSize = $scope.building.buildingOfficeArea;
        }
      }
    };

    $scope.setAssetsPaths = function() {
      var company = $scope.lease.company.name;
      var parentDocumentType = $scope.lease.type.toLowerCase();
      var currentDocumentType = parentDocumentType;
      var buildingVersion = $scope.lease.form.buildingVersion;
      var listItemsVersion = $scope.lease.form.listItemsVersion;
      var flavor = $scope.lease.form.flavor.toLowerCase();
      var formVersion = $scope.lease.form.version;

      if (window.name === 'download-abstract') {
        currentDocumentType = 'abstract';
        flavor = 'abstract_for_' + flavor;

        // TODO: right now we only support 1 version for abstracts,
        // we'll have to think about how to make it dynamic
        formVersion = "00001";
      }

      var baseBuildingUrl =
        'companies/' +
        company +
        '/' +
        currentDocumentType +
        '/forms/' +
        flavor + 
        '/buildings/' +
        buildingVersion;

      var desiredStylesUrl =
        'companies/' +
        company +
        '/' +
        currentDocumentType +
        '/forms/' +
        flavor +
        '/versions/' +
        formVersion +
        '/style.docx';

      $scope.ASSETS_BUILDING_DIR_URL = '/assets/' + baseBuildingUrl;
      $scope.ASSETS_DESIRED_STYLES = '/assets/' + desiredStylesUrl;

      if ($window.parent) {
        $window.parent.ASSETS_DESIRED_STYLES = $scope.ASSETS_DESIRED_STYLES;
      }
    };

    /**
     * [lkedmi] NOTE:
     * Both `window.lease` and `lease` are set and equal, you could argue that we can
     * omit the `lease` parameter. the reasoning not to omit it is that instead of
     * counting `window.lease` to be set by some code, it forces you to pass the lease
     * as a param.
     */
    /**
     * Turns on the "does this lease have X" flags implied by the detail fields
     * underneath them, so the sections holding those details are visible.
     *
     * These derivations exist nowhere else. Editing a field through its control
     * fires that control's ng-change, and the deal terms import reproduces it --
     * triggerChangeEvents finds the field's editor-control and calls its handler --
     * so the rent and renewal chains recompute on both paths. Nothing in either
     * chain sets these flags, and the controls that would are inside the sections
     * the flags gate: permittedUseRestaurant lives under
     * `editor-show='lease.hasSpecialUseProvisions'`, so while the flag is off its
     * control is not rendered and there is no ng-change to fire in the first place.
     *
     * That leaves setLease as the only place the implication is drawn, which is
     * why an import could set permittedUseRestaurant, leave hasSpecialUseProvisions
     * false, and the special use section would stay hidden and out of the
     * downloaded document. Call this wherever these fields are written after load.
     *
     * One-directional on purpose. It reveals a section the data implies; it never
     * hides one, because the flags are user-editable and a drafter may have reason
     * to keep a section open that the data alone would not justify. An explicit
     * false is left alone, while null or undefined -- these columns are nullable
     * with no default, so both mean "never answered" -- is filled in.
     *
     * `changedPath` is the field that was just written, and it carries the one
     * exception. Accepting permittedUseRestaurant is a request for the thing
     * hasSpecialUseProvisions gates, so a stale false must not veto it; without
     * that the value applies and its section stays hidden, which is the bug this
     * exists to fix. The exception is limited to the flag whose own source
     * changed -- writing an unrelated field still leaves every false alone -- and
     * on load, where there is no changedPath, nothing is overridden at all.
     */
    var DEPENDENT_FLAG_SOURCES = {
      hasRadiusRestriction: ['radiusRestriction'],
      hasSpecialUseProvisions: [
        'permittedUseRestaurant',
        'permittedUseLiquorSales',
        'permittedUseMedical',
      ],
      hasFreeRent: ['freeRent'],
      hasFixturingPeriod: ['fixturingPeriod'],
    };

    $scope.normalizeDependentFlags = function(lease, changedPath) {
      if (!lease) {
        return;
      }

      // rentPeriods[2].x -> rentPeriods
      const changedRoot = changedPath
        ? String(changedPath).split(/[.[]/)[0]
        : null;

      function raiseFlag(flag, isImplied) {
        if (!isImplied) {
          return;
        }

        // An existing false is an answer, so filling in only what is unset is
        // right on load and for unrelated writes. But when the write *is* one of
        // this flag's own sources, the user has just asked for the thing the flag
        // gates, and a stale false should not veto it -- that would leave the
        // value set with its section hidden, which is the bug this exists to fix.
        const changedItsOwnSource =
          changedRoot &&
          DEPENDENT_FLAG_SOURCES[flag].indexOf(changedRoot) !== -1;

        if (lease[flag] === false && !changedItsOwnSource) {
          return;
        }

        lease[flag] = true;
      }

      raiseFlag('hasRadiusRestriction', !!lease.radiusRestriction);

      raiseFlag(
        'hasSpecialUseProvisions',
        !!(
          lease.permittedUseRestaurant ||
          lease.permittedUseLiquorSales ||
          lease.permittedUseMedical
        )
      );

      raiseFlag('hasFreeRent', !!lease.freeRent);
      raiseFlag('hasFixturingPeriod', !!lease.fixturingPeriod);
    };

    $scope.setLease = function(lease, lease_id, download, isBlank) {
      $scope.lease = lease || window.lease;
      $scope.lease.version = $scope.lease.form.version;
      $scope.isMultipleTerms = lease.form.editor && lease.form.editor.isMultipleTerms;
      $scope.getFreeText();
      $scope.setAssetsPaths();

      if (window.lease) {
        try {
          $scope.isClausebookEnabled = false;

          const formConfig = JSON.parse(window.lease.form.config);
    
          if (formConfig?.clausebook) {
            $scope.isClausebookEnabled = true;
          }
        } catch (ex) {
          // Do nothing.
        }

        try {
          $scope.isCopilotEnabled = false;

          if (window?.user?.company?.companyProfile?.hasCoPilot) {
            $scope.isCopilotEnabled = true;
          }
        } catch (ex) {
          // Do nothing.
        }
      }

      var promises = [];

      var attachmentsList = _.keys(AttachmentService.attachments);
      for (var i = 0; i < attachmentsList.length; i++) {
        if (lease[attachmentsList[i]]) {
          var attachmentPromise = AttachmentService.getFile(
            lease[attachmentsList[i]],
            attachmentsList[i],
          );

          if (window.isDownload) {
            promises.push(attachmentPromise);
          }
        }
      }

      $scope.isPrerender = download ? true : $scope.isPrerender;
      $scope.download = $window.location.href.indexOf('/edit') < 0 && download;

      if ($scope.isFirstLeaseLoading) {
        // marks prerender that document is not ready
        $window.prerenderReady = false;
        $scope.isFirstLeaseLoading = false;
      }

      // temp initialization for exists lists
      $scope.normalizeDependentFlags(lease);

      $scope.inInitialLoad = true;
      $rootScope.lease = lease;
      $scope.currentRootAddress = window.location.origin;

      $scope.setLeaseDefaults();

      if ($scope.lease.company) $scope.setCompany();
      else $scope.getCompany();

      if (!$rootScope.adminMode) {
        $scope.setBuilding();
      }

      // Init amendments fields
      if ($scope.lease.type === 'Amendment') {
        $scope.initAmendment($scope.lease);
      }

      // NOTE: for leases fetched from VTS calculate frontend fields on the basis of backend preset fields
      if ($scope.lease.source && $scope.lease.source.sourceId) {
        $scope.baseRent = $scope.lease.baseRent;
        $scope.annualIncrease = $scope.lease.annualIncrease;
        $scope.calculateRentPSF($scope.lease);
        $scope.consolidateRentPeriods();
      }

      if ($scope.lease.rentInputType === 'automatic') {
        $scope.updateBaseRent();
        $scope.updateAnnualIncrease();
      }

      //initialize calculate fields
      if ($scope.lease.form.customTransformations) {
        const customTransformations = JSON.parse($scope.lease.form.customTransformations);
        const keys = Object.keys(customTransformations);
        for (let i = 0; i < keys.length; i++) {
          customTransformations[keys[i]].calcFunc = eval("(" + customTransformations[keys[i]].calcFunc + ")");
          $scope.calculatedFields.calculatedFields[keys[i]] = customTransformations[keys[i]];
        }
      }
      $scope.calculatedFields.init();

      $timeout(function() {
        var unhiddenElements = document.querySelectorAll('.lp-hide div[ng-repeat]:not(.lp-hide-element)');
        var hiddenElements = document.querySelectorAll('.lp-show div[ng-repeat]:not(.lp-show-element)');
        if (unhiddenElements.length !== 0 || hiddenElements.length !== 0) {
          var ifKeys = Object.keys(leaseIfManager.leaseIfItems);
          for (var index = 0; index < ifKeys.length; index++) {
            var ifEl = leaseIfManager.leaseIfItems[ifKeys[index]];
            if (ifEl && ifEl.element.tagName === "DIV") {
              try {
                ifEl.updateElementStyle();
              } catch (e) {}
            }
          }
        }
      }, 0);

      $scope.fromThen = $q.all(promises).then(function(values) {
        $scope.getOverrides();
        $scope.setOverrides();

        // This is stupid but unfortunately, couldn't find a better way
        // the `promises` array contains promise for each `getFile` call
        // When the promise is evaluated, its value is an array of promises,
        // one for each page in the pdf/image, we need to make sure to wait for those
        // too before we proceed, otherwise, the images might not show
        var nestedPromises = [];
        for (var i = 0; i < values.length; i++) {
          if (values[i] && values[i].isAttachment) {
            nestedPromises = _.concat(nestedPromises, values[i].list);
          }
        }

        $q.all(nestedPromises).then(function() {
          setTimeout(function() {
            $scope.completeLoading();
          });
          return;
        });
        return;
      });

      if ($scope.editingEnabled) {
        $rootScope.$emit('leaseStatusChanged', {
          lock: $scope.lease.isLocked,
        });
      }
    };

    $scope.initAmendment = function(lease) {
      //  convert string to dates
      if ($scope.lease.existingLeaseDate) {
        $scope.lease.existingLeaseDate = new Date(
          $scope.lease.existingLeaseDate,
        );
      }
      if ($scope.lease.existingLeaseExpirationDate) {
        $scope.lease.existingLeaseExpirationDate = new Date(
          $scope.lease.existingLeaseExpirationDate,
        );
      }
      if ($scope.lease.existingGuarantorInfo) {
        if ($scope.lease.existingGuarantorInfo.individuals) {
          for (
            var i = 0;
            i < $scope.lease.existingGuarantorInfo.individuals.length;
            i++
          ) {
            var curr = $scope.lease.existingGuarantorInfo.individuals[i];
            $scope.lease.existingGuarantorInfo.individuals[
              i
            ].hasBeenReleased = curr.hasBeenReleased
              ? curr.hasBeenReleased
              : false;
            $scope.lease.existingGuarantorInfo.individuals[
              i
            ].guarantyDate = curr.guarantyDate
              ? moment(curr.guarantyDate, 'YYYY-MM-DDTHH:mm:ssZ').toDate()
              : null;
            $scope.lease.existingGuarantorInfo.individuals[
              i
            ].releaseDate = curr.releaseDate
              ? moment(curr.releaseDate, 'YYYY-MM-DDTHH:mm:ssZ').toDate()
              : null;
          }
        }
        if ($scope.lease.existingGuarantorInfo.marriedCouples) {
          for (
            var i = 0;
            i < $scope.lease.existingGuarantorInfo.marriedCouples.length;
            i++
          ) {
            var curr = $scope.lease.existingGuarantorInfo.marriedCouples[i];
            $scope.lease.existingGuarantorInfo.marriedCouples[
              i
            ].hasBeenReleased = curr.hasBeenReleased
              ? curr.hasBeenReleased
              : false;
            $scope.lease.existingGuarantorInfo.marriedCouples[
              i
            ].guarantyDate = curr.guarantyDate
              ? moment(curr.guarantyDate, 'YYYY-MM-DDTHH:mm:ssZ').toDate()
              : null;
            $scope.lease.existingGuarantorInfo.marriedCouples[
              i
            ].releaseDate = curr.releaseDate
              ? moment(curr.releaseDate, 'YYYY-MM-DDTHH:mm:ssZ').toDate()
              : null;
          }
        }
        if ($scope.lease.existingGuarantorInfo.entities) {
          for (
            var i = 0;
            i < $scope.lease.existingGuarantorInfo.entities.length;
            i++
          ) {
            var curr = $scope.lease.existingGuarantorInfo.entities[i];
            $scope.lease.existingGuarantorInfo.entities[
              i
            ].hasBeenReleased = curr.hasBeenReleased
              ? curr.hasBeenReleased
              : false;
            $scope.lease.existingGuarantorInfo.entities[
              i
            ].guarantyDate = curr.guarantyDate
              ? moment(curr.guarantyDate, 'YYYY-MM-DDTHH:mm:ssZ').toDate()
              : null;
            $scope.lease.existingGuarantorInfo.entities[
              i
            ].releaseDate = curr.releaseDate
              ? moment(curr.releaseDate, 'YYYY-MM-DDTHH:mm:ssZ').toDate()
              : null;
          }
        }
      }
      if ($scope.lease.additionalExistingGuarantorInfo) {
        _.forEach(['individuals', 'marriedCouples', 'entities'], function(
          guarantorType,
        ) {
          _.forEach($scope.lease.additionalExistingGuarantorInfo[
            guarantorType
          ], function(guarantor) {
            guarantor.hasBeenReleased = guarantor.hasBeenReleased
              ? guarantor.hasBeenReleased
              : false;
            guarantor.guarantyDate = guarantor.guarantyDate
              ? moment(guarantor.guarantyDate, 'YYYY-MM-DDTHH:mm:ssZ').toDate()
              : null;
            guarantor.releaseDate = guarantor.releaseDate
              ? moment(guarantor.releaseDate, 'YYYY-MM-DDTHH:mm:ssZ').toDate()
              : null;
          });
        });
      }
      if ($scope.lease.previousAmendment) {
        for (var i = 0; i < $scope.lease.previousAmendment.length; i++) {
          if (typeof lease.previousAmendment[i].date === 'string') {
            $scope.lease.previousAmendment[i] = {
              date: moment(
                $scope.lease.previousAmendment[i].date,
                'YYYY-MM-DDTHH:mm:ssZ',
              ).toDate(),
            };
          }
        }
      }
      if ($scope.lease.abatementDates) {
        for (var i = 0; i < $scope.lease.abatementDates.length; i++) {
          if (typeof $scope.lease.abatementDates[i].date === 'string') {
            $scope.lease.abatementDates[i].date = moment(
              $scope.lease.abatementDates[i].date,
              'YYYY-MM-DDTHH:mm:ssZ',
            ).toDate();
          }
        }
      }
      // If needed, initialize the priorAssignments array
      if (!$scope.lease.priorAssignments) {
        $scope.lease.priorAssignments = [];
      }
      //  convert string to dates
      if ($scope.lease.priorAssignments) {
        for (var i = 0; i < $scope.lease.priorAssignments.length; i++) {
          $scope.lease.priorAssignments[i]._originalIndex = i;

          if (typeof $scope.lease.priorAssignments[i].date === 'string') {
            $scope.lease.priorAssignments[i].date = moment(
              $scope.lease.priorAssignments[i].date,
              'YYYY-MM-DDTHH:mm:ssZ',
            ).toDate();
          }
        }
      }
      // Maintain the `numberOfContinuingGuarantors`
      $scope.numberOfContinuingGuarantors = _.filter(
        _.union(
          $scope.lease.existingGuarantorInfo.individuals || [],
          $scope.lease.existingGuarantorInfo.marriedCouples || [],
          $scope.lease.existingGuarantorInfo.entities || [],
        ),
        {
          hasBeenReleased: false,
        },
      ).length;
      // Create the initial data for the breakpoint table
    };


    $scope.compileVisibleFreeTexts = async function(element) {
      setTimeout(() => {
        window.applyFilterVisible(() => {

          element = element ? element : document;

          let list = element.querySelectorAll("[free-text-placeholder]");
          let scope;

          for (let i = 0; i < list.length; i++) {
            list[i].setAttribute(
              "free-text",
              list[i].getAttribute("free-text-placeholder")
            );
            list[i].removeAttribute("free-text-placeholder");

            scope = angular.element(list[i]).scope();

            // NOTE: Every node SHOULD have a scope
            // The only rason we do this is to accomodate for SML errors which
            // breaks the document from loading. Specifically, this is a hint
            // of an SML issue where an `ifi` might not be colored properly
            if (scope) {
              $compile(list[i])(scope);
            } else {
              $compile(list[i])($scope);
              const errorMessage = `The following node doesn't have a scope! this might be an indication of missing colors in the SML: ${list[i].outerHTML}`;
              console.error(errorMessage);
              Raven.captureMessage(errorMessage);
            }
          }
        });
      });
    };

    $rootScope.hasEditorConfig = function(template, key = null) {
      let cards = [];

      if (
        window.lease &&
        window.lease.form &&
        window.lease.form.editor &&
        window.lease.form.editor.cards
      ) {
        cards = window.lease.form.editor.cards;
      }

      if ($scope.editor && $scope.editor.cards) {
        cards = $scope.editor.cards;
      }

      for (let cardIndex = 0; cardIndex < cards.length; cardIndex++) {
        let categories = cards[cardIndex].categories;

        for (let categoryIndex = 0; categoryIndex < categories.length; categoryIndex++) {
          let templates = categories[categoryIndex].templates;

          for (let templateIndex = 0; templateIndex < templates.length; templateIndex++) {
            if (templates[templateIndex].id !== template) {
              continue;
            }

            let config = templates[templateIndex].config;
            let keys = Object.keys(config);

            if (key === null) {
              return !!config;
            }

            if (keys.indexOf(key) !== -1) {
              return config[key];
            } else {
              return false;
            }
          }
        }
      }
      return false;
    }

    HTMLDocument.prototype.queryCommandState = (command) => {
      let sel = window.getSelection()
      let anchorNode = sel.anchorNode;
      if (anchorNode.nodeType === Node.TEXT_NODE) {
        anchorNode = anchorNode.parentElement;
      }
      let leaseVarDecorationNode = anchorNode.closest("[data='lease-var-decoration']");
      let leaseVarOriginalValue = leaseVarDecorationNode ? leaseVarDecorationNode.style.textDecoration : "";
      let computedStyle = getComputedStyle(anchorNode);
      let originalValue;
      let result = false;

      switch (command) {
        case 'bold':
          originalValue = computedStyle.fontWeight;
          if ((originalValue)/1 >= 700 || originalValue.contains(command)) {
            return true;
          } else {
            anchorNode.childNodes.forEach((child) => {
              if (child.nodeType !== Node.ELEMENT_NODE) {
                return;
              }
              computedStyle = getComputedStyle(child);
              originalValue = computedStyle.fontWeight;
              if ((originalValue)/1 >= 700 || originalValue.contains(command)) {
                result = true;
              }
            });
            return result;
          }
        case 'underline':
          originalValue = computedStyle.textDecoration;
          if (originalValue.contains(command) || leaseVarOriginalValue.contains(command)) {
            return true;
          } else {
            anchorNode.childNodes.forEach((child) => {
              if (child.nodeType !== Node.ELEMENT_NODE) {
                return;
              }
              computedStyle = getComputedStyle(child);
              originalValue = computedStyle.textDecoration;
              if (originalValue.contains(command)) {
                result = true;
              }
            });
            return result;
          }
        case 'italic':
          originalValue = computedStyle.fontStyle;
          if (originalValue.contains(command)) {
            return true;
          } else {
            anchorNode.childNodes.forEach((child) => {
              if (child.nodeType !== Node.ELEMENT_NODE) {
                return;
              }
              computedStyle = getComputedStyle(child);
              originalValue = computedStyle.fontStyle;
              if (originalValue.contains(command)) {
                result = true;
              }
            });
            return result;
          }
      }
      return false;
    }


    $scope.reloadDocument = function() {
      $scope.loadDocument(window._lease_id, window._lease_type);
    };

    $scope.loadDocument = function(id, type, download) {
      datesConvert(window.lease);
      
      if (!$scope._templatesReady) {
        if (!$scope._loadDocumentTemplatesListenerRegistered) {
          $scope._loadDocumentTemplatesListenerRegistered = true;
          $scope.$on('templatesReady:loadDocument', function() {
            $scope._templatesReady = true;
            $scope._doLoadDocument(id, type, download);
          });
        }
      } else {
        $scope._doLoadDocument(id, type, download);
      }
    };

    $scope._doLoadDocument = function(id, type, download) {
      let documentType;
      let dealType;
      let flavor;
      let version;
      let building;
      let tenant;

      window._lease_id = id;
      window._lease_type = type;

      $scope.compileVisibleFreeTexts();

      const elementsToRemove = document.querySelectorAll(".lease.lease--original .document > script");
      for (
        let elementIndex = 0;
        elementIndex < elementsToRemove.length;
        elementIndex++
      ) {
        elementsToRemove[elementIndex].classList.add("remove-on-download");;
      }

      if (window.lease?.form) {
        documentType = window.lease.form ? window.lease.form.name.toLowerCase() : '';
        dealType = window.lease.form ? window.lease.form.dealType : '';
        flavor = window.lease.form ? window.lease.form.flavor : '';
        version = window.lease.form ? window.lease.form.version : '';
        building = window.lease.building ? window.lease.building.displayName : '';
        tenant = window.lease.customFields?.fileName || window.lease.tenantInfo?.tradeName || window.lease.tenantInfo?.name;
      }

      window.track.event(new LoadDocumentEvent({
        documentType,
        dealType,
        flavor,
        version,
        building,
        tenant,
      }));

      if (window.isAbstract) {
        $rootScope.abstract = $scope.abstract = window.parent.abstractData;
      }

      $scope.download = download;
      if (download) {
        console.log('render start: ' + new Date().getTime()); // debug log
      }

      // Align the structure of advanced-edit with other free-texts
      // Other free-texts are nested within a container, but advanced-edit is not
      setTimeout(function() {
        const advancedEdit = document.querySelectorAll('[advanced-edit]');
        advancedEdit.forEach(node => {
          const container = document.createElement('div');
          const pid = node.getAttribute("free-text") || node.getAttribute("free-text-placeholder");
          container.setAttribute("pid", pid);
          node.parentElement.insertBefore(container, node);
          node.remove();
          container.appendChild(node);
        });
      });

      $scope.setLease(window.lease, null, download);

      $('body').addClass('skeleton-loader');
    };

    $scope.showPromptRefresh = function(msg, buttons) {
      $scope.openConfirmModal(
        msg,
        function showPromptRefresh() {
          $window.location.reload();
        },
        null,
        buttons,
      );
    };

    $scope.showPromptRedirectToDashboard = function(msg, buttons) {
      $scope.openConfirmModal(msg, null, null, buttons, '/');
    };

    $scope.setLockMessage = function() {
      var message = '';
      if ($scope.lockData) {
        message = 'This lease is locked by ' + $scope.lockData.email;
      }
      $rootScope.lockMessage = message;
    };

    $scope.downloadErrorHandling = function() {
      if ($scope.download) {
        $cookies.put('download_in_process', 'false', {
          path: '/',
        });
        alert($scope.downloadErrorMessage);
      }
    };

    $scope.updateAreaSize = function() {
      $scope.calculateRentPSF($scope.lease);
      $scope.calculateRenewalPSF($scope.lease);
      $scope.consolidateRentPeriods();
      $scope.consolidateRenewalInfo();
      $scope.$broadcast('rentValuesChanged');
    };

    $scope.updateAnnualIncrease = function() {
      $scope.annualIncrease = $scope.lease.annualIncrease;
      $scope.updateNumberOfRentPeriods(
        $scope.lease.leaseYears,
        $scope.lease.leaseMonths,
      );
      $scope.updateNumberOfRenewalTermsAndRentPeriods(
        $scope.lease.renewalCount,
        $scope.lease.renewalTerm,
      );
      $scope.updateCpiAnchorYears();
      $scope.updateRenewalCpiAnchorYears()
      $scope.calculateRentPSF($scope.lease);
      $scope.calculateRenewalPSF($scope.lease);
      $scope.consolidateRentPeriods();
      $scope.consolidateRenewalInfo();
      $scope.$broadcast('rentValuesChanged');
    };

    $scope.updateBaseRent = function() {
      $scope.baseRent = $scope.lease.baseRent;
      $scope.updateNumberOfRentPeriods(
        $scope.lease.leaseYears,
        $scope.lease.leaseMonths,
      );
      $scope.updateNumberOfRenewalTermsAndRentPeriods(
        $scope.lease.renewalCount,
        $scope.lease.renewalTerm,
      );
      $scope.calculateRentPSF($scope.lease);
      $scope.calculateRenewalPSF($scope.lease);
      $scope.consolidateRentPeriods();
      $scope.consolidateRenewalInfo();
      $scope.$broadcast('rentValuesChanged');
    };

    $scope.updateBuilding = async function(buildingId, showWarning = false) {
      if (!$scope._templatesReady) {
        if (!$scope._updateBuildingTemplatesListenerRegistered) {
          $scope._updateBuildingTemplatesListenerRegistered = true;
          $scope.$on('templatesReady:updateBuilding', function() {
            $scope._doUpdateBuilding(buildingId, showWarning);
          });
        }
      } else {
        $scope._doUpdateBuilding(buildingId, showWarning);
      }
    };

    $scope._doUpdateBuilding = async function(buildingId, showWarning = false) {
      $scope.setAssetsPaths();

      var building = _.find($scope.adminBuildings, function(b) {
        return b.id === buildingId;
      });
      $rootScope.buildingId = building.id;
      $scope.building = building;
      $scope.building.displayName ||= $scope.building.name;
      $scope.building.dashboardName ||= $scope.building.displayName;

      let specificUrl = $scope.building.specificUrl;
      const parts = specificUrl.split('/');

      if (parts.length == 2) {
        if ($scope.lease && typeof $scope.lease.error === 'undefined') {
          var buildingFileUrl = parts[1];

          var updatedAt = $scope.lease.form.updatedAt;
          if (typeof updatedAt === 'string') {
            updatedAt = new Date(updatedAt);
          }
          specificUrl =
            $scope.ASSETS_BUILDING_DIR_URL +
            "/" +
            buildingFileUrl +
            "?v=" +
            updatedAt.toISOString();

          $rootScope.isBuildingLanguageNeeded = true;
        } else {
          window.isBuildingLanguageNewPath = false;
        }
      }

      $scope.setAssetsPaths();
      $scope.setBuildingUrl(specificUrl);

      setTimeout(function() {
        $scope.structureChanged();
      });

      if (showWarning) {
        $mdToast.show({
          hideDelay: 10000,
          position: 'top right',
          template:
            '<md-toast>' +
            '<div class="md-toast-content">' +
            '<span class="md-toast-text">Changes are made to the form template and are not asset specific</span>' +
            '</div>' +
            '<div class="bottom-arrow"></div>' +
            '</md-toast>',
        });
      }
    };

    $scope.refreshRentParams = function(rentPeriods) {
      if (rentPeriods && rentPeriods[0]) {
        $scope.baseRent = rentPeriods[0].rentPerSqFoot;
        if (rentPeriods.length > 1) {
          var rent0 = rentPeriods[0].rentPerSqFoot;
          var rent1 = rentPeriods[1].rentPerSqFoot;
          $scope.annualIncrease = parseFloat(
            ((rent1 / rent0 - 1) * 100).toFixed(2),
          );
        }
      }
    };

    function updateTerms(updatedTerms) {
      var deletedTerm = _.differenceBy(
        $scope.lease.terms,
        updatedTerms,
        'id',
      )[0];
      if (deletedTerm) {
        _.remove(lease.terms, function(t) {
          return t.id == deletedTerm.id;
        });
      }
      var addedTerm = _.differenceBy(updatedTerms, $scope.lease.terms, 'id');
      if (addedTerm) {
        $scope.lease.terms = $scope.lease.terms.concat(addedTerm);
      }
      $scope.allowRemoveTerm = true;
    }

    $scope.isUpdating = false;

    function timeout(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }

    async function sleep(fn, ...args) {
      await timeout(1500);
      return fn(...args);
    }

    async function retry(fn, error, n) {
      for (let i = 0; i < n; i++) {
        try {
          if (i === 0) {
            return await fn();
          } else {
            return await sleep(fn);
          }
        } catch {}
      }

      if (error) {
        error();
      }
    }

    function updateError() {
      const errorMessage = "Due to a network error your latest change wasn't saved, please reload the document and try again";

      $scope.showPromptRedirectToDashboard(errorMessage, {
        ok: 'OK'
      });

      $scope.isUpdating = false;
      $rootScope.serverRunning = false;
      $timeout(function() {
        $rootScope.serverRunning = true;
        $rootScope.$digest();
      }, 10000);

      throw new Error(errorMessage);
    }

    function updateInternal() {
      var url =
        '/api/' +
        pluralize($scope.lease.type).toLowerCase() +
        '/' +
        $scope.lease.id;
        $scope.undoDisabled = true;

      $scope.startSaving();
      
      return DocumentService.$put(url, $scope.lease).then(
        function(res) {
          $rootScope.serverRunning = true;
          // update last updated lease to be the current lease
          $scope.isUpdating = false;
          $scope.undoDisabled = false;
          $scope.endSaving();

          if (res?.terms) {
            updateTerms(res.terms);
          } else {
            $scope.allowRemoveTerm = true;
          }

          LeaseEditorService.setLeaseTitle();
        },
        function(err) {
          $scope.undoDisabled = false;
          $scope.endSaving();
          throw err;
        },
      );
    }

    $scope.removeListItemsInWrongLocation = async ()=>{
      let deferred = $q.defer();
      let refresh = false;
      if($scope.listItems && listItemsManager){
        $scope.listItems.forEach(function (itemObject){
          let freeText = document.querySelector('[free-text="' +itemObject.afterFreeText +'"]');

          if(freeText){
            let anchor = freeText.getTopAnchor();
            let listItemFreeText = document.querySelector('[free-text="' +itemObject.headerFreeText +'"]');
            listItemFreeText = listItemFreeText ? listItemFreeText : document.querySelector('[free-text="' +itemObject.bodyFreeText +'"]');
            if(listItemFreeText){
              let listAnchor = listItemFreeText.getTopAnchor();

              if(anchor.parentElement.hasAttribute('lease-if')){
                if(!anchor.parentElement.contains(listAnchor)){
                  let insertedListItemId = listAnchor.getAttribute('inserted-list-item-id');

                  document.querySelectorAll('[inserted-list-item-id="'+insertedListItemId+'"]').forEach((el)=>{
                    refresh = true;
                    el.remove();
                  });
                }
              }
            }
          }
        });
      }

      if (refresh) {
        listItemsManager.createListItemsMap();
      }

      deferred.resolve();
      return deferred;
    }

    $scope.reInsertMissingListItems = async () => {
      setTimeout(async function() {
        if (!window.isDownload) {
          // Remove list items that are not in the right location
          await $scope.removeListItemsInWrongLocation();
        }

        if ($scope.listItems && listItemsManager) {
          $scope.listItems.forEach(function(itemObject) {
            if (itemObject.json || itemObject.fromPID) {
              return;
            }

            const headerFreeText = document.querySelector(
              '[free-text="' + itemObject.headerFreeText + '"]',
              true
            );
            const bodyFreeText = document.querySelector(
              '[free-text="' + itemObject.bodyFreeText + '"]',
              true
            );

            if (!headerFreeText && !bodyFreeText) {
              if (itemObject && itemObject.afterFreeText && pageLayersManager) {
                pageLayersManager.updateOutlineById(itemObject.afterFreeText);
              }
              listItemsManager.listsInsertItem(itemObject);
            }
          });
        }
      });
    };

    function forceLeaseIfCompile(){
      document.querySelectorAll('.lease [lease-if]:not([data-if-id])').forEach((node)=>{
        var scope = angular.element(node).scope();
        $compile(node)(scope);

      });
    }

    const debouncedUpdate = _.debounce(
      function() {
        window.pendingSaves.delete(debouncedUpdate);

        if ($scope.isLoading || document.body.classList.contains("skeleton-loader")) {
          return;
        }

        $scope.compileVisibleFreeTexts();
        forceLeaseIfCompile();

        const unhiddenElements = document.querySelectorAll('.lp-hide div[ng-repeat]:not(.lp-hide-element)');
        const hiddenElements = document.querySelectorAll('.lp-show div[ng-repeat]:not(.lp-show-element)');
        if(unhiddenElements.length !== 0 || hiddenElements.length !== 0){
          const ifKeys = Object.keys( leaseIfManager.leaseIfItems);

          for(let index=0; index < ifKeys.length; index ++){
            let ifEl = leaseIfManager.leaseIfItems[ifKeys[index]];
            if(ifEl && ifEl.element.tagName === "DIV"){
              try{
                ifEl.updateElementStyle();
              }
              catch(e){
                
              }
            }
          }
        }

        if (!$scope.adminMode) {
          if ($scope.lease && $scope.lease.id) {
            retry(updateInternal, updateError, 3);
          }
        }

        autopilotManager.updateDocumentContext();
      },
      window.CONSTS.SAVE_WAIT,
      { trailing: true, leading: false, maxWait: window.CONSTS.SAVE_MAX_WAIT },
    );

    $scope.update = function() {
      window.pendingSaves.add(debouncedUpdate);
      return debouncedUpdate();
    };
    $scope.update.flush = () => debouncedUpdate.flush();
    $scope.update.cancel = () => {
      window.pendingSaves.delete(debouncedUpdate);
      debouncedUpdate.cancel();
    };

    $scope.hideWhiteSpaceInOldTables = function() {
      $('p:has( > span > table)').addClass('no-after');
    }

    $scope.appendEditingSpaceToLeaseVars = function() {
      if ($scope.isPrerender) {
        return;
      }

      var leaseVars = $('lease-var, [data-list-static-item="true"], crr');

      leaseVars.each(function() {
        var $current = $(this).get(0);
        var $parent = $(this).parent();

        if ($current.tagName === "CRR") {
          if ($current.contentEditable !== "false") {
            $current.setAttribute("contenteditable", false);
          }
        }

        if ($parent[0].childNodes.length === 1) {
          if (!$current.classList.contains("inserted-list-item")) {
            $parent.append('<span class="SKIP">&#xFEFF;</span>');
            $parent.prepend('<span class="SKIP">&#xFEFF;</span>');
          }
        }
      });
    };


    $scope.initializeVersionControl = function() {
      
      const leaseEditor = document.querySelector('.lease--editor');

      if (!leaseEditor) {
        return;
      }

      if ($window.frameElement && $window.frameElement.getAttribute('isDiff') === 'true') {
        return;
      }

      if (!$rootScope.user) {
        return;
      }

      if (!$rootScope.user.company.companyProfile.versionControl) {
        return;
      }

      const editor = window.getComputedStyle(leaseEditor);
      const paddingLeft = editor['padding-left'];
      const paddingRight = editor['padding-right'];

      var style = document.createElement('style');
      style.type = 'text/css';
      style.innerHTML = `
        .review-changes .lease.lease--modified .lease-nav {
          padding-left: ${paddingLeft};
          padding-right: ${paddingRight};
        }

        .annotations .annotation {
          width: calc(100% - ${paddingLeft} - ${paddingRight});
          right: ${paddingRight};
        }
      `;
      document.getElementsByTagName('head')[0].appendChild(style);
    };

    /*-----------------------------------------------------------------------------

       Ordered Lists related functionality

     ----------------------------------------------------------------------------*/
    /* list items manager ------------------------------------------------------------------------------------ Start  */

    (function (scope) {

      function ListItemsManager(scope) {
        let $scope = scope;

        this.listItemObjectsArray = [];
        this.listLevelsMap = null;
        let listLevelByName = null;
        this.listsCounters = null;
        this.listCurrentLevels = {};
        this.listOulinesDisplayName = {};
        this.$templateCache = $templateCache;

        this.getOutlineParagraphLevel = function(outlineName) {
          if (
            outlineName &&
            this.listLevelsMap &&
            this.listLevelsMap[outlineName]
          ) {
            if (
              this.listLevelsMap[outlineName].filter(o => {
                return o.level == `${outlineName}/paragraph`;
              }).length !== 0
            ) {
              return `${outlineName}/paragraph`;
            }
          }
          return "paragraph";
        };

        this.getLevelInfoByName = (name) => {
          return listLevelByName[name];
        };

        this.getLevelInfoByHeader = (header) => {
          var level =  $scope.listLevels.filter((o)=>{ return o.listHeader == header});
          return level[0] ? level[0] : null;
        };

        this.getListItemObjectFromNode = node => {
          let containerNode = node.closest("[data-list-map-index]");
          let returnObject = null;
          
          if (containerNode) {
            returnObject =
              listItemsManager.listItemObjectsArray[
                containerNode.getAttribute("data-list-map-index") * 1
              ];
          }

          return new Block(returnObject,node,$scope,$q,LeaseEditorService,$compile);
        };

        this.getInsertedListItemByElement = (el) => {
          if(el.querySelector('[list]')){
            let ids = [];
            el.querySelectorAll('.editable').forEach((node) => {
              ids.push(node.getAttribute('section-id') * 1);
            });

            for (let i = 0; i < $scope.listItems.length; i++) {
              let li = $scope.listItems[i];
              if (ids.indexOf(li.headerFreeText) !== -1 || ids.indexOf(li.bodyFreeText) !== -1) {
                return li;
              }
            }
          }
          return null;
        }


        this.addSectionEndForOutlineEnd = () => {
          let count=0;
          document.querySelectorAll('outline-end,lease-end').forEach((outline_end) => {
            if (!outline_end.getAttribute('section-end-added')) {
              let name = outline_end.getAttribute('name');
              let el = document.createElement('section-end');
              el.setAttribute('listlevel', 0);
              el.setAttribute('data-list-static-level', 0);
              el.setAttribute('data-list-static-outline', name);
              el.setAttribute('data-list-static-id', count);
              outline_end.parentElement.insertBefore(el, outline_end);
              outline_end.setAttribute('section-end-added', true);
              outline_end.setAttribute('data-list-static-id', count);
              count++;
            }
          });

        }
        this.createListItemsMap = ()=>{
          window.applyFilterVisible(this._createListItemsMap);
        }
        this._createListItemsMap = () => {
          this.addSectionEndForOutlineEnd();
          this.listsCounters = {};

          if (!this.listLevelsMap && $scope.listLevels) {
            this.listLevelsMap = {};
            this.listOulinesDisplayName = {};
            listLevelByName = {};
            $scope.listLevelDepth = {};

            for (let index = 0; index < $scope.listLevels.length; index++) {
              let levelInfo = $scope.listLevels[index];
              listLevelByName[levelInfo.level] = levelInfo;
              let outline = levelInfo.outline ? levelInfo.outline : 'main';

              if (!this.listLevelsMap[outline]) {
                this.listLevelsMap[outline] = [];
              }

              if(outline !== 'main'){
                if (!this.listOulinesDisplayName[outline]) {
                  this.listOulinesDisplayName[
                    outline
                  ] = levelInfo.outlineDisplayName
                    ? levelInfo.outlineDisplayName
                    : outline;
                }
              }

              this.listLevelsMap[outline].push(levelInfo);
              $scope.listLevelDepth[index] = this.listLevelsMap[outline].length - 1;
              $scope.listLevelDepth[levelInfo.level] = this.listLevelsMap[outline].length - 1;
            }

            $scope.addOutlinesDisabled = Object.keys( this.listOulinesDisplayName).length === 0 ;
            
          

            $scope.safeApply();
          }
          let newList = [];
          let uniqueId = `outline--${Math.random() * 100000000000}`;
          this.outlineArray = [{ name: "static", uniqueId: uniqueId }];
          let lease = document.querySelector('.lease');
          if (lease) {
            lease.querySelectorAll('span[list], br[style*="mso-break-type:section-break"],outline,outline-end,lease-start,lease-end,section-end,reset-lists')
              .forEach((node) => {
                if (node.tagName) {
                  if (node.tagName === 'OUTLINE') {
                    this.listsCounters[node.getAttribute('name')] = {};
                  }
                  if (node.tagName === 'OUTLINE' || node.tagName === 'LEASE-START') {
                    uniqueId = `outline--${Math.random() * 100000000000}`;
                    node.setAttribute('data-outline-unique-id', uniqueId)
                    this.outlineArray.push({
                      name: node.tagName === 'LEASE-START' ? 'main' : node.getAttribute('name'),
                      uniqueId: uniqueId,
                    });
                    return;
                  } else if (node.tagName === 'OUTLINE-END' || node.tagName === 'LEASE-END') {
                    const outline = this.outlineArray.pop();
                    if (outline) {
                      node.setAttribute('data-outline-unique-id', outline.uniqueId);
                    }
                    return;
                  }
                }
                insertListItemToMap(node,null,newList);
              });
          }
          this.listItemObjectsArray = newList;
        };

        this.getNewItemListIdAndFormat = (afterFreeText, level, outline, isNewArticle) => {

          let returnObject = {
            listId: null,
            listFormat: '',
            nodeToInsertBefor: null,
            level : null,
          };

          let nodeToInsertAfter = getNodeToInsertAfter(afterFreeText, outline);


          let newItemLevelInfo = listLevelByName[level];
          let newItemLevelDepth = $scope.listLevelDepth[level];
          returnObject.level = newItemLevelInfo ? newItemLevelInfo.level : null;

          if(nodeToInsertAfter.tagName === "OUTLINE"){
            returnObject.listId = newItemLevelInfo.name;
            returnObject.listFormat = newItemLevelInfo.format;
            returnObject.nodeToInsertBefor = nodeToInsertAfter.nextElementSibling;
            return returnObject;
          }

          let prevListItemNode = this.getClosestListItem(nodeToInsertAfter);
          let prevListItemInfo = prevListItemNode ? prevListItemNode.getListItemInfoFromNode() : null;
          let prevItemLevelDepth = prevListItemInfo ? $scope.listLevelDepth[prevListItemInfo.levelName] : -1;



          let nextListItemNode;
          if (level.toLowerCase() === "article") {
            nextListItemNode = isNewArticle ?
              getBeforeNode(nodeToInsertAfter, level) :
              $(document.querySelector('lease-end'));
          } else {
            nextListItemNode = getBeforeNode(nodeToInsertAfter, level);
          }

          nextListItemNode = nextListItemNode.get ? nextListItemNode[0] : nextListItemNode;
          let nextListItemLevelInfo = nextListItemNode ? nextListItemNode.getListItemInfoFromNode() : null;

          if(nextListItemLevelInfo && nextListItemLevelInfo.static){
            let workinfo = nextListItemLevelInfo;
            while(workinfo && workinfo.static){
              workinfo = this.listItemObjectsArray[workinfo.currentIndex+1];
            }
            if(workinfo && workinfo.outline === newItemLevelInfo.outline &&
             newItemLevelDepth === $scope.listLevelDepth[workinfo.levelName]){
                nextListItemLevelInfo = workinfo;
             }

          }

          if (!nextListItemLevelInfo && nextListItemNode) {
            let infoNode = nextListItemNode.querySelector('[data-list-map-index]');
            nextListItemLevelInfo = infoNode && infoNode.getAttribute('data-list-map-index') ?
              this.listItemObjectsArray[infoNode.getAttribute('data-list-map-index') * 1] : null;
          }

          let nextListItemLevelDepth = nextListItemLevelInfo ? $scope.listLevelDepth[nextListItemLevelInfo.levelName] : -1;

          if (prevItemLevelDepth > -1) {
            if (newItemLevelDepth === prevItemLevelDepth) {
              returnObject.listId = prevListItemInfo.list;
              returnObject.listFormat = prevListItemInfo.format;
            } else if (newItemLevelDepth > prevItemLevelDepth) {
              if (newItemLevelDepth === nextListItemLevelDepth) {
                returnObject.listId = nextListItemLevelInfo.list;
                returnObject.listFormat = nextListItemLevelInfo.format;
              } else {
                // new list id
                returnObject.listId = createNewListId(newItemLevelInfo.name, newItemLevelInfo.levelName);
                returnObject.listFormat = newItemLevelInfo.format;
              }
            } else if (newItemLevelDepth < prevItemLevelDepth) {
              if (nextListItemLevelDepth > -1 && nextListItemLevelDepth === newItemLevelDepth) {
                returnObject.listId = nextListItemLevelInfo.list;
                returnObject.listFormat = nextListItemLevelInfo.format;
              } else {
                // locate previous list item with same level
                let scanIndex = prevListItemInfo.currentIndex;

                while (
                  scanIndex >= 0 &&
                  ((this.listItemObjectsArray[scanIndex].outline ===
                    prevListItemInfo.outline &&
                    $scope.listLevelDepth[
                      this.listItemObjectsArray[scanIndex].levelName
                    ] !== newItemLevelDepth) ||
                    (this.listItemObjectsArray[scanIndex] &&
                      this.listItemObjectsArray[scanIndex].static))
                ) {
                  scanIndex--;
                }

                if (scanIndex !== -1 && $scope.listLevelDepth[this.listItemObjectsArray[scanIndex].levelName] === newItemLevelDepth) {
                  returnObject.listId = this.listItemObjectsArray[scanIndex].list;
                  returnObject.listFormat = this.listItemObjectsArray[scanIndex].format;
                } else {
                  // new list id
                  returnObject.listId = createNewListId(newItemLevelInfo.name, newItemLevelInfo.levelName);
                  returnObject.listFormat = newItemLevelInfo.format;
                }
              }
            }
          } else {
            if (newItemLevelDepth === nextListItemLevelDepth) {
              returnObject.listId = nextListItemLevelInfo.list;
              returnObject.listFormat = nextListItemLevelInfo.format;
            } else {
              // new list id
              returnObject.listId = createNewListId(newItemLevelInfo.name, newItemLevelInfo.levelName);
              returnObject.listFormat = newItemLevelInfo.format;
            }
          }

          
          if (nextListItemNode && nextListItemNode.previousElementSibling &&
            nextListItemNode.previousElementSibling.textContent.trim().startsWith('!!') &&
            nextListItemNode.previousElementSibling.textContent.trim().endsWith('!!')) {
            nextListItemNode = nextListItemNode.previousElementSibling;
          }
          returnObject.nodeToInsertBefor = nextListItemNode;

          if(!returnObject.nodeToInsertBefor){
            
            const freeText  = document.querySelector(`[free-text="${afterFreeText}"]`);
            const blockData = freeText ?  freeText.getBlockData() : null;
            
            if(blockData){
              returnObject.nodeToInsertBefor = blockData.containingNode.nextElementSibling;
            }

          }

          return returnObject;
        }


        this.listsInsertItemAnywhere = (itemObject, byAdmin) => {
          const retValue = applyShowAll(this._listsInsertItemAnywhere,[itemObject, byAdmin]);
          return retValue;
        };

        this.locateNewListItemAnchor = (itemObject) => {

          if (itemObject.fromUI) {

            delete itemObject.fromUI;
            const depth = $scope.listLevelDepth[itemObject.level];
            let fromAnchor = $scope.currentlyEditedNode
              ? $scope.currentlyEditedNode.getTopAnchor
                ? $scope.currentlyEditedNode.getTopAnchor()
                : null
              : null;

            if (fromAnchor) {
              const block = fromAnchor.getBlockData();

              if (
                block.isInsertedListItem() &&
                !block.isInsertedListItemSplitted()
              ) {
                const nodes = block.getInsertedListItemIdElements();

                if (
                  nodes &&
                  nodes.length > 1 &&
                  nodes[0].closest("table") === null
                ) {
                  fromAnchor = nodes[nodes.length - 1];
                }
              }

              const leaseElement = document.querySelector(
                ".lease.lease--original"
              );

              const allFreeTexts = leaseElement.querySelectorAll(
                "[pid],outline-end,lease-end,lease-start,outline"
              );

              const myIndex = allFreeTexts.indexOf(fromAnchor);

              let scanIndex = myIndex + 1;
              let stopScan = false;

              let currentAnchor = fromAnchor;

              while (
                !stopScan &&
                allFreeTexts[scanIndex] &&
                allFreeTexts[scanIndex].tagName !== "OUTLINE" &&
                allFreeTexts[scanIndex].tagName !== "LEASE-START" &&
                allFreeTexts[scanIndex].tagName !== "OUTLINE-END" &&
                allFreeTexts[scanIndex].tagName !== "LEASE-END" &&
                !allFreeTexts[scanIndex].closest(".inserted-list-item") &&
                allFreeTexts[scanIndex].closest(".lp-hide")
              ) {
                if (
                  allFreeTexts[scanIndex].getListLevelNumber() > depth &&
                  !allFreeTexts[scanIndex].closest("del.diff-item--accepted") &&
                  !allFreeTexts[scanIndex].closest("table")
                ) {
                  currentAnchor = allFreeTexts[scanIndex];
                } else {
                  stopScan = true;
                }
                scanIndex++;
              }

              itemObject.afterFreeText = currentAnchor.getAttribute("pid");
              itemObject.index = currentAnchor.getNodeOccurrenceIndexByAttribute(
                "pid"
              );
            }
        }
      }

        this._listsInsertItemAnywhere = (itemObject, byAdmin) => {
          const levelInfo = listItemsManager.getLevelInfoByName(
            itemObject.level
          );
          const depth = $scope.listLevelDepth[itemObject.level];

          if(itemObject.fromUI && !itemObject.singleFT ){
            itemObject.singleFT = true;
          }

          let template = this.createListItemFromTemplate(
            levelInfo ? levelInfo.level : itemObject.level,
            levelInfo ? levelInfo.format : null,
            levelInfo ? levelInfo.name : null,
            itemObject,
          );

          this.locateNewListItemAnchor(itemObject);

          let containerToIsertAfter = null;

          if (itemObject.level === "sub-paragraph") {
            let sectionElement = document.querySelector(`[section-id="${itemObject.afterFreeText}"]`);

            if (sectionElement) {
              let freeTextElement = sectionElement.closest("[free-text]");
              containerToIsertAfter = freeTextElement && freeTextElement.parentElement ? freeTextElement.parentElement : null;
            }
          } else {
            containerToIsertAfter = document.querySelectorWithInstance(
              `[pid="${itemObject.afterFreeText}"]`,
              itemObject.index
            );
          }

          // handle lease if deprecated
          if (!containerToIsertAfter) {
            const lastDataFTSElement = document
              .querySelectorAll(
                '[data-fts*="|' + itemObject.afterFreeText + '|"]'
              )
              .lastItem();
            const ftsData = lastDataFTSElement
              ? lastDataFTSElement.getAttribute("data-fts")
              : null;

            if (ftsData) {
              containerToIsertAfter = document.querySelectorWithInstance(
                '[data-fts="' + ftsData + '"]',
                itemObject.index
              );

              containerToIsertAfter = containerToIsertAfter
                ? containerToIsertAfter
                : document
                    .querySelectorAll('[data-fts="' + ftsData + '"]')
                    .lastItem();
            }
          }

          if (!containerToIsertAfter) {
            return null;
          }

          const freeTextInDom = document.querySelector(`[free-text="${itemObject.headerFreeText}"]`);
          // Get block data for list items based on PID
          const block = itemObject.fromPID ? containerToIsertAfter.getBlockData() : null;
          if(!freeTextInDom){
            const myListItemId = $scope.nextUnusedListItemId;
  
            // insert to document
            const newListItemBox = document.createElement('div');
            newListItemBox.classList.add('new-list-item-container');
            newListItemBox.classList.add("lp-show-element", "lp-if-ignore");
  
            while (template.children.length !== 0) {
              const element = template.children[template.children.length - 1];
  
              if (itemObject.deleted && element && element.classList) {
                element.classList.add("deleted-container");
              }
  
              element.classList.add("lp-show-element", "lp-if-ignore");
  
              if (!byAdmin) {
                element.classList.add("inserted-list-item");
                element.setAttribute("inserted-list-item-id", myListItemId);
              } else {
                element.classList.add("list-item-override");
              }
              newListItemBox.insertBefore(element,newListItemBox.children[0]);
            }
  
            
           
  
            if (
              containerToIsertAfter.parentElement.closest(
                ".new-list-item-container"
              )
            ) {
              while (newListItemBox.children.length !== 0) {
                containerToIsertAfter.parentElement.insertBefore(
                  newListItemBox.children[newListItemBox.children.length - 1],
                  containerToIsertAfter.nextElementSibling
                );
              }
            } else {
              containerToIsertAfter.parentElement.insertBefore(
                newListItemBox,
                containerToIsertAfter.nextElementSibling
              );
            }
  
            
            $scope.nextUnusedListItemId++;
            $scope.listItemUndoTracking[
              itemObject.headerFreeText + "-" + itemObject.bodyFreeText
            ] = myListItemId;
            $scope.listItemObjects[
              itemObject.headerFreeText + "-" + itemObject.bodyFreeText
            ] = itemObject;
          }



          if (block) {
            block.remove();
            containerToIsertAfter = block.containingNode;
          }

          return containerToIsertAfter;

          
        };

        this.listsInsertItem = (itemObject, byAdmin) => {
          if (itemObject.isNewListItem) {
            return this.listsInsertItemAnywhere(itemObject, byAdmin);
          }
          else
          {
            return window.applyFilterVisible(this._listsInsertItem,[itemObject, byAdmin]);
          }
        }
        this._listsInsertItem = (itemObject, byAdmin) => {

          /* make sure new article will be placed in correct place */
          if (itemObject.level.toLowerCase() === "article") {
            if (Object.keys(itemObject).indexOf('isNewArticle') === -1) {
              itemObject.isNewArticle = true;
            }
          }

          let levelAndLocationInfo = this.getNewItemListIdAndFormat(itemObject.afterFreeText, itemObject.level, itemObject.outline, itemObject.isNewArticle);
          var template = this.createListItemFromTemplate(levelAndLocationInfo.level, levelAndLocationInfo.listFormat, levelAndLocationInfo.listId, itemObject);
          var insertList = template.childNodes;
          var myListItemId = $scope.nextUnusedListItemId;
          const insertListParts = Array.from(insertList);

          for (let i = 0; i < insertListParts.length; i++) {
            var toInsert = $(insertListParts[i]);

            if (insertListParts[i].nodeType === Node.TEXT_NODE) {
              continue;
            }

            if (itemObject.deleted && toInsert[0] && toInsert[0].classList) {
              toInsert[0].classList.add('deleted-container');
            }

            if (!byAdmin) {
              toInsert.addClass('inserted-list-item');
              toInsert.attr('inserted-list-item-id', myListItemId);
            } else {
              toInsert.addClass('list-item-override');
            }
            $(levelAndLocationInfo.nodeToInsertBefor).before(toInsert);
            if (toInsert[0].querySelector('[list]')) {
              insertListItemToMap(toInsert.get(0).querySelector('[list]'), true);
            }
          }
          $scope.nextUnusedListItemId++;

          $scope.listItemUndoTracking[
            itemObject.headerFreeText + '-' + itemObject.bodyFreeText
          ] = myListItemId;
          $scope.listItemObjects[
            itemObject.headerFreeText + '-' + itemObject.bodyFreeText
          ] = itemObject;


          return $(levelAndLocationInfo.nodeToInsertBefor);

        };

        this.registerClientTemplates = function(){
          let styleSettings = null;
          let defaultFontSize = '';
          let defaultFontFamily = '';
          let fontSize;
          let fontFamily;
          let lineHeight;

          const fontFamilyNode = document.querySelector(".lease--original [style*='font-family']");
          const fontSizeNode = document.querySelector(".lease--original [style*='font-size']");
          const lineHeightNode = document.querySelector(".lease--original [style*='line-height']");

          if (fontFamilyNode) {
            fontFamily = fontFamilyNode.style.fontFamily.replace(/"/g, "");
          }

          if (fontSizeNode) {
            fontSize = fontSizeNode.style.fontSize;
          }

          if (lineHeightNode) {
            lineHeight = lineHeightNode.style.lineHeight;
          }

          $scope.defaultFont = styleSettings = {
            fontSize: fontSize,
            fontFamily: fontFamily,
            lineHeight: lineHeight,
          }

          if($scope?.lease?.form?.defaultFontData){
            try{
              const data = JSON.parse($scope.lease.form.defaultFontData);
              
              $scope.defaultFont = styleSettings = {
                ...styleSettings,
                ...data,
              }
            }
            catch(e){

            }
          }

          if(styleSettings){
            $scope.defaultFontData= styleSettings;
            if(styleSettings.fontSize){
              defaultFontSize = `font-size:${styleSettings.fontSize};`
            }
            if(styleSettings.fontFamily){
              defaultFontFamily = `font-family:${styleSettings.fontFamily};`
            }
          }

          const paragraphTemplate = `<p class="list-item-paragraph" style="margin-top:0px; margin-bottom:0px;${defaultFontSize}"><span free-text="[[[ headerfreetext ]]]"><span style="${defaultFontFamily}"></span></span></p>`;
          const normalTemplate = `<p class="list-item-paragraph" style="margin-top:0px; margin-bottom:0px;${defaultFontSize}"><span free-text="[[[ headerfreetext ]]]"><span style="${defaultFontFamily}"></span></span></p>`;
          const subParagraphTemplate = `<p class="list-item-paragraph list-item-sub-paragraph" style="margin-top:0px;margin-bottom:0px;${defaultFontSize}"><span free-text="[[[ headerfreetext ]]]"><span style="${defaultFontFamily}"></span></span></p>`;
          const ulParagraphTemplate = `<ul class="list-item-paragraph list-item-ul-paragraph" free-text="[[[ headerfreetext ]]]" bullet="true"></ul>`;

          $templateCache.put('paragraph' + '.html', paragraphTemplate);
          $templateCache.put('normal' + '.html', normalTemplate);
          $templateCache.put('sub-paragraph' + '.html', subParagraphTemplate);
          $templateCache.put('ul-paragraph' + '.html', ulParagraphTemplate);
        }

        // remove body free-text from none table list items
        this.removeBodyFromTemplate = (template,itemObject)=>{
          const keepSingleFT = itemObject.singleFT ? itemObject.singleFT : false;
          if(keepSingleFT){
            const freetextList = template.querySelectorAll('[free-text]');
            if(freetextList.length > 1 && freetextList[0].closest('table') === null){
              const nodeToRemove = freetextList[1].closest('p,h1,h2,h3,h4,h5,h6');
              if(nodeToRemove){
                nodeToRemove.remove();
              }
            }
          }

        };

        this.createListItemFromTemplate = (templateName, listFormat, listId, itemObject,compileTemplate = true, isImage = false) => {
          
          // after reload all "normal" list items will be converted to "paragraph" list items

          let templateText;
          let headerFreeText = itemObject.headerFreeText;
          let bodyFreeText = itemObject.bodyFreeText;
          let addMissingFreeText = itemObject.addMissingFreeText;
          let fromPID = itemObject.fromPID;
          let fromJson = itemObject.json;
          let jsonId = itemObject.jsonID || itemObject.afterFreeText;

          if (fromPID) {
            compileTemplate = false;
          }
          
          templateText = $templateCache.get(templateName + '.html');

          templateText = templateText.replace(/&#91;/gi, '[');
          templateText = templateText.replace(/&#93;/gi, ']');
          templateText = templateText.replace('[[[ listname ]]]', listId);
          templateText = templateText.replace('[[[ listformat ]]]', listFormat);
          templateText = templateText.replace(/free-text-placeholder/g, 'free-text');


          // header, if exists
          templateText = templateText.replace(
            '[[[ headerfreetext ]]]',
            headerFreeText,
          );
          templateText = templateText.replace('[[[ headerdefault ]]]', '');

          // body, if exists
          templateText = templateText.replace(
            '[[[ bodyfreetext ]]]',
            bodyFreeText,
          );
          templateText = templateText.replace('[[[ bodydefault ]]]', '');

          var template = document.createElement('div');
          template.innerHTML = templateText;


          if (templateName === "normal" || templateName == "paragraph" || templateName == "sub-paragraph") {
            let fontFamilyElement = document.querySelector(
              `[free-text="${itemObject.afterFreeText}"] span[style*="font-family"]`
            );

            fontFamilyElement = fontFamilyElement
              ? fontFamilyElement
              : document.querySelector(
                  `[free-text="${itemObject.afterFreeText}"][style*="font-family"]`
                );

            if (fontFamilyElement) {
              let templateFreeText = template.querySelector("[free-text]");
              if (templateFreeText) {
                templateFreeText.style.fontFamily =
                  fontFamilyElement.style.fontFamily;
              }
            }
          }

          // add marker class for image-container
          if(isImage){
            template.firstElementChild.classList.add('image-container');
          }

          // copy font to containing element for level change 

          const freeTextFirstSpanElement = template.querySelector('[free-text] span[style]');
          if(freeTextFirstSpanElement){
            const paretContainer = template.querySelector('[free-text]').parentElement;

            if(freeTextFirstSpanElement.style.fontSize){
              paretContainer.style.fontSize = freeTextFirstSpanElement.style.fontSize;
            }

            if(freeTextFirstSpanElement.style.fontFamily){
              paretContainer.style.fontFamily = freeTextFirstSpanElement.style.fontFamily;
            }
          }


          /* add pid to template Start */

          Array.from( template.children).forEach((node)=>{
            if(node && node.querySelector ){
              let freeTextNode = node.querySelector('[free-text]');
              if(freeTextNode){
                node.setAttribute('pid',freeTextNode.getAttribute('free-text'));
              }
            }
          });

          this.removeBodyFromTemplate(template,itemObject);

          // handle from json

          if(fromJson){
            const  jsonElement = ElementJson.fromJson(itemObject.json);
            const templateFreeText = template.querySelector('[free-text]');

            while (templateFreeText.nextElementSibling) {
              templateFreeText.nextElementSibling.remove();
            }

            while (jsonElement.childNodes.length > 0) {
              let el = jsonElement.childNodes[0];
              el.remove();
              templateFreeText.parentElement.appendChild(el);
            }
            templateFreeText.remove();

            let newPid = template.querySelector('[free-text]').getAttribute('free-text');

          }

          let listSpan = template.querySelector('[list]');
          
          if (listSpan && listSpan.parentElement) {
            const freeTextElement = listSpan.parentElement.querySelector(
              "[free-text]"
            );

            if (freeTextElement) {
              const parent = freeTextElement.closest("p, h1, h2, h3, h4, h5, h6");
  
              if (parent) {
                parent.setAttribute(
                  "pid",
                  freeTextElement.getAttribute("free-text")
                );
                parent.classList.add("inserted-list-item");
  
                if (itemObject.jsonID) {
                  parent.setAttribute("data-json-id", jsonId);
                }
              }
            }
          } else {
            const freeTextElement = template.querySelector("[free-text]");

            if (freeTextElement) {
              const parent = freeTextElement.closest("p, h1, h2, h3, h4, h5, h6");
  
              if (parent) {
                parent.setAttribute(
                  "pid",
                  freeTextElement.getAttribute("free-text")
                );
                parent.classList.add("inserted-list-item");
  
                if (itemObject.jsonID) {
                  parent.setAttribute("data-json-id", jsonId);
                }
              }
            }
          }

          /* add missing free text if needed */
          if(addMissingFreeText){
            const bodyFreeTextEl = template.querySelector(`[free-text="${bodyFreeText}"]`);
            const headreFreeTextEl = template.querySelector(`[free-text="${headerFreeText}"]`);
            if(!bodyFreeTextEl || !headreFreeTextEl ){
              const exsitingFreeText = bodyFreeTextEl || headreFreeTextEl;
              const freeTextId = bodyFreeTextEl ?  headerFreeText : bodyFreeText;

              if(exsitingFreeText){
                const el = document.createElement('span');
                el.setAttribute('free-text',freeTextId);
                if(exsitingFreeText === headreFreeTextEl){
                  exsitingFreeText.parentElement.insertBefore(el,exsitingFreeText.nextElementSibling);

                }
                else{
                  exsitingFreeText.parentElement.insertBefore(el,exsitingFreeText);
                }
              }

            }
            else{
              delete itemObject.addMissingFreeText;
            }
          }

          /* add pid to template End */

          $(template)
            .find('[list]')
            .attr('inserted-list', true);


          template.querySelectorAll('[free-text]').forEach((e)=>{
            if(e.previousSibling && e.previousSibling.nodeType === Node.TEXT_NODE){
              e.previousSibling.remove();
            }
            e.removeWhitespace();
          });

          if(templateName === "normal"){
            const element = document.querySelector(`[free-text="${itemObject.afterFreeText}"]`);
            const sourStyleBlock =  element && element.getBlockData ?  element.getBlockData() : null;
            const pidElement = template.querySelector('[pid]');
            let stylePid = pidElement ? pidElement.getAttribute('pid') : null;
            stylePid = stylePid == "null" ? null : stylePid;
            if(!stylePid  && fromPID ){
              stylePid = itemObject.afterFreeText;
            }
            
            

            let restyle = $scope.lease.restyle[stylePid];

            if (!restyle) {
              restyle =
                sourStyleBlock && sourStyleBlock.containingNode && stylePid
                  ? window.getRestyleObjectFromParagraph(
                      sourStyleBlock.containingNode,
                      stylePid
                    )
                  : null;
            }

            if (restyle) {
              restyle = { ...restyle, textIndent: 0, paragraphIndent: 0 };
            }

            if (restyle && stylePid ) {
              $scope.lease.restyle[stylePid] = restyle;
              setTimeout($scope.restyle,100);              
            }


          }


          if (compileTemplate) {
            if ($rootScope.listItemsPromises) {
              //Know when all free-texts in list-item completely compiled.
              $(template)
                .find('[free-text]')
                .each(function () {
                  var freeTextId = $(this).attr('free-text');
                  $rootScope.listItemsPromises[freeTextId] = $q.defer();
                });
            }
            $compile(template)($scope);
          }

          if (fromPID) {            
            const pidElement = document.querySelector(`[pid="${itemObject.afterFreeText}"]`);

            if(pidElement){
              const block = pidElement.getBlockData();
              block.moveBlockElementsToTemplate(template);
            }
          }

          // setTimeout(()=>{
            
          // });

          return template;

        }

        this.asyncCreateListItemFromTemplate = async (templateName, listFormat, listId, headerFreeText, bodyFreeText) => {

          const templateText = $templateCache.get(templateName + '.html');
          const templateHasBody = templateText.indexOf('bodyfreetext') !== -1;
          const templateHasHeader = templateText.indexOf('headerfreetext') !== -1;
          let itemObject = {headerFreeText:headerFreeText,bodyFreeText:bodyFreeText};

          let template = this.createListItemFromTemplate(templateName, listFormat, listId, itemObject);

          const waitForFreeTextCompile = function (resolve, reject) {
            let testCounter = 0;

            const isReady = () => {
              const headerReady = template.querySelector(`[free-text="${headerFreeText}"] .EOCE`);
              const bodyReady = template.querySelector(`[free-text="${bodyFreeText}"] .EOCE`);
              if (templateHasBody && templateHasHeader && headerReady && bodyReady) {
                return true;
              } else if (templateHasBody && bodyReady) {
                return true;
              } else if (templateHasHeader && headerReady) {
                return true;
              }
            }

            const reTest = () => {
              testCounter++;
              if (isReady()) {
                resolve(template);
              } else if (testCounter < 100) {
                setTimeout(reTest, 20);
              } else {
                reject(template);
              }
            };

            if (isReady()) {
              resolve(template);
            } else {
              setTimeout(reTest, 20);
            }
          }
          return new Promise(waitForFreeTextCompile);
        }


        this.updateListItemsLevelsInDom = ()=>{
          applyShowAll(()=>{
            const list = document.querySelectorAll('[list]');
            let containingNode = null;

            for (let index = 0; index < list.length; index++) {
              containingNode = list[index].getTopAnchor();

              if (!containingNode.hasAttribute('data-list-set-level') && containingNode.previousElementSibling) {
                let roundTripInfo = containingNode.previousElementSibling.textContent.split('!!').filter((item) => {
                  return item.trim() !== ''
                });

                if (roundTripInfo && roundTripInfo.length && roundTripInfo.length === 2) {
                  containingNode.setAttribute('data-list-set-level', roundTripInfo[0]);
                  containingNode.setAttribute('data-list-set-heading', roundTripInfo[1]);
                }
              }
            }

          });
        }

        let getNodeToInsertAfter = (afterFreeText, outline) => {
          // add support for outlines
          let nodeToInsertAfter = document.querySelector('[free-text="' + afterFreeText + '"]');
          if (!nodeToInsertAfter) {
            nodeToInsertAfter = document.querySelector('outline[pid="' + afterFreeText + '"]');
            if(nodeToInsertAfter)
            {
              return nodeToInsertAfter;
            }
            let orgFreeTextNumber = +afterFreeText;
            let freeTextId = 0;
            document.querySelectorAll('[free-text]').forEach((node) => {
              let textId = node.getAttribute('free-text') * 1;
              if (textId < orgFreeTextNumber) {
                freeTextId = Math.max(textId, freeTextId);
              }
            });
            if (freeTextId != 0) {
              // we hit the end of the lease if there was a previous
              nodeToInsertAfter = $("[free-text='" + freeTextId + "']");
            } else {
              // couldn't find anything with a ftId just below the missing one
              nodeToInsertAfter = getOutlineStartNode(outline);
            }
          }
          return nodeToInsertAfter;
        }

        this.getClosestListItem = (node) => {
          node = node.get ? node[0] : node;
          if (!node || !node.getAttribute) {
            return null;
          }

          node = node.getAttribute('pid') ? node :
            node.closest('[pid]') ? node.closest('[pid]') : node.parentElement;

          if (node.hasAttribute('data-list-map-index') &&
            (!node.hasAttribute('data-list-static-item') || node.getAttribute('data-list-static-item') == 'false')) {
            return node;
          } else {
            if (!node.getAttribute('pid')) {
              let freeTextNode = node.querySelector('[free-text]');
              if (freeTextNode) {
                node.setAttribute('pid', freeTextNode.getAttribute('free-text'));
              }
            }
            let list = Array.from(document.querySelectorAll('[pid], outline'));
            let index = list.indexOf(node);

            while (index >= 0 && list[index].tagName !== "OUTLINE" && !list[index].hasAttribute('data-list-map-index') &&
              (!list[index].hasAttribute('data-list-static-item') || list[index].getAttribute('data-list-static-item') == 'false')) {
              index--;
            }
            if (index === -1) {
              return document.querySelector('lease-start');
            }
            return list[index];
          }
        }

        let getBeforeNode = (afterNode, insertLevel) => {
          afterNode = afterNode.get ? afterNode : $(afterNode);
          if (afterNode && afterNode.selector === 'lease-start') {
            return $(document.querySelector('lease-end'));
          }
          let returnNode = null;
          let currentNode = afterNode.closest('[pid]').get(0);
          if (!currentNode) {
            currentNode = afterNode.parent().get(0);
          }
          currentNode = this.getClosestListItem(currentNode);

          if (currentNode.tagName === "OUTLINE") {
            returnNode = document.querySelector('outline-end[data-outline-unique-id="' + currentNode.getAttribute('data-outline-unique-id') + '"]').previousElementSibling;
          } else if (!currentNode || !currentNode.hasAttribute('data-list-map-index')) {
            let node = document.querySelector('[data-list-map-index][data-list-static-item="false"]');
            if (node) {
              let info = this.listItemObjectsArray[node.getAttribute('data-list-map-index') * 1];
              returnNode = info.tableNode ? $(info.tableNode) : $(info.containingNode);
            } else {
              returnNode = $(document.querySelector('lease-end').previousElementSibling);
            }
          }

          if (currentNode && currentNode.hasAttribute('data-list-map-index')) {
            let currentListItemIdex = currentNode.getAttribute('data-list-map-index') * 1;
            let currentListItemInfo = this.listItemObjectsArray[currentListItemIdex];

            if (currentListItemInfo) {
              let outlineLevels = this.listLevelsMap[currentListItemInfo.outline];
              let listDefItem = outlineLevels ? outlineLevels.filter((item) => {
                return item.level === insertLevel
              }) : null;
              let levelIndex = outlineLevels && listDefItem && listDefItem.length === 1 ? outlineLevels.indexOf(listDefItem[0]) : -1;

              if (levelIndex !== -1 && currentListItemIdex < this.listItemObjectsArray.length - 1) {
                let searchIndex = currentListItemIdex + 1;
                let item = this.listItemObjectsArray[searchIndex];

                while ((item.level > levelIndex || (item.static && !item.isSectionEnd)) && searchIndex < this.listItemObjectsArray.length - 1) {
                  searchIndex++;
                  item = this.listItemObjectsArray[searchIndex];
                }
                if (item.outline === currentListItemInfo.outline || item.isSectionEnd) {
                  if (item.level <= levelIndex) {
                    returnNode = item.tableNode ? $(item.tableNode) : $(item.containingNode);
                  } else {
                    if (currentListItemInfo.outline === 'main') {
                      returnNode = $('lease-end');
                    } else {
                      returnNode = $(document.querySelector(`outline-end[name="${currentListItemInfo.outline}"]`));
                    }
                  }
                } else {
                  if (currentListItemInfo.outline === 'main') {
                    returnNode = $('lease-end');
                  } else {
                    returnNode = $(document.querySelector(`outline-end[name="${currentListItemInfo.outline}"]`));
                  }
                }

              } else {
                if (currentListItemInfo.outline === 'main') {
                  returnNode = $('lease-end');
                } else {
                  returnNode = $(document.querySelector(`outline-end[name="${currentListItemInfo.outline}"]`));
                }
              }
            }
          }
          if (!returnNode) {
            console.error('error finding location to add new list item');
          }
          return returnNode;
        }

        let insertListItemToMap = (node, isInsertedListItem, workList) => {
          
          if (isInsertedListItem) {
            listItemsManager.createListItemsMap();
            return;
          }

          if(node.previousElementSibling && node.tagName && node.tagName === "SPAN"){            
            node.remove();
            return;
          }

          workList = workList ? workList : this.listItemObjectsArray;
          let nodeData = node.getListItemInfoFromNode(true);
          if (nodeData) {
            workList.push(nodeData);
            if (nodeData.node.hasAttribute('data-list-map-index')) {
              nodeData.node.setAttribute('data-list-map-index', workList.length - 1);              
            } else {
              nodeData.containingNode.setAttribute('data-list-map-index', workList.length - 1);
            }
            nodeData.currentIndex = workList.length - 1;
            updateListItemNumber(nodeData, workList.length - 1,workList);
            return nodeData;
          }

          let returnValue = null;
          if (node.tagName && ( node.tagName === "BR" || node.tagName === "RESET-LISTS") ) {
            workList.forEach((item) => {
              if (!item.isAutoNumber && item.list && item.outline && this.listsCounters[item.outline]) {
                this.listsCounters[item.outline][item.list] = 0;
              }
            });
          } else if (node.tagName && node.tagName === "SPAN") {
            let itemLevel = getListItemLevelAndOutLine(node);
            let containingNode = node.closest('[pid]');
            containingNode = containingNode ? containingNode : node.parentElement;

            let insNode = node.closest('ins');
            if (insNode && insNode.classList.contains('diff-item--processing')) {
              if (itemLevel && itemLevel.listLevel  && this.listCurrentLevels[itemLevel.listLevel.name]){
                node.setAttribute('list', this.listCurrentLevels[itemLevel.listLevel.name]);
              }
            }

            if (itemLevel.level !== -1) {
              let newItem = {
                ...itemLevel,
                node: node,
                containingNode: containingNode,
                format: node.getAttribute('format'),
                list: node.getAttribute('list'),
                tableNode: node.closest('table'),
                static: false,
                levelName: itemLevel.listLevel.level,
              };
              returnValue = newItem;

              if (!isInsertedListItem) {
                workList.push(newItem);
                newItem.currentIndex = workList.length - 1;
                containingNode.setAttribute('data-list-map-index', newItem.currentIndex);
                containingNode.setAttribute('data-list-static-item', false);

                updateListItemNumber(newItem, newItem.currentIndex ,workList);
              }

              // update props on continer for map and round trip

              containingNode.setAttribute('data-list-format', newItem.format);
              containingNode.setAttribute('data-list-list', newItem.list);

              if (!containingNode.hasAttribute('pid')) {
                let freeTextElement = containingNode.querySelector('[free-text]');
                if (freeTextElement) {
                  containingNode.setAttribute('pid', freeTextElement.getAttribute('pid'));
                }
              }

              if (!containingNode.hasAttribute('data-list-set-level') && containingNode.previousElementSibling) {
                let roundTripInfo = containingNode.previousElementSibling.textContent.split('!!').filter((item) => {
                  return item.trim() !== ''
                });

                if (roundTripInfo && roundTripInfo.length && roundTripInfo.length === 2) {
                  containingNode.setAttribute('data-list-set-level', roundTripInfo[0]);
                  containingNode.setAttribute('data-list-set-heading', roundTripInfo[1]);
                  newItem.setLevel = roundTripInfo[0];
                  newItem.setHeading = roundTripInfo[1];
                }
              }
              else {
                newItem.setLevel = containingNode.getAttribute('data-list-set-level');
                newItem.setHeading = containingNode.getAttribute('data-list-set-heading');
              }

            } else {
              let newStaticItem = {
                ...itemLevel,
                node: node,
                containingNode: containingNode,
                format: node.getAttribute('format'),
                list: node.getAttribute('list'),
                tableNode: node.closest('table'),
                static: true,
                outline: 'static',
                isAutoNumber: (node.getAttribute('style') && node.getAttribute('style').indexOf('pre-wrap') !== -1) ||
                  !node.hasAttribute('style') ? true : false,
              };
              workList.push(newStaticItem);
              if (containingNode) {
                containingNode.setAttribute('data-list-map-index', workList.length - 1);
                containingNode.setAttribute('data-list-static-item', true);
              } else {
                node.setAttribute('data-list-map-index', workList.length - 1);
                node.setAttribute('data-list-static-item', true);
              }

              updateListItemNumber(newStaticItem, workList.length - 1,workList);
              returnValue = newStaticItem;
            }
          } else if (node && node.tagName === "SECTION-END" && node.getAttribute('data-listlevel') !== "999" ) {
            let newStaticItem = {
              node: node,
              containingNode: node,
              static: true,
              outline: 'static',
              isSectionEnd: true,
            };
            newStaticItem.currentIndex = workList.length;

            // get prev level if needed for section-end
            if (!node.hasAttribute('data-list-static-level')) {

              let index = -1;

              if (workList.length > 1) {
                index = workList.length - 1;
                while (workList[index].static && index > 0) {
                  index--;
                }
              }

              let itemData = index !== -1 ? workList[index] : {
                level: 0,
                outline: 'main'
              };

              newStaticItem.level = itemData.level;

              if (node.hasAttribute('data-listlevel')) {
                newStaticItem.level = node.getAttribute('data-listlevel') * 1;
                let elList = Array.from(document.querySelectorAll('span[list], br[style*="mso-break-type:section-break"],outline,outline-end,lease-start,lease-end,section-end,reset-lists'));
                let myIndex = elList.indexOf(node) - 1;
                while (myIndex >= 0 && elList[myIndex].tagName === 'BR') {
                  myIndex--;
                }
                if(myIndex === -1){
                  return;
                }
                if (elList[myIndex].tagName === 'OUTLINE-END' || elList[myIndex].tagName === 'LEASE-END') {
                  newStaticItem.outline = 'static';
                } else if (elList[myIndex].tagName === 'OUTLINE') {
                  newStaticItem.outline = elList[myIndex].getAttribute('name');
                } else if (elList[myIndex].tagName === 'LEASE-START') {
                  newStaticItem.outline = 'main';
                } else if (elList[myIndex].tagName === 'SECTION-END') {
                  newStaticItem.outline = elList[myIndex].getAttribute('data-list-static-outline');
                } else {
                  let listItemIndex = elList[myIndex].getAttribute('data-list-map-index') * 1;
                  newStaticItem.outline = workList[listItemIndex].outline;
                }
              } else {
                newStaticItem.outline = itemData.outline;
              }
              node.setAttribute('data-list-static-level', newStaticItem.level);
              node.setAttribute('data-list-static-outline', newStaticItem.outline);
            } else {
              newStaticItem.level = node.getAttribute('data-list-static-level') * 1;
              newStaticItem.outline = node.getAttribute('data-list-static-outline');
            }
            workList.push(newStaticItem);
            newStaticItem.currentIndex = workList.length;
            node.setAttribute('data-list-map-index', workList.length - 1);
            node.setAttribute('data-list-static-item', true);
            returnValue = newStaticItem;
          }
          return returnValue;
        };


        let getListItemFormattedText = (item) => {
          if (!item || item.isSectionEnd) {
            return '';
          }


          let matches = item.formatUpdate.replace(/({.*?})/g, (data) => {
            const words = formatWordListToken(data, this.listsCounters[item.outline], toWords);
            if (words !== null) return words;

            let counterIndex = data.replace(/{/, '').replace(/}/, '');
            let value = this.listsCounters[item.outline][counterIndex];
            if (value === undefined) {
              value = 1;
              this.listsCounters[item.outline][counterIndex] = 1;
            }
            let returnValue;

            if (counterIndex.indexOf(':') !== -1) {
              let tmp = counterIndex.split(':');
              let leadingZero = tmp[0] * 1;
              value = this.listsCounters[item.outline][tmp[1]];
              returnValue = pad(value, leadingZero);
            } else if (counterIndex.startsWith('D#')) {
              returnValue = this.listsCounters[item.outline][counterIndex.substr(1)];
            } else if (counterIndex.startsWith('#')) {
              returnValue = toRoman(value, counterIndex.startsWith('#U'));
            } else if (!Number.isNaN(counterIndex * 1)) {
              returnValue = value;
            } else {
              let useRepeatingLetters = false;

              if (counterIndex.toLowerCase().endsWith("/r")) {
                counterIndex = counterIndex.replace("/r", "");
                useRepeatingLetters = true;
              }

              let toLowerCase = counterIndex.toUpperCase() === counterIndex;
              returnValue = toLowerCase ? numberToLetters(value, useRepeatingLetters) : numberToLetters(value, useRepeatingLetters).toLowerCase();
            }

            if(returnValue === undefined){
              returnValue = 0;
            }

            return returnValue;
          });

          if (item.node.textContent !== matches) {
            if (
              item.node.children.length === 0 ||
              !$scope.diffgram.processing
            ) {
              item.node.textContent = matches;
            }
          }
        };

        let updateListItemNumber = (newItem, itemIndex, liteItemsList) => {
          newItem.levelCounters = [];

          //update list Identifier from list level details
          if(newItem.listLevel && newItem.listLevel.name){
            newItem.list = newItem.listLevel.name;
            newItem.format = newItem.listLevel.format;
          }

          if (newItem.containingNode && newItem.containingNode.classList.contains('deleted-container')) {
            return;
          }

          let delElement = newItem.node.closest('del');
          if (delElement && delElement.classList.contains('diff-item--accepted')) {
            newItem.vcNotInUse = true;
            return;
          }

          let insElement = newItem.node.closest('ins');
          if (insElement && !insElement.classList.contains('diff-item--accepted')) {
            newItem.vcNotInUse = true;
            return;
          }
          newItem.vcNotInUse = false;

          if (!this.listsCounters) {
            this.listsCounters = {};
          }

          if (!this.listsCounters[newItem.outline]) {
            this.listsCounters[newItem.outline] = {};
          }

          if (!this.listsCounters[newItem.outline][newItem.list] || newItem.reset) {
            this.listsCounters[newItem.outline][newItem.list] = 0;
          }

          this.listsCounters[newItem.outline][newItem.list] = this.listsCounters[newItem.outline][newItem.list] + 1 + newItem.skip;
          newItem.counter = this.listsCounters[newItem.outline][newItem.list];

          if (newItem.listLevel) {
            if (
              this.listCurrentLevels[newItem.listLevel.name] &&
              this.listCurrentLevels[newItem.listLevel.name] !== newItem.list
            ) {
              this.listCurrentLevels[newItem.listLevel.name] = newItem.list;
            } else {
              this.listCurrentLevels[newItem.listLevel.name] = newItem.list;
            }

            newItem.formatUpdate = resolveListFormat(
              newItem.listLevel.format,
              this.listCurrentLevels,
              newItem.levelCounters,
            );
          } else {
            this.listCurrentLevels[newItem.list] = newItem.list;
            newItem.formatUpdate = newItem.format;
          }

          if (
            !newItem.static &&
            newItem.levelCounters &&
            newItem.levelCounters.length > 0
          ) {
            let prevItem = null;
            itemIndex--;
            while (itemIndex >= 0 && !prevItem) {
              if (!liteItemsList[itemIndex].static && !liteItemsList[itemIndex].vcNotInUse &&
                !liteItemsList[itemIndex].containingNode.classList.contains('deleted-container')) {
                prevItem = liteItemsList[itemIndex];
              }
              itemIndex--;
            }

            if (prevItem) {
              if (prevItem.level < newItem.level) {
                let fullPrevCounters = this.getListItemFullCounters(prevItem);
                newItem.levelCounters.forEach((item) => {
                  if (fullPrevCounters.indexOf(item) === -1) {
                    this.listsCounters[newItem.outline][item] = 1;
                  }
                });
              } else if (prevItem.level > newItem.level && prevItem.levelCounters) {
                prevItem.levelCounters.forEach((item) => {
                  if (newItem.levelCounters.indexOf(item) === -1) {
                    this.listsCounters[newItem.outline][item] = 0;
                  }
                });
              }
            }
          }

          getListItemFormattedText(newItem);
        };

        this.getListItemFullCounters = (item)=>{
          let retArr = [];
          let counter =item.level;

          while(listItemsManager.listLevelsMap[item.outline][counter])
          {
            retArr.push(listItemsManager.listLevelsMap[item.outline][counter].name);
            counter--;
          }
          return retArr;
        }

        let numberToLetters = (number, useRepeatingLetters = false) => {
          if (useRepeatingLetters) {
            // Generate AA, BB, CC, etc. pattern
            let letterIndex = ((number - 1) % 26) + 1;
            let letter = String.fromCharCode(64 + letterIndex);
            let repeatCount = Math.ceil(number / 26);
            return letter.repeat(repeatCount);
          } else {
            // Default behavior: A, B, C, ..., Z, AA, AB, AC, etc.
            let count = 0;
            let tempNumber = number;
            while (Math.floor(tempNumber / 26) > 0 && tempNumber > 26) {
              count++;
              tempNumber -= 26;
            }
            let ret = count > 0 ? `${String.fromCharCode(64 + count)}${String.fromCharCode(64 + tempNumber)}` : `${String.fromCharCode(64 + tempNumber)}`;
            return ret;
          }
        };

        let getListItemLevelAndOutLine = (node) => {
          let arr = $scope.listLevels;
          let currentOutline = this.outlineArray[this.outlineArray.length - 1];

          if (!currentOutline || (currentOutline && currentOutline.name === 'main')) {
            currentOutline = null;
          }

          let levelAndOutline = {
            level: -1,
            outline: '',
            listLevel: null,
            skip: node.getAttribute && node.getAttribute('skip') ? node.getAttribute('skip') * 1 : 0,
            reset: node.getAttribute && node.getAttribute('reset') && (node.getAttribute('reset') === "true") ? true : false,
          };

          if (!!node.getAttribute('format')) {
            for (var i = 0; i < arr.length; i++) {
              if (node.parentElement.previousElementSibling &&
                node.parentElement.previousElementSibling.textContent.indexOf(`!!${arr[i].listHeader}!!`) !== -1) {
                levelAndOutline.outline = arr[i].outline;
                levelAndOutline.outline = levelAndOutline.outline ? levelAndOutline.outline : 'main';

                if (arr[i].outline && this.listLevelsMap[arr[i].outline]) {
                  levelAndOutline.level = $scope.listLevelDepth[i];
                  levelAndOutline.listLevel = arr[i];
                } else {
                  levelAndOutline.level = i;
                  levelAndOutline.listLevel = arr[i];
                }
                break;
              }
            }
          }

          if (levelAndOutline.level === -1) {
            var levelIdx = $scope.listsLookupLevelIdx(
              node.getAttribute('list'),
              node.getAttribute('format'),
              node.parentElement.tagName,
              node.getAttribute('outline') ? node.getAttribute('outline') : currentOutline,
            );

            if (levelIdx !== null) {
              levelAndOutline.outline = arr[levelIdx].outline;
              levelAndOutline.outline = levelAndOutline.outline ? levelAndOutline.outline : 'main';

              if (arr[levelIdx].outline && this.listLevelsMap[arr[levelIdx].outline]) {
                levelAndOutline.level = $scope.listLevelDepth[levelIdx];
                levelAndOutline.listLevel = arr[levelIdx];
              } else {
                levelAndOutline.level = levelIdx;
                levelAndOutline.listLevel = arr[levelIdx];
              }
            }
          }

          return levelAndOutline;
        };

        let listsLevelIncrement = (level, amount) => {
          level = level.toString();
          var val = level.replace('#L', '').replace('#U', '');

          if (!isNaN(level)) {
            val = 1 * val + amount;
          } else if (isLowercaseLetter(level.charAt(0))) {
            val = numberToLettersIncrement(
              lettersToNumber(val, false) + amount - 1,
              false,
            );
          } else if (isUppercaseLetter(level.charAt(0))) {
            val = numberToLettersIncrement(
              lettersToNumber(val, true) + amount - 1,
              true,
            );
          } else if (_.startsWith(level, '#L')) {
            val = '#L' + (parseInt(val) + amount);
          } else if (_.startsWith(level, '#U')) {
            val = '#U' + (parseInt(val) + amount);
          }

          return val;
        };

        let createNewListId = (listName, levelName) => {

          if (levelName === 'article') {
            return listName;
          }
          let increment = 100000;
          let newListName = listsLevelIncrement(listName, increment);
          while (document.querySelector('span[list="' + newListName + '"]')) {
            increment++;
            newListName = listsLevelIncrement(listName, increment);
          }
          return newListName;
        };


        if (!HTMLElement.prototype.getListItemInfoFromNode) {
          HTMLElement.prototype.getListItemInfoFromNode = function (fromInserListItem = false) {
            if (!this.hasAttribute('data-list-map-index') && ! fromInserListItem) {
              let node = this.closest('[data-list-map-index]');
              return node ? node.getListItemInfoFromNode() : null;
            } else {
              let ret = this.hasAttribute('data-list-map-index') ?
                listItemsManager.listItemObjectsArray[this.getAttribute('data-list-map-index') * 1] :
                null;
                if(fromInserListItem){
                  return ret && ret.node === this ? ret : null;
                }
                else{
                  return ret;
                }
            }
          }
        }
        this.registerClientTemplates();
      }

      window.listItemsManager = new ListItemsManager(scope);

      scope.listItemsManager = window.listItemsManager;
    })($scope);

    /* list items manager ------------------------------------------------------------------------------------ End    */

    /* SML Snippet ------------------------------------------------------------------------------------ Start    */

    (function() {
      function SMLSnippetManager() {
        this.editorElement = null;
        this.leaseElement = null;
        this.scope = null;
        this.leaseDocumentElement = null;

        Object.defineProperty(SMLSnippetManager.prototype,'inited',{
          value: function() {
            return this.leaseElement && this.scope ? true : false;
          },
          writable: false,
        });

        this.show = ()=>{
          if(!this.inited()){
            init();
          }
          // this.editorElement.innerHTML = '';
          showSmlEditor();
        };

        this.hide = ()=>{
          tinymce.remove('#sml-snippet-editor');
          showDocumentElement();
        };

        this.testSML = () => {
          const snippet = tinyMCE.get("sml-snippet-editor").getContent();

          if (!snippet) {
            this.displayElement.innerText = '';
            return;
          }

          this.displayElement.innerText = "Loading...";

          ApiService.snippet(snippet).then(data => {
            if (!this.specificUrl) {
              const data = Object.keys(this.scope).filter(x => {
                return x.indexOf("SpecificUrl") !== -1;
              });
              if (data && data[0]) {
                this.specificUrl = this.scope[data[0]];
              }
            }

            const decoder = new TextDecoder();
            const answer = decoder.decode(data.data);
            let dummy = document.createElement("dummy");
            dummy.innerHTML = answer;
            this.displayElement.innerHTML = dummy.firstElementChild.innerText;
            this.displayElement
              .querySelectorAll("building-variable")
              .forEach(buildingVariable => {
                buildingVariable.setAttribute("ng-attr-resource-url", "specificUrl");
              });
            $compile(this.displayElement)(this.scope);
          });
        };

        this.clearTestSML = () => {
          this.displayElement.innerText = '';
          tinyMCE.get("sml-snippet-editor").setContent("");
        };

        const init = () => {
          this.leaseElement = document.querySelector(".lease.lease--original");
          this.leaseDocumentElement = this.leaseElement.querySelector('.document');
          this.scope = angular.element(this.leaseElement).scope();
          this.editorElement = document.createElement('div');
          this.editorElement.innerHTML = smlSnippetEditrHTML;
          this.leaseElement.appendChild(this.editorElement);
          this.closeBtn = document.querySelector('.btn-close-sml-editor');
          this.closeBtn.addEventListener('click',this.hide);
          this.testBtn = document.querySelector('.btn-test-sml');
          this.testBtn.addEventListener('click',this.testSML);
          this.clearTestBtn = document.querySelector('.btn-clear-test-sml');
          this.clearTestBtn.addEventListener('click',this.clearTestSML);
          this.displayElement = document.querySelector('.sml-snippet__result');
        };

        const smlSnippetEditrHTML = `
          <div id="sml-snippet">
            <div class="sml-snippet__title">
              <div class="text"> Edit SML </div>
              <button class="btn-close-sml-editor btn dangerous">Close</button>
              <button class="btn-test-sml btn blue">Test</button>
              <button class="btn-clear-test-sml btn blue">Clear</button>
            </div>
            <div class="sml-editor--wrapper">
              <div id="sml-snippet-editor"> </div>
            </div>
            <div class="sml-snippet__result"></div>
          </div>
        `;

        let hideDocumentElement = ()=>{
          this.editorElement.classList.remove('lp-hide-element');
          this.leaseDocumentElement.classList.add('lp-hide-element');
        }

        let showDocumentElement = ()=>{
          this.leaseDocumentElement.classList.remove('lp-hide-element');
          this.editorElement.classList.add('lp-hide-element');
        }

        let showSmlEditor = ()=>{
          hideDocumentElement();
          tinymce.init({
            selector: '#sml-snippet-editor',
            resize: 'false',
            plugins: [
              'autoresize',
              'advlist autolink lists link image charmap print preview anchor',
              'searchreplace visualblocks fullscreen',
              'insertdatetime media table contextmenu',
              'textcolor colorpicker',
            ],
            menubar : false,
            // invalid_styles: 'color',
            branding: false,
            auto_focus: 'sml-snippet-editor',
            invalid_elements: 'a',
            toolbar: [
              {
                name: 'history', items: [ 'undo', 'redo' ]
              },
              {
                name: 'styles', items: [ 'styleselect' ]
              },
              {
                name: 'fontsize', items: [ 'fontsizeselect' ]
              },
              {
                name: 'formatting', items: [ 'bold', 'italic', 'underline']
              },
              {
                name : 'color', items: ['forecolor', 'backcolor']
              },
              {
                name: 'alignment', items: [ 'alignleft', 'aligncenter', 'alignright', 'alignjustify' ]
              },
              {
                name: 'indentation', items: [ 'outdent', 'indent' ]
              },

            ],
          });
        }
      }

      window.smlSnippetManager = new SMLSnippetManager();
    })();

    /* SML Snippet ------------------------------------------------------------------------------------ End      */



    /* editor search  -----------------------------------------------  start */
    (function (scopeVar) {

      function getSingleItemFromArrayByProperty(array, property, value) {
        let returnValue = null;
        for (let i = 0; i < array.length && !returnValue; i++) {
          if (array[i][property] === value) {
            returnValue = array[i];
          }
        }
        return returnValue;
      }

      function cleanLeaseVarName(name) {
        if (Object.keys(window?.lease?.form?.customEditor || {}).length === 0)  {
          return name.replace(/\[.*?]/g, '');
        } else {
          return name.replace(/\[.*?]/g, '').replace("lease.", "").replace("title.", "");
        }
      }

      function FieldDataItem(spanLabelElement) {

        this.leaseVarName = spanLabelElement.closest('editor-label').getAttribute('for');
        this.cleanLeaseVarName = cleanLeaseVarName(this.leaseVarName);
        if (Object.keys(window?.lease?.form?.customEditor || {}).length === 0)  {
          this.element = spanLabelElement;
          this.name = scopeVar.labels[this.cleanLeaseVarName];
          this.longName = scopeVar.fullLabels[this.cleanLeaseVarName];
        } else {
          this.element = spanLabelElement.closest("[data-type]").querySelector('label');
          this.name = this.element.textContent.trim();
          this.longName = this.name;
        }
        this.lowerCaseName = this.name ? this.name.toLowerCase() : '';
        this.lowerCaseLongName = this.longName ? this.longName.toLowerCase() : '';
        this.cardElement = this.element.closest('.card');
        this.cardElement = this.cardElement ? this.cardElement : this.element.closest('.collapse-card');
        if (!this.cardElement) return;
        this.cardTitle =  this.cardElement.querySelector('.collapse-card__title');
        this.cardName = this.cardElement ? this.cardElement.querySelector('.collapse-card__title').textContent.trim() : '';
        if (this.cardName === '') {
          let input = this.cardElement.querySelector('.collapse-card__title')
            .querySelector('input');
          if (input) {
            this.cardName = input.value.trim()
          }
        }
        this.searchName = this.lowerCaseName !== '' ? this.lowerCaseName : this.lowerCaseLongName;
        this.displayName = `${this.cardName} -> ${this.name ? this.name : this.longName}`;
        this.editorControl = this.element.closest('editor-control');
        this.isVisible = () => {
          this.isCurrenlyVisible = this.element.closest('.ng-hide') ? false : true;
          return this.isCurrenlyVisible;
        };
        this.isCurrenlyVisible = this.isVisible();

        this.hasName = () => {
          return this.name || this.longName;
        }

        if (this.editorControl) {
          this.editorControlFor = this.editorControl.getAttribute('for');
        }

        this.goTo = async (displayCardIfNotVisible = true) => {
          var deferred = $q.defer();
          await this._openContainingCard();
          setTimeout(()=>{
            this._scrollTo(displayCardIfNotVisible);
            deferred.resolve();
          }, 300);
          await deferred.promise;
          return this.isVisible();
        }

        this._getConditionFields = (condition)=>{
          let reg = new RegExp(/[a-zA-Z.\[\]0-9]*/g);
          let fieldOptions = condition.match(reg).filter((item) => {
            return item.trim() !== ""
          });
          return fieldOptions;
        }
        this._findEditorShowElements = async (element, allFields) => {
          if (!element) {
            return;
          }
          let editorShowElement = element.closest('[editor-show]');
          if (editorShowElement) {
            allFields = allFields ? allFields : leftSideEditorSearch.getAllFields();
            let editorShowCondition = editorShowElement.getAttribute('editor-show');
            let fieldOptions = this._getConditionFields(editorShowCondition)
            let field = null;
            for (let index = 0; index < fieldOptions.length && field === null; index++) {
              field = getSingleItemFromArrayByProperty(allFields, 'leaseVarName', fieldOptions[index]);
              if (field) {
                await field._openContainingCard();
              }
              if (field && !field.isVisible()) {
                field = null;
              }
            }
            if (field) {
              field.goTo();
            } else {
              this._findEditorShowElements(editorShowElement.parentElement, allFields);
            }
          }

        }
        this._scrollTo = async (displayCardIfNotVisible) => {
          if (this.isVisible()) {
            this.element.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
            this.removeHighlight();
            this.highlight();
          } else if(!displayCardIfNotVisible) {
            this._findEditorShowElements(this.element);
          }
          else{
            this.removeCardHighlight();
            this.highlightCard();
          }
        }

        this.highlight = (time = 3) => {
          this.element.classList.add('search-highlight');
          if (time > 0) {
            setTimeout(this.removeHighlight, time * 1000);
          }
        }

        this.removeHighlight = () => {
          this.element.classList.remove('search-highlight');
        }

        this.highlightCard = (time = 3) => {

          this.cardTitle.classList.add('search-highlight');
          this.cardTitle.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          if (time > 0) {
            setTimeout(this.removeCardHighlight, time * 1000);
          }
        }

        this.removeCardHighlight = () => {
          this.cardTitle.classList.remove('search-highlight');
        }

        this._openContainingCard = async () => {
          let cardBody;
          let cardHeading;
          if (this.cardElement) {
            cardBody = this.cardElement.querySelector('.collapse-card__body');
            cardHeading = this.cardElement.querySelector('.collapse-card__heading');
            if (!cardBody.offsetParent) {
              cardHeading.click();
            }
          }

          const waitForCardToOpen = function (resolve, reject) {
            let testCounter = 0;
            const isReady = () => {
              return (cardBody.offsetParent ? true : false);
            }
            const reTest = () => {
              if (!cardBody) {
                reject();
              }
              testCounter++;
              if (isReady()) {
                resolve();
              } else if (testCounter < 100) {
                setTimeout(reTest, 20);
              } else {
                reject();
              }
            };

            if (isReady()) {
              resolve();
            } else {
              setTimeout(reTest, 20);
            }
          }
          return new Promise(waitForCardToOpen);
        }
      }

      function LeftSideEditorSearch() {
        this.getAllFields = () => {
          let fields = [];
          Array.from(document.querySelectorAll('editor-label span')).forEach((node) => {
            let field = new FieldDataItem(node);
            if (field.hasName && field.hasName()) {
              fields.push(field);
            }
          });
          return fields;
        }

        this.searchByName = (name) => {
          let workName = name.toLowerCase();
          const result = this.getAllFields().filter((filed) => {
            return (filed.searchName.indexOf(workName) !== -1);
          });
          return result;
        }

        this.searchByLeaseVarName = (name) => {
          const cleanName = cleanLeaseVarName(name);
          const result = this.getAllFields().filter((filed) => {
            return filed.leaseVarName === name || filed.cleanLeaseVarName == cleanName;
          });
          return result;
        }

        this.getFieldWithIndex = (name, index) => {
          return this.searchByLeaseVarName(name)[index];
        }

        this.openFieldByRelateds = (element) => {
          let name = element.getAttribute('ng-attr-name');
          var relateds = scopeVar.inline.findNestedControls(element);
          // remove yourself from array and reposition at beginning
          var myIdx = relateds.indexOf(name);
          if (myIdx > -1) {
            relateds.splice(myIdx, 1);
          }
          relateds.unshift(name);
          relateds = scopeVar.inline.util.checkRelateds(relateds, element);

          var index = null;
          if (name.indexOf('[') !== -1) {
            var elScope = angular.element(element).scope();
            index = elScope.$index;
          }

          let field = null;
          let allFields = this.getAllFields();
          if (index !== null) {
            field = getSingleItemFromArrayByProperty(allFields, 'leaseVarName', relateds[index])
          }

          for (let i = 0; i < relateds.length && !field; i++) {
            let item = relateds[i];
            item = item.replace('.length', '');
            field = getSingleItemFromArrayByProperty(allFields, 'leaseVarName', item)
          }

          if (field) {
            field.goTo();
          }
        }

        this.searchByLeaseVarElement = element => {
          if (element instanceof jQuery) {
            element = element[0];
          }
          
          let name;
          
          if (element.tagName === "LEASE-VAR") {
            name = element.getAttribute("ng-attr-name");
          } else {
            name = element.getAttribute("var");
          }

          let cleanName = cleanLeaseVarName(name);
          var scope = angular.element(element).scope();

          let fields = this.searchByLeaseVarName(cleanName);
          if (fields.length === 0) {
            this.openFieldByRelateds(element);
          } else if (fields.length === 1) {
            fields[0].goTo();
          } else {
            if (scope.$index && fields[scope.$index].isVisible()) {
                fields[scope.$index].goTo();
            } else {
              let index = Array.from(
                document.querySelectorAll(
                  'lease-var[ng-attr-name="' + name + '"]'
                )
              ).indexOf(element);
              if (fields[index] && fields[index].isVisible()) {
                fields[index].goTo();
              } else {
                for (var i = 0; i < fields.length; i++) {
                  if (fields[i].isVisible()) {
                    fields[i].goTo();
                    break;
                  }
                }
              }
            }
          }
        };

        // Cache search results by cleaned variable name to avoid repeated DOM scans
        this._fieldsByVarName = {};

        this.searchByVariableName = (name, index) => {
          const cleanName = cleanLeaseVarName(name);

          let fields = this._fieldsByVarName[cleanName];
          if (!fields) {
            fields = this.searchByLeaseVarName(cleanName);
            this._fieldsByVarName[cleanName] = fields;
          }

          if (!fields || fields.length === 0) {
            // Try to find an element by variable name to use as fallback
            const element = document.querySelector('lease-var[ng-attr-name="' + name + '"]');
            if (element) {
              this.openFieldByRelateds(element);
            }
            return;
          }

          // Normalize preferred index to a valid number
          const preferredIndex =
            typeof index === 'number' && index >= 0 && index < fields.length
              ? index
              : 0;

          let targetField = null;

          if (fields[preferredIndex] && fields[preferredIndex].isVisible()) {
            targetField = fields[preferredIndex];
          } else {
            for (let i = 0; i < fields.length; i++) {
              if (fields[i].isVisible()) {
                targetField = fields[i];
                break;
              }
            }
          }

          if (targetField) {
            targetField.goTo();
          }
        };
      }

      window["leftSideEditorSearch"] = new LeftSideEditorSearch();

    })($scope);

    /* editor search  -----------------------------------------------  end   */

    function preserveAsposeCustomStyles() {
      // Evidently, browsers don't support custom css rules
      // meaning, when you try to make a change to any css rules,
      // they disappear altogether. we need to preserve them to allow
      // Aspose convert HTml to Word properly
      $('.lease')
      .not('.lease--modified')
      .find('span[style*="-aw-"]')
      .each(function() {
        let style = $(this).attr('style') || '';
        let parts = style.split(/[:;]/g);

        for (let partIndex = 0; partIndex < parts.length; partIndex += 2) {
          if (parts[partIndex].indexOf('-aw-') !== -1) {
            let customStyle = $(this).attr('data-custom-style') || '';

            if (customStyle.indexOf(parts[partIndex] === -1)) {
              customStyle += `${parts[partIndex]}: ${parts[partIndex + 1]};`;
              $(this).attr('data-custom-style', customStyle);
            }
          }

          if (parts[partIndex].trim() === '-aw-import' && parts[partIndex + 1].trim() === 'spaces' && this.textContent) {
            if (style.indexOf('7pt') === -1) {
              // Replace hard-space with normal space
              this.textContent = this.textContent.replace(/ /g, ' ');
            }
          }
        }
      });
    }

    function fixSubSupTextDecorations() {
      const root = document.querySelector('.lease:not(.lease--modified)');
      
      if (root) {
        const sup = root.querySelectorAll("[style*='vertical-align: super']");
        sup.forEach((item) => {
          item.style.fontSize = "75%";

          const span = document.createElement("span");

          if (item.style.textDecoration === "underline") {
            span.style.textDecoration = "underline";
            item.style.textDecoration = "none";
            item.parentElement.insertBefore(span, item);
            span.appendChild(item);
          }
        });
  
        const sub = root.querySelectorAll("[style*='vertical-align: sub']");
        sub.forEach((item) => {
          item.style.fontSize = "75%";

          const span = document.createElement("span");

          if (item.style.textDecoration === "underline") {
            span.style.textDecoration = "underline";
            item.style.textDecoration = "none";
            item.parentElement.insertBefore(span, item);
            span.appendChild(item);
          }
        });
      }
    }

    function fixEmptyParagraphs() {
      const root = document.querySelector('.lease:not(.lease--modified)');

      if (root) {
        const paragraphs = Array.from(root.querySelectorAll("[pid]"));
        let paragraph;
        let placeholder;
        let clone;
  
        for (let i = 0; i < paragraphs.length; i++) {
          paragraph = paragraphs[i];
          clone = document.createElement("dummy");
          clone.innerHTML = paragraph.outerHTML;
          clone.removeWhitespace();
  
          // This checks if the paragraph only consists of a &nbsp;
          if (clone.textContent === ' ' && !clone.querySelector("img")) {
            placeholder = paragraphs[i].querySelector(".PLACEHOLDER");
    
            if  (placeholder) {
              placeholder.textContent = window.CONSTS.SPECIAL_CHARS.ZERO_WIDTH_NO_BREAKING_SPACE;
            }
          }
        }
      }
    }

    function fixInsertedImages() {
      const root = document.querySelector('.lease:not(.lease--modified)');

      if (root) {
        const images = root.querySelectorAll(".inserted-list-item img[data-original-src]");
        let container;

        for (let i = 0; i < images.length; i++) {
          container = images[i].getTopAnchor();
          container.classList.add("image-container");
        }
      }
    }

    function fixGenerateTableFirstColumnStyles() {
      const root = document.querySelector('.lease:not(.lease--modified)');

      if (root) {
        const cells = root.querySelectorAll("generate-table tbody tr td:not([style]) + td[style]");

        for (let i = 0; i < cells.length; i++) {
          cells[i].previousElementSibling.setAttribute("style", cells[i].getAttribute("style"));
        }
      }
    }

    $scope.listsRefresh = function(invalidateCrossReferences = true) {
      if (!$scope.inInitialLoad) {
        $scope.lists = [];

        listItemsManager.createListItemsMap();

        if (invalidateCrossReferences) {
          const crrs = document.querySelectorAll('crr[id]', true);

          // Index the bookmarks once instead of scanning the document for each
          // reference. `document.querySelector(q, true)` is filterVisible(qsa(q))[0],
          // so keeping the first visible crb per id is the same lookup, and the loop
          // below only ever writes to crr elements -- the index cannot go stale.
          const crbsById = new Map();
          const visibleCrbs = document.querySelectorAll('crb[id]', true);

          for (let crbIndex = 0; crbIndex < visibleCrbs.length; crbIndex++) {
            const visibleCrb = visibleCrbs[crbIndex];
            const crbId = visibleCrb.getAttribute('id');

            if (!crbsById.has(crbId)) {
              crbsById.set(crbId, visibleCrb);
            }
          }

          for (let crrIndex = 0; crrIndex < crrs.length; crrIndex++) {
            let crr = crrs[crrIndex];
            let id = crr.getAttribute('id');

            if (!id.match(/^[a-zA-Z0-9]*(-?[a-zA-Z0-9])*$/)) {
              const errorMessage = `There's an issue with the way the \`crr-${id}\` is set up`;
              console.error(errorMessage);
              Raven.captureMessage(errorMessage);
            } else {
              let crb = crbsById.get(id);

              // A crb nested inside an already-rewritten crr would have been
              // detached by that rewrite; querySelector would not have found it
              // either, so fall through to the same error path.
              if (crb && !crb.isConnected) {
                crb = undefined;
              }

              if (!crb) {
                const errorMessage = `Couldn't find the bookmark for \`crr-${id}\``;
                console.error(errorMessage);
                Raven.captureMessage(errorMessage);
                continue;
              }

              let parent = $(crb.closest("[pid]"));
              let refs = Array.from(parent.get(0).querySelectorAll('[format], crb'));
              let refCrbIndex = refs.indexOf(crb);
              let ref = '';
              let transform;

              // Generate the reference,
              // we concatenate all lists prior to the bookmark, to support
              // complex scenarios consisting of multiple auto-numbers
              for (let refIndex = 0; refIndex < refCrbIndex; refIndex++) {
                ref += refs[refIndex].textContent.trim();
              }

              // Legacy - Then, we check if the CRB specifies any transformation
              if (crb.getAttribute('is-lower-case')) {
                if (crb.getAttribute('is-lower-case') === 'true') {
                  ref = ref.toLowerCase();
                }
              }

              // New - Then, we check if the CRB specifies any transformation
              if (crb.hasAttribute('transform')) {
                transform = crb.getAttribute('transform');

                if (transform === 'lowercase') {
                  ref = ref.toLowerCase();
                } else if (transform === 'uppercase') {
                  ref = ref.toUpperCase();
                } else if (transform === 'capitalize') {
                  ref = $filter('capitalize')(ref).toString();
                } else if (transform === 'capitalize all') {
                  ref = $filter('capitalizeAll')(ref).toString();
                }
              }

              // Then, we check if the CRR override it with a different transformation
              if (crr.hasAttribute('transform')) {
                transform = crr.getAttribute('transform');

                if (transform === 'lowercase') {
                  ref = ref.toLowerCase();
                } else if (transform === 'uppercase') {
                  ref = ref.toUpperCase();
                } else if (transform === 'capitalize') {
                  ref = $filter('capitalize')(ref).toString();
                } else if (transform === 'capitalize all') {
                  ref = $filter('capitalizeAll')(ref).toString();
                }
              }

              if (crr.hasAttribute("format")) {
                const format = crr.getAttribute('format');
                const matcher = new RegExp(format, 'g');
                let delimiter = "";
                
                if (crr.hasAttribute('delimiter')) {
                  delimiter = crr.getAttribute('delimiter');
                }

                const result = ref.match(matcher);

                if (result.length <= 1) {
                  ref = ref[0];
                } else {
                  ref = result.join(delimiter);
                }
              }

              // If the reference ends with a dot, remove it
              if (ref[ref.length - 1] === '.') {
                ref = ref.substring(0, ref.length - 1);
              }

              crr.innerHTML = `<a href="#cross_ref_${id}">${ref}</a>`
            }
          }
        }

        if (!$scope.isPrerender) {
          // set lists spacing (based on listSpaces global array variable specification) -- not required for pre-render
          $scope.listsSetSpaces();
        }
      }
    };

    // check if two list formats are equal, besides their set numbering
    $scope.listFormatsEqual = function(format1, format2) {
      return normalizeListFormat(format1) === normalizeListFormat(format2);
    };

    // check if two list levels are of the same type
    $scope.listLevelsEqual = function(level1, level2) {
      level1 = level1
        .replace('#U', '$$')
        .replace('#L', '!!')
        .replace(/([0-9])+/g, '1')
        .replace(/([A-Z])+/g, 'A')
        .replace(/([a-z])+/g, 'a');
      level2 = level2
        .replace('#U', '$$')
        .replace('#L', '!!')
        .replace(/([0-9])+/g, '1')
        .replace(/([A-Z])+/g, 'A')
        .replace(/([a-z])+/g, 'a');
      return level1 == level2;
    };

    // go through the listLevels list to find the index of the matching level for given attributes
    $scope.listsLookupLevelIdx = function(name, format, outerTag, outlineName) {
      if (name) {
        for (var i = 0; i < $scope.listLevels.length; i++) {
          var level = $scope.listLevels[i];
          if (
            $scope.listLevelsEqual(level.name, name) &&
            $scope.listFormatsEqual(level.format, format) &&
            outlineName == level.outline &&
            level.outerTag == outerTag
          ) {
            return i;
          }
        }
      }
      return null;
    };

    $scope.listsFindMostInferiorLevelIdx = function(nodeSectionPath) {
      var toReturn = -1;
      for (var i = 0; i < $scope.listLevels.length; i++) {
        if (nodeSectionPath[$scope.listLevels[i].level]) {
          toReturn = Math.max(toReturn, i);
        }
      }
      return toReturn;
    };

    // get the next freetext value
    $scope.getNextFreeTextValue = function() {
      for (
        var i = 0;
        $scope.overrideOrderedListitem &&
        i < $scope.overrideOrderedListitem.length;
        i++
      ) {
        $scope.lastFreeText = Math.max(
          Math.max(
            $scope.overrideOrderedListitem[i].bodyFreeText,
            $scope.overrideOrderedListitem[i].headerFreeText,
          ),
          $scope.lastFreeText,
        );
      }
      for (var i = 0; i < $scope.listItems.length; i++) {
        $scope.lastFreeText = Math.max(
          Math.max(
            $scope.listItems[i].bodyFreeText,
            $scope.listItems[i].headerFreeText,
          ),
          $scope.lastFreeText,
        );
      }
      $scope.lastFreeText += 100;
      return $scope.lastFreeText;
    };


    /*
     * Anything in $scope.listItems pointing to the oldPointingTo value
     * will be redirected to point to the newPointingTo value as it's afterVal
     */
    $scope.listsFixLinkedList = function(oldPointingTo, newPointingTo) {
      for (var i = 0; i < $scope.listItems.length; i++) {
        if ($scope.listItems[i].afterFreeText == oldPointingTo) {
          $scope.listItems[i].afterFreeText = newPointingTo;
        }
      }
    };

    // http://stackoverflow.com/questions/4233265/contenteditable-set-caret-at-the-end-of-the-text-cross-browser
    $rootScope.createCaretPlacer = $scope.createCaretPlacer = function(
      atStart
    ) {
      return function(el) {
        if (!el) {
          return;
        }
        try {
          var $elCe = $(el).closest("[contenteditable='tempFalse']");
          if ($elCe.length > 0) {
            $elCe.attr("contenteditable", "true");
          }
          $(el).focus();
          if (
            typeof window.getSelection != "undefined" &&
            typeof document.createRange != "undefined"
          ) {
            var range = document.createRange();
            range.selectNodeContents($(el).get(0));
            range.collapse(atStart);
            var sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
          } else if (typeof document.body.createTextRange != "undefined") {
            var textRange = document.body.createTextRange();
            textRange.moveToElementText(el);
            textRange.collapse(atStart);
            textRange.select();
          }
        } catch (e) {}
      };
    };
    $rootScope.placeCaretAtStart = $scope.placeCaretAtStart = $scope.createCaretPlacer(
      true,
    );
    $rootScope.placeCaretAtEnd = $scope.placeCaretAtEnd = $scope.createCaretPlacer(
      false,
    );

    $rootScope.placeCaretAtPosition = function(element, offset) {
      if (!$.contains(document, element)) {
        return;
      }
      var range = rangy.createRange();
      range.setStart(element, offset);
      range.collapse(true);
      var sel = rangy.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    };

    $rootScope.selectNode = function(node) {
      const sel = rangy.getSelection();
      sel.removeAllRanges();
      const range = rangy.createRange();
      range.selectNode(node);
      sel.addRange(range);
    };

    $rootScope.setSelection = function(
      anchorNode,
      anchorOffset,
      focusNode,
      focusOffset,
    ) {
      if (anchorNode instanceof Array) {
        anchorNode = anchorNode[0];
      }
      if (focusNode instanceof Array) {
        focusNode = focusNode[0];
      }

      var range = rangy.createRange();
      range.setStart(anchorNode, anchorOffset);
      range.setEnd(focusNode, focusOffset);
      var sel = rangy.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    };

    // for use by contenteditable directives
    $scope.listsDeleteListItem = function(
      hvbvId,
      noUndo,
      insertedListItemId,
      fromBulkAction,
    ) {
      if (hvbvId === false) {
        hvbvId = _.findKey($scope.listItemUndoTracking, function(o) {
          return o === +insertedListItemId;
        });
      }

      if (!hvbvId) {
        return;
      }

      window.track.event(new DeleteListItemEvent({
        context: $rootScope.getContext(),
      }));

      let currentLayers = $scope.lease.layers
        ? JSON.stringify($scope.lease.layers)
        : JSON.stringify([]);
      let newLayers =  $scope.lease.layers
      ? JSON.stringify($scope.lease.layers)
      : JSON.stringify([]);
      let changeLayers = false;

      var getDeleteListItemFunc = function($scope, hvbvId, noUndo) {
        return function(isFirstTimeOfRedo) {
        
          var insertedListItemId = $scope.listItemUndoTracking[hvbvId];
          var allToDelete = $(
            "[inserted-list-item-id='" + insertedListItemId + "']",
          );

          var actionsCount = 0;

          // update anchors for outlines 
          
          if (allToDelete.length > 0) {
            const blockData = allToDelete[0].getBlockData();
            const listItemsIndex = blockData.getListItemObjectIndex();
            const deletingItemObject =
              listItemsIndex !== -1 ? $scope.listItems[listItemsIndex] : null;
            if (deletingItemObject) {
              blockData.containingNode
                .querySelectorAll("[free-text]")
                .forEach(span => {
                  const freeText = span.getAttribute("free-text");
                  pageLayersManager.getByAnchorId(freeText).forEach(layer => {
                    if (changeLayers === false) {
                      changeLayers = true;
                    }

                    layer.anchor = deletingItemObject.afterFreeText;
                  });
                });
            }
          }

          if (changeLayers) {
            newLayers = JSON.stringify(JSON.stringify($scope.lease.layers));
          }

            


          if (allToDelete.length > 0) {
            var selection = $rootScope.getSelectionData();
            allToDelete.find('[free-text]').each(function() {
              var ftSectionId = +$(this).attr('free-text');

              if (!noUndo) {
                $(this)
                  .find('[contenteditable]')
                  .text('');
                $(this)
                  .find('[contenteditable]')
                  .trigger('change');
              }

              // delete freetext from listItems
              var listItemIdx = $scope.listItems.findIndex(function(ele) {
                return (
                  +ele.headerFreeText == ftSectionId ||
                  +ele.bodyFreeText == ftSectionId
                );
              });
              if (listItemIdx > -1) {
                // fix the linked list
                var newPointingTo = $scope.listItems[listItemIdx].afterFreeText;
                $scope.listsFixLinkedList(
                  $scope.listItems[listItemIdx].headerFreeText,
                  newPointingTo,
                );
                $scope.listsFixLinkedList(
                  $scope.listItems[listItemIdx].bodyFreeText,
                  newPointingTo,
                );

                // remove the specific listItem
                $scope.listItems.splice(listItemIdx, 1);
                $scope.lease.orderedListitem = $scope.serializeOrderedListitem();
              }

              if (!fromBulkAction && $scope.leaseFreeTexts[ftSectionId]) {
                $scope.addToHistory({
                  desc: 'deleted free-text [section id: ' + ftSectionId + ']',
                  redo: (function(sectionId) {
                    return function() {
                      var deferred = $q.defer();
                      $scope.lease.layers = JSON.parse(newLayers);
                      $scope.deleteFreeText(sectionId).then(function() {
                        deferred.resolve({
                          selection: selection,
                        });
                      });
                      return deferred.promise;
                    };
                  })(ftSectionId),
                  undo: (function(sectionId, html) {
                    return function() {
                      var deferred = $q.defer();
                      $scope.lease.layers = JSON.parse(currentLayers);
                      var freeText = new FreeTextService({
                        section_id: sectionId,
                        lease_id: $rootScope.getLeaseId(),
                        amendment_id: $rootScope.getAmendmentId(),
                        text: html,
                      });
                      freeText.create().then(function(res) {
                        $scope.leaseFreeTexts[sectionId] = res;
                        //we need to wait the new contenteditable to be compiled before continue.
                        $timeout(function() {
                          deferred.resolve();
                        }, 200);
                      });
                      return deferred.promise;
                    };
                  })(ftSectionId, $scope.leaseFreeTexts[ftSectionId].text),
                });
                actionsCount++;
                $scope.histIgnoreClearBatch++;
              }
            });

            if (isFirstTimeOfRedo && !fromBulkAction) {
              $scope.histRegisterBatch($scope.histIndex + 1, actionsCount + 1);
            }

            allToDelete.remove();
            $scope.update();
          }

          $scope.structureChanged();
          var currSelection = $rootScope.getSelectionData();
          return Promise.resolve({
            selection: currSelection,
          });
        };
      };

      var itemObject = $scope.listItemObjects[hvbvId];

      var getUndeleteListItemFunc = function($scope, itemObject) {
        return function() {
          const originalAfterFreeTextElement = document.querySelector(`[section-id="${itemObject.afterFreeText}"]`);
          $scope.lease.layers = JSON.parse(currentLayers);
          if (originalAfterFreeTextElement) {
            const eocElements = originalAfterFreeTextElement.querySelectorAll('.EOCE');
            const eocElement =
              eocElements && eocElements.length > 0
                ? eocElements[eocElements.length - 1]
                : null;

            if (eocElement && eocElement.previousElementSibling) {
              scopeVar.$root.placeCaretAtEnd(eocElement.previousElementSibling);
            }
          }

          return $scope.listsNewListItem(false, itemObject, true);
        };
      };

      if (noUndo) {
        return getDeleteListItemFunc($scope, hvbvId, noUndo)();
      }

      var idxOne = $scope.addToHistory({
        desc: 'removed list item [article: ' + itemObject.afterFreeText + ']',
        redo: getDeleteListItemFunc($scope, hvbvId, noUndo),
        undo: getUndeleteListItemFunc($scope, itemObject),
      });
    };


    $scope.changeCurrentListLevel = function(e,name){
      e.stopPropagation();
      e.preventDefault();

      if($scope.currentlyEditedNode){
        const block =  $scope.currentlyEditedNode.getBlockData ?
          $scope.currentlyEditedNode.getBlockData() :
          $scope.currentlyEditedNode.get(0).getBlockData()
        if(block.listLevel.level !== name){
          block.moveToLevel(name);
          document.body.click();
        }
      }
    }

    /*
     * Inserts a new list item, at the specified level, at the most recently edited location
     * If itemObject is false, must create one based on insertLevel - otherwise, complete insertion without undo
     */
    $scope.listsNewListItem = function(
      insertLevel,
      itemObject,
      isUndo,
      outline,
      forceHistory = false
    ) {
      return window.applyFilterVisible($scope._listsNewListItem, [
        insertLevel,
        itemObject,
        isUndo,
        outline,
        forceHistory
      ]);
    };

    $scope._listsNewListItem = function(
      insertLevel,
      itemObject,
      isUndo,
      outline,
      forceHistory = false,
    ) {


      let useOldListItems = !(
        ($scope.features && $scope.features.insertListItemsAnywhere) ||
        window.isDebug
      );


      if ($scope.outlineToInsert) {
        let newOutline = $scope.outlineToInsert;
        $scope.outlineToInsert = null;
        $scope.addOutlineAndListItem(newOutline, insertLevel, !useOldListItems);
        return;
      }
      var getNewListItemFunc = function($scope, itemObject, isUndo, outline) {
        return function() {
          // new insertions happen after the most recently inserted node

          // new list items from UI flags
          if (!itemObject.isNewListItem) {
            itemObject.isNewListItem = !useOldListItems;
          }

          if (itemObject.fromUI !== false && !isUndo) {
            itemObject.fromUI = !useOldListItems;
          }

          var insertedItem = listItemsManager.listsInsertItem(itemObject);

          if (!insertedItem) {
            return;
          }

          let scorllToItem = insertedItem.get ? insertedItem[0] : insertedItem;

          if (itemObject.json ) {
            $scope.safeApply();
          }

          if(!scorllToItem.parentElement){
            scorllToItem = document.querySelector(`[free-text="${itemObject.afterFreeText}"]`);
          }

          if(scorllToItem && scorllToItem.scrollIntoViewIfNeeded && !itemObject.fromPID && !itemObject.json){
            scorllToItem.scrollIntoViewIfNeeded({
              behavior: 'smooth',
              block: 'center'
            });
          }
          $scope.currentlyEditedNode = $(insertedItem)
            .prevUntil("div[class*='Section']")
            .find('[free-text]')
            .last()
            .children()
            .first();

          // add the article to the database
          $scope.listItems.push(itemObject);
          $scope.lease.orderedListitem = $scope.serializeOrderedListitem();

          if (!isUndo) {
            // focusing on the first possible free text value, children to get the actual contenteditable
            setTimeout(function() {
              let anchor;

              // do the body then the header - if header doesn't exist, will end up defaulting to body
              anchor = $(`span[section-id='${itemObject.bodyFreeText}'][contenteditable]`)
                .attr('contenteditable', 'true')
                .focus();

              if ($(anchor).find('.LIST-BODY').length > 0) {
                $rootScope.placeCaretAtEnd($(anchor).find('.LIST-BODY').get(0));
              } else {
                $rootScope.placeCaretAtEnd($(anchor).find('.PLACEHOLDER').next().get(0));
              }

              anchor = $(`span[section-id='${itemObject.headerFreeText}'][contenteditable]`)
                .attr('contenteditable', 'true')
                .focus();

              if ($(anchor).find('.LIST-HEADER').length > 0) {
                $rootScope.placeCaretAtEnd($(anchor).find('.LIST-HEADER').get(0));
              } else {
                $rootScope.placeCaretAtEnd($(anchor).find('.PLACEHOLDER').next().get(0));
              }

            }, 200); // timeout so it's not at the exact same time the user is clicking on the buttons
            if(!itemObject.fromPID && !itemObject.json){
              scorllToItem.scrollIntoViewIfNeeded({
                behavior: 'smooth',
                block: 'center'
              });
            }
          }

          if (!$rootScope.adminMode) {
            $scope.update(); // TODO: check if needed
          }

          $scope.structureChanged();
          var currSelection = $rootScope.getSelectionData();
          return Promise.resolve({
            selection: currSelection,
          });
        };
      };

      var getDeleteListItemFunc = function($scope, hvbvId) {
        return function() {
          return $scope.listsDeleteListItem(hvbvId, true, null);
        };
      };

      if (!itemObject) {
        let afterFreeText = $scope.currentlyEditedNode.length !== 0 ?
          $($scope.currentlyEditedNode).parents('[free-text]')
          .first()
          .attr('free-text') : null;
        if (!afterFreeText) {
          let item = parseOrderedListitem(
            scopeVar.lease.orderedListitem,
          ).listItems.pop();
          if (item) {
            if (document.querySelector('[free-text="' + item.bodyFreeText + '"]')) {
              afterFreeText = item.bodyFreeText;
            } else if (document.querySelector('[free-text="' + item.headerFreeText + '"]')) {
              afterFreeText = item.headerFreeText;
            }
          }
        }

        itemObject = {
          level: insertLevel,
          outline: outline,
          headerFreeText: $scope.getNextFreeTextValue(),
          bodyFreeText: $scope.getNextFreeTextValue(),
          afterFreeText: afterFreeText,
        };
        // itemObject.afterFreeText = $compile(itemObject.afterFreeText)($scope); // TODO:
      } else {
        if (!forceHistory) {
          return getNewListItemFunc($scope, itemObject, isUndo, outline)();
        }
      }

      window.track.event(new InsertListItemEvent({
        level: itemObject.level,
        outline: itemObject.outline,
        context: $rootScope.getContext(),
      }));

      if ($scope.histStack[$scope.histIndex]) {
        var bookmark = $scope.histStack[$scope.histIndex].currSelection;
        $rootScope.saveSelection(true, bookmark);
      }
      var myIdx = $scope.addToHistory({
        desc:
          'added list item [afterFreeText: ' + itemObject.afterFreeText + ']',
        redo: getNewListItemFunc($scope, itemObject, isUndo),
        undo: getDeleteListItemFunc(
          $scope,
          itemObject.headerFreeText + '-' + itemObject.bodyFreeText,
        ),
      });

      if (itemObject.level === 'article') {
        $scope.histIgnoreClearBatch++;
        $scope.histRegisterBatch(myIdx + 1, 2); // Article triggers section, TODO: verify for all forms/customers
      }

      return Promise.resolve();
    };

    $rootScope.isListItem = function(container) {
      let isListItem = false;

      if(!container.get(0).parentElement){
        return false;
      }

      container = container.get(0);

      const list = container.querySelector("[list]");

      if (list) {
        const roundTrip = list.parentElement.previousElementSibling;

        if (LeaseEditorService.isNodeWordStyle($(roundTrip))) {
          isListItem = true;
        }
      }

      return isListItem;
    };

    $rootScope.isNewParagraph = function(container) {
      return container.classList.contains('new-paragraph');
    };

    $rootScope.listItemIsEmpty = function(listItem) {
      return (
        !$rootScope.listItemHasContent(listItem) &&
        !$rootScope.listItemHasChildLists(listItem)
      );
    };

    $rootScope.isDummyListItem = function(element) {
      return
        element.get(0).nodeName === 'P' &&
        element.find('.word-indent[style*="7pt"]').length !== 0;
    }

    $rootScope.listItemHasContent = function(listItem) {
      if (!listItem) {
        return false;
      }

      if (listItem.get && !listItem[0]) {
        return false;
      }

      var contents = listItem.find('[free-text] [contenteditable]');

      listItem = $(listItem).get(0);

      if (!!listItem.querySelector('.new-paragraph')) {
        return true;
      }

      return _.some(contents, function(el) {
        return !LeaseEditorService.isEmpty(el, true);
      });
    };

    $rootScope.listItemHasChildLists = function(listItem) {

      if (
        ($scope.features && $scope.features.insertListItemsAnywhere) ||
        window.isDebug
      ) {
        return false;
      }

      var list = listItem.find('[list]');
      var levelId = $scope.listsLookupLevelIdx(
        list.attr('list'),
        list.attr('format'),
        listItem[0].tagName,
        list.attr('outline'),
      );
      var nextCE = LeaseEditorService.findAllContentEditable(listItem).last();

      var nextContainer;
      while (nextCE.length > 0) {
        let newNextCE = LeaseEditorService.findNextContentEditable(nextCE);
        if(nextCE[0] === newNextCE[0]){
          break;
        }
        nextCE = newNextCE;
        nextContainer = LeaseEditorService.findClosestContainer(nextCE);
        if (nextContainer && $rootScope.isListItem(nextContainer)) {
          var nextList = nextContainer.find('[list]');
          var nextLevelId = $scope.listsLookupLevelIdx(
            nextList.attr('list'),
            nextList.attr('format'),
            nextContainer[0].tagName,
            list.attr('outline'),
          );
          return nextLevelId > levelId;
        }
      }

      return false;
    };

    /*----------------------------------------------------------------------------*/

    // Writing an inline style invalidates layout, so the next getBoundingClientRect
    // or width() in the loop below forces a synchronous reflow. Most of those writes
    // set the value it already had, which costs a reflow and changes nothing.
    // Skipping the no-op writes leaves the reads to be served from one layout pass
    // instead of one per tab, without reordering anything.
    //
    // Note the reads here are position-dependent -- `left` measures an absolute
    // viewport coordinate -- so the writes cannot simply be batched to the end
    // without changing the result for any paragraph holding more than one
    // measured tab.
    const setStyleIfChanged = function(node, property, nextValue) {
      if (node.style[property] !== nextValue) {
        node.style[property] = nextValue;
      }
    };

    $scope.listsSetSpaces = function() {
      if ($scope.listSpaces) {
        var spaces = [];
        var freeListGap = -1;

        for (var i = 0; i < $scope.listSpaces.length; i++) {
          if ($scope.listSpaces[i].name == '*') {
            freeListGap = $scope.listSpaces[i].value;
          }
        }

        var prevListItemNode = null;
        var prevListName = '';

        $('.lease')
          .not('.lease--modified')
          .find('span[style*="inline-block"]:not(.word-indent)')
          .addClass('word-indent word-indent--no-cursor')
          .css({
            'user-select': 'none',
            'overflow': 'hidden',
          })
          .attr('contenteditable', 'false');

        $('.word-indent.PLACEHOLDER').each(function() {
          $(this).removeClass('PLACEHOLDER');
          var closestCE = LeaseEditorService.findClosestContentEditable($(this));
          LeaseEditorService.addMarkers(closestCE);
        });

        $('.word-indent').each(function() {
          if ($(this).text().length === 0) {
            $(this).html("&nbsp;");
          }
        });

        const tabs = applyFilterVisible(()=>{
          return $('.lease')
          .not('.lease--modified')
          .find('.word-indent[style*="7pt"]')
          .filter((index,item)=> {return item.closest('.deleted-container') === null});
        })  

        let current;
        for (let tabIndex = 0; tabIndex < tabs.length; tabIndex++) {
          current = tabs[tabIndex];
          if(current.closest('.list-item-paragraph.inserted-list-item')){
            continue;
          }
          setStyleIfChanged(current, 'userSelect', 'none');

          // auto numbered list
          var prev = $(current).prev();
          var listItemInfo = current.getListItemInfoFromNode();
          var listName = $(prev).attr('list');

          if(listName === ""){
            continue;
          }

          if (listItemInfo && listItemInfo.listLevel) {
            const levelLookup = $scope.listSpaces.filter(level => level.name === listName);
            if (levelLookup.length === 0) {
              listName = listItemInfo.listLevel.name;
            }
          }

          if (listName) {
            for (var i = 0; i < $scope.listSpaces.length; i++) {
              var name = $scope.listSpaces[i].name;
              var type = $scope.listSpaces[i].type;
              var value = $scope.listSpaces[i].value;

              // check if the current list name appears in the list of list spaces
              if (
                listName && name &&
                /* list names equal */
                (listName === name ||
                  /* list names start with */
                  (name[name.length - 1] === '%' &&
                    listName.length >= name.length &&
                    listName.startsWith(
                      name.substring(0, name.length - 1),
                    )) ||
                  /* list names end with */
                  (name[0] === '%' && listName.endsWith(name.substring(1))) ||
                  /* list names a lower case letter */
                  (name === '?1' &&
                    isLowercaseLetter(listName[0]) &&
                    listName.length == 1) ||
                  /* list names a digit             */
                  (name === '*1' &&
                    !isNaN(listName[0]) &&
                    listName.length == 1) ||
                  /* list names a upper case letter */
                  (name === '^1' &&
                    isUppercaseLetter(listName[0]) &&
                    listName.length == 1) ||
                  /* list names lower case letters  */
                  (name === '?' && isLowercaseLetter(listName[0])) ||
                  /* list names is a number         */
                  (name === '*' && !isNaN(listName)) ||
                  /* list names upper case letters  */
                  (name === '^' && isUppercaseLetter(listName[0])))
              ) {
                if (type === 'left') {
                  var data1 = prev[0].getBoundingClientRect();
                  var first = data1.right;
                  // set spaces width according to variable distance from list item text
                  if ($scope.inline.on) {
                    setStyleIfChanged(current, 'width', value - 160.0 - first + 'px');
                  } else {
                    setStyleIfChanged(current, 'width', value - first + 'px');
                  }
                } else if (type === 'gap') {
                  // initialize gap
                  let calculatedGap = value;

                  var listItemNodeWidth = prev.width();

                  var space = spaces[listName];

                  // calculate the gap starting from the configured value, up or down according to the size of the preceding list item width
                  if (space) {
                    var prevListItemNodeWidth = space.width;
                    calculatedGap = space.gap;
                    if(listItemNodeWidth !== prevListItemNodeWidth) {
                      // calculate gab by list item width (add if previous was wider and subtract if previous was narrower)
                      if (listItemNodeWidth > prevListItemNodeWidth) {
                        calculatedGap -=
                          listItemNodeWidth - prevListItemNodeWidth;
                      } else {
                        calculatedGap +=
                          prevListItemNodeWidth - listItemNodeWidth;
                      }
                      // Update list width & space gap on space object
                      space.width = listItemNodeWidth;
                      space.gap = calculatedGap;
                    }
                  } else {
                    // Init space object with list item width & gap by list name
                    spaces[listName] = {width: listItemNodeWidth, gap: calculatedGap};
                  }

                  prevListItemNode = prev;
                  // set spaces width according to absolute gap size
                  setStyleIfChanged(current, 'width', calculatedGap + 'px');
                  setStyleIfChanged(current, 'height', '0px');
                } else if (type === 'total') {
                  const total = value;
                  const numberSize = prev.width();
                  const spaceSize = total - numberSize;

                  setStyleIfChanged(current, 'width', spaceSize + 'px');
                  setStyleIfChanged(current, 'height', '0px');
                }

                break;
              }
            }
          } else if (freeListGap >= 0) {
            // "free" list
            setStyleIfChanged(current, 'width', freeListGap + 'px');
          }

          // set previous list name for tracking list changes
          prevListName = listName;
        }
      }
    };

    /*
     * Call when the DOM structure changes
     */
    $scope.structureChanged = function(invalidateCrossReferences = true) {
      return window.applyFilterVisible($scope._structureChanged, [invalidateCrossReferences]);
    }
    $scope._structureChanged = async function(invalidateCrossReferences = true) {
      $scope.listsRefresh(invalidateCrossReferences);
      $scope.appendEditingSpaceToLeaseVars();
      $scope.maintainHardReturns();
      $scope.maintainSubdocuments();
      $scope.maintainBookmarksIndicator();
      setTimeout(LeaseEditorService.createRemoveTable);
      
      if (!$scope.diffgram.on) {
        $scope.maintainNestedParagraphsIndex();
      }

      // lp-015. The checks panel listens for this. Structure changes are when a
      // check can go stale without anybody touching the panel — a clause moved,
      // a section deleted, an exhibit renumbered — and they are rare enough to
      // re-read on. Deliberately NOT on keystroke: the mechanical families are
      // cheap but the panel re-rendering under somebody's cursor is not what
      // they asked for while typing.
      // `$scope.$root`, because $rootScope is not injected into this
      // controller — the first version of this line would have thrown.
      $scope.$root.$broadcast('lp:document-structure-changed');
    };

    $scope.maintainBookmarksIndicator = function() {
      const bookmarks = document.querySelectorAll("crb");

      for (let i = 0; i < bookmarks.length; i++) {
        let bookmarkName = bookmarks[i].getAttribute("title") || bookmarks[i].getAttribute("id");
        let blockData = bookmarks[i].getBlockData();
        // A bookmark inside freshly inserted content has no containing block yet, and
        // throwing here is expensive out of proportion to the miss: _structureChanged is
        // async, so the exception becomes an unhandled rejection and every step after this
        // call is silently skipped for the rest of that pass.
        let container = blockData && blockData.containingNode;
        if (!container) {
          continue;
        }

        if (!container.querySelector(".bookmark")) {
          let bookmarkContainer = document.createElement("span");
          bookmarkContainer.classList.add("bookmark", "remove-on-download");

          let mdTooltip = document.createElement("md-tooltip");
          bookmarkContainer.appendChild(mdTooltip);
          mdTooltip.innerText = bookmarkName;

          let bookmarkNode = document.createElement("svg");
          bookmarkContainer.appendChild(bookmarkNode);
          bookmarkNode.outerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="#000000" viewBox="0 0 256 256"><path d="M184,32H72A16,16,0,0,0,56,48V224a8,8,0,0,0,12.24,6.78L128,193.43l59.77,37.35A8,8,0,0,0,200,224V48A16,16,0,0,0,184,32Zm0,177.57-51.77-32.35a8,8,0,0,0-8.48,0L72,209.57V48H184Z"></path></svg>`;

          const referenceNode = bookmarks[i].parentElement;
          referenceNode.parentElement.insertBefore(bookmarkContainer, referenceNode);

          $compile(bookmarkContainer)(scopeVar);
        }
      }
    };

    $scope.maintainHardReturns = function() {
      const originalHardReturns = document.querySelectorAll('hard-return.hard-return--original');

      if (originalHardReturns.length > 0) {
        originalHardReturns.forEach(hardReturn => {
          let container = LeaseEditorService.findClosestContainer(hardReturn).get(0);
          let originalHardReturn = container.querySelector('hard-return:not([class])');

          // Deletes the `hard-return` from the original HTML
          if (originalHardReturn) {
            originalHardReturn.remove();
          }
        });
      }

      const root = document.querySelector('.lease:not(.lease--modified)');

      if (root) {
        const containers = root.querySelectorAll('h1, h2, h3, h4, h5, h6, p');
  
        containers.forEach(container => {
          if (container.textContent.trim().startsWith('!!')) {
            return;
          }

          // If this container has nested paragraphs, remove its own direct-child
          // <hard-return> to avoid a double hard-return (the last nested paragraph
          // already provides one).
          if (container.querySelector('p[data-nested-paragraph-index]')) {
            const directHardReturns = Array.from(container.children).filter(
              child => child.tagName === 'HARD-RETURN'
            );
            directHardReturns.forEach(hr => hr.remove());
            return;
          }

          const hardReturn = container.querySelector('hard-return');

          if (!hardReturn) {
            container.appendChild(document.createElement('hard-return'));
          } else if (
            hardReturn.previousSibling &&
            hardReturn.previousSibling.nodeType === Node.TEXT_NODE &&
            /^\s*\n\s*$/.test(hardReturn.previousSibling.textContent)
          ) {
            // Remove whitespace text nodes that contain newlines before <hard-return>
            // to prevent the pilcrow glyph from wrapping to a new line. Only target
            // indentation whitespace (contains newline), not intentional spaces.
            hardReturn.previousSibling.remove();
          }
        });
      }
    };

    $scope.maintainSubdocuments = function() {
      $scope.subdocuments = [];
      
      const subdocuments = document.querySelectorAll(".lease.lease--original subdocument");

      let subdocument;
      let subdocumentName;
      for (let index = 0; index < subdocuments.length; index++) {
        subdocument = subdocuments[index];
        subdocumentName = subdocument.getAttribute("name");
        $scope.subdocuments.push(subdocumentName);
      }
    };

    $scope.maintainNestedParagraphsIndex = function() {
      // Snapshot as array: NodeList keeps removed nodes; mixing remove() with index--/continue
      // can retry the same slot and mis-assign indices. Remove in a separate backward pass, then re-query.
      const paragraphList = Array.prototype.slice.call(
        document.querySelectorAll('.lease.lease--original [contenteditable] p')
      );

      for (let i = paragraphList.length - 1; i >= 0; i--) {
        const paragraph = paragraphList[i];

        // Remove empty nested paragraphs that only contain background-colored spans
        // These originate from block tags in building language files (e.g. yellow highlight markers)
        //
        // Emptiness is tested first on purpose. `span[style*="background-color"]` is an
        // attribute-substring selector, which no index can serve, so each call walks the
        // paragraph's whole subtree comparing strings. Running it on every paragraph to find
        // the handful that are empty put ~1.2s of querySelectorAll on the main thread in a
        // profile that returned nine matches. textContent short-circuits nearly all of them.
        if (paragraph.textContent === '' && paragraph.querySelector('span[style*="background-color"]')) {
          paragraph.remove();
        }
      }

      const paragraphs = document.querySelectorAll('.lease.lease--original [contenteditable] p');
      for (let index = 0; index < paragraphs.length; index++) {
        // Written only when it differs. setAttribute emits a mutation record even when the
        // value is unchanged, so an unconditional rewrite made every sweep announce a few
        // thousand attribute mutations to every observer on the document — and this function
        // is called once per provision re-render, not once per document.
        const value = String(index);
        if (paragraphs[index].getAttribute('data-nested-paragraph-index') !== value) {
          paragraphs[index].setAttribute('data-nested-paragraph-index', value);
        }
      }
    };

    function getOutlineStartNode(outlineName) {
      if (outlineName) {
        return $('outline[name="' + outlineName + '"]');
      } else {
        return $('lease-start');
      }
    }

    function getOutlineEndNode(outlineName) {
      if (outlineName) {
        return $('outline-end[name="' + outlineName + '"]');
      } else {
        return $('lease-end');
      }
    }

    function getParentOutline(node) {


      let currentOutline = null;
      let returnObject = null;
      let startNode;
      let endNode;

      if (!$(node)[0]) {
        return null;
      }

      if ($(node)[0].nodeType === Node.TEXT_NODE) {
        node = $(node).parentElement;
      }

      if(node && node.get){
        node = node.get(0);
      }

      const topAnchor = node.getTopAnchor();
      const pid = topAnchor.getAttribute("pid");
      let query;
      if (!pid || pid === "null") {
        // add to support test leases only
        // in some test leases the polyfill for pid is placing 'null'
        query =
          "lease-start,lease-end,outline,outline-end," + topAnchor.tagName + "";
      } else {
        query = 'lease-start,lease-end,outline,outline-end,[pid="' + pid + '"]';
      }

      const elementsArray = Array.from(document.querySelectorAll(query));
      const myIndex = elementsArray.indexOf(topAnchor);
      const nodeNameArray = ["LEASE-END", "LEASE-START", "OUTLINE-END","OUTLINE"];

      if (elementsArray[myIndex - 1]) {
        let scanIndex = myIndex;
        while (elementsArray[scanIndex] && !startNode) {
          let tagname = elementsArray[scanIndex].tagName;
          if (nodeNameArray.indexOf(tagname) !== -1) {
            startNode = elementsArray[scanIndex];
          }
          scanIndex--;
        }
        if (startNode) {
          if (
            startNode.tagName === "LEASE-END" ||
            startNode.tagName === "OUTLINE-END"
          ) {
            currentOutline = null;
          } else if (startNode.tagName === "LEASE-START") {
            currentOutline = "main";
            endNode = document.querySelector("LEASE-END");
          } else if (startNode.tagName === "OUTLINE") {
            currentOutline = startNode.getAttribute("name");
            endNode = document.querySelector(
              'OUTLINE-END[name="' + currentOutline + '"]'
            );
          }
        }
      }
      else{
        currentOutline = null;
      }

      if (currentOutline !== null) {
        returnObject = {
          startNode: startNode,
          endNode: endNode,
          name: currentOutline
        };
      }

      return returnObject;
    }

    $scope.updateCurrentOutline = function(){
      $scope.currentOutline = getParentOutline($scope.currentlyEditedNode);
      $scope.updateAddListItems();
    }

    $scope.$watch('currentlyEditedNode', function(newValue) {
      if (!newValue || (newValue && newValue.length === 0)) {
        $scope.addItemDisabled = true;
        return;
      }

      if(newValue.nodeType === Node.TEXT_NODE) {
        newValue = newValue.parentElement;
      }

      $scope.listItemsRootLevel = 'atricle';
      $scope.currentOutline = getParentOutline(newValue);
      let leaseEnd = document.querySelector('lease-end');
      let leaseStart =document.querySelector('lease-start');
      let currentElement;

      if (newValue) {
        currentElement = newValue.get ? newValue[0] : newValue;
      }

      let articleLevel = $scope.listLevels.filter((item)=>{ return item.level === 'article' });
      $scope.hasArticleLevel = articleLevel && articleLevel.length !== 0;
      $scope.hasOutlineLevel = listItemsManager.listLevelsMap && Object.keys(listItemsManager.listLevelsMap).length > 0;

      if ($scope.hasArticleLevel) {
        let getBoundingClientRect = currentElement.getBoundingClientRect();
        let newItemY = getBoundingClientRect ? getBoundingClientRect.y : null;

        if (newItemY) {
          if (
            (leaseEnd &&
              leaseEnd.getBoundingClientRect &&
              leaseEnd.getBoundingClientRect().y < newItemY) ||
            (leaseStart &&
              leaseStart.getBoundingClientRect &&
              leaseStart.getBoundingClientRect().y > newItemY)
          ) {
            $scope.addArticleDisabled = true;
          } else {
            $scope.addArticleDisabled = false;
          }
        }
      } else {
        $scope.addArticleDisabled = true;
      }

      if (newValue.closest('ol')) {
        $scope.addItemDisabled = true;
        return;
      }

      $scope.updateAddListItems();
    });

    $scope.updateAddListItems = function(){

      $scope.changeListItemLevel = false;

      if ($scope.currentOutline) {

        if($scope.currentlyEditedNode && !LeaseEditorService.isSelecting()){
          const block =  $scope.currentlyEditedNode.getBlockData ?
           $scope.currentlyEditedNode.getBlockData():
           $scope.currentlyEditedNode.get(0).getBlockData();

          if(block.containingNode && block.blockType !== BLOCK_TYPE.NESTED_PARAGRAPH){
            const tblNode = block.containingNode.closest('table');

            if(!tblNode && block.containingNode.getNodeOutline() && !block.tableNode){
              $scope.changeListItemLevel = true;
            }
          }
          else{
            $scope.changeListItemLevel = false;
          }
          
        }

        const normalListLevel = {
          format: "",
          level: "normal",
          listHeader: "",
          name: "",
          outerTag: "P",
          outlineDisplayName: "",
          previewText: "Normal"
        };

        $scope.outlineListLevels = $scope.listLevels.filter(function(item) {
          return $scope.currentOutline && item.outline == $scope.currentOutline.name || (
            !item.outline && $scope.currentOutline.name === 'main'
          );
        });
        
        if ($scope.outlineListLevels.length) {
          if (
            listItemsManager.getOutlineParagraphLevel(
              $scope.currentOutline.name
            ) === listItemsManager.getOutlineParagraphLevel(null)
          ) {
            $scope.outlineListLevels.push(normalListLevel);
          }
          
          $scope.listItemsRootLevel = $scope.outlineListLevels[0].level;
          $scope.addItemDisabled = false;
        } else {
          $scope.addItemDisabled = true;
        }
      } else {
        $scope.outlineListLevels = [];
        $scope.addItemDisabled = true;
      }
    }

    $scope.$watch('numberOfHighlighted', function(newValue) {
      if (newValue > 0) {
        window.applyFilterVisible(() => {
          var allHls = $scope.getAllHls();
          var hls = allHls.filter("[exclude!='true']");

          // restore original classes
          $('.current').each(function() {
            $(this).removeClass('current');
          });

          $(hls[$scope.highlightScrollIndex]).addClass('current');

          // Handle excluded part's styles
          var newInAll = 0;

          var i = newInAll + 1;
          while (i < allHls.length && $(allHls[i]).attr('exclude') === 'true') {
            $(allHls[i]).addClass('current');
            i++;
          }
        });
      }
    });

    $scope.updateCurrentHighlightedItem = function(newValue, oldValue) {
      var allHls = $scope.getAllHls();
      var hls = allHls.filter("[exclude!='true']");

      if (newValue != oldValue) {
        // restore original classes
        if (!isNaN(oldValue) && oldValue >= 0) {
          $("*[class*='current']").each(function() {
            $(this).removeClass('current');
          });
        }

        // Highlight the current item
        $(hls[newValue]).addClass('current');

        // Handle excluded part's styles
        var newInAll = allHls.index(hls[newValue]);

        var i = newInAll + 1;
        while (i < allHls.length && $(allHls[i]).attr('exclude') === 'true') {
          // Add 'current' class as first (to allow priority on '!Important' css
          $(allHls[i]).addClass('current');
          i++;
        }
      }

      // Only highlight if there's anything to highlight!
      if (newValue > 0) {
        $scope.setCurrentlyUpdatingElement(hls[newValue]);
      }
    };

    $scope.setTermsIncludingFreeRent = function(term) {
      var newCollection = [];

      if (term && term.length) {
        // push an additional rent period for free rent
        if ($scope.lease.freeRent) {
          newCollection.push({
            rentPerSqFoot: 0.0,
          });
        }

        for (var i = 0; i < term.length; i++) {
          newCollection.push(term[i]);
        }
      }

      $scope.termsIncludingFreeRent = newCollection;
    };

    $scope.getHalfTerm = function(term) {
      var newCollection = [];

      if (term && term.length) {
        for (var i = 0; i < Math.ceil(term.length / 2); i++) {
          newCollection.push(term[i]);
        }
      }
      return newCollection;
    };

    $scope.getHighlightedElement = function() {
      var hls = $scope.getAllHls().filter("[exclude!='true']");

      if (hls && hls[$scope.highlightScrollIndex]) {
        return hls[$scope.highlightScrollIndex];
      }

      return null;
    };

    $scope.setCurrentlyUpdatingElement = function(element) {
      if (element && element.className.indexOf('current') < 0) {
        $(element).addClass('current');
      }
    };

    $scope.$watch('highlightScrollIndex', function(newValue, oldValue) {
      if (!isNaN(newValue)) {
        $scope.updateCurrentHighlightedItem(newValue, oldValue);
      }
    });

    /*
     *
     * Limit number of lease years to 50, to prevent massive DB entries.
     *
     */
    $scope.$watch('lease.leaseYears', function(newValue) {
      if (newValue) {
        if (newValue > 50) {
          $scope.lease.leaseYears = 50;
        }

        setTimeout(function() {
          $scope.setCalculatedExpirationDate();
          $scope.update();
        });
      }
    });

    $scope.$watch('lease.leaseMonths', function(newValue) {
      if (newValue) {
        if (newValue > 50) {
          $scope.lease.leaseMonths = 50;
        }

        setTimeout(function() {
          $scope.setCalculatedExpirationDate();
          $scope.update();
        });
      }
    });

    /* Limit number of terms to 50, to prevent massive DB entries. */
    $scope.$watch('lease.renewalTerm', function(newValue, oldValue) {
      if (newValue > 50) $scope.lease.renewalTerm = 50;
    });

    $scope.startSaving = function() {
      $scope.allChangesSaved.push(0)
      LeaseEditorService.setLeaseTitle();
      $scope.isSaving = true;
      ProcessStatusService.start('save');
      $scope.safeApply();
    };

    $scope.endSaving = function() {
      if ($scope.allChangesSaved.length > 0) {
        $scope.allChangesSaved.pop();
      }

      if ($scope.allChangesSaved.length === 0) {
        setTimeout(() => {
          LeaseEditorService.setLeaseTitle();
          $scope.isSaving = false;
          ProcessStatusService.end('save');
          $scope.safeApply();
        }, 1000);
      }
    };

    function isSupportedSelection() {
      let result = true;

      if (
        ($rootScope.hasEditorConfig('premisesModType', 'noPremisesModNoExtensionUnsupported') &&
        !$scope.lease.hasExtensionOfTerm &&
        $scope.lease.premisesModType === 'none') ||
        ($rootScope.hasEditorConfig('premisesModType', 'reductionNoExtensionUnsupported') &&
        !$scope.lease.hasExtensionOfTerm &&
        $scope.lease.premisesModType === 'reduction')
      ) {
        result = false;
      }

      return result;
    }

    function showUnsupportedActionMessage(message, delay = 5000) {
      if (message) {
        $rootScope.infoMessage = message;
        $rootScope.infoMessageType = 'error';
        $rootScope.safeApply();

        setTimeout(function() {
          $rootScope.infoMessage = '';
          $rootScope.infoMessageType = '';
          $rootScope.safeApply();
        }, delay);
      }
    }

    window.showUnsupportedActionMessage = showUnsupportedActionMessage;

    $scope.$watch('lease.premisesModType', function(newValue, oldValue) {
      if ($scope.isLoading || !$scope.lease) {
        return;
      }

      let message = '';

      if (!isSupportedSelection()) {
        message = `The scenario with this selection is not supported within LeasePilot`;
        $scope.lease.premisesModType = oldValue;
      }

      if (message) {
        showUnsupportedActionMessage(message);
      }
    });

    $scope.$watch('lease.hasExtensionOfTerm', function(newValue, oldValue) {
      if ($scope.isLoading || !$scope.lease) {
        return;
      }

      let message = '';

      if (!isSupportedSelection()) {
        message = `The scenario with this selection is not supported within LeasePilot`;
        $scope.lease.hasExtensionOfTerm = oldValue;
      }

      if (message) {
        showUnsupportedActionMessage(message);
      }
    });
    
    $rootScope.$on('leaseStatusChanged', function(event, status) {
      if (!window.isDownload) {
        if (status.lock) {
          $scope.disableEditing();
        } else {
          $scope.enableEditing();
        }
      }
    });

    $scope.disableEditing = function() {
      $scope.editingEnabled = false;
      $scope.lease.isLocked = true;

      const contentEditables = document.querySelectorAll(".lease.lease--original .editable");

      for (let i = 0; i < contentEditables.length - 1; i++) {
        contentEditables[i].setAttribute("contenteditable", "false");
        contentEditables[i].setAttribute("data-is-editable", "true");
        contentEditables[i].classList.remove("editable");
      }
    };

    $scope.enableEditing = function() {
      $scope.editingEnabled = true;
      $scope.lease.isLocked = false;

      const contentEditables = document.querySelectorAll(".lease.lease--original [data-is-editable]");

      for (let i = 0; i < contentEditables.length - 1; i++) {
        contentEditables[i].setAttribute("contenteditable", "true");
        contentEditables[i].removeAttribute("data-is-editable");
        contentEditables[i].classList.add("editable");
      }
    };

    $scope.$watch('lease.rentCommenceAt', function(newValue) {
      if (newValue) {
        setTimeout(function() {
          $scope.setCalculatedExpirationDate();
          $scope.update();
        });
      }
    });

    $scope.$watch('isConnected', function() {
      if ($scope.isConnected) {
        $('.leases-link').removeClass('ng-hide');
        $('.title').removeClass('ng-hide');
      } else {
        $('.leases-link').addClass('ng-hide');
        $('.title').addClass('ng-hide');
      }
    });

    $window.addEventListener(
      'offline',
      function() {
        $scope.isConnected = false;
        $scope.safeApply();
      },
      false,
    );

    $window.addEventListener(
      'online',
      function() {
        $scope.isConnected = true;
        $scope.safeApply();
      },
      false,
    );

    // Handle exceptions which are tied to an updated field yet use 'hl' technique to highlight.
    $scope.$watch('currentlyChanging', function(newValue, oldValue) {
      setTimeout(function() {
        if (
          typeof oldValue != 'undefined' &&
          typeof newValue != 'undefined'
        )
          if (newValue.indexOf('renewalValues') >= 0) {
            // consolidated renewal values (rebewalValuesX all highlight 'renewalValues' in addition to support for currentlyUpdating field renewalValuesX
            $scope.highlightItem('renewalValues');
          }

        // consolidated reduction values
        if (newValue.indexOf('reductionValues') >= 0) {
          $scope.highlightItem('reductionValues');
        }
      }, 1);
    });

    $scope.combinedArrays = function(variables, condition) {
      var combinedArray = [];
      for (let index in variables) {
        combinedArray = combinedArray.concat(
          _.filter(
            _.map(variables[index], function(element, i) {
              element._leaseVariableIndex = index;
              element._originalIndex = i;
              return element;
            }),
            function(item) {
              $scope._tmpItem = item;
              return !condition || $parse('_tmpItem.' + condition)($scope);
            },
          ),
        );
      }
      delete $scope._tmpItem;
      return combinedArray;
    };

    function calcMonthlyBaseRentPerTerm(term) {
      switch (term.rentInputType) {
        case 'fixed':
        case 'fixed-no-escalation':
          if (term.rentPeriods && term.rentPeriods[0]) {
            term.monthlyBaseRent = term.rentPeriods[0].totalRent || 0;
          }
          break;
        case 'manual':
          if (term.rentPeriods && term.rentPeriods[0]) {
            term.monthlyBaseRent =
              (term.rentPeriods[0].rentPerSqFoot *
                $scope.lease.premiseAreaSize || 0) / 12;
          }
          break;
        case 'automatic':
          term.monthlyBaseRent =
            (term.baseRent * $scope.lease.premiseAreaSize || 0) / 12;
          break;
      }
    }

    function calcAnnualBaseRentPerTerm(term) {
      switch (term.rentInputType) {
        case 'fixed':
        case 'fixed-no-escalation':
          if (term.rentPeriods && term.rentPeriods[0]) {
            term.annualBaseRent = term.rentPeriods[0].totalRent * 12 || 0;
          }
          break;
        case 'manual':
          if (term.rentPeriods && term.rentPeriods[0]) {
            term.annualBaseRent =
              term.rentPeriods[0].rentPerSqFoot *
                $scope.lease.premiseAreaSize || 0;
          }
          break;
        case 'automatic':
          term.annualBaseRent =
            term.baseRent * $scope.lease.premiseAreaSize || 0;
          break;
        case 'flexible':
          var annualBaseRent = 0;
          for(var i=0; term.rentBumps[i] && term.rentBumps[i].begin < 13; i++) {
            var duration = term.rentBumps[i].duration;
            if ((term.rentBumps[i].begin + duration) > 13) {
              duration = 13 - term.rentBumps[i].begin;
            }
            annualBaseRent += (term.rentBumps[i].rentPerSqFoot * term.premiseAreaSize) / 12 * duration;
          }
          term.annualBaseRent = annualBaseRent;
          break;
      }
    }

    function calcTotalRentPerTerm(term) {
      if (term.rentInputType === 'fixed-no-escalation') {
        _.mapValues(term.rentPeriods, function(period) {
          period.totalRent = term.rentPeriods[0].totalRent;
          return period;
        });
      }
    }

    function calcRemainingMonthlyRentPerTerm(term) {
      term.remainingMonthlyRent = [];
      term.payableMonthlyRent = [];

      let areaSize;
      let rentBumps;

      if (!term.premisesModType) {
        areaSize = term.premiseAreaSize;
        rentBumps = term.rentBumps;
      } else {
        if ($scope.lease.premisesModType === 'expansion') {
          if ($scope.lease.hasExtensionOfTerm) {
            areaSize = $scope.lease.expansionPremiseAreaSize;
            rentBumps = $scope.lease.expansion.rentBumps;

            if ($scope.lease.rentTableType === 'blended') {
              areaSize += $scope.lease.premiseAreaSize;
            }
          } else {
            areaSize = $scope.lease.expansionPremiseAreaSize;
            rentBumps = $scope.lease.expansion.rentBumps;
          }
        } else if ($scope.lease.premisesModType === 'reduction') {
          areaSize = $scope.lease.reductionRemainingAreaSize;
          rentBumps = $scope.lease.reduction.rentBumps;
        } else if ($scope.lease.premisesModType === 'relocation') {
          areaSize = $scope.lease.relocationPremisesAreaSize;
          rentBumps = $scope.lease.relocation.rentBumps;
        } else if ($scope.lease.premisesModType === 'none') {
          areaSize = $scope.lease.premiseAreaSize;
          rentBumps = $scope.lease.rentBumps;
        }
      }

      if (term.rentInputType === 'manual' || term.rentInputType === 'automatic') {
        let freeRentPeriods = term.freeRentPeriods;

        for (let i = 0; i < freeRentPeriods.length; i++) {
          if (!(term.freeRentPeriods[i] && term.freeRentPeriods[i].beginIn)) {
            continue;
          }

          const rentPeriodIndex = Math.trunc(term.freeRentPeriods[i].beginIn / 12);

          if (!(term.rentPeriods[rentPeriodIndex] && term.rentPeriods[rentPeriodIndex].rentPerSqFoot)) {
            continue;
          }

          const monthlyBaseRent =
                  (term.rentPeriods[rentPeriodIndex].rentPerSqFoot * areaSize) / 12;

          const monthlyRent = {
            beginIn: term.freeRentPeriods[i].beginIn,
            duration: term.freeRentPeriods[i].duration,
            remainingRentAmount:
              monthlyBaseRent - term.freeRentPeriods[i].amount,
          };

          if (term.rentPeriods[rentPeriodIndex].rentPerSqFoot * areaSize / 12 - term.freeRentPeriods[i].amount >= 0.01) {
            term.remainingMonthlyRent.push(monthlyRent);
          } else {
            monthlyRent.remainingRentAmount = 0;
          }

          term.payableMonthlyRent.push(monthlyRent);
        }
      } else if (term.rentInputType === 'flexible') {
        if (!rentBumps) {
          return;
        }

        for (let i = 0; i < rentBumps.length; i++) {
          let currentRentBump = rentBumps[i];
          if (term.freeRentPeriods) {
            for (let j = 0; j < term.freeRentPeriods.length; j++) {
              let currentFreeRentPeriod = term.freeRentPeriods[j];

              if (
                currentRentBump.begin <= currentFreeRentPeriod.beginIn &&
                currentFreeRentPeriod.beginIn +
                  currentFreeRentPeriod.duration -
                  1 <=
                  currentRentBump.begin + currentRentBump.duration
              ) {
                const monthlyBaseRent =
                  (currentRentBump.rentPerSqFoot * areaSize) / 12;

                const monthlyRent = {
                  beginIn: currentFreeRentPeriod.beginIn,
                  duration: currentFreeRentPeriod.duration,
                  remainingRentAmount:
                    monthlyBaseRent - currentFreeRentPeriod.amount,
                };

                if (monthlyBaseRent - currentFreeRentPeriod.amount > 0.01) {
                  term.remainingMonthlyRent.push(monthlyRent);
                } else {
                  monthlyRent.remainingRentAmount = 0;
                }

                term.payableMonthlyRent.push(monthlyRent);
              }
            }
          }
        }
      }
    }

    function getRentPeriodsInMonths() {
      var rentPeriods;
      if ($scope.lease.rentInputType === 'flexible') {
        rentPeriods = $scope.lease.rentBumps;
      } else if ($scope.lease.rentInputType === 'flexible-amendment') {
        if ($scope.lease.premisesModType === 'expansion') {
          if ($scope.lease.hasExtensionOfTerm) {
            if ($scope.lease.rentTableType === 'blended') {
              rentPeriods = $scope.lease.expansion.rentBumps;
            } else if ($scope.lease.rentTableType === 'separate') {
              rentPeriods = $scope.lease.expansion.rentBumps;
            }
          } else {
            rentPeriods = $scope.lease.expansion.rentBumps;
          }
        } else if ($scope.lease.premisesModType === 'reduction') {
          rentPeriods = $scope.lease.reduction.rentBumps;
        } else if ($scope.lease.premisesModType === 'relocation') {
          rentPeriods = $scope.lease.relocation.rentBumps;
        } else if ($scope.lease.premisesModType === 'none') {
          rentPeriods = $scope.lease.rentBumps;
        }
      } else {
        rentPeriods = $scope.lease.rentPeriods;
      }
      var rentPeriodsInMonths = _.chain(rentPeriods)
        .cloneDeep()
        .flatMap(function(row, rowIndex) {
          var monthsInRow = ($scope.lease.rentInputType === 'flexible') ? row.duration : 12;
          var monthsList = _.times(monthsInRow, function(i) {
            var obj = _.clone(row);
            return obj;
          });
        return monthsList;
      }).value();
      return rentPeriodsInMonths;
    }

    $scope.calculatedFields = {
      calculatedFields: {
        'lease.customFields.fileName': {
          dependsOn: [
            "lease.tenantInfo.name",
            "lease.tenantInfo.tradeName",
          ],
          calcFunc: function() {
            if (window.lease?.tenantInfo) {
              const tenant = $scope.lease.tenantInfo.tradeName || $scope.lease.tenantInfo.name;

              if (!$scope.lease.customFields) {
                $scope.lease.customFields = {};  
              }
              
              $scope.lease.customFields.fileName = tenant;

              LeaseEditorService.setLeaseTitle();
            }
          },
        },
        'building.landlords': {
          dependsOn: [],
          calcFunc: function() {
            if ($scope.building && $scope.building.landlordName && $scope.building.landlordStateOfFormation && $scope.building.landlordEntityType) {
              const landlords = $scope.building.landlordName.split(";");
              const states = $scope.building.landlordStateOfFormation.split(';')
              const entities = $scope.building.landlordEntityType.split(';')
              const result = [];
  
              for (let i = 0; i < landlords.length; i++) {
                result.push({
                  name: landlords[i],
                  state: states[i],
                  entityType: entities[i],
                });
              }
  
              $scope.building.landlords = result;
            }
          },
        },
        'lease.tiAllowance.multipleDrawsPercentCompletion': {
          dependsOn: [
            'lease.tiAllowance.multipleDrawsNumberPayments'
          ],
          calcFunc: function() {
            if (!$scope.lease.tiAllowance) {
              $scope.lease.tiAllowance = {};
            }

            if (!$scope.lease.tiAllowance.multipleDrawsPercentCompletion) {
              $scope.lease.tiAllowance.multipleDrawsPercentCompletion = [];
            }

            const targetLength = $scope.lease.tiAllowance.multipleDrawsNumberPayments - 1 || 0;
            const currentLength = $scope.lease.tiAllowance.multipleDrawsPercentCompletion.length;

            if (currentLength < targetLength) {
              // Add empty entries until we reach target length
              while ($scope.lease.tiAllowance.multipleDrawsPercentCompletion.length < targetLength) {
                let defaultValue = {};

                if ($scope.lease?.form?.editor?.defaultFields?.tiAllowance?.multipleDrawsPercentCompletion?.length > 0) {
                  defaultValue = {
                    ...$scope.lease.form.editor.defaultFields.tiAllowance.multipleDrawsPercentCompletion[0],
                  }
                }

                $scope.lease.tiAllowance.multipleDrawsPercentCompletion.push(defaultValue);
              }
            } else if (currentLength > targetLength) {
              // Remove excess entries
              $scope.lease.tiAllowance.multipleDrawsPercentCompletion.length = targetLength;
            }
          },
        },
        'lease.calc.tiAllowance.multipleDrawsPercentCompletionGroupBy': {
          dependsOn: [
            'lease.tiAllowance.multipleDrawsNumberPayments',
            'lease.tiAllowance.multipleDrawsPercentCompletion.percent',
            'lease.tiAllowance.multipleDrawsPercentCompletion.length'
          ],
          calcFunc: function() {
            // Group by percentage and calculate cumulative percentages
            const grouped = $scope.lease.tiAllowance.multipleDrawsPercentCompletion.reduce((acc, draw, index) => {
              if (index === 0) {
                acc.push({
                  percent: draw.percent,
                  cumulativePercent: draw.percent,
                  indexes: [index]
                });
              } else {
                if (draw.percent === acc[acc.length - 1].percent) {
                  // Same percentage, add to existing group
                  acc[acc.length - 1].cumulativePercent += draw.percent;
                  acc[acc.length - 1].indexes.push(index);
                } else {
                  // Different percentage, start new group
                  const previousCumulative = acc[acc.length - 1] ? acc[acc.length - 1].cumulativePercent : 0;
                  acc.push({
                    percent: draw.percent,
                    cumulativePercent: previousCumulative + draw.percent,
                    indexes: [index]
                  });
                }
              }
              return acc;
            }, []);

            // Add remainder record if needed
            const totalCumulative = grouped.length > 0 ? grouped[grouped.length - 1].cumulativePercent : 0;
            if (totalCumulative < 100) {
              const remainder = 100 - totalCumulative;
              const nextIndex = $scope.lease.tiAllowance.multipleDrawsPercentCompletion.length;
              grouped.push({
                percent: remainder,
                cumulativePercent: 100,
                indexes: [nextIndex]
              });
            }

            const result = grouped;

            if (!$scope.lease.calc.tiAllowance) {
              $scope.lease.calc.tiAllowance = {};
            }

            $scope.lease.calc.tiAllowance.multipleDrawsPercentCompletionGroupBy = result;
          }
        },
        'lease.priorDocuments.number': {
          dependsOn: [
            'lease.priorDocuments.length',
            'lease.priorDocuments.type'
          ],
          calcFunc: function() {
            let counters = {
              amendment: 0,
              assignment: 0,
            };

            for (let i = 0; i < $scope.lease.priorDocuments.length; i++) {
              switch ($scope.lease.priorDocuments[i].type) {
                case "Amendment": {
                  counters.amendment++;
                  $scope.lease.priorDocuments[i].amendmentNumber = counters.amendment;
                  delete $scope.lease.priorDocuments[i].assignmentNumber;
                  break;
                }
                case "Assignment": {
                  counters.assignment++;
                  $scope.lease.priorDocuments[i].assignmentNumber = counters.assignment;
                  delete $scope.lease.priorDocuments[i].amendmentNumber;
                  break;
                }
                case "Amendment & Assignment": {
                  counters.amendment++;
                  $scope.lease.priorDocuments[i].amendmentNumber = counters.amendment;
                  counters.assignment++;
                  $scope.lease.priorDocuments[i].assignmentNumber = counters.assignment;
                  break;
                }
              }
            }
          }
        },
        'lease.calc.priorDocuments.amendments': {
          dependsOn: [
            'lease.hasPriorDocuments',
            'lease.priorDocuments.length',
            'lease.priorDocuments.type'
          ],
          calcFunc: function() {
            if (typeof $scope.lease.calc.priorDocuments === 'undefined') {
              $scope.lease.calc.priorDocuments = {};
            }

            if (!$scope.lease.hasPriorDocuments) {
              $scope.lease.calc.priorDocuments.amendments = [];
              return;
            }

            const result = $scope.lease.priorDocuments?.filter(x => x.type?.contains("Amendment"));

            $scope.lease.calc.priorDocuments.amendments = result;
          }
        },
        'lease.calc.priorDocuments.assignments': {
          dependsOn: [
            'lease.hasPriorDocuments',
            'lease.priorDocuments.length',
            'lease.priorDocuments.type'
          ],
          calcFunc: function() {
            if (typeof $scope.lease.calc.priorDocuments === 'undefined') {
              $scope.lease.calc.priorDocuments = {};
            }

            if (!$scope.lease.hasPriorDocuments) {
              $scope.lease.calc.priorDocuments.assignments = [];
              return;
            }

            const result = $scope.lease.priorDocuments?.filter(x => x.type?.contains("Assignment"));

            $scope.lease.calc.priorDocuments.assignments = result;
          }
        },
        'lease.calc.priorDocuments.sorted': {
          dependsOn: [
            'lease.hasPriorDocuments',
            'lease.priorDocuments.date',
            'lease.priorDocuments.name',
            'lease.priorDocuments.assigneeName',
            'lease.priorDocuments.type',
            'lease.priorDocuments.length',
          ],
          calcFunc: function() {
            if (!$scope.lease.hasPriorDocuments) {
              $scope.lease.calc.priorDocuments.sorted = [];
              return;
            }

            $scope.lease.priorDocuments.forEach((doc, index) => {
              doc._originalIndex = index;
            });

            // Sort, but treat null/undefined/empty dates as coming last
            $scope.lease.calc.priorDocuments.sorted = _.orderBy(
              $scope.lease.priorDocuments,
              [
                function(doc) {
                  // If no date, return a value that always sorts last (Infinity for asc)
                  if (!doc || !doc.date) return Infinity;
                  // Some existing dates may not be parseable, so handle that
                  const dt = new Date(doc.date);
                  return isNaN(dt) ? Infinity : dt.getTime();
                }
              ],
              ['asc']
            );
          }
        },
        'lease.calc.tiAllowance.multipleDrawsPercentCompletionCumulative': {
          dependsOn: [
            'lease.tiAllowance.drawType',
            'lease.tiAllowance.multipleDrawsNumberPayments',
            'lease.tiAllowance.multipleDrawsPercentCompletion.percent',
            'lease.tiAllowance.multipleDrawsPercentCompletion.length'
          ],
          calcFunc: function() {
            const result = [];

            if (
              $scope.lease.tiAllowance.drawType == 'multiple' && 
              $scope.lease.tiAllowance.multipleDrawsPercentCompletion.length > 0
            ) {
              result.push({
                percent: $scope.lease.tiAllowance.multipleDrawsPercentCompletion[0].percent,
                cumulativePercent: $scope.lease.tiAllowance.multipleDrawsPercentCompletion[0].percent,
              })
  
              for (let i = 1; i < $scope.lease.tiAllowance.multipleDrawsPercentCompletion.length; i++) {
                result.push({
                  percent: $scope.lease.tiAllowance.multipleDrawsPercentCompletion[i].percent,
                  cumulativePercent: $scope.lease.tiAllowance.multipleDrawsPercentCompletion[i].percent + result[result.length - 1].cumulativePercent,
                })
              }
            }

            $scope.lease.calc.tiAllowance.multipleDrawsPercentCompletionCumulative = result;
          }
        },
        'lease.fixedCam': {
          dependsOn: ['lease.camType'],
          calcFunc: function() {
            if (!$scope.lease.camType) {
              $scope.lease.camType = $scope.lease.fixedCam
                ? 'fixed'
                : 'passthrough';
            } else {
              $scope.lease.fixedCam =
                $scope.lease.camType === 'passthrough' ? false : true;
            }
          },
        },
        'lease.hasConsecutiveAbatementDates': {
          dependsOn: [
            'lease.abatementDates.date',
            'lease.abatementDates.length',
          ],
          calcFunc: function() {
            if ($scope.lease.abatementDates.length >= 3) {
              $scope.lease.hasConsecutiveAbatementDates = true;
              _.forEach($scope.lease.abatementDates, function(date, i) {
                if (
                  i > 0 &&
                  moment(date.date).diff(
                    $scope.lease.abatementDates[i - 1].date,
                    'month',
                  ) !== 1
                ) {
                  $scope.lease.hasConsecutiveAbatementDates = false;
                }
              });
            } else {
              $scope.lease.hasConsecutiveAbatementDates = false;
            }
          },
        },
        'lease.numberOfExistingReleasedGuarantors': {
          dependsOn: [
            'lease.existingGuarantorInfo.entities.length',
            'lease.existingGuarantorInfo.marriedCouples.length',
            'lease.existingGuarantorInfo.individuals.length',
            'lease.existingGuarantorInfo.individuals.hasBeenReleased',
            'lease.existingGuarantorInfo.entities.hasBeenReleased',
            'lease.existingGuarantorInfo.marriedCouples.hasBeenReleased',
          ],
          calcFunc: function() {
            $scope.lease.numberOfExistingReleasedGuarantors = _.filter(
              _.union(
                $scope.lease.existingGuarantorInfo.individuals || [],
                $scope.lease.existingGuarantorInfo.marriedCouples || [],
                $scope.lease.existingGuarantorInfo.entities || [],
              ),
              {
                hasBeenReleased: true,
              },
            ).length;
          },
        },
        'lease.firstExistingReleasedGuarantor': {
          dependsOn: [
            'lease.existingGuarantorInfo.entities.length',
            'lease.existingGuarantorInfo.marriedCouples.length',
            'lease.existingGuarantorInfo.individuals.length',
            'lease.existingGuarantorInfo.individuals.hasBeenReleased',
            'lease.existingGuarantorInfo.entities.hasBeenReleased',
            'lease.existingGuarantorInfo.marriedCouples.hasBeenReleased',
          ],
          calcFunc: function() {
            $scope.lease.firstExistingReleasedGuarantor = _.first(
              _.filter(
                _.union(
                  $scope.lease.existingGuarantorInfo.individuals || [],
                  $scope.lease.existingGuarantorInfo.marriedCouples || [],
                  $scope.lease.existingGuarantorInfo.entities || [],
                ),
                {
                  hasBeenReleased: true,
                },
              ),
            );
          },
        },
        'lease.radiusRestriction': {
          dependsOn: [
            'lease.hasRadiusRestriction'
          ],
          calcFunc: function() {
            if (!$scope.lease.hasRadiusRestriction) {
              if ($scope.hasEditorConfig("radiusRestrictions", "resetRadiusRestrictionWhenUnchecked")) {
                $scope.lease.radiusRestriction = null;
                $scope.update();
              }
            }
          }
        },
        'lease.calc.fixedRenewalOptions': {
          dependsOn: [
            'lease.hasRenewalOption',
            'lease.renewalOptionRent',
            'lease.renewalCount',
            'lease.renewalInfo.length',
            'lease.renewalInfo.renewalOptionRent',
          ],
          calcFunc: function() {
            let result = [];

            if ($rootScope.hasEditorConfig("renewalRent", "rentOptionTypePerRenewalTerm")) {
              if ($scope.lease?.renewalInfo?.length > 0) {
                result = $scope.lease.renewalInfo.filter(x => x.renewalOptionRent == "fixed");
              }
            } else {
              if ($scope.lease.renewalOptionRent === "fixed") {
                result = $scope.lease.renewalInfo;
              }
            }

            $scope.lease.calc.fixedRenewalOptions = result;
          }
        },
        'lease.calc.fmvRenewalOptions': {
          dependsOn: [
            'lease.hasRenewalOption',
            'lease.renewalOptionRent',
            'lease.renewalCount',
            'lease.renewalInfo.length',
            'lease.renewalInfo.renewalOptionRent',
          ],
          calcFunc: function() {
            let result = [];

            if ($rootScope.hasEditorConfig("renewalRent", "rentOptionTypePerRenewalTerm")) {
              if ($scope.lease?.renewalInfo?.length > 0) {
                result = $scope.lease.renewalInfo.filter(x => x.renewalOptionRent == "fmv");
              }
            } else {
              if ($scope.lease.renewalOptionRent === "fmv") {
                result = $scope.lease.renewalInfo;
              }
            }

            $scope.lease.calc.fmvRenewalOptions = result;
          }
        },
        'lease.calc.termLengthPeriods': {
          dependsOn: [
            'lease.leaseYears',
            'lease.leaseMonths',
          ],
          calcFunc: function() {
            let periods = $scope.lease.leaseYears;
            let result = [];

            if ($scope.lease.leaseMonths > 0) {
              periods++;
            }

            for (let i = 0; i < periods; i++) {
              result.push(i);
            }

            $scope.lease.calc.termLengthPeriods = result;
          },
        },
        'lease.calc.existingGuarantors': {
          dependsOn: [
            'lease.existingGuarantorInfo.entities.length',
            'lease.existingGuarantorInfo.marriedCouples.length',
            'lease.existingGuarantorInfo.individuals.length',
          ],
          calcFunc: function() {
            const result = [];

            $scope.lease.existingGuarantorInfo.individuals.forEach(guarantor => {
              result.push({
                ...guarantor,
                type: "individual",
              });
            });

            $scope.lease.existingGuarantorInfo.marriedCouples.forEach(guarantor => {
              result.push({
                ...guarantor,
                type: "married couple",
              });
            });

            $scope.lease.existingGuarantorInfo.entities.forEach(guarantor => {
              result.push({
                ...guarantor,
                type: "entity",
              });
            });

            $scope.lease.calc.existingGuarantors = result;
          },
        },
        'lease.calc.existingGuarantorsGroupByDate': {
          dependsOn: [
            'lease.existingGuarantorInfo.entities.length',
            'lease.existingGuarantorInfo.entities.name',
            'lease.existingGuarantorInfo.entities.nickname',
            'lease.existingGuarantorInfo.entities.address',
            'lease.existingGuarantorInfo.entities.city',
            'lease.existingGuarantorInfo.entities.state',
            'lease.existingGuarantorInfo.entities.zip',
            'lease.existingGuarantorInfo.entities.stateOfFormation',
            'lease.existingGuarantorInfo.entities.entityTypeCombined',
            'lease.existingGuarantorInfo.entities.guarantyDate',
            'lease.existingGuarantorInfo.marriedCouples.length',
            'lease.existingGuarantorInfo.marriedCouples.name1',
            'lease.existingGuarantorInfo.marriedCouples.name2',
            'lease.existingGuarantorInfo.marriedCouples.nickname',
            'lease.existingGuarantorInfo.marriedCouples.address',
            'lease.existingGuarantorInfo.marriedCouples.city',
            'lease.existingGuarantorInfo.marriedCouples.state',
            'lease.existingGuarantorInfo.marriedCouples.zip',
            'lease.existingGuarantorInfo.marriedCouples.guarantyDate',
            'lease.existingGuarantorInfo.individuals.length',
            'lease.existingGuarantorInfo.individuals.name',
            'lease.existingGuarantorInfo.individuals.nickname',
            'lease.existingGuarantorInfo.individuals.address',
            'lease.existingGuarantorInfo.individuals.city',
            'lease.existingGuarantorInfo.individuals.state',
            'lease.existingGuarantorInfo.individuals.zip',
            'lease.existingGuarantorInfo.individuals.guarantyDate',
          ],
          calcFunc: function() {
            const guarantors = [];

            let counter = 0;

            $scope.lease.existingGuarantorInfo.individuals.forEach((guarantor, index) => {
              guarantors.push({
                ...guarantor,
                type: "individual",
                key: "individuals",
                _originalIndex: index,
                _sequence: counter++,
              });
            });

            $scope.lease.existingGuarantorInfo.marriedCouples.forEach((guarantor, index) => {
              guarantors.push({
                ...guarantor,
                type: "married couple",
                key: "marriedCouples",
                _originalIndex: index,
                _sequence: counter++,
              });
            });

            $scope.lease.existingGuarantorInfo.entities.forEach((guarantor, index) => {
              guarantors.push({
                ...guarantor,
                type: "entity",
                key: "entities",
                _originalIndex: index,
                _sequence: counter++,
              });
            });

            const result = guarantors.reduce((acc, guarantor) => {
              const date = guarantor.guarantyDate;

              if (!acc[date]) {
                acc[date] = {
                  individuals: [],
                  marriedCouples: [],
                  entities: [],
                  all: [],
                };
              }

              if (guarantor.type === "individual") {
                acc[date].individuals.push(guarantor);
              } else if (guarantor.type === "married couple") {
                acc[date].marriedCouples.push(guarantor);
              } else if (guarantor.type === "entity") {
                acc[date].entities.push(guarantor);
              }

              acc[date].all.push(guarantor);

              return acc;
            }, {});

            $scope.lease.calc.existingGuarantorsGroupByDate = Object.keys(result).map(key => {
              return result[key];
            });
          },
        },
        'lease.calc.existingGuarantorsUniqueNicknames': {
          dependsOn: [
            'lease.existingGuarantorInfo.entities.length',
            'lease.existingGuarantorInfo.entities.nickname',
            'lease.existingGuarantorInfo.marriedCouples.length',
            'lease.existingGuarantorInfo.marriedCouples.nickname',
            'lease.existingGuarantorInfo.individuals.length',
            'lease.existingGuarantorInfo.individuals.nickname',
          ],
          calcFunc: function() {
            const nicknames = [];

            let counter  = 0;

            $scope.lease.existingGuarantorInfo.individuals.forEach((guarantor, index) => {
              if (nicknames.filter(x => x.nickname === guarantor.nickname).length === 0) {
                nicknames.push({
                  nickname: guarantor.nickname,
                  type: "individual",
                  key: "individuals",
                  _originalIndex: index,
                  _sequence: counter++,
                });
              }
            });

            $scope.lease.existingGuarantorInfo.marriedCouples.forEach((guarantor, index) => {
              if (nicknames.filter(x => x.nickname === guarantor.nickname).length === 0) {
                nicknames.push({
                  nickname: guarantor.nickname,
                  type: "married couple",
                  key: "marriedCouples",
                  _originalIndex: index,
                  _sequence: counter++,
                });
              }
            });

            $scope.lease.existingGuarantorInfo.entities.forEach((guarantor, index) => {
              if (nicknames.filter(x => x.nickname === guarantor.nickname).length === 0) {
                nicknames.push({
                  nickname: guarantor.nickname,
                  type: "entity",
                  key: "entities",
                  _originalIndex: index,
                  _sequence: counter++,
                });
              }
            });

            $scope.lease.calc.existingGuarantorsUniqueNicknames = nicknames;
          },
        },
        'lease.calc.existingGuarantorsGroupByAddress': {
          dependsOn: [
            'lease.existingGuarantorInfo.entities.length',
            'lease.existingGuarantorInfo.entities.name',
            'lease.existingGuarantorInfo.entities.address',
            'lease.existingGuarantorInfo.entities.city',
            'lease.existingGuarantorInfo.entities.state',
            'lease.existingGuarantorInfo.entities.zip',
            'lease.existingGuarantorInfo.entities.stateOfFormation',
            'lease.existingGuarantorInfo.entities.entityTypeCombined',
            'lease.existingGuarantorInfo.entities.guarantyDate',
            'lease.existingGuarantorInfo.marriedCouples.length',
            'lease.existingGuarantorInfo.marriedCouples.name',
            'lease.existingGuarantorInfo.marriedCouples.address',
            'lease.existingGuarantorInfo.marriedCouples.city',
            'lease.existingGuarantorInfo.marriedCouples.state',
            'lease.existingGuarantorInfo.marriedCouples.zip',
            'lease.existingGuarantorInfo.marriedCouples.guarantyDate',
            'lease.existingGuarantorInfo.individuals.length',
            'lease.existingGuarantorInfo.individuals.name',
            'lease.existingGuarantorInfo.individuals.address',
            'lease.existingGuarantorInfo.individuals.city',
            'lease.existingGuarantorInfo.individuals.state',
            'lease.existingGuarantorInfo.individuals.zip',
            'lease.existingGuarantorInfo.individuals.guarantyDate',
          ],
          calcFunc: function() {
            const guarantors = [];

            let counter = 0;

            $scope.lease.existingGuarantorInfo.individuals.forEach((guarantor, index) => {
              guarantors.push({
                ...guarantor,
                type: "individual",
                key: "individuals",
                _originalIndex: index,
                _sequence: counter++,
              });
            });

            $scope.lease.existingGuarantorInfo.marriedCouples.forEach((guarantor, index) => {
              guarantors.push({
                ...guarantor,
                type: "married couple",
                key: "marriedCouples",
                _originalIndex: index,
                _sequence: counter++,
              });
            });

            $scope.lease.existingGuarantorInfo.entities.forEach((guarantor, index) => {
              guarantors.push({
                ...guarantor,
                type: "entity",
                key: "entities",
                _originalIndex: index,
                _sequence: counter++,
              });
            });

            const result = guarantors.reduce((acc, guarantor) => {
              const address = guarantor.address || "";
              const city = guarantor.city || "";
              const state = guarantor.state || "";
              const zip = guarantor.zip || "";
              const fullAddress = [address, city, state, zip].join(", ");
              const date = guarantor.guarantyDate;

              if (!acc[date]) {
                acc[date] = {};
              }

              if (!acc[date][fullAddress]) {
                acc[date][fullAddress] = {
                  individuals: [],
                  marriedCouples: [],
                  entities: [],
                  all: [],
                };
              }

              if (guarantor.type === "individual") {
                acc[date][fullAddress].individuals.push(guarantor);
              } else if (guarantor.type === "married couple") {
                acc[date][fullAddress].marriedCouples.push(guarantor);
              } else if (guarantor.type === "entity") {
                acc[date][fullAddress].entities.push(guarantor);
              }

              acc[date][fullAddress].all.push(guarantor);

              return acc;
            }, {});

            $scope.lease.calc.existingGuarantorsGroupByAddress = Object.keys(result).map(key1 => {
              return Object.keys(result[key1]).map(key2 => {
                return result[key1][key2];
              });
            });
          },
        },
        'lease.calc.existingReleasedGuarantors': {
          dependsOn: [
            'lease.existingGuarantorInfo.individuals.length',
            'lease.existingGuarantorInfo.individuals.name',
            'lease.existingGuarantorInfo.individuals.address',
            'lease.existingGuarantorInfo.individuals.city',
            'lease.existingGuarantorInfo.individuals.state',
            'lease.existingGuarantorInfo.individuals.zip',
            'lease.existingGuarantorInfo.individuals.guarantyDate',
            'lease.existingGuarantorInfo.individuals.hasBeenReleased',
            'lease.existingGuarantorInfo.individuals.hasReleaseDate',
            'lease.existingGuarantorInfo.individuals.releaseDate',
            'lease.existingGuarantorInfo.entities.length',
            'lease.existingGuarantorInfo.entities.name',
            'lease.existingGuarantorInfo.entities.address',
            'lease.existingGuarantorInfo.entities.city',
            'lease.existingGuarantorInfo.entities.state',
            'lease.existingGuarantorInfo.entities.zip',
            'lease.existingGuarantorInfo.entities.stateOfFormation',
            'lease.existingGuarantorInfo.entities.entityTypeCombined',
            'lease.existingGuarantorInfo.entities.guarantyDate',
            'lease.existingGuarantorInfo.entities.hasBeenReleased',
            'lease.existingGuarantorInfo.entities.hasReleaseDate',
            'lease.existingGuarantorInfo.entities.releaseDate',
            'lease.existingGuarantorInfo.marriedCouples.length',
            'lease.existingGuarantorInfo.marriedCouples.name',
            'lease.existingGuarantorInfo.marriedCouples.address',
            'lease.existingGuarantorInfo.marriedCouples.city',
            'lease.existingGuarantorInfo.marriedCouples.state',
            'lease.existingGuarantorInfo.marriedCouples.zip',
            'lease.existingGuarantorInfo.marriedCouples.guarantyDate',
            'lease.existingGuarantorInfo.marriedCouples.hasBeenReleased',
            'lease.existingGuarantorInfo.marriedCouples.hasReleaseDate',
            'lease.existingGuarantorInfo.marriedCouples.releaseDate', 
          ],
          calcFunc: function() {
            $scope.lease.calc.existingReleasedGuarantors = _.filter(
              _.union(
                ($scope.lease.existingGuarantorInfo.individuals || []).map((g, index) => ({ ...g, _type: 'individuals', _originalIndex: index })),
                ($scope.lease.existingGuarantorInfo.marriedCouples || []).map((g, index) => ({ ...g, _type: 'marriedCouples', _originalIndex: index })), 
                ($scope.lease.existingGuarantorInfo.entities || []).map((g, index) => ({ ...g, _type: 'entities', _originalIndex: index }))
              ),
              {
                hasBeenReleased: true,
              },
            );
          },
        },
        'lease.calc.existingUnreleasedGuarantors': {
          dependsOn: [
            'lease.existingGuarantorInfo.entities.length',
            'lease.existingGuarantorInfo.marriedCouples.length',
            'lease.existingGuarantorInfo.individuals.length',
            'lease.existingGuarantorInfo.individuals.hasBeenReleased',
            'lease.existingGuarantorInfo.entities.hasBeenReleased',
            'lease.existingGuarantorInfo.marriedCouples.hasBeenReleased',
          ],
          calcFunc: function() {
            $scope.lease.calc.existingUnreleasedGuarantors = _.filter(
              _.union(
                $scope.lease.existingGuarantorInfo.individuals || [],
                $scope.lease.existingGuarantorInfo.marriedCouples || [],
                $scope.lease.existingGuarantorInfo.entities || [],
              ),
              {
                hasBeenReleased: false,
              },
            );
          },
        },
        'lease.calc.additionalExistingGuarantors': {
          dependsOn: [
            'lease.additionalExistingGuarantorInfo.entities.length',
            'lease.additionalExistingGuarantorInfo.marriedCouples.length',
            'lease.additionalExistingGuarantorInfo.individuals.length',
          ],
          calcFunc: function() {
            const result = [];

            $scope.lease.additionalExistingGuarantorInfo.individuals.forEach(guarantor => {
              result.push({
                ...guarantor,
                type: "individual",
              });
            });

            $scope.lease.additionalExistingGuarantorInfo.marriedCouples.forEach(guarantor => {
              result.push({
                ...guarantor,
                type: "married couple",
              });
            });

            $scope.lease.additionalExistingGuarantorInfo.entities.forEach(guarantor => {
              result.push({
                ...guarantor,
                type: "entity",
              });
            });

            $scope.lease.calc.additionalExistingGuarantors = result;
          },
        },
        'lease.calc.additionalExistingGuarantorsGroupByDate': {
          dependsOn: [
            'lease.additionalExistingGuarantorInfo.entities.length',
            'lease.additionalExistingGuarantorInfo.entities.name',
            'lease.additionalExistingGuarantorInfo.entities.nickname',
            'lease.additionalExistingGuarantorInfo.entities.address',
            'lease.additionalExistingGuarantorInfo.entities.city',
            'lease.additionalExistingGuarantorInfo.entities.state',
            'lease.additionalExistingGuarantorInfo.entities.zip',
            'lease.additionalExistingGuarantorInfo.entities.stateOfFormation',
            'lease.additionalExistingGuarantorInfo.entities.entityTypeCombined',
            'lease.additionalExistingGuarantorInfo.entities.guarantyDate',
            'lease.additionalExistingGuarantorInfo.marriedCouples.length',
            'lease.additionalExistingGuarantorInfo.marriedCouples.name1',
            'lease.additionalExistingGuarantorInfo.marriedCouples.name2',
            'lease.additionalExistingGuarantorInfo.marriedCouples.nickname',
            'lease.additionalExistingGuarantorInfo.marriedCouples.address1',
            'lease.additionalExistingGuarantorInfo.marriedCouples.address2',
            'lease.additionalExistingGuarantorInfo.marriedCouples.city1',
            'lease.additionalExistingGuarantorInfo.marriedCouples.city2',
            'lease.additionalExistingGuarantorInfo.marriedCouples.state1',
            'lease.additionalExistingGuarantorInfo.marriedCouples.state2',
            'lease.additionalExistingGuarantorInfo.marriedCouples.zip1',
            'lease.additionalExistingGuarantorInfo.marriedCouples.zip2',
            'lease.additionalExistingGuarantorInfo.marriedCouples.guarantyDate',
            'lease.additionalExistingGuarantorInfo.individuals.length',
            'lease.additionalExistingGuarantorInfo.individuals.name',
            'lease.additionalExistingGuarantorInfo.individuals.nickname',
            'lease.additionalExistingGuarantorInfo.individuals.address',
            'lease.additionalExistingGuarantorInfo.individuals.city',
            'lease.additionalExistingGuarantorInfo.individuals.state',
            'lease.additionalExistingGuarantorInfo.individuals.zip',
            'lease.additionalExistingGuarantorInfo.individuals.guarantyDate',
          ],
          calcFunc: function() {
            const guarantors = [];

            let counter = 0;

            $scope.lease.additionalExistingGuarantorInfo.individuals.forEach((guarantor, index) => {
              guarantors.push({
                ...guarantor,
                type: "individual",
                key: "individuals",
                _originalIndex: index,
                _sequence: counter++,
              });
            });

            $scope.lease.additionalExistingGuarantorInfo.marriedCouples.forEach((guarantor, index) => {
              guarantors.push({
                ...guarantor,
                type: "married couple",
                key: "marriedCouples",
                _originalIndex: index,
                _sequence: counter++,
              });
            });

            $scope.lease.additionalExistingGuarantorInfo.entities.forEach((guarantor, index) => {
              guarantors.push({
                ...guarantor,
                type: "entity",
                key: "entities",
                _originalIndex: index,
                _sequence: counter++,
              });
            });

            const result = guarantors.reduce((acc, guarantor) => {
              const date = guarantor.guarantyDate;

              if (!acc[date]) {
                acc[date] = {
                  individuals: [],
                  marriedCouples: [],
                  entities: [],
                  all: [],
                };
              }

              if (guarantor.type === "individual") {
                acc[date].individuals.push(guarantor);
              } else if (guarantor.type === "married couple") {
                acc[date].marriedCouples.push(guarantor);
              } else if (guarantor.type === "entity") {
                acc[date].entities.push(guarantor);
              }

              acc[date].all.push(guarantor);

              return acc;
            }, {});

            $scope.lease.calc.additionalExistingGuarantorsGroupByDate = Object.keys(result).map(key => {
              return result[key];
            });
          },
        },
        'lease.calc.additionalExistingGuarantorsUniqueNicknames': {
          dependsOn: [
            'lease.additionalExistingGuarantorInfo.entities.length',
            'lease.additionalExistingGuarantorInfo.entities.nickname',
            'lease.additionalExistingGuarantorInfo.marriedCouples.length',
            'lease.additionalExistingGuarantorInfo.marriedCouples.nickname',
            'lease.additionalExistingGuarantorInfo.individuals.length',
            'lease.additionalExistingGuarantorInfo.individuals.nickname',
          ],
          calcFunc: function() {
            const nicknames = [];

            let counter  = 0;

            $scope.lease.additionalExistingGuarantorInfo.individuals.forEach((guarantor, index) => {
              if (nicknames.filter(x => x.nickname === guarantor.nickname).length === 0) {
                nicknames.push({
                  nickname: guarantor.nickname,
                  type: "individual",
                  key: "individuals",
                  _originalIndex: index,
                  _sequence: counter++,
                });
              }
            });

            $scope.lease.additionalExistingGuarantorInfo.marriedCouples.forEach((guarantor, index) => {
              if (nicknames.filter(x => x.nickname === guarantor.nickname).length === 0) {
                nicknames.push({
                  nickname: guarantor.nickname,
                  type: "married couple",
                  key: "marriedCouples",
                  _originalIndex: index,
                  _sequence: counter++,
                });
              }
            });

            $scope.lease.additionalExistingGuarantorInfo.entities.forEach((guarantor, index) => {
              if (nicknames.filter(x => x.nickname === guarantor.nickname).length === 0) {
                nicknames.push({
                  nickname: guarantor.nickname,
                  type: "entity",
                  key: "entities",
                  _originalIndex: index,
                  _sequence: counter++,
                });
              }
            });

            $scope.lease.calc.additionalExistingGuarantorsUniqueNicknames = nicknames;
          },
        },
        'lease.calc.additionalExistingGuarantorsGroupByAddress': {
          dependsOn: [
            'lease.additionalExistingGuarantorInfo.entities.length',
            'lease.additionalExistingGuarantorInfo.entities.name',
            'lease.additionalExistingGuarantorInfo.entities.address',
            'lease.additionalExistingGuarantorInfo.entities.city',
            'lease.additionalExistingGuarantorInfo.entities.state',
            'lease.additionalExistingGuarantorInfo.entities.zip',
            'lease.additionalExistingGuarantorInfo.entities.stateOfFormation',
            'lease.additionalExistingGuarantorInfo.entities.entityTypeCombined',
            'lease.additionalExistingGuarantorInfo.entities.guarantyDate',
            'lease.additionalExistingGuarantorInfo.marriedCouples.length',
            'lease.additionalExistingGuarantorInfo.marriedCouples.name1',
            'lease.additionalExistingGuarantorInfo.marriedCouples.name2',
            'lease.additionalExistingGuarantorInfo.marriedCouples.address1',
            'lease.additionalExistingGuarantorInfo.marriedCouples.address2',
            'lease.additionalExistingGuarantorInfo.marriedCouples.city1',
            'lease.additionalExistingGuarantorInfo.marriedCouples.city2',
            'lease.additionalExistingGuarantorInfo.marriedCouples.state1',
            'lease.additionalExistingGuarantorInfo.marriedCouples.state2',
            'lease.additionalExistingGuarantorInfo.marriedCouples.zip1',
            'lease.additionalExistingGuarantorInfo.marriedCouples.zip2',
            'lease.additionalExistingGuarantorInfo.marriedCouples.guarantyDate',
            'lease.additionalExistingGuarantorInfo.individuals.length',
            'lease.additionalExistingGuarantorInfo.individuals.name',
            'lease.additionalExistingGuarantorInfo.individuals.address',
            'lease.additionalExistingGuarantorInfo.individuals.city',
            'lease.additionalExistingGuarantorInfo.individuals.state',
            'lease.additionalExistingGuarantorInfo.individuals.zip',
            'lease.additionalExistingGuarantorInfo.individuals.guarantyDate',
          ],
          calcFunc: function() {
            const guarantors = [];

            let counter = 0;

            $scope.lease.additionalExistingGuarantorInfo.individuals.forEach((guarantor, index) => {
              guarantors.push({
                ...guarantor,
                type: "individual",
                key: "individuals",
                _originalIndex: index,
                _sequence: counter++,
              });
            });

            $scope.lease.additionalExistingGuarantorInfo.marriedCouples.forEach((guarantor, index) => {
              guarantors.push({
                ...guarantor,
                type: "married couple",
                key: "marriedCouples",
                _originalIndex: index,
                _sequence: counter++,
              });
            });

            $scope.lease.additionalExistingGuarantorInfo.entities.forEach((guarantor, index) => {
              guarantors.push({
                ...guarantor,
                type: "entity",
                key: "entities",
                _originalIndex: index,
                _sequence: counter++,
              });
            });

            const result = guarantors.reduce((acc, guarantor) => {
              const address = guarantor.address || "";
              const city = guarantor.city || "";
              const state = guarantor.state || "";
              const zip = guarantor.zip || "";
              const fullAddress = [address, city, state, zip].join(", ");
              const date = guarantor.guarantyDate;

              if (!acc[date]) {
                acc[date] = {};
              }

              if (!acc[date][fullAddress]) {
                acc[date][fullAddress] = {
                  individuals: [],
                  marriedCouples: [],
                  entities: [],
                  all: [],
                };
              }

              if (guarantor.type === "individual") {
                acc[date][fullAddress].individuals.push(guarantor);
              } else if (guarantor.type === "married couple") {
                acc[date][fullAddress].marriedCouples.push(guarantor);
              } else if (guarantor.type === "entity") {
                acc[date][fullAddress].entities.push(guarantor);
              }

              acc[date][fullAddress].all.push(guarantor);

              return acc;
            }, {});

            $scope.lease.calc.additionalExistingGuarantorsGroupByAddress = Object.keys(result).map(key1 => {
              return Object.keys(result[key1]).map(key2 => {
                return result[key1][key2];
              });
            });
          },
        },
        'lease.calc.additionalExistingReleasedGuarantors': {
          dependsOn: [
            'lease.additionalExistingGuarantorInfo.individuals.length',
            'lease.additionalExistingGuarantorInfo.individuals.name',
            'lease.additionalExistingGuarantorInfo.individuals.address',
            'lease.additionalExistingGuarantorInfo.individuals.city',
            'lease.additionalExistingGuarantorInfo.individuals.state',
            'lease.additionalExistingGuarantorInfo.individuals.zip',
            'lease.additionalExistingGuarantorInfo.individuals.guarantyDate',
            'lease.additionalExistingGuarantorInfo.individuals.hasBeenReleased',
            'lease.additionalExistingGuarantorInfo.individuals.hasReleaseDate',
            'lease.additionalExistingGuarantorInfo.individuals.releaseDate',
            'lease.additionalExistingGuarantorInfo.entities.length',
            'lease.additionalExistingGuarantorInfo.entities.name',
            'lease.additionalExistingGuarantorInfo.entities.address',
            'lease.additionalExistingGuarantorInfo.entities.city',
            'lease.additionalExistingGuarantorInfo.entities.state',
            'lease.additionalExistingGuarantorInfo.entities.zip',
            'lease.additionalExistingGuarantorInfo.entities.stateOfFormation',
            'lease.additionalExistingGuarantorInfo.entities.entityTypeCombined',
            'lease.additionalExistingGuarantorInfo.entities.guarantyDate',
            'lease.additionalExistingGuarantorInfo.entities.hasBeenReleased',
            'lease.additionalExistingGuarantorInfo.entities.hasReleaseDate',
            'lease.additionalExistingGuarantorInfo.entities.releaseDate',
            'lease.additionalExistingGuarantorInfo.marriedCouples.length',
            'lease.additionalExistingGuarantorInfo.marriedCouples.name1',
            'lease.additionalExistingGuarantorInfo.marriedCouples.name2',
            'lease.additionalExistingGuarantorInfo.marriedCouples.address1',
            'lease.additionalExistingGuarantorInfo.marriedCouples.address2',
            'lease.additionalExistingGuarantorInfo.marriedCouples.city1',
            'lease.additionalExistingGuarantorInfo.marriedCouples.city2',
            'lease.additionalExistingGuarantorInfo.marriedCouples.state1',
            'lease.additionalExistingGuarantorInfo.marriedCouples.state2',
            'lease.additionalExistingGuarantorInfo.marriedCouples.zip1',
            'lease.additionalExistingGuarantorInfo.marriedCouples.zip2',
            'lease.additionalExistingGuarantorInfo.marriedCouples.guarantyDate',
            'lease.additionalExistingGuarantorInfo.marriedCouples.hasBeenReleased',
            'lease.additionalExistingGuarantorInfo.marriedCouples.hasReleaseDate',
            'lease.additionalExistingGuarantorInfo.marriedCouples.releaseDate',
          ],
          calcFunc: function() {
            $scope.lease.calc.additionalExistingReleasedGuarantors = _.filter(
              _.union(
                ($scope.lease.additionalExistingGuarantorInfo.individuals || []).map((g, index) => ({ ...g, _type: 'individuals', _originalIndex: index })),
                ($scope.lease.additionalExistingGuarantorInfo.marriedCouples || []).map((g, index) => ({ ...g, _type: 'marriedCouples', _originalIndex: index })), 
                ($scope.lease.additionalExistingGuarantorInfo.entities || []).map((g, index) => ({ ...g, _type: 'entities', _originalIndex: index }))
              ),
              {
                hasBeenReleased: true,
              },
            );
          },
        },
        'lease.calc.additionalExistingUnreleasedGuarantors': {
          dependsOn: [
            'lease.additionalExistingGuarantorInfo.entities.length',
            'lease.additionalExistingGuarantorInfo.marriedCouples.length',
            'lease.additionalExistingGuarantorInfo.individuals.length',
            'lease.additionalExistingGuarantorInfo.individuals.hasBeenReleased',
            'lease.additionalExistingGuarantorInfo.entities.hasBeenReleased',
            'lease.additionalExistingGuarantorInfo.marriedCouples.hasBeenReleased',
          ],
          calcFunc: function() {
            $scope.lease.calc.additionalExistingUnreleasedGuarantors = _.filter(
              _.union(
                $scope.lease.additionalExistingGuarantorInfo.individuals || [],
                $scope.lease.additionalExistingGuarantorInfo.marriedCouples || [],
                $scope.lease.additionalExistingGuarantorInfo.entities || [],
              ),
              {
                hasBeenReleased: false,
              },
            );
          },
        },
        'lease.calc.orderedPreviousAmendments': {
          dependsOn: [
            'lease.previousAmendment.date',
            'lease.previousAmendment.length',
          ],
          calcFunc: function() {
            const orderedPreviousAmendments = _.orderBy($scope.lease.previousAmendment, ['date'], ['asc']);

            $scope.lease.calc.orderedPreviousAmendments = orderedPreviousAmendments;
          }
        },
        'lease.calc.orderedPriorAssignments': {
          dependsOn: [
            'lease.priorAssignments.date',
            'lease.priorAssignments.length',
          ],
          calcFunc: function() {
            const orderedPriorAssignments = _.orderBy($scope.lease.priorAssignments, ['date'], ['asc']);

            $scope.lease.calc.orderedPriorAssignments = orderedPriorAssignments;
          }
        },
        'lease.rentInputType': {
          dependsOn: [
            'lease.baseRentType'
          ],
          calcFunc: function() {
            if ($scope.lease.baseRentType == "text" && $scope.lease.rentInputType != "psf") {
              if ($scope.hasEditorConfig('baseRent', 'disableRentInputType')) {
                $scope.lease.rentInputType = "automatic";
                $scope.update();
              }
            }
          }
        },
        'lease.hasMultipleGuarantors': {
          dependsOn: [
            'lease.existingGuarantorInfo.individuals.length',
            'lease.existingGuarantorInfo.marriedCouples.length',
            'lease.existingGuarantorInfo.entities.length',
          ],
          calcFunc: function() {
            $scope.lease.hasMultipleGuarantors =
              $scope.lease.guarantorInfo.individuals.length +
                $scope.lease.guarantorInfo.marriedCouples.length +
                $scope.lease.guarantorInfo.entities.length >
              1;
          },
        },
        'lease.multipleGuarantors': {
          dependsOn: [
            'lease.guarantorInfo.individuals.length',
            'lease.guarantorInfo.individuals.address',
            'lease.guarantorInfo.individuals.city',
            'lease.guarantorInfo.individuals.name',
            'lease.guarantorInfo.individuals.phone',
            'lease.guarantorInfo.individuals.email',
            'lease.guarantorInfo.individuals.SSN',
            'lease.guarantorInfo.individuals.state',
            'lease.guarantorInfo.individuals.zip',
            'lease.guarantorInfo.marriedCouples.length',
            'lease.guarantorInfo.marriedCouples.address1',
            'lease.guarantorInfo.marriedCouples.address2',
            'lease.guarantorInfo.marriedCouples.city1',
            'lease.guarantorInfo.marriedCouples.city2',
            'lease.guarantorInfo.marriedCouples.hasSameAddress',
            'lease.guarantorInfo.marriedCouples.name1',
            'lease.guarantorInfo.marriedCouples.name2',
            'lease.guarantorInfo.marriedCouples.phone1',
            'lease.guarantorInfo.marriedCouples.phone2',
            'lease.guarantorInfo.marriedCouples.email1',
            'lease.guarantorInfo.marriedCouples.email2',
            'lease.guarantorInfo.marriedCouples.state1',
            'lease.guarantorInfo.marriedCouples.state2',
            'lease.guarantorInfo.marriedCouples.zip1',
            'lease.guarantorInfo.marriedCouples.zip2',
            'lease.guarantorInfo.marriedCouples.SSN1',
            'lease.guarantorInfo.marriedCouples.SSN2',
            'lease.guarantorInfo.entities.length',
            'lease.guarantorInfo.entities.address',
            'lease.guarantorInfo.entities.attention',
            'lease.guarantorInfo.entities.city',
            'lease.guarantorInfo.entities.entityType',
            'lease.guarantorInfo.entities.entityTypeCombined',
            'lease.guarantorInfo.entities.entityTypeOther',
            'lease.guarantorInfo.entities.name',
            'lease.guarantorInfo.entities.phone',
            'lease.guarantorInfo.entities.email',
            'lease.guarantorInfo.entities.stateOfFormation',
            'lease.guarantorInfo.entities.state',
            'lease.guarantorInfo.entities.taxId',
            'lease.guarantorInfo.entities.zip',
            'lease.guarantorInfo.entities.agentName',
            'lease.guarantorInfo.entities.agentAddress',
            'lease.guarantorInfo.entities.agentCity',
            'lease.guarantorInfo.entities.agentState',
            'lease.guarantorInfo.entities.agentZip',
          ],
          calcFunc: function() {
            const result = [];

            for (let i = 0; i < $scope.lease.guarantorInfo.individuals.length; i++) {
              result.push({
                ...$scope.lease.guarantorInfo.individuals[i],
                type: "individual",
                _originalIndex: i,
              })
            }
            for (let i = 0; i < $scope.lease.guarantorInfo.marriedCouples.length; i++) {
              result.push({
                ...$scope.lease.guarantorInfo.marriedCouples[i],
                type: "marriedCouple",
                _originalIndex: i,
              })
            }
            for (let i = 0; i < $scope.lease.guarantorInfo.entities.length; i++) {
              result.push({
                ...$scope.lease.guarantorInfo.entities[i],
                type: "entity",
                _originalIndex: i,
              })
            }

            $scope.lease.multipleGuarantors = result;
          },
        },
        'lease.hasExistingMarriedCouplesReleasedGuarantors': {
          dependsOn: [
            'lease.existingGuarantorInfo.marriedCouples.length',
            'lease.existingGuarantorInfo.marriedCouples.hasBeenReleased',
          ],
          calcFunc: function() {
            $scope.lease.hasExistingMarriedCouplesReleasedGuarantors =
              _.filter($scope.lease.existingGuarantorInfo.marriedCouples, {
                hasBeenReleased: true,
              }).length > 0
                ? true
                : false;
          },
        },
        'lease.hasHardCommencementDate': {
          dependsOn: [
            'lease.hasSpecificExpirationDate',
            'lease.estimatedCommencementDate',
            'lease.termCommencementDate',
            'lease.commencementDateType',
            'lease.fixedCommencementDateType',
            'lease.estimatedCommencementDateType',
            'lease.terms.hasSpecificExpirationDate',
            'lease.terms.estimatedCommencementDate',
            'lease.terms.termCommencementDate',
            'lease.terms.estimatedCommencementDateType',
            'lease.hasInitialDeliveryWork',
            'lease.tenantImprovements.type',
          ],
          calcFunc: function() {
            $scope.lease.hasHardCommencementDate =
              $scope.lease.tenantImprovements &&
              (($scope.lease.tenantImprovements.type === 'landlord' &&
                ($scope.lease.hasSpecificExpirationDate ||
                ($scope.hasEditorConfig('commencementDate') &&
                  ($scope.lease.fixedCommencementDateType !== 'execution of lease' ||
                    !$scope.hasEditorConfig('commencementDate', 'landlordFixedExecutionOfLease')) &&
                    ($scope.lease.commencementDateType !== 'floating' ||
                      (!$scope.hasEditorConfig('commencementDate', 'landlordFloatingSoftPeriodDays') &&
                        !$scope.hasEditorConfig('commencementDate', 'landlordFloatingDate')) ) )) ) ||
                ($scope.lease.tenantImprovements.type === 'tenant' &&
                  ((!$scope.hasEditorConfig('commencementDate') && !$scope.lease.hasInitialDeliveryWork &&
                  $scope.lease.estimatedCommencementDateType === 'hard date') ||
                  (($scope.hasEditorConfig('commencementDate') &&
                    ($scope.lease.fixedCommencementDateType !== 'execution of lease' ||
                      !$scope.hasEditorConfig('commencementDate', 'tenantFixedExecutionOfLease')) &&
                      ($scope.lease.commencementDateType !== 'floating' ||
                        (!$scope.hasEditorConfig('commencementDate', 'tenantFloatingSoftPeriodDays') &&
                          !$scope.hasEditorConfig('commencementDate', 'tenantFloatingDate')) ) )) ))  ||
                ($scope.lease.tenantImprovements.type === 'none' &&
                  ($scope.lease.fixedCommencementDateType !== 'execution of lease' ||
                    !$scope.hasEditorConfig('commencementDate', 'asIsFixedExecutionOfLease')) &&
                    ($scope.lease.commencementDateType !== 'floating' ||
                      (!$scope.hasEditorConfig('commencementDate', 'asIsFloatingSoftPeriodDays') &&
                        !$scope.hasEditorConfig('commencementDate', 'asIsFloatingDate')) ) ));
          },
        },
        'lease.hasAmendedProvisions': {
          dependsOn: [
            "lease.hasAmendedProvisions",
          ],
          calcFunc: function() {
            if ($scope.lease.hasLeaseAssignment && !$scope.lease.hasAmendedProvisions) {
              $scope.lease.hasGrossSalesReporting = false;
              $scope.lease.hasExtensionOfTerm = false;
              $scope.lease.hasReplacementSignageCriteria = false;
              $scope.lease.tenantInfo.hasBroker = false;
              $scope.lease.hasRenewalOption = false;
              $scope.lease.hasRentAbatement = false;
              $scope.lease.hasRentDeferral = false;
              $scope.lease.hasPylonSign = false;
              $scope.lease.hasSecurityDeposit = false;
              $scope.lease.hasExtensionOfTerm = false;
              $scope.lease.changesToPercentageRent = "none";
            }
          }
        },
        'lease.hasSpecialRights': {
          dependsOn: [
            'lease.hasRenewalOption',
            'lease.hasRooftopEquipment',
            'lease.hasTenantTerminationOption',
            'lease.hasRightOfFirstOffer',
            'lease.hasExpansionOption',
            'lease.hasParking',
          ],
          calcFunc: function() {
            $scope.lease.hasSpecialRights =
              $scope.lease.hasRenewalOption ||
              $scope.lease.hasRightOfFirstOffer ||
              $scope.lease.hasExpansionOption ||
              $scope.lease.hasParking ||
              $scope.lease.hasRooftopEquipment ||
              $scope.lease.hasTenantTerminationOption;
          },
        },
        'lease.hasLimitedGrossSalesReporting': {
          dependsOn: ['lease.hasPercentageRent'],
          calcFunc: function() {
            if ($scope.lease.hasPercentageRent) {
              $scope.lease.hasLimitedGrossSalesReporting = false;
            }
          },
        },
        'lease.percentageRent': {
          dependsOn: ['lease.hasPercentageRent'],
          calcFunc: function() {
            if ($scope.lease.hasPercentageRent == false) {
              $scope.lease.percentageRent = '';
            }
          },
        },
        'lease.hasAreaRemeasurement': {
          dependsOn: ['lease.hasPremisesAreaSizeStipulated'],
          calcFunc: function() {
            if ($scope.lease.hasPremisesAreaSizeStipulated) {
              $scope.lease.hasAreaRemeasurement = false;
            }
          },
        },
        'lease.minimumSales': {
          dependsOn: [
            'lease.hasExtensionOfTerm',
            'lease.hasRenewalOption',
            'lease.leaseYears',
            'lease.leaseMonths',
            'lease.renewalCount',
            'lease.renewalTerm',
          ],
          calcFunc: function() {
            let count = 0;

            if ($rootScope.hasEditorConfig('optionalExtensionTermLength')) {
              if ($scope.lease.hasExtensionOfTerm) {
                count = $scope.lease.leaseYears;
              }
            } else {
              count = $scope.lease.leaseYears;
            }

            if ($scope.lease.hasRenewalOption) {
              count += $scope.lease.renewalCount * $scope.lease.renewalTerm;
            }

            if (
              $scope.lease.type ===  "Lease" &&
              $scope.lease.leaseMonths > 0 &&
              $rootScope.hasEditorConfig('termLength', 'addRentRowForPartialYear')
            ) {
              count += 1;
            }

            if (
              $scope.lease.type ===  "Amendment" &&
              $scope.lease.leaseMonths > 0 &&
              $rootScope.hasEditorConfig('optionalExtensionTermLength', 'addRentRowForPartialYear')
            ) {
              count += 1;
            }

            if (count > $scope.lease.minimumSales.length) {
              var totalMinimumSales =
                count - $scope.lease.minimumSales.length;
              for (var i = 0; i < totalMinimumSales; i++) {
                $scope.lease.minimumSales.push({
                  value: undefined,
                });
              }
            } else {
              count = $scope.lease.minimumSales.length - count;
              $scope.lease.minimumSales.splice(-count, count);
            }
          },
        },
        'lease.rentRows': {
          dependsOn: [
            'lease.existingLeaseExpirationDate',
            'lease.hasFreeRentAddedToTerm',
            'lease.freeRentPeriods.softEndDate',
            'lease.freeRentPeriods.softStartDate',
            'lease.freeRentPeriods.hardEndDate',
            'lease.freeRentPeriods.hardStartDate',
            'lease.freeRentPeriods.isFree',
            'lease.freeRentExtendedAt',
            'lease.abatementDates.date',
            'lease.hasRentAbatement',
            'lease.hasBlendAndExtend',
            'lease.abatementDates.length',
            'lease.leaseYears',
            'lease.leaseMonths',
            'lease.rentCommenceAt',
            'rentValues',
            'lease.rentInputType',
            'lease.hasFreeRent',
            'lease.hasCpiRent',
            'lease.cpiAnchorYears',
            'lease.freeRent',
            'lease.premiseAreaSize',
            'lease.expansionPremiseAreaSize',
            'lease.relocationPremisesAreaSize',
            'lease.reductionRemainingAreaSize',
            'lease.freeRentPeriods.beginIn',
            'lease.freeRentPeriods.duration',
            'lease.freeRentPeriods.percentFree',
            'lease.freeRentPeriods.amount',
            'lease.freeRentPeriods.length',
            'lease.rentBumps.length',
            'lease.rentBumps.begin',
            'lease.rentBumps.duration',
            'lease.rentBumps.rentPerSqFoot',
            'lease.expansion.rentBumps.length',
            'lease.expansion.rentBumps.begin',
            'lease.expansion.rentBumps.duration',
            'lease.expansion.rentBumps.rentPerSqFoot',
            'lease.reduction.rentBumps.length',
            'lease.reduction.rentBumps.begin',
            'lease.reduction.rentBumps.duration',
            'lease.reduction.rentBumps.rentPerSqFoot',
            'lease.relocation.rentBumps.length',
            'lease.relocation.rentBumps.begin',
            'lease.relocation.rentBumps.duration',
            'lease.relocation.rentBumps.rentPerSqFoot',
            'lease.rentTableType',
            'lease.terms.hasFreeRentAddedToTerm',
            'lease.terms.freeRentPeriods.softEndDate',
            'lease.terms.freeRentPeriods.softStartDate',
            'lease.terms.freeRentPeriods.hardEndDate',
            'lease.terms.freeRentPeriods.hardStartDate',
            'lease.terms.freeRentPeriods.isFree',
            'lease.terms.freeRentExtendedAt',
            'lease.terms.rentInputType',
            'lease.terms.hasFreeRent',
            'lease.terms.freeRent',
            'lease.terms.freeRentPeriods.beginIn',
            'lease.terms.freeRentPeriods.duration',
            'lease.terms.freeRentPeriods.percentFree',
            'lease.terms.freeRentPeriods.amount',
            'lease.terms.freeRentPeriods.length',
            'lease.terms.rentBumps.length',
            'lease.terms.rentBumps.begin',
            'lease.terms.rentBumps.duration',
            'lease.terms.rentBumps.rentPerSqFoot',
            'lease.termCommencementDate',
            'lease.hasMonthToMonth',
            'lease.monthToMonthEnd',
          ],
          calcFunc: function() {
            $scope.$broadcast('rentValuesChanged');
            return true;
          },
        },
        'lease.renewalRows': {
          dependsOn: [
            'lease.hasRenewalOption',
            'lease.hasExtensionOfTerm',
            'lease.renewalCount',
            'lease.renewalTerm',
            'lease.renewalInfo.renewalInputType',
            'lease.renewalInfo.renewalRentPeriods.rentPerSqFoot',
            'lease.renewalInfo.renewalBaseRent',
            'lease.renewalInfo.renewalAnnualIncrease',
            'lease.renewalInfo.hasCpiRent',
            'lease.renewalInfo.cpiAnchorYears',
            'renewalValues',
            'lease.existingLeaseExpirationDate',
            'lease.hasFreeRentAddedToTerm',
            'lease.freeRentPeriods.softEndDate',
            'lease.freeRentPeriods.softStartDate',
            'lease.freeRentPeriods.hardEndDate',
            'lease.freeRentPeriods.hardStartDate',
            'lease.freeRentPeriods.isFree',
            'lease.freeRentExtendedAt',
            'lease.abatementDates.date',
            'lease.hasRentAbatement',
            'lease.abatementDates.length',
            'lease.leaseYears',
            'lease.leaseMonths',
            'rentValues',
            'lease.rentInputType',
            'lease.hasFreeRent',
            'lease.freeRent',
            'lease.freeRentExtendedAt',
            'lease.premiseAreaSize',
            'lease.freeRentPeriods.beginIn',
            'lease.freeRentPeriods.duration',
            'lease.freeRentPeriods.percentFree',
            'lease.freeRentPeriods.amount',
            'lease.freeRentPeriods.length',
            'lease.renewalRentBumps',
            'lease.renewalRentBumps',
            'lease.renewalRentBumps.periods.length',
            'lease.renewalRentBumps.periods.begin',
            'lease.renewalRentBumps.periods.duration',
            'lease.renewalRentBumps.periods.rentPerSqFoot',
            'lease.terms.hasFreeRentAddedToTerm',
            'lease.terms.freeRentPeriods.softEndDate',
            'lease.terms.freeRentPeriods.softStartDate',
            'lease.terms.freeRentPeriods.hardEndDate',
            'lease.terms.freeRentPeriods.hardStartDate',
            'lease.terms.freeRentPeriods.isFree',
            'lease.terms.freeRentExtendedAt',
            'lease.terms.freeRentPeriods.beginIn',
            'lease.terms.freeRentPeriods.duration',
            'lease.terms.freeRentPeriods.percentFree',
            'lease.terms.freeRentPeriods.amount',
            'lease.terms.freeRentPeriods.length',
          ],
          calcFunc: function() {
            $scope.$broadcast('rentValuesChanged');
            return true;
          },
        },
        'lease.annualBaseRent': {
          dependsOn: [
            'lease.baseRent',
            'lease.rentInputType',
            'lease.rentPeriods.rentPerSqFoot',
            'lease.rentPeriods.totalRent',
            'rentValues',
            'lease.premiseAreaSize',
            'lease.terms.baseRent',
            'lease.terms.rentInputType',
            'lease.terms.rentPeriods.rentPerSqFoot',
            'lease.terms.rentPeriods.totalRent',
            'lease.rentBumps.length',
            'lease.rentBumps.begin',
            'lease.rentBumps.duration',
            'lease.rentBumps.rentPerSqFoot',
            'lease.terms.rentBumps.length',
            'lease.terms.rentBumps.begin',
            'lease.terms.rentBumps.duration',
            'lease.terms.rentBumps.rentPerSqFoot',
          ],
          calcFunc: function() {
            if ($scope.isMultipleTerms) {
              for (
                var termIndex = 0;
                termIndex < $scope.lease.terms.length;
                termIndex++
              ) {
                calcAnnualBaseRentPerTerm($scope.lease.terms[termIndex]);
              }
            } else {
              calcAnnualBaseRentPerTerm($scope.lease);
            }
          },
        },
        'lease.monthlyBaseRent': {
          dependsOn: [
            'lease.baseRent',
            'lease.rentInputType',
            'lease.rentPeriods.rentPerSqFoot',
            'lease.rentPeriods.totalRent',
            'rentValues',
            'lease.premiseAreaSize',
            'lease.terms.baseRent',
            'lease.terms.rentInputType',
            'lease.terms.rentPeriods.rentPerSqFoot',
            'lease.terms.rentPeriods.totalRent',
          ],
          calcFunc: function() {
            if ($scope.isMultipleTerms) {
              for (
                var termIndex = 0;
                termIndex < $scope.lease.terms.length;
                termIndex++
              ) {
                calcMonthlyBaseRentPerTerm($scope.lease.terms[termIndex]);
              }
            } else {
              calcMonthlyBaseRentPerTerm($scope.lease);
            }
          },
        },
        'lease.hasBroker': {
          dependsOn: ['lease.hasLandlordBroker'],
          calcFunc: function() {
            var broker = $scope.lease.hasLandlordBroker;

            if ($scope.lease.tenantInfo) {
              broker = $scope.lease.tenantInfo.hasBroker;
            }

            $scope.lease.hasBroker = broker;
          },
        },
        'lease.rentPeriods': {
          dependsOn: [
            'lease.rentPeriods.totalRent',
            'lease.terms.rentPeriods.totalRent',
          ],
          calcFunc: function() {
            if ($scope.isMultipleTerms) {
              for (
                var termIndex = 0;
                termIndex < $scope.lease.terms.length;
                termIndex++
              ) {
                calcTotalRentPerTerm($scope.lease.terms[termIndex]);
              }
            } else {
              calcTotalRentPerTerm($scope.lease);
            }
          },
        },
        'lease.landlords': {
          dependsOn: [],
          calcFunc: function() {
            $scope.lease.landlords = [];

            if ($scope.building && $scope.building.hasMultipleLandlords) {
              var landlordNames = $scope.building.landlordName.split(';');
              var landlordStateOfFormations = $scope.building.landlordStateOfFormation.split(
                ';',
              );
              var landlordEntityTypes = $scope.building.landlordEntityType.split(
                ';',
              );

              for (var i = 0; i < landlordNames.length; i++) {
                $scope.lease.landlords.push({
                  name: landlordNames[i],
                  stateOfFormation: landlordStateOfFormations[i],
                  entityType: landlordEntityTypes[i],
                });
              }
            }
          },
        },
        'lease.premiseAreaSize': {
          dependsOn: [
            'lease.premiseSuites.areaSize',
            'lease.premiseSuites.length',
          ],
          calcFunc: function() {
            if ($rootScope.hasEditorConfig("multiplePremiseSuites")) {
              let areaSize = null;

              for (var i = 0; i < $scope.lease.premiseSuites.length; i++) {
                let suiteAreaSize = parseFloat($scope.lease.premiseSuites[i].areaSize);

                if (!isNaN(suiteAreaSize)) {
                  if (areaSize === null) {
                    areaSize = 0;
                  }
                  areaSize += suiteAreaSize;
                }
              }

              $scope.lease.premiseAreaSize = areaSize;
            }

            $scope.calculateRentPSF($scope.lease);
            $scope.calculateRenewalPSF($scope.lease);
            $scope.consolidateRentPeriods();
            $scope.consolidateRenewalInfo();
          },
        },
        'lease.terms.premiseAreaSize': {
          dependsOn: ['lease.terms.spaces', 'lease.terms.spaces.length', 'lease.premiseSuites.areaSize'],
          calcFunc: function() {
            for (
              var termIndex = 0;
              termIndex < $scope.lease.terms.length;
              termIndex++
            ) {
              var areaSize = null;
              var term = $scope.lease.terms[termIndex];
              var spaces = term.spaces || [];

              for (var i = 0; i < spaces.length; i++) {
                var last = _.findLast($scope.lease.premiseSuites, function(x) {
                  return x.number == spaces[i].number;
                });
                var currAreaSize = 1 * (last ? last.areaSize : null);

                if (!isNaN(currAreaSize)) {
                  if (areaSize === null) {
                    areaSize = 0;
                  }

                  areaSize += currAreaSize;
                  term.spaces[i].areaSize = currAreaSize;
                }
              }

              if ($rootScope.hasEditorConfig("multiplePremiseSuites")) {
                term.premiseAreaSize = areaSize;
              }

              let rentBumpsInMonths = [];
              let rentBump = null;

              if (term.rentBumps) {
                for (let rentBumpIndex = 0; rentBumpIndex < term.rentBumps.length; rentBumpIndex++) {
                  rentBump = term.rentBumps[rentBumpIndex];
                  
                  for (let month = 0; month < rentBump.duration; month++) {
                    rentBumpsInMonths.push(rentBump.rentPerSqFoot);
                  }
                }
  
                if (term.freeRentPeriods) {
                  for (let i = 0; i < term.freeRentPeriods.length; i++) {
                    if (term.freeRentPeriods[i].percentFree) {
                      term.freeRentPeriods[i].amount = term.premiseAreaSize * rentBumpsInMonths[term.freeRentPeriods[i].beginIn] * (term.freeRentPeriods[i].percentFree / 100 / 12);
                    }
                  }
                }
              }
            }

            $scope.calculateRentPSF($scope.lease);
            $scope.calculateRenewalPSF($scope.lease);
            $scope.consolidateRentPeriods();
            $scope.consolidateRenewalInfo();
          },
        },
        'lease.terms.freeRentPeriods.amount': {
          dependsOn: [
            'lease.premiseSuites.areaSize',
            'lease.premiseSuites.length',
            'lease.freeRentPeriods.beginIn', 
            'lease.freeRentPeriods.percentFree', 
            'lease.freeRentPeriods.length', 
            'lease.rentBumps.length',
            'lease.rentBumps.duration',
            'lease.rentBumps.rentPerSqFoot',
            'lease.terms.spaces', 
            'lease.terms.spaces.length', 
            'lease.terms.freeRentPeriods.beginIn', 
            'lease.terms.freeRentPeriods.percentFree', 
            'lease.terms.freeRentPeriods.length', 
            'lease.terms.rentBumps.length',
            'lease.terms.rentBumps.duration',
            'lease.terms.rentBumps.rentPerSqFoot',
          ],
          calcFunc: function() {
            let terms;

            if ($scope.lease.terms.length > 0) {
              terms = $scope.lease.terms;
            } else {
              terms = [$scope.lease];
            }

            for (
              var termIndex = 0;
              termIndex < terms.length;
              termIndex++
            ) {
              let term = terms[termIndex];
              let rentBumpsInMonths = [];
              let rentBump = null;

              if (term.rentBumps) {
                for (let rentBumpIndex = 0; rentBumpIndex < term.rentBumps.length; rentBumpIndex++) {
                  rentBump = term.rentBumps[rentBumpIndex];
                  
                  for (let month = 0; month < rentBump.duration; month++) {
                    rentBumpsInMonths.push(rentBump.rentPerSqFoot);
                  }
                }

                if (term.freeRentPeriods) {
                  for (let i = 0; i < term.freeRentPeriods.length; i++) {
                    if (term.freeRentPeriods[i].percentFree) {
                      term.freeRentPeriods[i].amount = term.premiseAreaSize * rentBumpsInMonths[term.freeRentPeriods[i].beginIn] * (term.freeRentPeriods[i].percentFree / 100 / 12);
                    }
                  }
                }
              }
            }
          },
        },
        'lease.calculatedAreaSize': {
          dependsOn: [
            'lease.premiseAreaSize',
            'lease.premisesModType',
            'lease.expansionPremiseAreaSize',
            'lease.reductionRemainingAreaSize',
            'lease.relocationPremisesAreaSize',
          ],
          calcFunc: function() {
            $scope.lease.calculatedAreaSize = getAreaSize();
          },
        },
        'lease.freeRentPeriods': {
          dependsOn: ['lease.leaseYears'],
          calcFunc: function() {
            if (!$scope.isMultipleFreeRent) {
              return;
            }
            if (!$scope.lease.freeRentPeriods) {
              $scope.lease.freeRentPeriods = [];
            }

            if ($scope.lease.freeRentPeriods.length < $scope.lease.leaseYears) {
              for (
                var i = $scope.lease.freeRentPeriods.length;
                i < $scope.lease.leaseYears;
                i++
              ) {
                $scope.lease.freeRentPeriods.push({});
              }
            } else {
              var count =
                $scope.lease.freeRentPeriods.length - $scope.lease.leaseYears;
              $scope.lease.freeRentPeriods.splice(-count, count);
            }
          },
        },
        'lease.multipleFreeRent.length': {
          dependsOn: [
            'lease.baseRent',
            'lease.leaseYears',
            'lease.leaseMonths',
            'lease.hasFreeRent',
            'lease.rentBumps.length',
            'lease.rentBumps.duration',
            'lease.rentBumps.rentPerSqFoot',
            'lease.freeRentPeriods.isFree',
            'lease.freeRentPeriods.hardStartDate',
            'lease.freeRentPeriods.hardEndDate',
            'lease.freeRentPeriods.softStartDate',
            'lease.freeRentPeriods.softEndDate',
            'rentValues',
            'lease.tenantImprovements.type',
          ],
          calcFunc: function() {
            let freeRent = [];
            let period;

            if ($scope.lease.hasFreeRent) {
              if ($scope.lease.freeRentPeriods) {
                if ($scope.lease.rentInputType === 'flexible') {
                  let totalTermLength = getTermLength();
                  let freeRentPeriodsInMonths = []
                  let rentBumpsInMonths = new Array(totalTermLength);
                  let areaSize = getAreaSize();

                  rentBumpsInMonths.fill(0);
                  for (let rentBumpIndex = 0; rentBumpIndex < $scope.lease.rentBumps.length; rentBumpIndex++) {
                    for (let rentBumpMonthIndex = $scope.lease.rentBumps[rentBumpIndex].begin - 1; rentBumpMonthIndex <= $scope.lease.rentBumps[rentBumpIndex].begin - 1 + $scope.lease.rentBumps[rentBumpIndex].duration - 1; rentBumpMonthIndex++) {
                      let amount = $scope.lease.rentBumps[rentBumpIndex].rentPerSqFoot * areaSize / 12;
                      rentBumpsInMonths[rentBumpMonthIndex] = amount;
                    }
                  }

                  for (let freeRentIndex = 0; freeRentIndex < $scope.lease.freeRentPeriods.length; freeRentIndex++) {
                    for (let freeRentMonthIndex = 0; freeRentMonthIndex < 12; freeRentMonthIndex++) {
                      if (freeRentMonthIndex >= $scope.lease.freeRentPeriods[freeRentIndex].softStartDate - 1 && freeRentMonthIndex <= $scope.lease.freeRentPeriods[freeRentIndex].softEndDate - 1) {
                        freeRentPeriodsInMonths.push($scope.lease.freeRentPeriods[freeRentIndex].isFree);
                      } else {
                        freeRentPeriodsInMonths.push(false);
                      }
                    }
                  }

                  for (let i = 0; i < $scope.lease.freeRentPeriods.length; i++) {
                    let freeRentAmount = 0;

                    if (
                      $scope.lease.hasHardCommencementDate &&
                      $scope.lease.freeRentPeriods[i].hardEndDate &&
                      $scope.lease.freeRentPeriods[i].hardStartDate
                    ) {
                      period =
                        moment($scope.lease.freeRentPeriods[i].hardEndDate).diff(
                          $scope.lease.freeRentPeriods[i].hardStartDate,
                          'months',
                        ) + 1;
                    } else {
                      period =
                        $scope.lease.freeRentPeriods[i].softEndDate -
                        $scope.lease.freeRentPeriods[i].softStartDate +
                        1;
                    }

                    for (let j = 0; j < 12; j++) {
                      let index = i * 12 + j;

                      if (freeRentPeriodsInMonths[index]) {
                        freeRentAmount += rentBumpsInMonths[index];
                      }
                    }

                    if (freeRentAmount) {
                      freeRent.push({
                        period: period,
                        hardStartDate:
                          $scope.lease.freeRentPeriods[i].hardStartDate,
                        hardEndDate: $scope.lease.freeRentPeriods[i].hardEndDate,
                        softStartDate:
                          $scope.lease.freeRentPeriods[i].softStartDate,
                        softEndDate: $scope.lease.freeRentPeriods[i].softEndDate,
                        freeRentYear: i + 1,
                        freeRentAmount: freeRentAmount,
                      });
                    }
                  }
                } else {
                  for (let i = 0; i < $scope.lease.freeRentPeriods.length; i++) {
                    if ($scope.lease.freeRentPeriods[i].isFree) {
                      if (
                        $scope.lease.hasHardCommencementDate &&
                        $scope.lease.freeRentPeriods[i].hardEndDate &&
                        $scope.lease.freeRentPeriods[i].hardStartDate
                      ) {
                        period =
                          moment($scope.lease.freeRentPeriods[i].hardEndDate).diff(
                            $scope.lease.freeRentPeriods[i].hardStartDate,
                            'months',
                          ) + 1;
                      } else {
                        period =
                          $scope.lease.freeRentPeriods[i].softEndDate -
                          $scope.lease.freeRentPeriods[i].softStartDate +
                          1;
                      }

                      freeRent.push({
                        period: period,
                        hardStartDate:
                          $scope.lease.freeRentPeriods[i].hardStartDate,
                        hardEndDate: $scope.lease.freeRentPeriods[i].hardEndDate,
                        softStartDate:
                          $scope.lease.freeRentPeriods[i].softStartDate,
                        softEndDate: $scope.lease.freeRentPeriods[i].softEndDate,
                        freeRentYear: i + 1,
                        freeRentAmount:
                          period * ($scope.lease.rentPeriods[i].totalRent / 12),
                      });
                    }
                  }
                }
              }
            }

            $scope.lease.multipleFreeRent = freeRent;
          },
        },
        reductionValues: {
          //used only for highlighting oxford-office-lease rent-table
          dependsOn: [
            'lease.reductionPeriods.year',
            'lease.reductionPeriods.amountReduced',
          ],
          calcFunc: function() {
            return true;
          },
        },
        'lease.reductionPeriods.length': {
          dependsOn: ['lease.reductionPeriodsNumber', 'reductionValues'],
          calcFunc: function() {
            return true;
          },
        },
        'lease.assigneeInfo.entityTypeCombined': {
          dependsOn: [
            'lease.assigneeInfo.entityType',
            'lease.assigneeInfo.entityTypeOther',
          ],
          calcFunc: function() {
            if ($scope.lease.assigneeInfo) {
              if ($scope.lease.assigneeInfo.entityType === 'Other') {
                $scope.lease.assigneeInfo.entityTypeCombined =
                  $scope.lease.assigneeInfo.entityTypeOther;
              } else {
                $scope.lease.assigneeInfo.entityTypeCombined =
                  $scope.lease.assigneeInfo.entityType;
              }
            }
          },
        },
        'lease.tenantInfo.postLeaseAddress': {
          dependsOn: [
            'lease.hasPostAddressSameAsPre',
            'lease.tenantInfo.preLeaseAddress',
          ],
          calcFunc: function() {
            if ($scope.lease.tenantInfo) {
              if ($scope.lease.hasPostAddressSameAsPre) {
                $scope.lease.tenantInfo.postLeaseAddress =
                  $scope.lease.tenantInfo.preLeaseAddress;
              }
            }
          },
        },
        'lease.tenantInfo.postLeaseCity': {
          dependsOn: [
            'lease.hasPostAddressSameAsPre',
            'lease.tenantInfo.preLeaseCity',
          ],
          calcFunc: function() {
            if ($scope.lease.tenantInfo) {
              if ($scope.lease.hasPostAddressSameAsPre) {
                $scope.lease.tenantInfo.postLeaseCity =
                  $scope.lease.tenantInfo.preLeaseCity;
              }
            }
          },
        },
        'lease.tenantInfo.postLeaseState': {
          dependsOn: [
            'lease.hasPostAddressSameAsPre',
            'lease.tenantInfo.preLeaseState',
          ],
          calcFunc: function() {
            if ($scope.lease.tenantInfo) {
              if ($scope.lease.hasPostAddressSameAsPre) {
                $scope.lease.tenantInfo.postLeaseState =
                  $scope.lease.tenantInfo.preLeaseState;
              }
            }
          },
        },
        'lease.tenantInfo.postLeaseZipCode': {
          dependsOn: [
            'lease.hasPostAddressSameAsPre',
            'lease.tenantInfo.preLeaseZipCode',
          ],
          calcFunc: function() {
            if ($scope.lease.tenantInfo) {
              if ($scope.lease.hasPostAddressSameAsPre) {
                $scope.lease.tenantInfo.postLeaseZipCode =
                  $scope.lease.tenantInfo.preLeaseZipCode;
              }
            }
          },
        },
        'lease.tenantInfo.postLeasePhoneNumber': {
          dependsOn: [
            'lease.hasPostAddressSameAsPre',
            'lease.tenantInfo.preLeasePhoneNumber',
          ],
          calcFunc: function() {
            if ($scope.lease.tenantInfo) {
              if ($scope.lease.hasPostAddressSameAsPre) {
                $scope.lease.tenantInfo.postLeasePhoneNumber =
                  $scope.lease.tenantInfo.preLeasePhoneNumber;
              }
            }
          },
        },
        'lease.tenantInfo.postLeaseEmail': {
          dependsOn: [
            'lease.hasPostAddressSameAsPre',
            'lease.tenantInfo.preLeaseEmail',
          ],
          calcFunc: function() {
            if ($scope.lease.tenantInfo) {
              if ($scope.lease.hasPostAddressSameAsPre) {
                $scope.lease.tenantInfo.postLeaseEmail =
                  $scope.lease.tenantInfo.preLeaseEmail;
              }
            }
          },
        },
        'lease.tenantInfo.postLeaseAttention': {
          dependsOn: [
            'lease.hasPostAddressSameAsPre',
            'lease.tenantInfo.preLeaseAttention',
          ],
          calcFunc: function() {
            if ($scope.lease.tenantInfo) {
              if ($scope.lease.hasPostAddressSameAsPre) {
                $scope.lease.tenantInfo.postLeaseAttention =
                  $scope.lease.tenantInfo.preLeaseAttention;
              }
            }
          },
        },
        'lease.remainingMonthlyRent': {
          dependsOn: [
            'rentValues',
            'lease.baseRent',
            'lease.annualIncrease',
            'lease.freeRentPeriods.beginIn',
            'lease.freeRentPeriods.duration',
            'lease.freeRentPeriods.amount',
            'lease.rentBumps.begin',
            'lease.rentBumps.duration',
            'lease.rentBumps.rentPerSqFoot',
            'lease.expansion.rentBumps.begin',
            'lease.expansion.rentBumps.duration',
            'lease.expansion.rentBumps.rentPerSqFoot',
            'lease.relocation.rentBumps.begin',
            'lease.relocation.rentBumps.duration',
            'lease.relocation.rentBumps.rentPerSqFoot',
            'lease.reduction.rentBumps.begin',
            'lease.reduction.rentBumps.duration',
            'lease.reduction.rentBumps.rentPerSqFoot',
            'lease.rentTableType',
            'lease.hasExtensionOfTerm',
          ],
          calcFunc: function() {
            calcRemainingMonthlyRentPerTerm($scope.lease);
          },
        },
        'lease.terms.remainingMonthlyRent': {
          dependsOn: [
            'rentValues',
            'lease.baseRent',
            'lease.annualIncrease',
            'lease.terms.freeRentPeriods.beginIn',
            'lease.terms.freeRentPeriods.duration',
            'lease.terms.freeRentPeriods.amount',
            'lease.terms.rentBumps.begin',
            'lease.terms.rentBumps.duration',
            'lease.terms.rentBumps.rentPerSqFoot',
          ],
          calcFunc: function() {
            if ($scope.lease.terms) {
              for (
                var termIndex = 0;
                termIndex < $scope.lease.terms.length;
                termIndex++
              ) {
                calcRemainingMonthlyRentPerTerm($scope.lease.terms[termIndex]);
              }
            }
          },
        },
        'lease.payableMonthlyRent': {
          dependsOn: [
            'rentValues',
            'lease.baseRent',
            'lease.annualIncrease',
            'lease.rentPeriods.rentPerSqFoot',
            'lease.rentPeriods.length',
            'lease.freeRentPeriods.beginIn',
            'lease.freeRentPeriods.duration',
            'lease.freeRentPeriods.amount',
            'lease.freeRentPeriods.length',
            'lease.rentBumps.begin',
            'lease.rentBumps.duration',
            'lease.rentBumps.rentPerSqFoot',
            'lease.rentBumps.length',
            'lease.expansion.rentBumps.begin',
            'lease.expansion.rentBumps.duration',
            'lease.expansion.rentBumps.rentPerSqFoot',
            'lease.expansion.rentBumps.length',
            'lease.relocation.rentBumps.begin',
            'lease.relocation.rentBumps.duration',
            'lease.relocation.rentBumps.rentPerSqFoot',
            'lease.relocation.rentBumps.length',
            'lease.reduction.rentBumps.begin',
            'lease.reduction.rentBumps.duration',
            'lease.reduction.rentBumps.rentPerSqFoot',
            'lease.reduction.rentBumps.length',
            'lease.rentTableType',
            'lease.hasExtensionOfTerm',
          ],
          calcFunc: function() {
            calcRemainingMonthlyRentPerTerm($scope.lease);
          },
        },
        'lease.terms.payableMonthlyRent': {
          dependsOn: [
            'rentValues',
            'lease.baseRent',
            'lease.annualIncrease',
            'lease.rentPeriods.length',
            'lease.rentPeriods.rentPerSqFoot',
            'lease.terms.freeRentPeriods.beginIn',
            'lease.terms.freeRentPeriods.duration',
            'lease.terms.freeRentPeriods.amount',
            'lease.terms.freeRentPeriods.length',
            'lease.terms.rentBumps.begin',
            'lease.terms.rentBumps.duration',
            'lease.terms.rentBumps.rentPerSqFoot',
            'lease.terms.rentBumps.length',
          ],
          calcFunc: function() {
            if ($scope.lease.terms) {
              for (
                var termIndex = 0;
                termIndex < $scope.lease.terms.length;
                termIndex++
              ) {
                calcRemainingMonthlyRentPerTerm($scope.lease.terms[termIndex]);
              }
            }
          },
        },
        'lease.totalAbatedBaseRent': {
          dependsOn: [
            'lease.freeRentPeriods.isFree',
            'lease.freeRentPeriods.hardStartDate',
            'lease.freeRentPeriods.hardEndDate',
            'lease.freeRentPeriods.softStartDate',
            'lease.freeRentPeriods.softEndDate',
            'lease.freeRentPeriods.beginIn',
            'lease.freeRentPeriods.duration',
            'lease.freeRentPeriods.percentFree',
            'lease.freeRentPeriods.amount',
            'lease.freeRentPeriods.length',
            'lease.terms.freeRentPeriods.isFree',
            'lease.terms.freeRentPeriods.hardStartDate',
            'lease.terms.freeRentPeriods.hardEndDate',
            'lease.terms.freeRentPeriods.softStartDate',
            'lease.terms.freeRentPeriods.softEndDate',
            'lease.terms.freeRentPeriods.beginIn',
            'lease.terms.freeRentPeriods.duration',
            'lease.terms.freeRentPeriods.percentFree',
            'lease.terms.freeRentPeriods.amount',
            'lease.terms.freeRentPeriods.length',
            'lease.rentInputType',
            'lease.rentPeriods.rentPerSqFoot',
            'lease.rentBumps.rentPerSqFoot',
            'lease.expansion.rentBumps.rentPerSqFoot',
            'lease.reduction.rentBumps.rentPerSqFoot',
            'lease.relocation.rentBumps.rentPerSqFoot',
            'lease.rentPeriods.totalRent',
            'lease.premisesModType',
            'lease.premiseAreaSize',
            'lease.rentTableType',
            'lease.hasExtensionOfTerm',
            'lease.expansionPremiseAreaSize',
            'lease.reductionRemainingAreaSize',
            'lease.relocationPremisesAreaSize',
            'rentValues',
          ],
          calcFunc: function() {
            let totalAbatedBaseRent = 0;
            var rentPeriodsInMonths = getRentPeriodsInMonths();

            var freeRentPeriods = $scope.isMultipleTerms
              ? _.filter(_.flatMap($scope.lease.terms, 'freeRentPeriods'), null)
              : $scope.lease.freeRentPeriods;
            for (let i = 0; i < freeRentPeriods.length; i++) {
              let currAbatement = 0;

              if (freeRentPeriods[i].amount !== undefined) {
                currAbatement = freeRentPeriods[i].amount * freeRentPeriods[i].duration;
              } else if (
                freeRentPeriods[i].beginIn !== undefined &&
                freeRentPeriods[i].duration !== undefined &&
                freeRentPeriods[i].amount === undefined)
              {
                let endIn = freeRentPeriods[i].beginIn + freeRentPeriods[i].duration;
                let percentFree = freeRentPeriods[i].percentFree ? freeRentPeriods[i].percentFree / 100 : 1;
                const areaSize = getFreeRentAreaSize();
                for (var j = freeRentPeriods[i].beginIn; j < endIn; j++) {
                  if (rentPeriodsInMonths[j - 1]) {
                    if (rentPeriodsInMonths[j - 1].rentPerSqFoot) {
                      var abatement = ((rentPeriodsInMonths[j - 1].rentPerSqFoot * areaSize) / 12) * percentFree;
                      totalAbatedBaseRent += abatement;
                    } else if (rentPeriodsInMonths[j - 1].totalRent) {
                      var abatement = rentPeriodsInMonths[j - 1].totalRent * percentFree;
                      totalAbatedBaseRent += abatement;
                    }
                  }
                }

              } else if (freeRentPeriods[i].percentFree === undefined && freeRentPeriods[i].amount === undefined) {
                if (freeRentPeriods[i].isFree) {
                  let duration = freeRentPeriods[i].softEndDate - freeRentPeriods[i].softStartDate + 1;
                  let amount = $scope.lease.rentPeriods[i].totalRent;
                  currAbatement = amount * (duration / 12);
                }
              }
              totalAbatedBaseRent += currAbatement;
            }

            $scope.lease.totalAbatedBaseRent = totalAbatedBaseRent;
          },
        },
        'lease.renewalRentFloor': {
          dependsOn: [
            'lease.rentBumps.length',
            'lease.rentBumps.rentPerSqFoot',
            'lease.terms.rentBumps.length',
            'lease.terms.rentBumps.rentPerSqFoot',
          ],
          calcFunc: function() {
            if ($scope.isMultipleTerms) {
              if ($scope.terms && $scope.terms.length) {
                var lastTerm =
                  _.findLast($scope.lease.terms, function(t) {
                    return t.rentInputType == 'flexible';
                  }) || {};
                $scope.lease.renewalRentFloor = lastTerm.rentPerSqFoot;
              }
            } else if ($scope.lease.rentBumps) {
              if ($scope.lease.rentBumps.length > 0) {
                $scope.lease.renewalRentFloor =
                  $scope.lease.rentBumps[
                    $scope.lease.rentBumps.length - 1
                  ].rentPerSqFoot;
              }
            }
          },
        },
        'lease.calc.hasRenewalRentFMV': {
          dependsOn: [
            'lease.renewalInfo.length',
            'lease.renewalInfo.renewalOptionRent'
          ],
          calcFunc: function() {
            $scope.lease.calc.hasRenewalRentFMV = false;

            if ($rootScope.hasEditorConfig("renewalRent", "rentOptionTypePerRenewalTerm")) {
              $scope.lease.calc.hasRenewalRentFMV = $scope.lease.renewalInfo.filter(renewalInfo => renewalInfo.renewalOptionRent === "fmv").length > 0
            }
          }
        },
        'lease.calc.rentBumpsAverages': {
          dependsOn: [
            'lease.premiseAreaSize',
            'lease.leaseYears',
            'lease.leaseMonths',
            'lease.rentBumps.length',
            'lease.rentBumps.duration',
            'lease.rentBumps.rentPerSqFoot',
            'lease.hasPercentageRent',
            'lease.percentageRent',
          ],
          calcFunc: function() {
            const result = [];

            if ($scope.lease.hasPercentageRent) {
              let rentBumpsInMonths = [];
              let rentBumpsInYears = [];
              let rentBump;
  
              for (let rentBumpIndex = 0; rentBumpIndex < $scope.lease.rentBumps.length; rentBumpIndex++) {
                rentBump = $scope.lease.rentBumps[rentBumpIndex];
                
                for (let month = 0; month < rentBump.duration; month++) {
                  rentBumpsInMonths.push(rentBump.rentPerSqFoot);
                }
              }
  
              let year = 0;
              while (rentBumpsInMonths.length > 0) {
                rentBumpsInYears[year++] = rentBumpsInMonths.slice(0, 12);
                rentBumpsInMonths = rentBumpsInMonths.slice(12);
              }
  
              let average = 0;
              for (let yearIndex = 0; yearIndex < rentBumpsInYears.length; yearIndex++) {
                average = rentBumpsInYears[yearIndex].reduce((a,b) => a+b) / rentBumpsInYears[yearIndex].length;
                result.push({
                  averagePSF: average,
                });
              }
            }

            $scope.lease.calc.rentBumpsAverages = result;
          },
        },
        'lease.calc.renewalRentBumpsAverages': {
          dependsOn: [
            'lease.premiseAreaSize',
            'lease.renewalCount',
            'lease.renewalTerm',
            'lease.renewalInfo.renewalInputType',
            'lease.renewalRentBumps.length',
            'lease.renewalRentBumps.periods.length',
            'lease.renewalRentBumps.periods.duration',
            'lease.renewalRentBumps.periods.rentPerSqFoot',
            'lease.hasPercentageRent',
            'lease.percentageRent',
          ],
          calcFunc: function() {
            const result = [];

            if ($scope.lease.hasPercentageRent) {
              for (let renewalRentBumpIndex = 0; renewalRentBumpIndex < $scope.lease.renewalRentBumps.length; renewalRentBumpIndex++) {
                let renewalResult = [];
                let renewalRentBumpsInMonths = [];
                let renewalRentBumpsInYears = [];
                let renewalRentBump;

                for (let renewalRentBumpPeriod = 0; renewalRentBumpPeriod < $scope.lease.renewalRentBumps[renewalRentBumpIndex].periods.length; renewalRentBumpPeriod++) {
                  renewalRentBump = $scope.lease.renewalRentBumps[renewalRentBumpIndex].periods[renewalRentBumpPeriod];
                  
                  for (let month = 0; month < renewalRentBump.duration; month++) {
                    renewalRentBumpsInMonths.push(renewalRentBump.rentPerSqFoot);
                  }
                }

                let year = 0;
                while (renewalRentBumpsInMonths.length > 0) {
                  renewalRentBumpsInYears[year++] = renewalRentBumpsInMonths.slice(0, 12);;
                  renewalRentBumpsInMonths = renewalRentBumpsInMonths.slice(12);
                }
    
                let average = 0;
                for (let yearIndex = 0; yearIndex < renewalRentBumpsInYears.length; yearIndex++) {
                  average = renewalRentBumpsInYears[yearIndex].reduce((a,b) => a+b) / renewalRentBumpsInYears[yearIndex].length;
                  renewalResult.push({
                    averagePSF: average,
                  });
                }

                result.push({
                  periods: renewalResult,
                });
              }
            }

            $scope.lease.calc.renewalRentBumpsAverages = result;
          },
        },
        'lease.calc.totalRenewalInfo': {
          dependsOn: [
            'lease.renewalRows',
          ],
          calcFunc: function() {
            const result = [];

            if ($scope.lease.hasRenewalOption) {
              let current = null;

              for (let i = 0; i < $scope.lease.renewalInfo.length; i++) {
                for (let j = 0; j < $scope.lease.renewalInfo[i].renewalRentPeriods.length; j++) {
                  current = {
                    ...$scope.lease.renewalInfo[i],
                    ...$scope.lease.renewalInfo[i].renewalRentPeriods[j],
                  };

                  delete current.renewalRentPeriods;

                  result.push(current)
                }
              }
            }

            $scope.lease.calc.totalRenewalInfo = result;
          },
        },
        'lease.reducedSDAmounts': {
          dependsOn: [
            'reductionValues',
            'lease.leaseYears',
            'lease.reductionPeriodsNumber',
            'lease.listOfLeaseYears',
            'lease.hasSecurityDeposit',
            'lease.hasSecurityDepositBurndown',
            'lease.reductionPeriods.year',
            'lease.reductionPeriods.amountReduced',
            'lease.securityDeposit',
          ],
          calcFunc: function() {
            const lease = $scope.lease;
            const rentReductions = [];

            if (lease.reductionPeriods) {
              lease.reductionPeriods.forEach(period => {
                if (period.year) {
                  rentReductions[period.year] = parseFloat(period.amountReduced);
                }
              });
            }
            
            lease.rentPeriods.forEach((period, index) => {
              period.reducedSDAmount = null;

              if (lease.hasSecurityDeposit && lease.securityDeposit) {
                if (index === 0) {
                  period.reducedSDAmount = parseFloat(lease.securityDeposit);
                } else {
                  if (rentReductions[index]) {
                    period.reducedSDAmount = rentReductions[index];
                  } else {
                    period.reducedSDAmount = lease.rentPeriods[index-1].reducedSDAmount;
                  }
                }
              }
            });

            $scope.lease.reducedSDAmounts = lease.rentPeriods;
          }
        },
        totalRentBumpsInMonths: {
          dependsOn: [
            'lease.leaseYears',
            'lease.leaseMonths',
            'lease.rentBumps.length',
            'lease.rentBumps.duration',
            'rentValues',
          ],
          calcFunc: function() {
            if ($scope.lease.rentBumps) {
              $scope.totalRentBumpsInMonths = $scope.getRentBumpsInMonths(
                $scope.lease.rentBumps,
              );
            }
          },
        },
        totalRenewalRentBumpsInMonths: {
          dependsOn: [
            'lease.renewalCount',
            'lease.renewalTerm',
            'lease.renewalInfo.renewalInputType',
            'lease.renewalRentBumps.length',
            'lease.renewalRentBumps.periods.length',
            'lease.renewalRentBumps.periods.duration',
            'rentValues',
          ],
          calcFunc: function() {
            let totalRenewalRentBumpsInMonths = [];
            if ($scope.lease.renewalRentBumps) {
              for (let i = 0; i < $scope.lease.renewalRentBumps.length; i++) {
                totalRenewalRentBumpsInMonths.push(
                  $scope.getRentBumpsInMonths(
                    $scope.lease.renewalRentBumps[i].periods,
                  ),
                );
              }
            }

            $scope.totalRenewalRentBumpsInMonths = totalRenewalRentBumpsInMonths;
          },
        },
        'terms.totalRentBumpsInMonths': {
          dependsOn: [
            'lease.leaseYears',
            'lease.leaseMonths',
            'lease.terms.rentBumps.length',
            'lease.terms.rentBumps.duration',
            'rentValues',
          ],
          calcFunc: function() {
            for (let i = 0; i < $scope.lease.terms.length; i++) {
              var term = $scope.lease.terms[i];
              if (term.rentBumps) {
                term.totalRentBumpsInMonths = $scope.getRentBumpsInMonths(
                  term.rentBumps,
                );
              }
            }
          },
        },
        'lease.numberOfMustTakeSpaces': {
          dependsOn: [
            'lease.terms.length',
            'lease.terms.hasMustTakeSpace',
          ],
          calcFunc: function() {
            $scope.lease.numberOfMustTakeSpaces = 0;

            if ($scope.lease.terms) {
              for (let i = 0; i < $scope.lease.terms.length; i++) {
                var term = $scope.lease.terms[i];
                if (term.hasMustTakeSpace) {
                  $scope.lease.numberOfMustTakeSpaces++;
                }
              }
            }
          },
        },
        "lease.totalMonthsFreeRent": {
          dependsOn: [
            "lease.freeRentPeriods.length",
            "lease.freeRentPeriods.softStartDate",
            "lease.freeRentPeriods.softEndDate",
            "lease.freeRentPeriods.duration",
            "lease.freeRent",
          ],
          calcFunc: function() {
            let result = 0;

            if ($rootScope.hasEditorConfig("freeRent", "flexible")) {
              if ($scope.lease.freeRentPeriods && $scope.lease.freeRentPeriods.length !== 0) {
                for (var i = 0; i < $scope.lease.freeRentPeriods.length; i++) {
                  let record = $scope.lease.freeRentPeriods[i];
                  let duration = Number(record.duration);
                  result += Number.isFinite(duration) ? duration : 0;
                }
              }
            }

            if ($rootScope.hasEditorConfig("freeRent", "multiplePeriods")) {
              if ($scope.lease.freeRentPeriods && $scope.lease.freeRentPeriods.length !== 0) {
                for (var i = 0; i < $scope.lease.freeRentPeriods.length; i++) {
                  let record = $scope.lease.freeRentPeriods[i];
                  result += record.softEndDate - record.softStartDate + 1;
                }
              }
            }

            if ($rootScope.hasEditorConfig("freeRent", "months")) {
              result = $scope.lease.freeRent;
            }

            $scope.lease.totalMonthsFreeRent = result;
          }
        },
        "lease.totalMonthsFreeRentAddedToTerm": {
          dependsOn: [
            "lease.hasFreeRentAddedToTerm",
            "lease.totalMonthsFreeRent",
          ],
          calcFunc: function() {
            let result = 0;

            if ($scope.lease.hasFreeRentAddedToTerm) {
              result = $scope.lease.totalMonthsFreeRent;
            }

            $scope.lease.totalMonthsFreeRentAddedToTerm = result;
          }
        },
        totalTermLength: {
          dependsOn: [
            'lease.leaseYears',
            'lease.leaseMonths',
            'lease.totalMonthsFreeRentAddedToTerm'
          ],
          calcFunc: function() {
            let totalTermLength = getTermLength();
            $scope.totalTermLength = totalTermLength + $scope.lease.totalMonthsFreeRentAddedToTerm;
          },
        },

        totalRenewalTermLength: {
          dependsOn: ['lease.renewalCount', 'lease.renewalTerm'],
          calcFunc: function() {
            let totalRenewalTermLength = 0;

            if ($scope.lease.renewalTerm) {
              totalRenewalTermLength = $scope.lease.renewalTerm * 12;
            }

            $scope.totalRenewalTermLength = totalRenewalTermLength;
          },
        },
        'lease.numberOfTenantAssignments': {
          dependsOn: [
            'lease.priorAssignments.length',
            'lease.priorAssignments.type',
          ],
          calcFunc: function() {
            $scope.lease.numberOfTenantAssignments = _.countBy($scope.lease.priorAssignments, 'type').tenant || 0;
          },
        },
        'lease.priorTenantAssignments': {
          dependsOn: [
            'lease.priorAssignments.length',
            'lease.priorAssignments.type',
            'lease.priorAssignments.assignor',
            'lease.priorAssignments.date',
          ],
          calcFunc: function() {
            $scope.lease.priorTenantAssignments = $scope.lease.priorAssignments.filter(assignment => !assignment.type || assignment.type === 'tenant');
          },
        },
        'lease.numberOfLandlordAssignments': {
          dependsOn: [
            'lease.priorAssignments.length',
            'lease.priorAssignments.type',
          ],
          calcFunc: function() {
            $scope.lease.numberOfLandlordAssignments = _.countBy($scope.lease.priorAssignments, 'type').landlord || 0;
          },
        },
        'lease.priorLandlordAssignments': {
          dependsOn: [
            'lease.priorAssignments.length',
            'lease.priorAssignments.type',
            'lease.priorAssignments.assignor',
            'lease.priorAssignments.date',
          ],
          calcFunc: function() {
            $scope.lease.priorLandlordAssignments = $scope.lease.priorAssignments.filter(assignment => assignment.type === 'landlord');
          },
        },
        "lease.calc.freeRentSpecificMonths": {
          dependsOn: [
            "lease.termCommencementDate",
            "lease.existingLeaseExpirationDate",
            "lease.freeRentPeriods.length",
            "lease.freeRentPeriods.duration",
            "lease.freeRentPeriods.beginIn",
          ],
          calcFunc: function() {
            let anchorDate;

            if ($scope.lease.type === 'Lease') {
              anchorDate = $scope.lease.termCommencementDate;

              // If not the first day of the month
              if (moment(anchorDate).date() > 1) {
                anchorDate = $scope.addMonths(anchorDate, 1);
              }
            } else if ($scope.lease.type === 'Amendment') {
              anchorDate = $scope.addDays($scope.lastDayOfMonth($scope.lease.existingLeaseExpirationDate), 1);
            }

            const result = [];

            for (let i = 0; i < $scope.lease.freeRentPeriods.length; i++) {
              let freeRentPeriodSpecificMonths = [];

              for (let j = 0; j < $scope.lease.freeRentPeriods[i].duration; j++) {
                let freeRentDate = null;

                if (anchorDate) {
                  freeRentDate = moment(anchorDate).add($scope.lease.freeRentPeriods[i].beginIn + j - 1, 'months');

                  freeRentPeriodSpecificMonths.push({
                    date: freeRentDate.toDate(),
                    month: freeRentDate.format('MMMM'),
                    year: parseInt(freeRentDate.format('YYYY')),
                  })
                } else {
                  freeRentPeriodSpecificMonths.push({
                    date: null,
                    month: null,
                    year: null,
                  })
                }
              }

              result.push(freeRentPeriodSpecificMonths);
            }

            $scope.lease.calc.freeRentSpecificMonths = result;
          }
        },
        "lease.calc.effectiveCommencementDate": {
          dependsOn: [
            "lease.tenantImprovements.type",
            "lease.termCommencementDate",
            "lease.estimatedCommencementDate",
            "lease.terms.termCommencementDate",
            "lease.terms.estimatedCommencementDate",
            "lease.rentCommenceAt"
          ],
          calcFunc: function() {
            if ($scope.lease.type === 'Lease') {
              var effectiveCommencementDate;
              var currentCommencementDate;
              var currTerm = null;

              if ($scope.lease.tenantImprovements && $scope.lease.tenantImprovements.type) {
                for (let i = 0; i < $scope.lease.terms.length; ++i) {
                  currTerm = $scope.lease.terms[i];
                  if ($rootScope.hasEditorConfig('commencementDate')) {
                    effectiveCommencementDate = currTerm.termCommencementDate;
                  } else {
                      switch ($scope.lease.tenantImprovements.type) {
                        case 'landlord':
                          currentCommencementDate = currTerm.estimatedCommencementDate;
                          break;
                        case 'tenant':
                          currentCommencementDate = currTerm.estimatedCommencementDate;
                          break;
                        case 'none':
                          currentCommencementDate = currTerm.termCommencementDate;
                          break;
                      }
                      if (!effectiveCommencementDate ||  moment(currentCommencementDate).isBefore(effectiveCommencementDate)) {
                        effectiveCommencementDate = currentCommencementDate;
                      }
                  }
                }
              } else if ($scope.lease.rentCommenceAt) {
                effectiveCommencementDate = $scope.lease.rentCommenceAt;
              }

              if (effectiveCommencementDate) {
                effectiveCommencementDate.setHours(12);
              }
              $scope.lease.calc.effectiveCommencementDate = effectiveCommencementDate;
              $scope.update();

              return effectiveCommencementDate;
            }
          }
        },
      },

      calc: function(field) {
        var prevValue = $parse(field)($scope);
        var updated = this.calculatedFields[field].calcFunc();
        var currentValue = $parse(field)($scope);
        if (updated || !_.isEqual(prevValue, currentValue)) {
          $timeout(function() {
            $scope.highlightItem(field, null, true);
          }, 0);

          this.updateDependencies(field);
        }
      },

      updateDependencies: function(changedField) {
        var that = this;
        changedField = changedField.replace(/\[[\d]+\]/g, '');

        _.each(that.calculatedFields, function(val, field) {
          if (_.includes(val.dependsOn, changedField)) {
            that.calc(field);
            $scope.highlightItem(field);
          }
        });
      },

      init: function() {
        var calculatedFields = this.calculatedFields;
        _.each(calculatedFields, function(val, key) {
          val.calcFunc();
        });
      },
    };

    // move old guarantors structure to the new structure
    const initGuarantorsFromOldObject = function() {
      if ($scope.lease.guarantorInfo.type == 'individual') {
        if (
          $scope.lease.guarantorInfo.marriedCouple &&
          !$scope.lease.guarantorInfo.marriedCouples
        ) {
          $scope.lease.guarantorInfo.marriedCouples = [
            _.merge(
              _.mapKeys($scope.lease.guarantorInfo.guarantors[0], function(
                value,
                key,
              ) {
                return key + '1';
              }),
              _.mapKeys($scope.lease.guarantorInfo.guarantors[1], function(
                value,
                key,
              ) {
                return key + '2';
              }),
            ),
          ];
        } else if (!$scope.lease.guarantorInfo.individuals) {
          $scope.lease.guarantorInfo.individuals = _.clone(
            $scope.lease.guarantorInfo.guarantors,
          );
        }
      } else if (!$scope.lease.guarantorInfo.entities) {
        $scope.lease.guarantorInfo.entities = [
          _.merge($scope.lease.guarantorInfo.guarantors[0], {
            stateOfFormation: $scope.lease.guarantorInfo.stateOfFormation,
            entityType: $scope.lease.guarantorInfo.entityType,
          }),
        ];
      }
    };

    $scope.initCombinedGuarantorEntities = function(context) {
      var i;

      if (context) {
        if (context.entities) {
          for (i in context.entities) {
            var entity = context.entities[i];
            $scope.setCombinedGuarantorEntity(entity);
          }
        }
        if (context.individuals) {
          for (i in context.individuals) {
            var individual = context.individuals[i];
            $scope.setCombinedGuarantorEntity(individual);
          }
        }
        if (context.marriedCouples) {
          for (i in context.marriedCouples) {
            var marriedCouple = context.marriedCouples[i];
            $scope.setCombinedGuarantorEntity(marriedCouple);
          }
        }
      }
    };

    $scope.setCombinedGuarantorEntity = function(guarantor) {
      if (guarantor) {
        if (guarantor.entityType === 'Other') {
          guarantor.entityTypeCombined = guarantor.entityTypeOther;
        } else {
          guarantor.entityTypeCombined = guarantor.entityType;
        }
      }
    };

    $scope.$watch('lease.estimatedCommencementDate', function() {
      $scope.setCalculatedExpirationDate();
    });

    $scope.$watch(
      'lease.existingGuarantorInfo',
      function(newValue, oldValue) {
        if (newValue) {
          // Maintain the `numberOfContinuingGuarantors`
          $scope.numberOfContinuingGuarantors = _.filter(
            _.union(
              $scope.lease.existingGuarantorInfo.individuals || [],
              $scope.lease.existingGuarantorInfo.marriedCouples || [],
              $scope.lease.existingGuarantorInfo.entities || [],
            ),
            {
              hasBeenReleased: false,
            },
          ).length;
        }
      },
      true,
    );

    $scope.$watch(
      'lease.tenantInfo',
      function(newValue, oldValue) {
        if ($scope.lease) {
          if ($scope.lease.hasSameBillingAddress) {
            $scope.lease.tenantInfo.billingAddress1 =
              $scope.lease.tenantInfo.address1;
            $scope.lease.tenantInfo.billingAddressCity =
              $scope.lease.tenantInfo.addressCity;
            $scope.lease.tenantInfo.billingAddressState =
              $scope.lease.tenantInfo.addressState;
            $scope.lease.tenantInfo.billingAddressZip =
              $scope.lease.tenantInfo.addressZip;
            $scope.lease.tenantInfo.billingPhoneNumber =
              $scope.lease.tenantInfo.phoneNumber;
            $scope.lease.tenantInfo.billingAttention =
              $scope.lease.tenantInfo.attention;
          }
        }
      },
      true,
    );

    $scope.$watch(
      'lease.guarantorInfo.marriedCouples',
      function(newValue, oldValue) {
        if (newValue) {
          var marriedCouple = newValue;

          for (var i = 0; i < marriedCouple.length; i++) {
            var item = marriedCouple[i];

            if (item.hasSameAddress) {
              item.address2 = item.address1;
              item.city2 = item.city1;
              item.state2 = item.state1;
              item.zip2 = item.zip1;

              if ($rootScope.hasEditorConfig("guarantors", "copyPhoneNumberForSpouse")) {
                item.phone2 = item.phone1;
              }
              if ($rootScope.hasEditorConfig("guarantors", "copyEmailForSpouse")) {
                item.email2 = item.email1;
              }
            }
          }
        }
      },
      true,
    );

    $scope.$watch('lease.tenantInfo.entityType', function(newValue, oldValue) {
      if (typeof newValue != 'undefined') {
        if (newValue === 'Other')
          $scope.lease.tenantInfo.entityTypeCombined =
            $scope.lease.tenantInfo.entityTypeOther;
        else $scope.lease.tenantInfo.entityTypeCombined = newValue;
      }
    });

    $scope.$watch('lease.tenantInfo.entityTypeOther', function(
      newValue,
      oldValue,
    ) {
      if (typeof newValue != 'undefined') {
        if ($scope.lease.tenantInfo.entityType === 'Other')
          $scope.lease.tenantInfo.entityTypeCombined = newValue;
      }
    });

    $scope.$watch('lease.landlordInfo.entityType', function(newValue, oldValue) {
      if (typeof newValue != 'undefined') {
        if (newValue === 'Other')
          $scope.lease.landlordInfo.entityTypeCombined =
            $scope.lease.landlordInfo.entityTypeOther;
        else $scope.lease.landlordInfo.entityTypeCombined = newValue;
      }
    });

    $scope.$watch('lease.landlordInfo.entityTypeOther', function(
      newValue,
      oldValue,
    ) {
      if (typeof newValue != 'undefined') {
        if ($scope.lease.landlordInfo.entityType === 'Other')
          $scope.lease.landlordInfo.entityTypeCombined = newValue;
      }
    });

    $scope.$watch('lease.franchisorInfo.entityType', function(
      newValue,
      oldValue,
    ) {
      if (typeof newValue != 'undefined') {
        if (newValue === 'Other')
          $scope.lease.franchisorInfo.entityTypeCombined =
            $scope.lease.franchisorInfo.entityTypeOther;
        else $scope.lease.franchisorInfo.entityTypeCombined = newValue;
      }
    });

    $scope.$watch('lease.franchisorInfo.entityTypeOther', function(
      newValue,
      oldValue,
    ) {
      if (typeof newValue != 'undefined') {
        if ($scope.lease.franchisorInfo.entityType === 'Other')
          $scope.lease.franchisorInfo.entityTypeCombined = newValue;
      }
    });

    $scope.$watch('lease.guarantorInfo.entityType', function(
      newValue,
      oldValue,
    ) {
      if (typeof newValue != 'undefined') {
        if (newValue === 'Other')
          $scope.lease.guarantorInfo.entityTypeCombined =
            $scope.lease.franchisorInfo.entityTypeOther;
        else $scope.lease.guarantorInfo.entityTypeCombined = newValue;
      }
    });

    $scope.$watch('lease.guarantorInfo.entityTypeOther', function(
      newValue,
      oldValue,
    ) {
      if (typeof newValue != 'undefined') {
        if ($scope.lease.guarantorInfo.entityType === 'Other')
          $scope.lease.guarantorInfo.entityTypeCombined = newValue;
      }
    });

    $scope.$watch('lease.warranties.length', function(newValue, oldValue) {
      if (typeof newValue != 'undefined') {
        $timeout(function() {
          $scope.highlightItem('warrantyText()[1]');
        }, 0);
      }
    });

    $scope.$watch('lease.hasReleaseGuarantors', function(newValue, oldValue) {
      if (
        typeof oldValue != 'undefined' &&
        typeof newValue != 'undefined'
      ) {
        $timeout(function() {
          $scope.highlightItem('lease.hasReleaseGuarantors');
        }, 0);
      }
    });

    $scope.$watch('lease.rentPeriods.length', function(newValue, oldValue) {
      if (
        typeof oldValue != 'undefined' &&
        typeof newValue != 'undefined'
      ) {
        $timeout(function() {
          $scope.highlightItem('lease.rentPeriods.length');
        }, 0);
      }
    });

    $scope.$watch('lease.renewalInfo[0].cpiAnchorYears', function(
      newValue,
      oldValue,
    ) {
      if (
        typeof oldValue != 'undefined' &&
        typeof newValue != 'undefined'
      ) {
        $timeout(function() {
          $scope.highlightItem('renewal.cpiAnchorYears');
        }, 0);
      }
    });

    $scope.$watch('lease.reductionPeriodsNumber', function(newValue, oldValue) {
      if (!$scope.isLoading) {
        if (newValue > 50) {
          $scope.lease.reductionPeriodsNumber = 50;
        }
  
        setTimeout(function() {
          if (
            typeof oldValue != 'undefined' &&
            typeof newValue != 'undefined'
          ) {
            // 
            $scope.structureChanged();
            $scope.highlightItem('lease.reductionPeriodsNumber');
          }
        }, 1);
      }
    });

    /* ================================= */

    function updateNumberOfRentPeriodsPerTerm(term, numberOfYears) {
      if (!term.rentPeriods) {
        term.rentPeriods = [];
      }

      // Update the collection of rent periods accordingly
      if (term.rentPeriods.length < numberOfYears) {
        for (var i = term.rentPeriods.length; i < numberOfYears; i++) {
          term.rentPeriods.push({});
        }
      } else {
        var count = term.rentPeriods.length - numberOfYears;
        term.rentPeriods.splice(-count, count);
      }
    }

    $scope.updateNumberOfRentPeriods = function(numberOfYears, numberOfMonth) {
      // Limit number of terms and number of years to 50, to prevent massive DB entries
      numberOfYears = Math.min(numberOfYears, 50);
      if (
        (($rootScope.optionalExtensionTermLengthConfig &&
        $rootScope.optionalExtensionTermLengthConfig.fields.addRentRowForPartialYear) ||
        ($rootScope.termLengthConfig &&
        $rootScope.termLengthConfig.fields.addRentRowForPartialYear)) &&
        numberOfMonth != 0 &&
        numberOfYears != 50 &&
        numberOfYears > 0
      ) {
        numberOfYears++;
      }
      numberOfYears =
        numberOfYears == 0 && numberOfMonth > 0 ? 1 : numberOfYears;
      $scope.lease.leaseYearsCalculated = numberOfYears;
      $scope.lease.calc.rentPeriods = Array.apply(null, Array(numberOfYears)).map((x, i) => i);

      // create rent periods if it does not exist

      if ($scope.isMultipleTerms) {
        for (
          var termIndex = 0;
          termIndex < $scope.lease.terms.length;
          termIndex++
        ) {
          updateNumberOfRentPeriodsPerTerm(
            $scope.lease.terms[termIndex],
            numberOfYears,
          );
        }
      } else {
        updateNumberOfRentPeriodsPerTerm($scope.lease, numberOfYears);
      }

      // Also, update the number of reduction periods accordingly
      var listOfLeaseYears = [];
      var listOfLeaseYearsWords = {};
      let listOfLeaseYearsLength;

      if ($rootScope.hasEditorConfig("optionalExtensionTermLength")) {
        listOfLeaseYearsLength = 20;
      } else {
        listOfLeaseYearsLength = $scope.lease.leaseYears || 0;
      }

      for (var i = 0; i < listOfLeaseYearsLength; i++) {
        listOfLeaseYears.push(i + 1);
        listOfLeaseYearsWords[i + 1] = 'Year ' + (i + 1);
      }

      $scope.lease.listOfLeaseYears = listOfLeaseYears;
      $scope.lease.listOfLeaseYearsWords = listOfLeaseYearsWords;
    };

    $scope.updateHasSecurityDepositBurndown = function() {
      if ($scope.lease.hasSecurityDepositBurndown) {
        if (
          !$scope.lease.reductionPeriods ||
          ($scope.lease.reductionPeriods &&
            $scope.lease.reductionPeriods.length === 0)
        ) {
          $scope.lease.reductionPeriodsNumber = 1;
          $scope.lease.reductionPeriods = [{}];
        }
      } else {
        $scope.lease.reductionPeriodsNumber = 0;
        $scope.lease.reductionPeriods = [];
      }
    };

    $scope.updateHasCashBurndown = function() {
      if ($scope.lease.security.hasCashBurndown) {
        if (
          !$scope.lease.security.cashBurndownPeriods ||
          ($scope.lease.security.cashBurndownPeriods &&
            $scope.lease.security.cashBurndownPeriods.length === 0)
        ) {
          $scope.lease.security.cashBurndownPeriodsNumber = 1;
          $scope.lease.security.cashBurndownPeriods = [{}];
        }
      } else {
        $scope.lease.security.cashBurndownPeriodsNumber = 0;
        $scope.lease.security.cashBurndownPeriods = [];
      }
    };

    $scope.updateHasLocBurndown = function() {
      if ($scope.lease.security.hasLocBurndown) {
        if (
          !$scope.lease.security.locBurndownPeriods ||
          ($scope.lease.security.locBurndownPeriods &&
            $scope.lease.security.locBurndownPeriods.length === 0)
        ) {
          $scope.lease.security.locBurndownPeriodsNumber = 1;
          $scope.lease.security.locBurndownPeriods = [{}];
        }
      } else {
        $scope.lease.security.locBurndownPeriodsNumber = 0;
        $scope.lease.security.locBurndownPeriods = [];
      }
    };

    $scope.updateHasGuarantyCapBurndown = function() {
      if ($scope.lease.hasGuarantyCapBurndown) {
        if (
          !$scope.lease.guarantyCapBurnOffReductionPeriods ||
          ($scope.lease.guarantyCapBurnOffReductionPeriods &&
            $scope.lease.guarantyCapBurnOffReductionPeriods.length === 0)
        ) {
          $scope.lease.guarantyCapBurnOffReductionPeriodsNumber = 1;
          $scope.lease.guarantyCapBurnOffReductionPeriods = [{}];
        }
      } else {
        $scope.lease.guarantyCapBurnOffReductionPeriodsNumber = 0;
        $scope.lease.guarantyCapBurnOffReductionPeriods = [];
      }
    };

    $scope.updateLengthOfArray = function(newLength, array) {
      // Limit length of array to 50, to prevent massive DB entries
      newLength = Math.min(newLength, 50);
      let arrayRef = _.get($scope.lease, array);

      // push new items if it does not exist
      if (!arrayRef) {
        _.set($scope.lease, array, []);
        arrayRef = _.get($scope.lease, array);
      }

      // Update the collection accordingly
      if (arrayRef.length < newLength) {
        for (var i = arrayRef.length; i < newLength; i++) {
          arrayRef.push({});
        }
      } else {
        var count = arrayRef.length - newLength;
        arrayRef.splice(-count, count);
      }
    };

    function updateCpiAnchorYearsPerTerm(term) {
      var numberOfYears = $scope.lease.leaseYearsCalculated;

      if (numberOfYears) {
        // If the lease term is only 1 year, then you won't be able to set CPI
        if (numberOfYears === 1) {
          term.hasCpiRent = false;
        } else {
          if (term.hasCpiRent) {
            // If the already selected cpiAnchorYears is greater than the new numberOfYears, let's reset to 1
            if (term.cpiAnchorYears > numberOfYears) {
              term.cpiAnchorYears = 1;
            }

            // Update the numberOfYears according the cpiAnchorYears
            numberOfYears = term.cpiAnchorYears;

            // Update the dropdown to select the number of years prior to CPI
            $scope.cpiAnchorYearsOptions = Array.from(
              new Array($scope.lease.leaseYears - 1),
              function(val, index) {
                return index + 1;
              },
            );
          }

          // Limit number of terms and number of years to 50, to prevent massive DB entries
          numberOfYears = Math.min(numberOfYears, 50);

          // Update the collection of rent periods accordingly
          if (term.rentPeriods.length < numberOfYears) {
            for (var i = term.rentPeriods.length; i < numberOfYears; i++) {
              term.rentPeriods.push({});
            }
          } else {
            var count = term.rentPeriods.length - numberOfYears;
            term.rentPeriods.splice(-count, count);
          }
        }
      }
    }

    $scope.updateCpiAnchorYears = function() {
      if ($scope.isMultipleTerms) {
        for (
          var termIndex = 0;
          termIndex < $scope.lease.terms.length;
          termIndex++
        ) {
          updateCpiAnchorYearsPerTerm($scope.lease.terms[termIndex]);
        }
      } else {
        updateCpiAnchorYearsPerTerm($scope.lease);
      }
    };

    $scope.updateRenewalCpiAnchorYears = function(index = 0) {
      if (!$scope.lease.hasRenewalOption) {
        return;
      }
      
      if ($scope.lease.renewalTerm) {
        $scope.lease.renewalInfo = $scope.lease.renewalInfo || [];
        if (!$scope.lease.renewalInfo[index]) {
          return;
        }

        var numberOfYears = $scope.lease.renewalTerm;

        // If the lease term is only 1 year, then you won't be able to set CPI
        if (numberOfYears === 1) {
          $scope.lease.renewalInfo[index].hasCpiRent = false;
        } else {
          if ($scope.lease.renewalInfo[index].hasCpiRent) {
            // If the already selected cpiAnchorYears is greater than the new numberOfYears, let's reset to 1
            if (
              $scope.lease.renewalInfo[index].cpiAnchorYears > numberOfYears
            ) {
              $scope.lease.renewalInfo[index].cpiAnchorYears = 1;
            }

            // Update the numberOfYears according the cpiAnchorYears
            numberOfYears =
              $scope.lease.renewalInfo[index].cpiAnchorYears || null;

            // Update the dropdown to select the number of years prior to CPI
            $scope.cpiRenewalAnchorYearsOptions = Array.from(
              new Array($scope.lease.renewalTerm - 1),
              function(val, index) {
                return index + 1;
              },
            );
          }

          // Limit number of terms and number of years to 50, to prevent massive DB entries
          numberOfYears = Math.min(numberOfYears, 50);

          // Update the collection of rent periods accordingly
          if (
            $scope.lease.renewalInfo[index].renewalRentPeriods.length <
            numberOfYears
          ) {
            for (
              var i = $scope.lease.renewalInfo[index].renewalRentPeriods.length;
              i < numberOfYears;
              i++
            ) {
              $scope.lease.renewalInfo[index].renewalRentPeriods.push({});
            }
          } else {
            var count =
              $scope.lease.renewalInfo[index].renewalRentPeriods.length -
              numberOfYears;
            $scope.lease.renewalInfo[index].renewalRentPeriods.splice(
              -count,
              count,
            );
            // remove all next renewals once the CPI is set for the first renewal option
            if ($scope.lease.renewalInfo[index].hasCpiRent) {
              for (var i = index + 1; i < $scope.lease.renewalInfo.length; i++) {
                $scope.lease.renewalInfo[i].renewalRentPeriods = [];
              }
            }
          }
        }
      }
    };

    $scope.getTermDatesWithFreeRent = function(
      index,
      momentDateFormat,
      angularDateFormat,
    ) {
      if (!$scope.lease) {
        return;
      }

      // If `hasFreeRent` is turned off, just continue with the normal flow
      if (!$scope.lease.hasFreeRent) {
        return $scope.getTermDates(index, angularDateFormat);
      }

      var commencementDate;

      // Find the correct deliveryDate based on the user selection
      if (
        $scope.lease.tenantImprovements &&
        $scope.lease.tenantImprovements.type
      ) {
        if ($scope.lease.tenantImprovements.type === 'landlord') {
          commencementDate = $scope.lease.estimatedCommencementDate;
        } else if ($scope.lease.tenantImprovements.type === 'tenant') {
          commencementDate = $scope.lease.estimatedCommencementDate;
        } else if ($scope.lease.tenantImprovements.type === 'none') {
          commencementDate = $scope.lease.termCommencementDate;
        }
      }

      // If `commencementDate` is not defined, return a placeholder
      if (!commencementDate) {
        return '________ - ________';
      }

      // NOTE: `index` refers to the first term after the free rent term!
      var startDate;
      var endDate;

      // Free rent is added to the term, meaning, the term length increased with the term of free rent
      if ($scope.lease.hasFreeRentAddedToTerm) {
        startDate = moment(commencementDate)
          .add('year', index)
          .add('month', $scope.lease.freeRent)
          .toDate();

        endDate = moment(commencementDate)
          .add('year', index + 1)
          .add('month', $scope.lease.freeRent)
          .add('day', -1)
          .toDate();
      } else {
        // Free rent isn't added to the term, meaning, part of the first year is free,
        // the term length remain as is

        startDate = moment(commencementDate)
          .add('year', index)
          .toDate();

        endDate = moment(commencementDate)
          .add('year', index + 1)
          .add('day', -1)
          .toDate();

        if (index === 0) {
          startDate = moment(commencementDate)
            .add('month', $scope.lease.freeRent)
            .toDate();
        }
      }

      startDate = moment(startDate).format(momentDateFormat);
      endDate = moment(endDate).format(momentDateFormat);

      return startDate + ' - ' + endDate;
    };

    $scope.getTermDates = function(index, dateFormat) {
      if ($scope.lease) {
        var deliveryDate;

        if (
          $scope.lease.tenantImprovements &&
          $scope.lease.tenantImprovements.type
        ) {
          if ($scope.lease.tenantImprovements.type === 'landlord') {
            deliveryDate = $scope.lease.estimatedCommencementDate;
          } else if ($scope.lease.tenantImprovements.type === 'tenant') {
            deliveryDate = $scope.lease.estimatedCommencementDate;
          } else if ($scope.lease.tenantImprovements.type === 'none') {
            deliveryDate = $scope.lease.termCommencementDate;
          } else {
            return 'Lease Year ' + (index + 1);
          }
        } else {
          if ($scope.lease.rentCommenceAt) {
            deliveryDate = $scope.lease.rentCommenceAt;
          } else {
            return 'Lease Year ' + (index + 1);
          }
        }

        // If `deliveryDate` is not defined, return a placeholder
        if (!deliveryDate) {
          return '________ - ________';
        }

        deliveryDate = new Date(deliveryDate);
        var startDate = new Date(
          deliveryDate.setFullYear(deliveryDate.getFullYear() + index),
        );
        var endDate = new Date(
          deliveryDate.setFullYear(deliveryDate.getFullYear() + 1),
        );

        if ($scope.lease.leaseMonths && $scope.lease.leaseMonths > 0) {
          startDate = moment(startDate)
            .add($scope.lease.leaseMonths, 'month')
            .toDate();
          endDate = moment(endDate)
            .add($scope.lease.leaseMonths, 'month')
            .toDate();
        }

        startDate = $filter('date')(startDate, dateFormat);
        endDate.setDate(endDate.getDate() - 1);
        endDate = $filter('date')(endDate, dateFormat);

        return startDate + ' - ' + endDate;
      } else {
        return 'Lease Year ' + (index + 1);
      }
    };

    $scope.updateNumberOfRenewalTermsAndRentPeriods = function(
      numberOfTerms,
      numberOfYears,
    ) {
      if (!$scope.lease.hasRenewalOption) {
        return;
      }

      // This builds each term's skeleton, so it cannot assume one. A deal
      // terms import merges extracted terms into renewalInfo by index and can
      // add a term carrying only the values the model read.
      $scope.lease.renewalInfo = $scope.lease.renewalInfo || [];
      $scope.lease.renewalInfo.forEach(function(renewalInfo) {
        renewalInfo.renewalRentPeriods = renewalInfo.renewalRentPeriods || [];
      });

      // Limit number of terms and number of years to 50, to prevent massive DB entries
      numberOfTerms = Math.min(numberOfTerms, 50);
      numberOfYears = Math.min(numberOfYears, 50);

      if (numberOfYears) {
        $scope.lease.calc.renewalRentPeriods = Array.apply(null, Array(numberOfYears)).map((x, i) => i);

        $scope.lease.renewalInfo.forEach(function(renewalInfo) {
          while (renewalInfo.renewalRentPeriods.length < numberOfYears) {
            renewalInfo.renewalRentPeriods.push({});
          }
          renewalInfo.renewalRentPeriods = renewalInfo.renewalRentPeriods.slice(
            0,
            numberOfYears,
          );
        });
      }
      
      if (numberOfTerms) {
        if (!$scope.lease.renewalInfo) {
          $scope.lease.renewalInfo = [];
        }

        if ($scope.lease.renewalInfo.length < numberOfTerms) {
          while ($scope.lease.renewalInfo.length < numberOfTerms) {
            let renewalInputType;

            if (
              $scope.lease?.form?.editor?.defaultFields?.renewalInfo?.[0]?.renewalInputType
            ) {
              renewalInputType = $scope.lease.form.editor.defaultFields.renewalInfo[0].renewalInputType;
            }
    
            if (
              !renewalInputType &&
              $scope.lease?.renewalInfo?.[0]?.renewalInputType
            ) {
              renewalInputType = $scope.lease.renewalInfo[0].renewalInputType;
            }

            if (
              $scope.lease?.form?.editor?.defaultFields?.renewalInfo?.[0]
            ) {
              $scope.lease.renewalInfo.push({
                renewalRentPeriods: [],
                renewalInputType,
                ...$scope.lease.form.editor.defaultFields.renewalInfo[0],
              });
            } else {
              $scope.lease.renewalInfo.push({
                renewalRentPeriods: [],
                renewalInputType,
              });
            }  
          }
        } else {
          $scope.lease.renewalInfo = $scope.lease.renewalInfo.slice(
            0,
            numberOfTerms,
          );
        }

        $scope.lease.renewalInfo.forEach(function(renewalInfo) {
          while (renewalInfo.renewalRentPeriods.length < numberOfYears) {
            renewalInfo.renewalRentPeriods.push({});
          }
          renewalInfo.renewalRentPeriods = renewalInfo.renewalRentPeriods.slice(
            0,
            numberOfYears,
          );
        });
      }
    };

    // TODO:[liork] do we need that?
    $scope.selected = function(buildingId) {
      $scope.lease.buildingId = buildingId;
      $scope.update();
      BuildingService.get({
        id: buildingId,
      }).then(function(building) {
        $scope.building = building;
        $scope.building.displayName ||= $scope.building.name;
        $scope.building.dashboardName ||= $scope.building.displayName;
      });
    };

    // TODO:[liork] do we need that?
    $scope.buildings = function() {
      BuildingService.get().then(function(buildings) {
        return buildings;
      });
    };

    $scope.formatDate = function(date, format) {
      if (!date) {
        return '________';
      }

      if (date === '________') {
        return;
      }

      if (!moment(date).isValid()) {
        return '________';
      }

      if (!format) {
        format = 'MMMM D, YYYY';
      }

      return moment(date).format(format);
    };

    function calculateRentPSFPerTerm(
      term,
      premiseAreaSize,
      baseRent,
      annualIncrease,
    ) {
      var roundTo =
        $scope.lease.rentCalculationMethod === 'psf escalation' ? 4 : 2;

      let isExcel = false;
      let isDecimal = false;

      if (
        $scope.lease.rentCalculationMethod &&
        $scope.lease.rentCalculationMethod.startsWith('psf escalation|')
      ) {
        roundTo = parseInt($scope.lease.rentCalculationMethod.match(/\d/)[0]);
        isExcel = true;
      }

      if (
        $scope.lease.rentCalculationMethod &&
        $scope.lease.rentCalculationMethod.startsWith('psf escalation decimal|')
      ) {
        roundTo = parseInt($scope.lease.rentCalculationMethod.match(/\d/)[0]);
        isDecimal = true;
      }

      if (term.rentInputType === 'automatic') {
        term.rentPeriods.forEach(function(rentPeriod, index) {
          if (!baseRent && baseRent !== 0) {
            rentPeriod.rentPerSqFoot = undefined;
            rentPeriod.totalRent = undefined;
          }

          if ((baseRent || baseRent === 0) && (annualIncrease || annualIncrease == 0)) {
            if (isExcel) {
              if (index === 0) {
                rentPeriod.rentPerSqFoot = baseRent;
              } else {
                rentPeriod.rentPerSqFoot = round(term.rentPeriods[index -1].rentPerSqFoot * (1 + annualIncrease / 100), roundTo)
              }
            } else if (isDecimal) {
              if (index === 0) {
                rentPeriod.rentPerSqFoot = baseRent;
              } else {
                rentPeriod.rentPerSqFoot = new Decimal(term.rentPeriods[index - 1].rentPerSqFoot).toDecimalPlaces(roundTo).mul(1 + annualIncrease / 100).toDecimalPlaces(roundTo).toNumber();
              }
            } else {
              rentPeriod.rentPerSqFoot =
                baseRent * Math.pow(1 + annualIncrease / 100, index);
              rentPeriod.rentPerSqFoot = parseFloat(
                rentPeriod.rentPerSqFoot.toFixed(roundTo),
              );
            }
  
            if (index == 0) {
              rentPeriod.totalRent = baseRent * premiseAreaSize;
            } else {
              rentPeriod.totalRent =
                term.rentPeriods[index - 1].totalRent *
                (1 + annualIncrease / 100);
              if ($scope.lease.rentCalculationMethod === 'annual escalation') {
                rentPeriod.rentPerSqFoot = parseFloat(
                  rentPeriod.totalRent / premiseAreaSize,
                );
              }
            }
          }
        });
      } else if (term.rentPeriods && term.rentInputType === 'manual') {
        for (var i = 0; i < term.rentPeriods.length; i++) {
          if (term.rentPeriods[i].rentPerSqFoot) {
            term.rentPeriods[i].totalRent =
              term.rentPeriods[i].rentPerSqFoot.toFixed(10) * premiseAreaSize;
          }
        }
      }
      $scope.setTermsIncludingFreeRent(term.rentPeriods);
    }

    $scope.calculateRentPSF = function(lease) {
      var premiseAreaSize;
      switch (lease.premisesModType) {
        case 'expansion':
          premiseAreaSize =
            parseInt(lease.premiseAreaSize) +
            parseInt(lease.expansionPremiseAreaSize);
          break;
        case 'relocation':
          premiseAreaSize = parseInt(lease.relocationPremisesAreaSize);
          break;
        case 'reduction':
          premiseAreaSize = parseInt(lease.reductionRemainingAreaSize);
          break;
        default:
          premiseAreaSize = parseInt(lease.premiseAreaSize);
          break;
      }

      if (lease.leaseYears) {
        if ($scope.isMultipleTerms) {
          for (
            var termIndex = 0;
            termIndex < $scope.lease.terms.length;
            termIndex++
          ) {
            let annualIncrease = $scope.lease.terms[termIndex].annualIncrease;

            if (($scope.lease.leaseYears * 12) + $scope.lease.leaseMonths <= 12) {
              annualIncrease = 0;
            }

            calculateRentPSFPerTerm(
              $scope.lease.terms[termIndex],
              premiseAreaSize,
              $scope.lease.terms[termIndex].baseRent,
              annualIncrease,
            );
          }
        } else {
          let annualIncrease = $scope.annualIncrease || $scope.lease.annualIncrease;

          if (($scope.lease.leaseYears * 12) + $scope.lease.leaseMonths <= 12) {
            annualIncrease = 0;
          }

          calculateRentPSFPerTerm(
            $scope.lease,
            premiseAreaSize,
            $scope.baseRent,
            annualIncrease,
          );
        }
      }
    };

    $scope.calculateRenewalPSF = function(lease) {
      if (!$scope.lease.hasRenewalOption) {
        return;
      }
      
      if (
        lease.renewalCount &&
        lease.renewalTerm
      ) {
        var roundTo = lease.rentCalculationMethod === 'psf escalation' ? 4 : 2;

        let isExcel = false;
        let isDecimal = false;
        
        if (
          $scope.lease.rentCalculationMethod &&
          $scope.lease.rentCalculationMethod.startsWith('psf escalation|')
        ) {
          roundTo = parseInt($scope.lease.rentCalculationMethod.match(/\d/)[0]);
          isExcel = true;
        }

        if (
          $scope.lease.rentCalculationMethod &&
          $scope.lease.rentCalculationMethod.startsWith('psf escalation decimal|')
        ) {
          roundTo = parseInt($scope.lease.rentCalculationMethod.match(/\d/)[0]);
          isDecimal = true;
        }

        if (lease.hasApplyIncreaseToRenewals) {
          if (
            lease.rentPeriods &&
            lease.rentPeriods.length > 0
          ) {
            lease.renewalInfo.forEach(function(renewalInfo, renewalInfoIndex) {
              renewalInfo.renewalInputType = 'automatic';
              renewalInfo.renewalAnnualIncrease = $scope.annualIncrease;

              var renewalBaseRent;
              if (renewalInfoIndex == 0) {
                renewalBaseRent =
                  lease.rentPeriods[lease.rentPeriods.length - 1].rentPerSqFoot;
              } else {
                renewalBaseRent =
                  lease.renewalInfo[renewalInfoIndex - 1].renewalRentPeriods[
                    lease.renewalTerm - 1
                  ].rentPerSqFoot;
              }

              if (isExcel) {
                renewalInfo.renewalBaseRent = round(round(renewalBaseRent, roundTo) * (1 + $scope.annualIncrease / 100), roundTo);
              } else if (isDecimal) {
                renewalInfo.renewalBaseRent = new Decimal(renewalBaseRent).toDecimalPlaces(roundTo).mul(1 + $scope.annualIncrease / 100).toDecimalPlaces(roundTo).toNumber();
              } else {
                renewalInfo.renewalBaseRent = round(parseFloat(
                  renewalBaseRent * (1 + $scope.annualIncrease / 100),
                ), roundTo);
              }

              renewalInfo.renewalRentPeriods.forEach(function(
                rentPeriod,
                renewalRentPeriodsIndex,
              ) {
                if (isExcel) {
                  if (renewalRentPeriodsIndex === 0) {
                    rentPeriod.rentPerSqFoot = renewalInfo.renewalBaseRent;
                  } else {
                    rentPeriod.rentPerSqFoot = round(round(renewalInfo.renewalRentPeriods[renewalRentPeriodsIndex - 1].rentPerSqFoot, roundTo) * (1 + $scope.annualIncrease / 100), roundTo);
                  }
                } else if (isDecimal) {
                  if (renewalRentPeriodsIndex === 0) {
                    rentPeriod.rentPerSqFoot = renewalInfo.renewalBaseRent;
                  } else {
                    rentPeriod.rentPerSqFoot = new Decimal(renewalInfo.renewalRentPeriods[renewalRentPeriodsIndex - 1].rentPerSqFoot).toDecimalPlaces(roundTo).mul(1 + $scope.annualIncrease / 100).toDecimalPlaces(roundTo).toNumber();
                  }
                } else {
                  rentPeriod.rentPerSqFoot =
                    renewalInfo.renewalBaseRent *
                    Math.pow(
                      1 + $scope.annualIncrease / 100,
                      renewalRentPeriodsIndex,
                    );
                  rentPeriod.rentPerSqFoot = parseFloat(
                    rentPeriod.rentPerSqFoot.toFixed(roundTo),
                  );
                }
                rentPeriod.totalRent =
                  rentPeriod.rentPerSqFoot * lease.premiseAreaSize;
                if (lease.rentCalculationMethod === 'annual escalation') {
                  rentPeriod.rentPerSqFoot = parseFloat(
                    rentPeriod.totalRent / lease.premiseAreaSize,
                  );
                }
              });
            });
          }
        } else {
          lease.renewalInfo.forEach(function(renewalInfo, renewalInfoIndex) {
            let renewalAnnualIncrease = renewalInfo.renewalAnnualIncrease;
            
            if (lease.renewalTerm <= 1) {
              renewalAnnualIncrease = 0;
            }
            
            if (
              renewalInfo.renewalInputType === 'automatic' &&
              renewalInfo.renewalBaseRent &&
              (renewalAnnualIncrease ||
                renewalAnnualIncrease == 0)
            ) {
              renewalInfo.renewalRentPeriods.forEach(function(
                rentPeriod,
                index,
              ) {
                if (isExcel) {
                  if (index === 0) {
                    rentPeriod.rentPerSqFoot = renewalInfo.renewalBaseRent;
                  } else {
                    rentPeriod.rentPerSqFoot = round(renewalInfo.renewalRentPeriods[index - 1].rentPerSqFoot * (1 + renewalAnnualIncrease / 100), roundTo);
                  }
                } else if (isDecimal) {
                  if (index === 0) {
                    rentPeriod.rentPerSqFoot = renewalInfo.renewalBaseRent;
                  } else {
                    rentPeriod.rentPerSqFoot = new Decimal(renewalInfo.renewalRentPeriods[index - 1].rentPerSqFoot).toDecimalPlaces(roundTo).mul(1 + renewalAnnualIncrease / 100).toDecimalPlaces(roundTo).toNumber();
                  }
                } else {
                  rentPeriod.rentPerSqFoot =
                    renewalInfo.renewalBaseRent *
                    Math.pow(1 + renewalAnnualIncrease / 100, index);
                  rentPeriod.rentPerSqFoot = parseFloat(
                    rentPeriod.rentPerSqFoot.toFixed(roundTo),
                  );
                }

                if (index == 0) {
                  rentPeriod.totalRent =
                    renewalInfo.renewalBaseRent * lease.premiseAreaSize;
                } else {
                  rentPeriod.totalRent =
                    lease.renewalInfo[renewalInfoIndex].renewalRentPeriods[
                      index - 1
                    ].totalRent *
                    (1 + renewalAnnualIncrease / 100);
                  if (lease.rentCalculationMethod === 'annual escalation') {
                    rentPeriod.rentPerSqFoot = parseFloat(
                      lease.renewalInfo[renewalInfoIndex].renewalRentPeriods[
                        index
                      ].totalRent / lease.premiseAreaSize,
                    );
                  }
                }
              });
            } else if (
              renewalInfo.renewalRentPeriods &&
              renewalInfo.renewalInputType === 'manual'
            ) {
              renewalInfo.renewalRentPeriods.forEach(function(rentPeriod) {
                rentPeriod.totalRent =
                  rentPeriod.rentPerSqFoot * lease.premiseAreaSize;
              });
            }
          });
        }
      }
    };

    function consolidateRentPeriodsPerTerm(term) {
      var rentPeriods = term.rentPeriods;

      if (rentPeriods) {
        var length = term.rentPeriods.length;

        if (rentPeriods.length === 0) {
          return;
        }

        term.consolidatedRentPeriods = [
          {
            startYear: 1,
            endYear: 1,
            rentPerSqFoot: rentPeriods[0].rentPerSqFoot,
            totalRent: rentPeriods[0].totalRent,
          },
        ];

        for (var i = 1; i < length; i++) {
          var isConsolidate = false;

          if (
            term.rentPeriods &&
            (term.rentInputType === 'manual' ||
              term.rentInputType === 'automatic')
          ) {
            isConsolidate =
              rentPeriods[i].rentPerSqFoot === rentPeriods[i - 1].rentPerSqFoot;
          } else if (
            term.rentPeriods &&
            (term.rentInputType === 'fixed' ||
              term.rentInputType === 'fixed-no-escalation')
          ) {
            isConsolidate =
              rentPeriods[i].totalRent === rentPeriods[i - 1].totalRent;
          }

          if (isConsolidate) {
            var lastItem = term.consolidatedRentPeriods.pop();
            lastItem.endYear++;
            term.consolidatedRentPeriods.push(lastItem);
          } else {
            var newRentPeriod = {
              startYear: i + 1,
              endYear: i + 1,
              rentPerSqFoot: rentPeriods[i].rentPerSqFoot,
              totalRent: rentPeriods[i].totalRent,
            };
            term.consolidatedRentPeriods.push(newRentPeriod);
          }
        }
      }
    }

    $scope.consolidateRentPeriods = function() {
      if ($scope.isMultipleTerms) {
        for (
          var termIndex = 0;
          termIndex < $scope.lease.terms.length;
          termIndex++
        ) {
          consolidateRentPeriodsPerTerm($scope.lease.terms[termIndex]);
        }
      } else {
        consolidateRentPeriodsPerTerm($scope.lease);
      }
    };

    $scope.consolidateRenewalInfo = function() {
      if (!$scope.lease.hasRenewalOption) {
        return;
      }

      var renewalInfo = $scope.lease.renewalInfo;
      if (!renewalInfo || renewalInfo.length === 0) {
        return; // prevent JS errors
      }
      var renewalLength = renewalInfo.length;
      $scope.lease.consolidatedRenewalInfo = [];

      for (var i = 0; i < renewalLength; i++) {
        var renewalPeriods = renewalInfo[i].renewalRentPeriods;
        var periodLength = renewalInfo[i].renewalRentPeriods.length;

        $scope.lease.consolidatedRenewalInfo.push({
          hasCpiRent: renewalInfo[i].hasCpiRent
            ? renewalInfo[i].hasCpiRent
            : false,
          cpiAnchorYears: renewalInfo[i].cpiAnchorYears,
          consolidatedRenewalRentPeriods: [
            {
              startYear: 1,
              endYear: 1,
              rentPerSqFoot: renewalInfo[i].renewalRentPeriods[0]
                ? renewalInfo[i].renewalRentPeriods[0].rentPerSqFoot
                : 0,
              totalRent: renewalInfo[i].renewalRentPeriods[0]
                ? renewalInfo[i].renewalRentPeriods[0].totalRent
                : 0,
            },
          ],
        });

        for (var j = 1; j < periodLength; j++) {
          var isConsolidate = false;
          if (
            renewalPeriods &&
            (renewalInfo[i].renewalInputType === 'manual' ||
              renewalInfo[i].renewalInputType === 'automatic')
          ) {
            isConsolidate =
              renewalPeriods[j].rentPerSqFoot ===
              renewalPeriods[j - 1].rentPerSqFoot;
          } else if (
            renewalPeriods &&
            renewalInfo[i].renewalInputType === 'fixed'
          ) {
            isConsolidate =
              renewalPeriods[j].totalRent === renewalPeriods[j - 1].totalRent;
          }

          if (isConsolidate) {
            var lastItem = $scope.lease.consolidatedRenewalInfo[
              i
            ].consolidatedRenewalRentPeriods.pop();
            lastItem.endYear++;
            $scope.lease.consolidatedRenewalInfo[
              i
            ].consolidatedRenewalRentPeriods.push(lastItem);
          } else {
            var newRenewalRentPeriod = {
              startYear: j + 1,
              endYear: j + 1,
              rentPerSqFoot: renewalPeriods[j].rentPerSqFoot,
              totalRent: renewalPeriods[j].totalRent,
            };
            $scope.lease.consolidatedRenewalInfo[i].hasCpiRent = renewalInfo[i]
              .hasCpiRent
              ? renewalInfo[i].hasCpiRent
              : false;
            $scope.lease.consolidatedRenewalInfo[i].cpiAnchorYears =
              renewalInfo[i].cpiAnchorYears;
            $scope.lease.consolidatedRenewalInfo[
              i
            ].consolidatedRenewalRentPeriods.push(newRenewalRentPeriod);
          }
        }
      }
    };

    $scope.setGuarantorLists = function(individuals, married, entities) {
      $scope.lease.guarantorInfo.individuals =
        $scope.lease.guarantorInfo.individuals || [];
      $scope.lease.guarantorInfo.marriedCouples =
        $scope.lease.guarantorInfo.marriedCouples || [];
      $scope.lease.guarantorInfo.entities =
        $scope.lease.guarantorInfo.entities || [];
    };

    $scope.initializeExistingGuarantors = function() {
      var individuals = $scope.lease.existingGuarantorInfo.individuals;
      var marriedCouples = $scope.lease.existingGuarantorInfo.marriedCouples;
      var entities = $scope.lease.existingGuarantorInfo.entities;

      // If the user turned on the hasExistingGuarantors flag and all the lists are empty
      // Lets create the first one for him - 1 individual guarantor
      if ($scope.lease.hasExistingGuarantors) {
        $scope.lease.individualExistingGuarantors = 1;
        if (
          individuals.length === 0 &&
          marriedCouples.length === 0 &&
          entities.length === 0
        ) {
          individuals.push({
            nickname: '',
            hasBeenReleased: false,
            guarantyDate: undefined,
            releaseDate: undefined,
          });
          $scope.lease.hasReleaseGuarantors = false;
        } else {
          var allGuarantors =
            _.union(individuals, marriedCouples, entities) || [];
          var hasReleaseGuarantors = _.find(allGuarantors, function(o) {
            return o.hasBeenReleased == true;
          });
          $scope.lease.hasReleaseGuarantors = hasReleaseGuarantors
            ? true
            : false;
        }
      }
    };

    $scope.initializeReleaseGuarantors = function() {
      if (!$scope.lease.hasExistingGuarantors) {
        $scope.lease.hasReleaseGuarantors = false;
      }
    };

    $scope.addExistingGuarantor = function(guarantorType) {
      $scope.lease.existingGuarantorInfo[guarantorType].push({
        nickname: '',
        hasBeenReleased: false,
        guarantyDate: undefined,
        releaseDate: undefined,
      });
      var index = $scope.lease.existingGuarantorInfo[guarantorType].length - 1;
      $scope.$watch(
        'lease.existingGuarantorInfo[' +
          guarantorType +
          '][' +
          index +
          '].hasBeenReleased',
        function(newValue, oldValue) {
          setTimeout(function() {
            if (
              typeof oldValue != 'undefined' &&
              typeof newValue != 'undefined'
            ) {
              $scope.structureChanged();
              $scope.highlightItem(
                'lease.existingGuarantorInfo[' +
                  guarantorType +
                  '][' +
                  index +
                  '].hasBeenReleased',
              );
            }
          }, 1);
        },
      );
    };

    $scope.removeExistingGuarantor = function(guarantorType, index) {
      $scope.lease.existingGuarantorInfo[guarantorType].splice(index, 1);
      $scope.updateReleaseGuarantors();
    };

    $scope.initializeAdditionalExistingGuarantors = function() {
      var individuals =
        $scope.lease.additionalExistingGuarantorInfo.individuals;
      var marriedCouples =
        $scope.lease.additionalExistingGuarantorInfo.marriedCouples;
      var entities = $scope.lease.additionalExistingGuarantorInfo.entities;

      // If the user turned on the hasAdditionalExistingGuarantors flag and all the lists are empty
      // Lets create the first one for him - 1 individual guarantor
      if ($scope.lease.hasAdditionalExistingGuarantors) {
        $scope.lease.individualAdditionalExistingGuarantors = 1;
        if (
          individuals.length === 0 &&
          marriedCouples.length === 0 &&
          entities.length === 0
        ) {
          individuals.push({
            nickname: '',
            hasBeenReleased: false,
            guarantyDate: undefined,
            releaseDate: undefined,
          });
        }
      }
    };

    $scope.addAdditionalExistingGuarantor = function(guarantorType) {
      $scope.lease.additionalExistingGuarantorInfo[guarantorType].push({
        nickname: '',
        hasBeenReleased: false,
        guarantyDate: undefined,
        releaseDate: undefined,
      });
      var index =
        $scope.lease.additionalExistingGuarantorInfo[guarantorType].length - 1;
      $scope.$watch(
        'lease.additionalExistingGuarantorInfo[' +
          guarantorType +
          '][' +
          index +
          '].hasBeenReleased',
        function(newValue, oldValue) {
          setTimeout(function() {
            if (
              typeof oldValue != 'undefined' &&
              typeof newValue != 'undefined'
            ) {
              $scope.structureChanged();
              $scope.highlightItem(
                'lease.additionalExistingGuarantorInfo[' +
                  guarantorType +
                  '][' +
                  index +
                  '].hasBeenReleased',
              );
            }
          }, 1);
        },
      );
    };

    $scope.removeAdditionalExistingGuarantor = function(guarantorType, index) {
      $scope.lease.additionalExistingGuarantorInfo[guarantorType].splice(
        index,
        1,
      );
    };

    $scope.addGuarantor = function(guarantorType = "guarantors") {
      const guarantor = {};

      if (guarantorType === 'marriedCouples') {
        guarantor.hasSameAddress = true;
      }

      $scope.lease.guarantorInfo[guarantorType] = $scope.lease.guarantorInfo[guarantorType] || [];
      $scope.lease.guarantorInfo[guarantorType].push(guarantor);
    };

    $scope.addSpaceToTerm = function(termIndex) {
      var term = $scope.lease.terms[termIndex];
      if (!term.spaces) {
        term.spaces = [];
      }
      term.spaces.push(JSON.parse(term.selectedSpace));
      term.selectedSpace = null;
      $scope.setAvailableSpaces();
    };

    $scope.removeSpaceFromTerm = function(termIndex, spaceIndex) {
      $scope.lease.terms[termIndex].spaces.splice(spaceIndex, 1);
      $scope.setAvailableSpaces();
    };

    $scope.allowRemoveTerm = true;
    $scope.addTerm = function() {
      if(!$scope.allowRemoveTerm){
        return;
      }
      if($scope.isAdmin){
        $scope.allowRemoveTerm = true;
      }
      else{
        $scope.allowRemoveTerm = false;
      }
      $scope.safeApply();
      var rentInputType =
        $scope.lease.terms && $scope.lease.terms.length
          ? $scope.lease.terms[0].rentInputType
          : null;
      $scope.lease.terms.push({
        rentBumps: [{ begin: 1 }],
        rentInputType: rentInputType,
        isInitialized: true,
        isNew: !$scope.isAdmin,
      });
      updateNumberOfRentPeriodsPerTerm(
        $scope.lease.terms[$scope.lease.terms.length - 1],
        $scope.lease.leaseYears,
      );
      $scope.setAvailableSpaces();
    };

    $scope.removeTerm = function(index) {
      if(!$scope.allowRemoveTerm){
        return;
      }
      if($scope.isAdmin){
        $scope.allowRemoveTerm = true;
      }
      else{
        $scope.allowRemoveTerm = false;
      }
      $scope.safeApply();
      $scope.lease.terms.splice(index, 1);
      $scope.setAvailableSpaces();
    };

    $scope.listGuarantor = function(data, typeArray) {
      var str,
        list = [];
      _.forEach(data, function(value) {
        str = [];
        _.forEach(typeArray, function(type) {
          if (value[type]) str.push(value[type]);
        });
        if (str.length) list.push(str.join(' and '));
      });
      return list.join(', ');
    };

    $scope.removeGuarantor = function(index, guarantorType) {
      $scope.lease.guarantorInfo[guarantorType || 'guarantors'].splice(
        index,
        1,
      );
    };

    $scope.removeGuarantorConfirm = function(index, type, typeObj) {
      var confirm = $mdDialog.confirm({
        template:
          '<div id="confirm-remove-guarantor-header">' +
          '<h2 class="modal-header">Remove Guarantor</h2>' +
          '<div class="md-dialog-content">' +
          '<span>  Are you sure you want to delete?</span>' +
          '<div class="modal-actions">' +
          '<button class="btn blue" id="delete" ng-click="dialog.hide()">OK</button>' +
          '<button class="btn blue-outline" id="cancel" ng-click="dialog.abort()">Cancel</button>' +
          '</div>' +
          '</div>' +
          '</div>',
      });

      $mdDialog.show(confirm).then(function() {
        $scope.removeGuarantor(index, type);
        $scope.update();
        $scope.updateCurrent(typeObj);
        $scope.inline.hideInline();
      });
    };

    $scope.guarantorList = function() {
      switch ($scope.lease.guarantorInfo.guarantors.length) {
        case 1:
          return $scope.lease.guarantorInfo.guarantors[0].name || '__________';
        case 2:
          return (
            ($scope.lease.guarantorInfo.guarantors[0].name || '__________') +
            ' and ' +
            ($scope.lease.guarantorInfo.guarantors[1].name || '__________')
          );
        default:
          var guarantorNames = $scope.lease.guarantorInfo.guarantors.map(
            function(currentValue) {
              return currentValue.name || '__________';
            },
          );
          var guarantorList =
            guarantorNames.slice(0, -1).join(', ') +
            ' and ' +
            guarantorNames.slice(-1);
          return guarantorList;
      }
    };

    $scope.updateGuarantors = function() {
      var guarantorInfo = $scope.lease.guarantorInfo;
      guarantorInfo.guarantors = guarantorInfo.guarantors.slice(0, 1);
      if (guarantorInfo.type == 'entity') {
        guarantorInfo.marriedCouple = false;
      }
      $scope.update();
    };

    $scope.stateAbbreviationToName = function(state) {
      if (state) {
        return stateAbbreviationToName(state);
      }
      return null;
    };

    $scope.toVowelA = function(str) {
      if (str) {
        if (str.toLowerCase() == 'utah') {
          return 'a';
        } else if (isVowel(str.charAt(0))) {
          return 'an';
        } else {
          return 'a';
        }
      } else {
        return 'a';
      }
    };

    $scope.securityDepositText = function(deposit) {
      if (deposit && deposit != 0) {
        var depositArray = deposit.split('.');
        if (parseInt(depositArray[1])) {
          return (
            toTitleCase(toWords(depositArray[0])) +
            'and ' +
            depositArray[1] +
            '/100 Dollars' +
            ' (' +
            $filter('currency')(deposit, '$', 2) +
            ')'
          );
        } else {
          return (
            toTitleCase(toWords(depositArray[0])) +
            'and No/100 Dollars' +
            ' (' +
            $filter('currency')(deposit, '$', 2) +
            ')'
          );
        }
      } else {
        return '______________ and No Dollars ($________)';
      }
    };

    $scope.premiseFullAddress = function(lease) {
      if (lease) {
        var address = lease.premiseAddress ? lease.premiseAddress + ', ' : '';
        address += lease.premiseAddressCity
          ? lease.premiseAddressCity + ', '
          : '';
        address += lease.premiseAddressState
          ? lease.premiseAddressState + ' '
          : '';
        address += lease.premiseAddressZip ? lease.premiseAddressZip : '';
        return address;
      }
    };

    $scope.sitePlanName = function() {
      if ($scope.lease && $scope.lease.sitePlan && $scope.lease.sitePlan.url) {
        return $scope.lease.sitePlan.url.split('/').pop();
      } else {
        return 'No File Chosen';
      }
    };

    $scope.premisesPlanName = function() {
      if (
        $scope.lease &&
        $scope.lease.premisesPlan &&
        $scope.lease.premisesPlan.url
      ) {
        return $scope.lease.premisesPlan.url.split('/').pop();
      } else {
        return 'No File Chosen';
      }
    };

    $scope.clearWarranties = function() {
      if ($scope.lease.landlordWarranty) {
        $scope.lease.landlordWarranty.hvac = false;
        $scope.lease.landlordWarranty.electrical = false;
        $scope.lease.landlordWarranty.plumbing = false;
        $scope.lease.landlordWarranty.roof = false;
        $scope.lease.landlordWarranty.commonAreas = false;
        $scope.lease.landlordWarranty.landlordContractor = false;
      }
    };

    $scope.countWarranties = function() {
      var warrantyArray = [];
      angular.forEach($scope.lease.landlordWarranty, function(value, system) {
        if (value == true && system != 'roof') {
          if (system == 'hvac') {
            warrantyArray.push('HVAC');
          } else {
            warrantyArray.push(system);
          }
        }
      });
      $scope.lease.warranties = warrantyArray;
    };

    $scope.warrantyText = function() {
      if ($scope.lease && $scope.lease.warranties) {
        $scope.countWarranties();
        var warranties = $scope.lease.warranties;
        switch (warranties.length) {
          case 0:
            return ['___________', '___________'];
          case 1:
            return [warranties[0] + ' system', warranties[0] + ' system'];
          case 2:
            return [
              warranties[0] + ' and ' + warranties[1] + ' systems',
              warranties[0] + ' or ' + warranties[1] + ' systems',
            ];
          case 3:
            return [
              warranties[0] +
                ', ' +
                warranties[1] +
                ' and ' +
                warranties[2] +
                ' systems',
              warranties[0] +
                ', ' +
                warranties[1] +
                ' or ' +
                warranties[2] +
                ' systems',
            ];
        }
      }
    };

    function getBuildingOverrides() {
      var res = [];
      var buildingOverrides = $rootScope.buildingOverrides || {};
      Object.values(buildingOverrides).forEach(function(buildingOverride) {
        Object.values(buildingOverride).forEach(function(data) {
          res.push({
            text: data.text,
            formId: data.formId,
            buildingId: data.buildingId,
            bsName: data.bsName,
          });
        });
      });
      // convert buildingOverrides array to object, before sending to the server
      res = res.reduce(function(data, cur, i) {
        data[i] = cur;
        return data;
      }, {});
      return res;
    }

    $rootScope.inlineComputedStyles = function(html, ignoreAutomationText) {
      if (typeof ignoreAutomationText === 'undefined') {
        ignoreAutomationText = false;
      }

      var $html = $(html);

      // copy computed styles into inline style
      $html.find('*').each(function() {
        var current = $(this);
        var computedStyle = window.getComputedStyle(this);
        var fontStyle = computedStyle.getPropertyValue('font-style');
        var fontWeight = computedStyle.getPropertyValue('font-weight');
        var textDecoration = computedStyle.getPropertyValue('text-decoration');
        var color = computedStyle.getPropertyValue('color');
        var backgroundColor = computedStyle.getPropertyValue(
          'background-color',
        );

        current.css('font-style', fontStyle);
        current.css('font-weight', fontWeight);

        if (backgroundColor !== 'rgba(0, 0, 0, 0)') {
          current.css('background-color', backgroundColor);
        }

        if (color !== 'rgb(0, 156, 33)' && color !== 'rgb(0, 0, 0)') {
          // We don't want to keep the 'blue's coming from the inline editor
          if (ignoreAutomationText && color === 'rgb(21, 133, 212)') {
            current.css('color', '');
          } else {
            current.css('color', color);
          }
        }

        if (textDecoration.indexOf('underline') !== -1) {
          current.css('text-decoration', 'underline');
        }
      });

      return $html.html();
    };

    $scope.saveOverride = function(isPublishVersion, pathToRedirect) {
      //remove inline-editor style
      $('.preview').addClass('ignore-style');

      var arrData = {};
      // save new section/article from previous minor version
      if (JSON.parse($scope.lease.orderedListitem).length) {
        arrData[0] = {
          overrideType: 'ordered_listitem',
          orderedListitem: $scope.lease.orderedListitem,
          formId: $scope.lease.formId,          
        };
      }

      // save restyle      
      if (Object.keys($scope.lease.restyle).length !== 0) {
        const restyleData = JSON.stringify(lease.restyle);
        arrData[1] = {
          overrideType: 'restyle',
          orderedListitem: restyleData,
          formId: $scope.lease.formId,
          sectionId: 1,
        };
      }

      // save layers/outlones  
      if (lease.layers && Object.keys(lease.layers).length !== 0) {
        const layersData = JSON.stringify(lease.layers);
        arrData[2] = {
          overrideType: 'layers',
          orderedListitem: layersData,
          formId: $scope.lease.formId,
          sectionId: 2,
        };
      }

      var className = 'override-color',
        color = 'rgb(0, 156, 33)';
      $('*[style*="color: ' + color + '"]')
        .addClass(className)
        .css('color', '');
      // save overrides from previous minor version
      for (var override in $scope.prevOverrides) {
        var overrideData = $scope.prevOverrides[override];
        if (overrideData && overrideData.overrideType != 'ordered_listitem' 
        && overrideData.overrideType != 'restyle' 
        && overrideData.overrideType != 'layers')
          arrData[overrideData.sectionId] = {
            sectionId: overrideData.sectionId,
            text: overrideData.text,
            originalText: overrideData.originalText,
            formId: $scope.lease.formId,
          };
      }


      // handle new list items changes      
      var deletedElements = document.querySelectorAll('.deleted-element');
      const deletedSectionInnerHtml = `<span style="display:none;user-select:none;" class="deleted-paragraph"></span>`;
      deletedElements.forEach((el)=>{
        el.classList.remove('deleted-element');
        el.classList.add('deleted-container');
        const sections = el.querySelectorAll('[section-id]');
        sections.forEach((section)=>{
          section.setAttribute('override',true);
          section.innerHTML = deletedSectionInnerHtml;
        });
      });

      // save overrides from current edit
      var overrides = document.querySelectorAll('[override=true]');
      overrides.forEach(function(override) {
        override = $(override)
          .find('building-variable')
          .empty()
          .end()[0];
        var sectionId = override.getAttribute('section-id');
        arrData[sectionId] = {
          sectionId: sectionId,
          text: $rootScope.inlineComputedStyles(override, true),
          originalText: $scope.defaultProvisions[sectionId].htmlWithStyle,
          formId: $scope.lease.formId,
        };
      });

      var data = {
        overrides: arrData,
        buildingOverrides: getBuildingOverrides(),
        formId: $scope.lease.formId,
        publishVersion: isPublishVersion,
      };
      
      if (
        Object.keys(data.overrides).length ||
        Object.keys(data.buildingOverrides).length
      ) {
        new AllOverrideService(data).create().then(function(override) {
          window.track.event(
            new SaveFormManagementChangesAsDraftEvent({
              id: $scope.lease.formId,
              name: $scope.lease.form.displayName,
            })
          );
          window.removeEventListener('beforeunload', beforeUnloadFn);
          window.location = pathToRedirect;
        });
      }

      $('.ignore-style').removeClass('ignore-style');
    };


    const beforeUnloadFn = function (e) {
      if (!window.isTestEnv) {
        // A change still waiting out SAVE_WAIT goes now, and the prompt keeps
        // the page open while it does.
        if (window.pendingSaves.size) {
          Array.from(window.pendingSaves).forEach(pending => pending.flush());

          e.preventDefault();
          e.returnValue = '';

          const confirmationMessage = 'You have unsaved changes. Are you sure you want to leave this page?';
          e.returnValue = confirmationMessage;
          return confirmationMessage;
        }

        if ($scope.hasNewOverrides()) {
          e.preventDefault();
          e.returnValue = '';
      
          const confirmationMessage = 'You have unsaved changes. Are you sure you want to leave this page?';
          e.returnValue = confirmationMessage;
          return confirmationMessage;
        }

        if ($scope.isSaving) {
          e.preventDefault();
          e.returnValue = '';
      
          const confirmationMessage = 'You have unsaved changes. Are you sure you want to leave this page?';
          e.returnValue = confirmationMessage;
          return confirmationMessage;
        }
      }
    };

    window.addEventListener('beforeunload', beforeUnloadFn);
    
    $scope.hasNewOverrides = function() {
      return (
        document.querySelectorAll('[override=true]').length > 0 ||
        $rootScope.hasNewBuildingOverride
      );
    };

    $scope.setPreviousAmendmentList = function() {
      if ($scope.lease.previousAmendment.length === 0) {
        $scope.addPriorAmendment();
      }
    };

    $scope.addPriorAmendment = function() {
      if ($scope.lease.form.editor.defaultFields.previousAmendment && $scope.lease.form.editor.defaultFields.previousAmendment[0]) {
        $scope.lease.previousAmendment.push({
          date: '',
          ...$scope.lease.form.editor.defaultFields.previousAmendment[0],
        });
      } else {
        $scope.lease.previousAmendment.push({
          date: '',
        });
      }
    };

    $scope.deletePriorAmendment = function(index) {
      $scope.lease.previousAmendment.splice(index, 1);
    };

    $scope.addPriorAssignment = function() {
      if ($scope.lease.form.editor.defaultFields.priorAssignments && $scope.lease.form.editor.defaultFields.priorAssignments[0]) {
        $scope.lease.priorAssignments.push({
          date: undefined,
          _originalIndex: $scope.lease.priorAssignments.length,
          ...$scope.lease.form.editor.defaultFields.priorAssignments[0],
        });
      } else {
        $scope.lease.priorAssignments.push({
          date: undefined,
          _originalIndex: $scope.lease.priorAssignments.length,
        });
      }
    };

    $scope.addPriorDocument = function() {
      if ($scope.lease.form.editor.defaultFields.priorDocuments && $scope.lease.form.editor.defaultFields.priorDocuments[0]) {
        $scope.lease.priorDocuments.push({
          name: '',
          date: '',
          ...$scope.lease.form.editor.defaultFields.priorDocuments[0],
        });
      } else {
        $scope.lease.priorDocuments.push({
          name: '',
          date: '',
        });
      }
    };

    $scope.deletePriorDocument = function(index) {
      $scope.lease.priorDocuments.splice(index, 1);
    };

    $scope.addEventTime = function() {
      if ($scope.lease.form.editor.defaultFields.event?.times && $scope.lease.form.editor.defaultFields.event?.times[0]) {
        $scope.lease.event.times.push({
          date: '',
          ...$scope.lease.form.editor.defaultFields.event.times[0],
        });
      } else {
        $scope.lease.event.times.push({
          date: '',
        });
      }

    };

    $scope.deleteEventTime = function(index) {
      $scope.lease.event.times.splice(index, 1);
    };

    $scope.addAdditionalInsured = function() {
      if (!$scope.lease.insurance.additionalInsured) {
        $scope.lease.insurance.additionalInsured = [];
      }
      
      if ($scope.lease.form.editor.defaultFields.insurance?.additionalInsured && $scope.lease.form.editor.defaultFields.insurance?.additionalInsured[0]) {
        $scope.lease.insurance.additionalInsured.push({
          date: '',
          ...$scope.lease.form.editor.defaultFields.insurance.additionalInsured[0],
        });
      } else {
        $scope.lease.insurance.additionalInsured.push({
          date: '',
        });
      }

    };

    $scope.deleteAdditionalInsured = function(index) {
      $scope.lease.insurance.additionalInsured.splice(index, 1);
    };

    /*  Add additional Copies Start  */
    $scope.showHideCopyToAddress = evt => {
      if ($scope.lease.tenantInfo.hasCopyTo) {
        if (!$scope.lease.tenantInfo.copyTo) {
          $scope.addCopyToAddress();
        }
      }
    };

    $scope.addCopyToAddress = () => {
      if (!$scope.lease.tenantInfo.copyTo) {
        $scope.lease.tenantInfo.copyTo = [];
      }
      if ($scope.lease.form.editor.defaultFields.tenantInfo.copyTo && $scope.lease.form.editor.defaultFields.tenantInfo.copyTo[0]) {
        $scope.lease.tenantInfo.copyTo.push({
          ...$scope.lease.form.editor.defaultFields.tenantInfo.copyTo[0],
        });
      } else {
        $scope.lease.tenantInfo.copyTo.push({});
      }
    };

    $scope.addLandlordCopyToAddress = () => {
      if (!$scope.lease.landlordInfo.copyTo) {
        $scope.lease.landlordInfo.copyTo = [];
      }
      if ($scope.lease?.form?.editor?.defaultFields?.landlordInfo?.copyTo && $scope.lease?.form?.editor?.defaultFields?.landlordInfo?.copyTo[0]) {
        $scope.lease.landlordInfo.copyTo.push({
          ...$scope.lease.form.editor.defaultFields.landlordInfo.copyTo[0],
        });
      } else {
        $scope.lease.landlordInfo.copyTo.push({});
      }
    };

    $scope.removeCopyToAddress = index => {
      if (
        !$scope.lease.tenantInfo.copyTo ||
        !$scope.lease.tenantInfo.copyTo[index]
      ) {
        return;
      }
      $scope.lease.tenantInfo.copyTo.splice(index, 1);
    };

    $scope.removeLandlordCopyToAddress = index => {
      if (
        !$scope.lease.landlordInfo.copyTo ||
        !$scope.lease.landlordInfo.copyTo[index]
      ) {
        return;
      }
      $scope.lease.landlordInfo.copyTo.splice(index, 1);
    };

    /*  Add additional Copies End  */

    /*  Add additional Tenant Consent Contingency Start  */

    $scope.showHideConsentContingencies = evt => {
      if ($scope.lease.hasTenantConsentContingency) {
        if (!$scope.lease.consentContingencies) {
          $scope.addTenantConsentContingency();
        }
      }
    };

    $scope.addConsentContingency = () => {
      if (!$scope.lease.consentContingencies) {
        $scope.lease.consentContingencies = [];
      }
      if ($scope.lease.form.editor.defaultFields.consentContingencies && $scope.lease.form.editor.defaultFields.consentContingencies[0]) {
        $scope.lease.consentContingencies.push({
          ...$scope.lease.form.editor.defaultFields.consentContingencies[0],
        });
      } else {
        $scope.lease.consentContingencies.push({});
      }
    };

    $scope.removeConsentContingency = index => {
      if (
        !$scope.lease.consentContingencies ||
        !$scope.lease.consentContingencies[index]
      ) {
        return;
      }
      $scope.lease.consentContingencies.splice(index, 1);
    };

    /*  Add additional Tenant Consent Contingency End  */

    $scope.addSuite = function() {
      if ($scope.lease.form.editor.defaultFields.premiseSuites && $scope.lease.form.editor.defaultFields.premiseSuites[0]) {
        $scope.lease.premiseSuites.push({
          ...$scope.lease.form.editor.defaultFields.premiseSuites[0],
        });
      } else {
        $scope.lease.premiseSuites.push({});
      }
      $scope.setAvailableSpaces();
    };

    $scope.deleteSuite = function(index) {
      $scope.lease.premiseSuites.splice(index, 1);
      $scope.setAvailableSpaces();
    };

    $scope.addFreeRentPeriod = function() {
      if ($scope.lease.form.editor.defaultFields.freeRentPeriods && $scope.lease.form.editor.defaultFields.freeRentPeriods[0]) {
        $scope.lease.freeRentPeriods.push({
          ...$scope.lease.form.editor.defaultFields.freeRentPeriods[0],
        });
      } else {
        $scope.lease.freeRentPeriods.push({});
      }
    };

    $scope.deleteFreeRentPeriod = function(index) {
      $scope.lease.freeRentPeriods.splice(index, 1);
    };

    $scope.addMultipleTermsFreeRentPeriod = function(termIndex) {
      if (!$scope.lease.terms[termIndex].freeRentPeriods) {
        $scope.lease.terms[termIndex].freeRentPeriods = [];
      }
      $scope.lease.terms[termIndex].freeRentPeriods.push({});
    };

    $scope.deleteMultipleTermsFreeRentPeriod = function(termIndex, index) {
      $scope.lease.terms[termIndex].freeRentPeriods.splice(index, 1);
    };

    $scope.addTenant = function() {
      if ($scope.lease.form.editor.defaultFields.tenantInfo.individuals && $scope.lease.form.editor.defaultFields.tenantInfo.individuals[0]) {
        $scope.lease.tenantInfo.individuals.push({
          ...$scope.lease.form.editor.defaultFields.tenantInfo.individuals[0],
        });
      } else {
        $scope.lease.tenantInfo.individuals.push({});
      }
    };

    $scope.addIndividualAssignee = function() {
      if ($scope.lease.form.editor.defaultFields.assigneeInfo.individuals && $scope.lease.form.editor.defaultFields.assigneeInfo.individuals[0]) {
        $scope.lease.assigneeInfo.individuals.push({
          ...$scope.lease.form.editor.defaultFields.assigneeInfo.individuals[0],
        });
      } else {
        $scope.lease.assigneeInfo.individuals.push({});
      }
    };

    $scope.deleteTenant = function(index) {
      $scope.lease.tenantInfo.individuals.splice(index, 1);
    };

    $scope.deleteIndividualAssignee = function(index) {
      $scope.lease.assigneeInfo.individuals.splice(index, 1);
    };

    $scope.deletePriorAssignment = function(index) {
      $scope.lease.priorAssignments.splice(index, 1);
      $scope.lease.priorAssignments.forEach((assignment, index) => assignment._originalIndex = index);
    };

    $scope.setPriorAssignmentList = function() {
      if ($scope.lease.priorAssignments.length === 0) {
        $scope.addPriorAssignment();
      }
    };

    /* The following functions handles the automatic increase renewal */
    $scope.clearHasApplyIncreaseToRenewals = function(lease) {
      if (lease.rentInputType === 'manual' || lease.hasCpiRent) {
        lease.hasApplyIncreaseToRenewals = false;
        $scope.update();
      }
      if (lease.rentInputType === 'manual') {
        $scope.calculateRentPSF(lease);
        $scope.consolidateRentPeriods();
      } else {
        $scope.updateBaseRent();
        $scope.updateAnnualIncrease();
      }
    };

    /* The following copies the tenant notice address to the billing address */
    $scope.setSameBillingAddress = function() {
      if ($scope.lease.hasSameBillingAddress) {
        $scope.lease.tenantInfo.billingAddress1 =
          $scope.lease.tenantInfo.address1;
        $scope.lease.tenantInfo.billingAddressCity =
          $scope.lease.tenantInfo.addressCity;
        $scope.lease.tenantInfo.billingAddressState =
          $scope.lease.tenantInfo.addressState;
        $scope.lease.tenantInfo.billingAddressZip =
          $scope.lease.tenantInfo.addressZip;
        $scope.lease.tenantInfo.billingPhoneNumber =
          $scope.lease.tenantInfo.phoneNumber;
        $scope.lease.tenantInfo.billingEmail =
          $scope.lease.tenantInfo.email;
        $scope.lease.tenantInfo.billingAttention =
          $scope.lease.tenantInfo.attention;
      }
    };

    /* The following copies the address of the first person in a married couple into the second person */
    $scope.setMarriedCoupleSameAddress = function(guarantor) {
      if (guarantor.hasSameAddress) {
        guarantor.address2 = guarantor.address1;
        guarantor.city2 = guarantor.city1;
        guarantor.state2 = guarantor.state1;
        guarantor.zip2 = guarantor.zip1;

        if ($rootScope.hasEditorConfig("guarantors", "copyPhoneNumberForSpouse")) {
          guarantor.phone2 = guarantor.phone1;
        }
        if ($rootScope.hasEditorConfig("guarantors", "copyEmailForSpouse")) {
          guarantor.email2 = guarantor.email1;
        }
      }
    };

    $scope.deleteAbatementDate = function(index) {
      $scope.lease.abatementDates.splice(index, 1);
    };

    $scope.addAbatementDate = function() {
      if ($scope.lease.form.editor.defaultFields.abatementDates && $scope.lease.form.editor.defaultFields.abatementDates[0]) {
        $scope.lease.abatementDates.push({
          date: '',
          ...$scope.lease.form.editor.defaultFields.abatementDates[0],
        });
      } else {
        $scope.lease.abatementDates.push({
          date: '',
        });
      }
    };

    $scope.setAbatementDatesList = function() {
      if ($scope.lease.abatementDates.length === 0) {
        $scope.addAbatementDate();
      }
    };

    $scope.addNamedTenant = function() {
      if ($scope.lease.form.editor.defaultFields.ongoingCoTenancy.namedTenantsList && $scope.lease.form.editor.defaultFields.ongoingCoTenancy.namedTenantsList[0]) {
        $scope.lease.ongoingCoTenancy.namedTenantsList.push({
          ...$scope.lease.form.editor.defaultFields.ongoingCoTenancy.namedTenantsList[0],
        });
      } else {
        $scope.lease.ongoingCoTenancy.namedTenantsList.push({});
      }
    };

    $scope.deleteNamedTenant = function(index) {
      $scope.lease.ongoingCoTenancy.namedTenantsList.splice(index, 1);
    };

    $scope.setNamedTenantsList = function() {
      if ($scope.lease.ongoingCoTenancy.namedTenantsList.length === 0) {
        $scope.addNamedTenant();
      }
    };

    /* The following maintains the validity of 'lease.hasAllGuarantorsRemoved',
     * Meaning, if all guarantors are released, it sets 'true', otherwise, it sets 'false' */
    $scope.updateReleaseGuarantors = function(turnOff, isOldDesign) {
      var individuals = $scope.lease.existingGuarantorInfo.individuals;
      var marriedCouples = $scope.lease.existingGuarantorInfo.marriedCouples;
      var entities = $scope.lease.existingGuarantorInfo.entities;
      var allGuarantors = _.union(individuals, marriedCouples, entities) || [];
      var hasAllGuarantorsRemoved = true;

      var hasReleaseGuarantors = !!_.find(allGuarantors, function(o) {
        return o.hasBeenReleased == true;
      });
      $scope.lease.hasReleaseGuarantors = isOldDesign
        ? $scope.lease.hasReleaseGuarantors
        : hasReleaseGuarantors;

      for (
        var i = 0;
        i < allGuarantors.length && hasAllGuarantorsRemoved;
        i++
      ) {
        // We're turning off all released guarantors if the toggle is turned off
        if (turnOff === false) {
          allGuarantors[i].hasBeenReleased = false;
        }
        if (!allGuarantors[i].hasBeenReleased) {
          hasAllGuarantorsRemoved = false;
        }
      }

      if (allGuarantors.length > 0) {
        $scope.lease.hasAllGuarantorsRemoved = hasAllGuarantorsRemoved;
      }
    };

    $scope.updateReleaseGuarantor = function() {
      $scope.updateReleaseGuarantors();

      setTimeout(function() {
        $scope.structureChanged();
        $scope.highlightItem('lease.hasReleaseGuarantors');
      }, 1);
    };

    $scope.createBreakpointTable = function(config) {
      var result = [];
      var consolidation = [];
      var start, end, breakpoint, period;

      if ($scope.lease.pctRentHasNaturalBreakpoint) {
        // Natural breakpoint
        if ($scope.lease.rentPeriods && $scope.lease.rentPeriods.length > 0) {
          // Add the first term
          start = $scope.formatDateAs(
            moment(
              $scope.lease.existingLeaseExpirationDate,
              'YYYY-MM-DDTHH:mm:ssZ',
            ).add(1, 'day'),
            config.format.date,
          );
          end = $scope.formatDateAs(
            moment(
              $scope.lease.existingLeaseExpirationDate,
              'YYYY-MM-DDTHH:mm:ssZ',
            ).add(1, 'year'),
            config.format.date,
          );
          breakpoint =
            ($scope.lease.rentPeriods[0].rentPerSqFoot *
              $scope.lease.premiseAreaSize) /
            (($scope.lease.existingPercentageRentRate
              ? $scope.lease.existingPercentageRentRate
              : 100) /
              100);
          result.push({
            start: start,
            end: end,
            breakpoint: breakpoint,
          });

          // We skip the first one, the first one is for the lease term itself,
          // We already took care of it above
          for (var i = 1; i < $scope.lease.rentPeriods.length; i++) {
            // Lets consolidate the data
            start = $scope.formatDateAs(
              moment(result[result.length - 1].start, config.format.date).add(
                1,
                'year',
              ),
              config.format.date,
            );
            end = $scope.formatDateAs(
              moment(result[result.length - 1].end, config.format.date).add(
                1,
                'year',
              ),
              config.format.date,
            );
            breakpoint =
              ($scope.lease.rentPeriods[i].rentPerSqFoot *
                $scope.lease.premiseAreaSize) /
              (($scope.lease.existingPercentageRentRate
                ? $scope.lease.existingPercentageRentRate
                : 100) /
                100);
            result.push({
              start: start,
              end: end,
              breakpoint: breakpoint,
            });
          }
        }

        // Now add the renewal years
        if (result.length > 0) {
          if ($scope.lease.renewalInfo && $scope.lease.renewalInfo.length > 0) {
            for (var j = 0; j < $scope.lease.renewalInfo.length; j++) {
              for (
                var k = 0;
                k < $scope.lease.renewalInfo[j].renewalRentPeriods.length;
                k++
              ) {
                period = $scope.lease.renewalInfo[j].renewalRentPeriods[k];
                start = $scope.formatDateAs(
                  moment(
                    result[result.length - 1].start,
                    config.format.date,
                  ).add(1, 'year'),
                  config.format.date,
                );
                end = $scope.formatDateAs(
                  moment(result[result.length - 1].end, config.format.date).add(
                    1,
                    'year',
                  ),
                  config.format.date,
                );
                breakpoint =
                  (period.rentPerSqFoot * $scope.lease.premiseAreaSize) /
                  (($scope.lease.existingPercentageRentRate
                    ? $scope.lease.existingPercentageRentRate
                    : 100) /
                    100);
                result.push({
                  start: start,
                  end: end,
                  breakpoint: breakpoint,
                });
              }
            }
          }

          // Add the first item as an anchor
          consolidation.push(result[0]);
        }
      } else {
        // Unnatural breakpoint
        if ($scope.lease.minimumSales && $scope.lease.minimumSales.length > 0) {
          // Add the first term
          start = $scope.formatDateAs(
            moment(
              $scope.lease.existingLeaseExpirationDate,
              'YYYY-MM-DDTHH:mm:ssZ',
            ).add(1, 'day'),
            config.format.date,
          );
          end = $scope.formatDateAs(
            moment(
              $scope.lease.existingLeaseExpirationDate,
              'YYYY-MM-DDTHH:mm:ssZ',
            ).add(1, 'year'),
            config.format.date,
          );
          result.push({
            start: start,
            end: end,
            breakpoint: $scope.lease.minimumSales[0].value,
          });

          if ($scope.lease.hasRenewalOption) {
            // We skip the first one, the first one is for the lease term itself,
            // We already took care of it above
            for (var i = 1; i < $scope.lease.minimumSales.length; i++) {
              // Lets consolidate the data
              start = $scope.formatDateAs(
                moment(result[result.length - 1].start, config.format.date).add(
                  1,
                  'year',
                ),
                config.format.date,
              );
              end = $scope.formatDateAs(
                moment(result[result.length - 1].end, config.format.date).add(
                  1,
                  'year',
                ),
                config.format.date,
              );
              result.push({
                start: start,
                end: end,
                breakpoint: $scope.lease.minimumSales[i].value,
              });
            }
          }

          // Add the first item as an anchor
          consolidation.push(result[0]);
        }
      }

      if (!config.consolidate) {
        $scope.breakpointTable = result;
      } else {
        for (var j = 1; j < result.length; j++) {
          if (
            result[j].breakpoint ===
            consolidation[consolidation.length - 1].breakpoint
          ) {
            consolidation[consolidation.length - 1].end = result[j].end;
          } else {
            consolidation.push(result[j]);
          }
        }
        $scope.breakpointTable = consolidation;
      }
    };

    var setDeepValue = function(pointer, array, value) {
      if (array.length > 1) {
        var first = array.shift();
        setDeepValue(pointer[first], array, value);
      } else {
        pointer[array[0]] = value;
      }
      return;
    };

    $scope.resetContent = function(
      elementsGroupId,
      resetTo,
      currentElementValue,
      value,
    ) {
      if (currentElementValue == value) {
        document
          .querySelectorAll('#' + elementsGroupId + ' [ng-model]')
          .forEach(function(element) {
            var elem = element
              .getAttribute('ng-model')
              .replace('lease.', '')
              .split('.');
            setDeepValue($scope.lease, elem, resetTo);
          });
      }
    };

    /* ============================= Flexible Rent Bumps ============================= */

    $scope.updateNumberOfRenewalRentBumps = function() {
      if (!$scope.lease.renewalRentBumps) {
        $scope.lease.renewalRentBumps = [];
      }

      if ($scope.lease.renewalRentBumps.length > $scope.lease.renewalCount) {
        var count =
          $scope.lease.renewalRentBumps.length - $scope.lease.renewalCount;
        $scope.lease.renewalRentBumps.splice(-count, count);
      } else if (
        $scope.lease.renewalRentBumps.length < $scope.lease.renewalCount
      ) {
        var count =
          $scope.lease.renewalCount - $scope.lease.renewalRentBumps.length;
        for (let i = 0; i < count; i++) {
          $scope.lease.renewalRentBumps.push({
            periods: [
              {
                begin: 1,
              },
            ],
          });
        }
      }
    };

    $scope.getRentBumpsInMonths = function(list) {
      let rentBumpsInMonths = 0;

      if (list) {
        list.forEach(rentPeriod => {
          if (rentPeriod.duration) {
            rentBumpsInMonths += rentPeriod.duration;
          }
        });
      }

      return rentBumpsInMonths;
    };

    $scope.deleteRentBumpPeriod = function(list, index) {
      if (list) {
        list.splice(index, 1);

        // Invalidate the `begin` field throughout the table
        list.forEach((rentPeriod, index) => {
          if (index === 0) {
            rentPeriod.begin = 1;
          } else {
            rentPeriod.begin = list[index - 1].begin + list[index - 1].duration;
          }
        });
      }
    };

    $scope.addRentBumpPeriod = function(list) {
      if (list) {
        let begin;

        // Calculate the `begin`ing of the new row based on the previous row
        if (list.length > 0) {
          begin = list[list.length - 1].begin + list[list.length - 1].duration;
        }

        list.push({
          begin,
        });
      }
    };
  },
]);
